import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

// ─── ENVIRONMENT ──────────────────────────────────────────────────────────────
// npm run dev  → uses .env.development  (test keys, dev_ Firestore collections)
// npm run prod → uses .env.production   (live keys, real Firestore collections)
const IS_PRODUCTION = process.env.REACT_APP_IS_PRODUCTION === "true";
const APP_ENV = process.env.REACT_APP_ENV || "development";

// Firestore collection router — dev data is fully isolated from production data
// DEV:  dev_users, dev_sessions
// PROD: users, sessions
const col = (name) => IS_PRODUCTION ? name : `dev_${name}`;

// ─── FIREBASE CONFIG ──────────────────────────────────────────────────────────
// Keys are loaded from .env.development or .env.production — never hardcoded
const FIREBASE_CONFIG = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// ─── RAZORPAY KEY ─────────────────────────────────────────────────────────────
// DEV:  rzp_test_... (no real money)
// PROD: rzp_live_... (real payments)
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY || "";

// ─── FREEMIUM MODEL ───────────────────────────────────────────────────────────
// Free tier gives users a permanent taste — enough to get hooked, not enough
// to feel complete. Premium unlocks everything.
const FREE_BREATHING   = ["box", "physiological"];   // Box + Physiological Sigh free
const FREE_MEDITATIONS = ["bodyscan"];                // Body Scan free only
const FREE_EXERCISES   = 3;                           // First 3 exercises per category
const FREE_CHALLENGE_DAYS = 7;                        // Week 1 of 30-day challenge
// Yoga/classes: all locked for free users (shown with lock overlay)
// AI Coach: 3 messages/day for free users

// Helper — checks if a specific feature is accessible
const canAccess = {
  breathing:  (id, isPremium) => isPremium || FREE_BREATHING.includes(id),
  meditation: (id, isPremium) => isPremium || FREE_MEDITATIONS.includes(id),
  yoga:       (isPremium)     => isPremium,
  exercise:   (idx, isPremium)=> isPremium || idx < FREE_EXERCISES,
  challenge:  (day, isPremium)=> isPremium || day <= FREE_CHALLENGE_DAYS,
};

// ─── ADMIN / DEVELOPER ACCESS ─────────────────────────────────────────────────
// Accounts listed here always have full premium — no payment required.
// Add your email(s) here to skip the paywall as the app creator/developer.
const ADMIN_EMAILS = ["kartikpandey6666@gmail.com"];
const isAdminUser = (email) => !!email && ADMIN_EMAILS.includes(email.toLowerCase().trim());

// ─── PROMO CODES ──────────────────────────────────────────────────────────────
// Add codes here to give friends free premium access — no payment required.
// Format: "CODE": "free_premium"
const PROMO_CODES = {
  "MAHADEV24":   "free_premium",
  "CALMFIT2024": "free_premium",
  "HAPPINESS24": "free_premium",
};

// ─── FIREBASE HELPERS ─────────────────────────────────────────────────────────
function getFirebase() {
  if (typeof window === "undefined" || !window.firebase) return null;
  try {
    if (!window.firebase.apps || !window.firebase.apps.length) {
      window.firebase.initializeApp(FIREBASE_CONFIG);
    }
    return window.firebase;
  } catch (e) { return null; }
}
function getDb()   { const fb = getFirebase(); return fb ? fb.firestore() : null; }
function getAuth() { const fb = getFirebase(); return fb ? fb.auth()      : null; }

// Firebase Auth helpers
async function fbSignUp(email, password, name) {
  const auth = getAuth();
  if (!auth) return { error: "Firebase not loaded" };
  try {
    const cred = await auth.createUserWithEmailAndPassword(email, password);
    await cred.user.updateProfile({ displayName: name });
    return { user: cred.user };
  } catch (e) { return { error: e.message }; }
}
async function fbSignIn(email, password) {
  const auth = getAuth();
  if (!auth) return { error: "Firebase not loaded" };
  try {
    const cred = await auth.signInWithEmailAndPassword(email, password);
    return { user: cred.user };
  } catch (e) { return { error: e.message }; }
}
async function fbSignInGoogle() {
  const fb = getFirebase();
  if (!fb) return { error: "Firebase not loaded" };
  try {
    const provider = new fb.auth.GoogleAuthProvider();
    const cred = await fb.auth().signInWithPopup(provider);
    return { user: cred.user };
  } catch (e) { return { error: e.message }; }
}
async function fbResetPassword(email) {
  const auth = getAuth();
  if (!auth) return { error: "Firebase not loaded" };
  try {
    await auth.sendPasswordResetEmail(email.trim());
    return { success: true };
  } catch (e) {
    const msg = e.message.replace("Firebase: ", "").replace(/\(auth\/[^)]+\)\.?/g, "").trim();
    if (msg.includes("user-not-found") || msg.includes("invalid-email"))
      return { error: "No account found with that email address." };
    return { error: msg || "Could not send reset email. Please try again." };
  }
}
async function fbSignOut() {
  const auth = getAuth();
  if (auth) await auth.signOut();
}
async function fbSaveFavorites(userId, favs) {
  const db = getDb();
  if (!db || !userId) return;
  try { await db.collection(col("users")).doc(userId).set({ favorites: favs }, { merge: true }); } catch {}
}
async function fbSaveSchedule(userId, schedule) {
  const db = getDb();
  if (!db || !userId) return;
  try { await db.collection(col("users")).doc(userId).set({ schedule }, { merge: true }); } catch {}
}

async function fbSaveSession(userId, session) {
  const db = getDb();
  if (!db || !userId) return false;
  try {
    await db.collection(col("sessions")).add({ ...session, userId, createdAt: new Date() });
    return true;
  } catch (e) { console.warn("Firebase:", e.message); return false; }
}

async function fbLoadSessions(userId) {
  const db = getDb();
  if (!db || !userId) return null;
  try {
    const snap = await db.collection(col("sessions"))
      .where("userId", "==", userId)
      .orderBy("createdAt", "desc")
      .limit(100)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data(), date: d.data().createdAt?.toDate?.()?.toISOString() || d.data().date }));
  } catch (e) { console.warn("Firebase load:", e.message); return null; }
}

async function fbSaveUser(userId, data) {
  const db = getDb();
  if (!db || !userId) return false;
  try {
    await db.collection(col("users")).doc(userId).set(data, { merge: true });
    return true;
  } catch (e) { console.warn("Firebase user:", e.message); return false; }
}

// ─── LOCAL STORAGE HOOK (offline fallback) ────────────────────────────────────
function useLocalStorage(key, initial) {
  const [val, setVal] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; }
    catch { return initial; }
  });
  const set = useCallback(v => {
    setVal(prev => {
      const next = typeof v === "function" ? v(prev) : v;
      try { localStorage.setItem(key, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [key]);
  return [val, set];
}

// ─── FIREBASE PREMIUM STATUS ──────────────────────────────────────────────────
async function fbSavePremiumStatus(userId, paymentId) {
  const db = getDb();
  if (!db || !userId) return;
  try {
    const premiumDate   = new Date().toISOString();
    const premiumExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days
    await db.collection(col("users")).doc(userId).set({
      isPremium: true,
      premiumDate,
      premiumExpiry,
      razorpayPaymentId: paymentId || "",
    }, { merge: true });
  } catch (e) { console.warn("fbSavePremiumStatus:", e.message); }
}
async function fbLoadPremiumStatus(userId) {
  const db = getDb();
  if (!db || !userId) return false;
  try {
    const doc = await db.collection(col("users")).doc(userId).get();
    if (!doc.exists || !doc.data()?.isPremium) return false;
    const expiry = doc.data()?.premiumExpiry;
    // Legacy purchases (before expiry field was added) are treated as valid
    if (!expiry) return true;
    const isValid = new Date(expiry) > new Date();
    // Auto-clear expired flag in Firestore so it stays clean
    if (!isValid) {
      try { await db.collection(col("users")).doc(userId).set({ isPremium: false }, { merge: true }); } catch (_) {}
    }
    return isValid;
  } catch { return false; }
}

// ─── FIREBASE ANALYTICS ───────────────────────────────────────────────────────
function logEvent(eventName, params = {}) {
  try {
    const fb = getFirebase();
    if (fb && fb.analytics) {
      fb.analytics().logEvent(eventName, { ...params, platform: "web", app: "calmfit" });
    }
    // Always also push to dataLayer for GTM compatibility
    if (window.dataLayer) window.dataLayer.push({ event: eventName, ...params });
  } catch (_) {}
}

// ─── RAZORPAY PAYMENT ─────────────────────────────────────────────────────────
function openRazorpay({ planLabel, amount, user, onSuccess, onFail }) {
  if (!window.Razorpay) {
    alert("Payment gateway loading… Please try again in a moment.");
    onFail && onFail();
    return;
  }
  if (!RAZORPAY_KEY || RAZORPAY_KEY.trim() === "") {
    alert("⚙️ Razorpay key not configured.\n\nAdd your key to the .env file:\nREACT_APP_RAZORPAY_KEY=rzp_test_XXXXXXXX\n\nSee the setup guide below for details.");
    onFail && onFail();
    return;
  }
  const options = {
    key: RAZORPAY_KEY,
    amount: amount * 100, // paise
    currency: "INR",
    name: "CalmFit",
    description: `CalmFit ${planLabel} Subscription`,
    image: "%PUBLIC_URL%/logo192.png",
    handler: function(response) {
      onSuccess && onSuccess(response);
    },
    prefill: { name: user?.name || "CalmFit User", email: user?.email || "" },
    notes: { plan: planLabel },
    theme: { color: "#4A6B55" },
    modal: { ondismiss: function() { onFail && onFail(); } },
  };
  try {
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", function() { onFail && onFail(); });
    rzp.open();
  } catch(e) {
    console.error("Razorpay error:", e);
    alert("Could not open payment. Please check your Razorpay key is valid and try again.");
    onFail && onFail();
  }
}

// ─── SESSION HELPERS ──────────────────────────────────────────────────────────
function makeSession({ type, title, category, emoji, calories, duration }) {
  return {
    id: Date.now().toString(),
    type,           // "workout" | "breathing" | "yoga"
    title,          // exercise/class name
    category,       // muscle group or breathing type
    emoji: emoji || "💪",
    calories: calories || 0,
    duration: duration || "—",
    date: new Date().toISOString(),
  };
}

function formatSessionDate(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 86400) {
    const hh = d.getHours().toString().padStart(2, "0");
    const mm = d.getMinutes().toString().padStart(2, "0");
    return `Today ${hh}:${mm}`;
  }
  if (diff < 172800) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

// ─── DESIGN SYSTEM — Headspace × CRED dark ────────────────────────────────
const FONT = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const COLORS = {
  // Backgrounds
  bg:       "#0C0B09",
  bgWarm:   "rgba(14,12,6,0.94)",
  card:     "rgba(22,18,11,0.82)",
  card2:    "rgba(30,25,14,0.80)",
  surface:  "rgba(36,30,16,0.76)",
  border:   "rgba(255,255,255,0.09)",
  borderWarm: "rgba(255,200,100,0.14)",
  // Text
  white:    "#FFFFFF",
  text:     "#F0EBE0",
  muted:    "rgba(240,235,224,0.45)",
  subtle:   "rgba(240,235,224,0.22)",
  // Brand accents (warm, Headspace-inspired)
  sage:     "#7FB89A",      // primary green
  sageLight:"#A8D4BC",
  sageDim:  "rgba(127,184,154,0.15)",
  coral:    "#E8714A",      // Headspace-like warm coral
  coralDim: "rgba(232,113,74,0.15)",
  gold:     "#D4A853",
  goldDim:  "rgba(212,168,83,0.15)",
  lavender: "#9B8EC5",
  lavDim:   "rgba(155,142,197,0.15)",
  blue:     "#5BA3D9",
  blueDim:  "rgba(91,163,217,0.15)",
  orange:   "#E8935D",
  red:      "#E85D75",
  // Legacy aliases
  cream: "#F5F0E8", deepSage: "#4A6B55",
  warmWhite: "#0C0B09", charcoal: "#F0EBE0",
  softPink: "#E8D5CC", purple: "#8B5DE8",
};

// ─── MEDITATION TECHNIQUES ────────────────────────────────────────────────────
const meditationTechniques = [
  {
    id: "mindfulness",
    name: "Mindfulness Meditation",
    subtitle: "Jon Kabat-Zinn's Method",
    emoji: "🌿",
    color: "#4A9B7F",
    bg: "#050F0A",
    duration: 10,
    benefit: "🧠 Clarity & Presence",
    description: "Observe your thoughts like clouds passing — no judgment. The most researched and accessible form of meditation.",
    steps: [
      { time: 60,  title: "Arrive",          instruction: "Close your eyes. Feel the weight of your body. Take 3 deep breaths and let go." },
      { time: 120, title: "Anchor",           instruction: "Bring attention to your breath at the nostrils. Feel each inhale and exhale." },
      { time: 120, title: "Notice",           instruction: "When a thought arises, simply note 'thinking' and gently return to the breath." },
      { time: 120, title: "Expand",           instruction: "Widen awareness to sounds, sensations, the room around you." },
      { time: 120, title: "Open Awareness",   instruction: "Rest in pure awareness itself. No object needed. Just being." },
      { time: 60,  title: "Return",           instruction: "Gently wiggle fingers, deepen breath. Carry this stillness into your day." },
    ],
  },
  {
    id: "bodyscan",
    name: "Body Scan",
    subtitle: "MBSR Deep Relaxation",
    emoji: "🫀",
    color: "#7B5EA7",
    bg: "#0A0512",
    duration: 15,
    benefit: "😴 Deep Rest & Release",
    description: "Systematic attention through every part of the body. Clinically proven to reduce chronic pain, anxiety and insomnia.",
    steps: [
      { time: 60,  title: "Settle",           instruction: "Lie down or sit comfortably. Close your eyes. Soften your jaw and shoulders." },
      { time: 120, title: "Feet & Legs",      instruction: "Feel your toes, soles, heels, ankles. Notice any warmth, tingling or tension. Breathe into it." },
      { time: 120, title: "Hips & Belly",     instruction: "Let your hips be heavy. Feel your belly rise and fall with each breath. Release holding." },
      { time: 120, title: "Chest & Heart",    instruction: "Place a hand on your heart. Feel it beating. Breathe love and warmth into your chest." },
      { time: 120, title: "Shoulders & Arms", instruction: "Let shoulders melt down. Feel arms heavy. Relax hands, fingers, fingertips." },
      { time: 120, title: "Face & Head",      instruction: "Soften forehead, eyes, cheeks, jaw. Let your whole skull be heavy and still." },
      { time: 60,  title: "Whole Body",       instruction: "Feel your body as one unified field of awareness. Perfect and complete as it is." },
    ],
  },
  {
    id: "lovingkindness",
    name: "Loving-Kindness",
    subtitle: "Buddhist Metta Practice",
    emoji: "💜",
    color: "#D45B75",
    bg: "#140308",
    duration: 12,
    benefit: "❤️ Compassion & Joy",
    description: "Metta (Pāli: loving-kindness) systematically cultivates unconditional love toward yourself and all beings.",
    steps: [
      { time: 60,  title: "Centre",           instruction: "Sit comfortably. Place a hand on your heart. Breathe warmly into your chest." },
      { time: 120, title: "Self",             instruction: "Silently repeat: 'May I be happy. May I be healthy. May I be safe. May I live with ease.'" },
      { time: 120, title: "Loved One",        instruction: "Picture someone you love. Offer them: 'May you be happy. May you be healthy. May you be safe.'" },
      { time: 120, title: "Neutral Person",   instruction: "Think of someone you barely know. Extend the same wishes. Feel warmth flowing outward." },
      { time: 120, title: "Difficult Person", instruction: "Call to mind someone challenging. Without needing to forgive, offer them: 'May you be happy.'" },
      { time: 60,  title: "All Beings",       instruction: "Expand love infinitely in all directions. 'May all beings everywhere be happy and free.'" },
    ],
  },
  {
    id: "mantra",
    name: "Mantra Meditation",
    subtitle: "Transcendental Style",
    emoji: "🕉️",
    color: "#E07840",
    bg: "#120804",
    duration: 20,
    benefit: "⚡ Deep Clarity & Focus",
    description: "Silently repeating a sacred sound dissolves surface mental activity and accesses deeper states of consciousness.",
    steps: [
      { time: 60,  title: "Prepare",          instruction: "Sit upright, eyes closed. Let your body breathe naturally. No effort." },
      { time: 180, title: "Begin Mantra",     instruction: "Silently repeat 'SO HUM' (I am that). Inhale 'SO'... exhale 'HUM'. Effortlessly." },
      { time: 180, title: "Deepen",           instruction: "Let the mantra become fainter. If thoughts arise, gently return to 'So... Hum...'." },
      { time: 240, title: "Rest",             instruction: "The mantra may dissolve. Rest in the silence between repetitions. This is the goal." },
      { time: 180, title: "Return",           instruction: "Let 'So Hum' return naturally. Don't force it. Float between mantra and silence." },
      { time: 60,  title: "Emerge Slowly",   instruction: "Stop repeating. Sit quietly for 2 more minutes before opening eyes. Very slowly." },
    ],
  },
  {
    id: "vipassana",
    name: "Vipassana",
    subtitle: "Insight Meditation · S.N. Goenka",
    emoji: "🔍",
    color: "#5B9BD5",
    bg: "#030C14",
    duration: 15,
    benefit: "🌊 Deep Insight",
    description: "The oldest Buddhist meditation technique. Observe impermanence directly through body sensations to dissolve conditioned reactions.",
    steps: [
      { time: 60,  title: "Equanimity",       instruction: "Sit perfectly still. Resolve not to move. Observe the body as it is — not as you wish it." },
      { time: 120, title: "Top of Head",      instruction: "Focus on the crown. Notice any sensation — tingling, pressure, warmth, or nothing. Be equanimous." },
      { time: 120, title: "Scan Downward",    instruction: "Move awareness slowly: forehead → face → throat → chest. Observe. Don't react. Just note." },
      { time: 120, title: "Abdomen & Back",   instruction: "Feel sensations through the belly, sides, spine. Everything arises and passes. Anicca — impermanence." },
      { time: 120, title: "Legs to Feet",     instruction: "Move awareness to thighs, knees, calves, feet. Feel the floor. Observe everything changing." },
      { time: 120, title: "Reverse Scan",     instruction: "Sweep upward from feet to crown. Continuous flow of awareness. No craving, no aversion." },
      { time: 60,  title: "Metta Close",      instruction: "Generate goodwill: 'May all beings be happy, be liberated.' Share your merits with all." },
    ],
  },
  {
    id: "visualization",
    name: "Guided Visualization",
    subtitle: "Inner Cinema Method",
    emoji: "🌅",
    color: "#C4A055",
    bg: "#100C02",
    duration: 15,
    benefit: "🎬 Rewire Mind & Mood",
    description: "Your brain cannot distinguish between vivid imagination and reality. Use this to rehearse peace, confidence, and joy.",
    steps: [
      { time: 60,  title: "Settle In",        instruction: "Close your eyes. Let your jaw soften. Feel gravity pulling you into the surface beneath you." },
      { time: 90,  title: "Breath Anchor",     instruction: "Take three long, deep breaths. With each exhale, let mental noise fade like static turning off." },
      { time: 90,  title: "Descend",           instruction: "Imagine a staircase of 10 steps going down into deep relaxation. With each breath, you descend one step." },
      { time: 120, title: "Your Cinema",       instruction: "A screen appears before you. On it: your ideal peaceful scene. Choose — ocean, forest, mountain meadow, warm room." },
      { time: 90,  title: "Step Inside",       instruction: "You move through the screen and into the scene. Feel the ground under your feet. You are here now." },
      { time: 90,  title: "Vivid Details",     instruction: "Look at the colours — how bright, how soft. Watch the light move. Take everything in slowly." },
      { time: 90,  title: "Sounds & Smell",    instruction: "What do you hear? Waves? Wind? Birdsong? Is there a scent — salt, pine, flowers? Breathe it in deeply." },
      { time: 90,  title: "Body Sensation",    instruction: "Feel the warmth or coolness of the air. A gentle breeze on your skin. Your body is perfectly at ease." },
      { time: 60,  title: "Gratitude Here",    instruction: "From this peaceful place, let gratitude arise. Think of one thing you're truly grateful for. Feel it in your chest." },
      { time: 60,  title: "Return Gently",     instruction: "Slowly become aware of your breath, your body, this room. Carry the calm with you as you open your eyes." },
    ],
  },
  {
    id: "yoganidra",
    name: "Yoga Nidra",
    subtitle: "Yogic Sleep · Ancient Tantric",
    emoji: "🌙",
    color: "#6B7FD4",
    bg: "#04051A",
    duration: 20,
    benefit: "💤 Deep Sleep & Restoration",
    description: "Known as 'yogic sleep' — a state between waking and sleeping that is 4x more restorative than regular sleep. Practiced lying down.",
    steps: [
      { time: 60,  title: "Shavasana",        instruction: "Lie completely flat. Arms slightly away from body, palms up. Let your eyes close. Don't try to sleep — stay aware." },
      { time: 90,  title: "Sankalpa",         instruction: "Set a short, positive intention. 'I am peaceful.' 'I am healthy.' Feel it as already true. Plant it like a seed." },
      { time: 120, title: "Rotation of Consciousness", instruction: "Move awareness rapidly: right thumb... index finger... middle finger... ring finger... little finger... palm... back of hand... wrist..." },
      { time: 120, title: "Continue Rotation", instruction: "...elbow... upper arm... shoulder... armpit... right side of chest... right side of belly... right hip... thigh... knee... calf... heel... sole... right big toe..." },
      { time: 120, title: "Left Side",        instruction: "Left thumb... fingers... palm... wrist... elbow... shoulder... left chest... belly... hip... thigh... knee... calf... sole... left big toe..." },
      { time: 90,  title: "Pairs of Opposites", instruction: "Feel heaviness... now lightness. Feel warmth... now cold. Feel pain... now pleasure. Don't think — just feel each sensation instantly." },
      { time: 120, title: "Visualisation",    instruction: "A candle flame in darkness... a golden sunrise... a calm lake reflecting stars... a lotus opening petal by petal... a full moon..." },
      { time: 90,  title: "Sankalpa Again",   instruction: "Return to your intention. Repeat it mentally 3 times with full feeling and conviction. It is sinking into the subconscious now." },
      { time: 60,  title: "Externalise",      instruction: "Slowly become aware of your body on the ground. The room around you. Your breath deepening. Begin to feel your fingers and toes." },
      { time: 30,  title: "Return",           instruction: "When you are ready, gently roll to your right side. Lie there for a moment before slowly sitting up. Carry this stillness with you." },
    ],
  },
  {
    id: "chakra",
    name: "Chakra Meditation",
    subtitle: "7 Energy Centers",
    emoji: "🔮",
    color: "#9B59B6",
    bg: "#0A0315",
    duration: 14,
    benefit: "⚡ Energy & Balance",
    description: "Systematically activate and balance the 7 chakras — energy centers along the spine — through breath, colour visualization and sound.",
    steps: [
      { time: 60,  title: "Ground",           instruction: "Sit cross-legged. Spine tall, hands on knees. Close eyes. Feel a cord of energy from your tailbone down into the earth." },
      { time: 90,  title: "Muladhara · Root", instruction: "At the base of your spine — visualise a spinning red wheel of light. Breathe into it. Feel: I am safe. I am grounded. I belong." },
      { time: 90,  title: "Svadhisthana · Sacral", instruction: "Just below the navel — a warm orange glow spinning. Breathe in creativity and flow. Feel: I am creative. I feel deeply. I am free." },
      { time: 90,  title: "Manipura · Solar Plexus", instruction: "At your solar plexus — a bright yellow sun spinning powerfully. Breathe in confidence. Feel: I am strong. I trust myself. I have power." },
      { time: 90,  title: "Anahata · Heart",  instruction: "At your heart — a radiant green light, expanding with each breath. Feel: I love and am loved. I am open. I forgive freely." },
      { time: 90,  title: "Vishuddha · Throat", instruction: "At your throat — a clear blue light, vibrating gently. Breathe into your voice. Feel: I speak my truth. I express clearly. I am heard." },
      { time: 90,  title: "Ajna · Third Eye", instruction: "Between your eyebrows — an indigo point of light, still and deep. Feel intuition awakening. Feel: I see clearly. I trust my wisdom." },
      { time: 90,  title: "Sahasrara · Crown", instruction: "At the top of your head — a violet and white lotus opening upward to the universe. Feel: I am connected. I am pure consciousness. I am whole." },
      { time: 60,  title: "Full Column",      instruction: "Feel all 7 centres glowing simultaneously — a column of rainbow light from earth to sky running through you. Breathe in this wholeness." },
      { time: 60,  title: "Seal",             instruction: "Bring hands to heart in Namaste. 'The light in me honours the light in all beings.' Slowly open your eyes." },
    ],
  },
  {
    id: "tonglen",
    name: "Tonglen",
    subtitle: "Tibetan Give & Take Practice",
    emoji: "🌀",
    color: "#E07896",
    bg: "#140209",
    duration: 12,
    benefit: "💗 Radical Compassion",
    description: "Tonglen (Tibetan: 'sending and taking') reverses our instinct to avoid suffering. Breathe in pain, breathe out relief. Transforms the heart.",
    steps: [
      { time: 60,  title: "Flash of Openness", instruction: "Begin with one moment of complete openness — no agenda, no story. Let your mind be like the sky: vast and still." },
      { time: 90,  title: "Texture",           instruction: "Breathe in: imagine breathing in hot, heavy, dark smoke. Breathe out: breathe out cool, light, refreshing air. Get the texture of each." },
      { time: 120, title: "Your Own Pain",     instruction: "Call to mind your own suffering right now — physical pain, anxiety, sadness. Breathe it in as dark smoke. Breathe out relief and light." },
      { time: 120, title: "Someone You Love",  instruction: "Think of someone close who is suffering. With each inhale, breathe in their pain. With each exhale, send them peace, joy, ease." },
      { time: 90,  title: "All Who Suffer Like You", instruction: "Think of all people everywhere suffering as you are right now. You are not alone. Breathe in all that pain. Breathe out freedom to all." },
      { time: 90,  title: "All Beings",        instruction: "Expand to all beings in all directions. Breathe in all suffering in the universe. Breathe out limitless love and spaciousness." },
      { time: 60,  title: "Rest",              instruction: "Let the practice dissolve. Sit in open awareness. Notice the quality of your heart right now. What has shifted?" },
      { time: 60,  title: "Dedication",        instruction: "Dedicate the merit: 'May whatever good has come from this practice benefit all beings everywhere without exception.' Namaste." },
    ],
  },
  {
    id: "peacefulplace",
    name: "Peaceful Place",
    subtitle: "Body Scan + Sanctuary",
    emoji: "🏝️",
    color: "#4AABB5",
    bg: "#020D10",
    duration: 18,
    benefit: "🌊 Profound Rest & Safety",
    description: "A therapeutic technique combining progressive body release with a vivid inner sanctuary. Used in trauma therapy and deep relaxation.",
    steps: [
      { time: 60,  title: "Arrive",           instruction: "Close your eyes. Place one hand on your belly, one on your chest. Just breathe. There's nowhere to be." },
      { time: 90,  title: "Release Face",     instruction: "Unclench your jaw. Soften your forehead. Let your tongue rest gently. Allow your eyes to sink back in their sockets." },
      { time: 90,  title: "Release Neck & Shoulders", instruction: "Roll your shoulders back slightly and let them drop. Feel the weight of your arms. Let your neck lengthen." },
      { time: 90,  title: "Release Chest",    instruction: "As you exhale, let your chest soften. No effort needed to breathe — your body knows exactly what to do." },
      { time: 90,  title: "Release Belly",    instruction: "Let your belly be completely soft — no holding in. Feel it rise with each inhale and fall with each exhale." },
      { time: 90,  title: "Release Legs",     instruction: "Let your hips be heavy. Thighs melt. Knees soft. Calves and feet completely loose and relaxed." },
      { time: 60,  title: "Whole Body Scan",  instruction: "Now sweep from head to toe. If you find any tension, breathe into it and let it dissolve on the exhale." },
      { time: 90,  title: "Find Your Place",  instruction: "Now, with your relaxed body, imagine a place where you feel completely safe and at peace. Let it appear naturally." },
      { time: 90,  title: "Enter & Explore",  instruction: "Step into this sanctuary. Notice the light, the colours, the textures. Your nervous system is drinking in safety." },
      { time: 90,  title: "All Five Senses",  instruction: "What do you see, hear, smell, feel? Touch something in this place — feel its texture. Taste the clean air." },
      { time: 90,  title: "Your Safe Self",   instruction: "In this place, you are free. No roles, no worries. Just you, peaceful and whole. Rest deeply here." },
      { time: 60,  title: "Seal It In",       instruction: "Take three deep breaths of this sacred air. Know you can return here anytime you close your eyes and breathe." },
    ],
  },
];

// ─── BREATHING TECHNIQUES ────────────────────────────────────────────────────
const breathingTechniques = [
  {
    id: "box", name: "Box Breathing", subtitle: "Navy SEAL Focus Technique",
    emoji: "⬛", color: "#4A9B7F", bg: "#0A1F18",
    description: "Equal counts create mental clarity and calm. Used by Navy SEALs before high-stress situations.",
    benefit: "🎯 Focus & Calm",
    phases: [
      { name: "Inhale",  duration: 4, type: "in",   color: "#6BCBA8", instruction: "Breathe in slowly through your nose" },
      { name: "Hold",    duration: 4, type: "hold",  color: "#F0D080", instruction: "Hold still... stay soft and relaxed" },
      { name: "Exhale",  duration: 4, type: "out",   color: "#80B8D8", instruction: "Release slowly through your mouth" },
      { name: "Hold",    duration: 4, type: "hold",  color: "#F0D080", instruction: "Empty hold... wait gently" },
    ],
  },
  {
    id: "478", name: "4-7-8 Breathing", subtitle: "Dr. Weil's Sleep Cure",
    emoji: "🌙", color: "#7B5EA7", bg: "#0D0818",
    description: "The long hold and extended exhale powerfully activates your parasympathetic nervous system. Perfect before sleep.",
    benefit: "😴 Sleep & Anxiety",
    phases: [
      { name: "Inhale",  duration: 4, type: "in",   color: "#A78BCA", instruction: "Breathe in quietly through your nose" },
      { name: "Hold",    duration: 7, type: "hold",  color: "#F0D080", instruction: "Hold completely still..." },
      { name: "Exhale",  duration: 8, type: "out",   color: "#7BA8C8", instruction: "Exhale fully through your mouth" },
    ],
  },
  {
    id: "physiological", name: "Physiological Sigh", subtitle: "Stanford's Fastest Reset",
    emoji: "💨", color: "#5B9BD5", bg: "#050E1A",
    description: "Double inhale fully inflates collapsed air sacs. Stanford research: just 1–2 cycles rapidly reduces stress.",
    benefit: "⚡ Instant Relief",
    phases: [
      { name: "Inhale",     duration: 2, type: "in",  color: "#6BB5E8", instruction: "Big inhale through your nose" },
      { name: "Top up",     duration: 1, type: "in",  color: "#4A9BE0", instruction: "Short second sniff — top up your lungs!" },
      { name: "Long Exhale",duration: 7, type: "out", color: "#80C8E8", instruction: "Slow complete exhale through mouth" },
    ],
  },
  {
    id: "coherence", name: "Heart Coherence", subtitle: "HeartMath Protocol",
    emoji: "❤️", color: "#D45B75", bg: "#180408",
    description: "Equal 5-second cycles synchronize heart rate and breathing. Reduces cortisol, boosts calm focus.",
    benefit: "❤️ Heart-Brain Sync",
    phases: [
      { name: "Inhale",  duration: 5, type: "in",  color: "#E8809A", instruction: "Breathe in gently, feel your heart" },
      { name: "Exhale",  duration: 5, type: "out", color: "#C0A8D8", instruction: "Breathe out fully, feel peace" },
    ],
  },
  {
    id: "calm", name: "Calm Breathing", subtitle: "Gentle Anxiety Relief",
    emoji: "🌊", color: "#C4A055", bg: "#150E03",
    description: "The longer exhale activates rest & digest. Great for daily anxiety relief anytime.",
    benefit: "🌿 Gentle Calm",
    phases: [
      { name: "Inhale",  duration: 4, type: "in",   color: "#D8B870", instruction: "Breathe in softly through your nose" },
      { name: "Hold",    duration: 4, type: "hold",  color: "#F0D080", instruction: "Gentle hold — stay relaxed" },
      { name: "Exhale",  duration: 6, type: "out",   color: "#90C8B0", instruction: "Long slow exhale through your mouth" },
    ],
  },
  {
    id: "energize", name: "Energizing Breath", subtitle: "Morning Activation",
    emoji: "⚡", color: "#E07840", bg: "#180804",
    description: "Short sharp ratio wakes your sympathetic system for alertness. Perfect morning ritual.",
    benefit: "🔥 Energy Boost",
    phases: [
      { name: "Inhale",  duration: 2, type: "in",   color: "#F09860", instruction: "Sharp inhale through your nose!" },
      { name: "Hold",    duration: 2, type: "hold",  color: "#F0C860", instruction: "Brief hold — feel the energy" },
      { name: "Exhale",  duration: 4, type: "out",   color: "#90D8B0", instruction: "Controlled exhale through mouth" },
    ],
  },
  {
    id: "nadi", name: "Nadi Shodhana", subtitle: "Alternate Nostril · Yoga Classic",
    emoji: "🌬️", color: "#7ECBA1", bg: "#04120C",
    description: "The most powerful balancing breath in yoga. Alternates airflow between nostrils to balance left and right brain hemispheres.",
    benefit: "☯️ Balance & Clarity",
    phases: [
      { name: "Left Inhale",  duration: 4, type: "in",   color: "#7ECBA1", instruction: "Close right nostril with thumb. Inhale slowly through left nostril" },
      { name: "Hold",         duration: 4, type: "hold",  color: "#F0D080", instruction: "Close both nostrils. Hold gently" },
      { name: "Right Exhale", duration: 4, type: "out",   color: "#80C8E8", instruction: "Release right nostril. Exhale completely through right side" },
      { name: "Right Inhale", duration: 4, type: "in",   color: "#7ECBA1", instruction: "Inhale through right nostril — keep left closed" },
      { name: "Hold",         duration: 4, type: "hold",  color: "#F0D080", instruction: "Close both nostrils. Hold gently" },
      { name: "Left Exhale",  duration: 4, type: "out",   color: "#80C8E8", instruction: "Release left nostril. Exhale fully through left side. One cycle complete." },
    ],
  },
  {
    id: "wimhof", name: "Wim Hof Method", subtitle: "Ice Man's Power Breath",
    emoji: "❄️", color: "#5BC8E8", bg: "#021018",
    description: "30 deep power breaths followed by a retention. Alkalises blood, boosts adrenaline, and creates profound stillness. Practice seated.",
    benefit: "🧊 Power & Immunity",
    phases: [
      { name: "Power Inhale", duration: 2, type: "in",   color: "#5BC8E8", instruction: "Full deep inhale through mouth — fill your lungs completely" },
      { name: "Release",      duration: 2, type: "out",   color: "#90D8F0", instruction: "Let go passively — no forced exhale, just release" },
      { name: "Power Inhale", duration: 2, type: "in",   color: "#5BC8E8", instruction: "Again — power inhale, fully in" },
      { name: "Release",      duration: 2, type: "out",   color: "#90D8F0", instruction: "Passive release... keep going, 30 total cycles" },
      { name: "Retention",    duration: 30, type: "hold", color: "#F0F0C8", instruction: "Exhale all air and hold empty. Feel the tingling, the stillness. Stay as long as comfortable." },
      { name: "Recovery",     duration: 15, type: "in",   color: "#A8E8D8", instruction: "Take one deep breath in and hold for 15 seconds. Feel your body re-oxygenate. Release slowly." },
    ],
  },
  {
    id: "resonance", name: "Resonance Breathing", subtitle: "Optimal HRV Protocol",
    emoji: "〰️", color: "#B088D8", bg: "#0C0618",
    description: "5.5 seconds in, 5.5 out — exactly 5.5 breaths per minute. Scientifically shown to maximise heart rate variability and calm.",
    benefit: "🫀 Heart & Calm",
    phases: [
      { name: "Inhale",  duration: 6, type: "in",  color: "#B088D8", instruction: "Inhale smoothly through nose for 5.5 seconds — slow and steady" },
      { name: "Exhale",  duration: 6, type: "out", color: "#88B8D8", instruction: "Exhale gently for 5.5 seconds — let it flow out completely" },
    ],
  },
  {
    id: "kapalbhati", name: "Kapalbhati", subtitle: "Skull-Shining Pranayama",
    emoji: "✨", color: "#FF8C42", bg: "#1A0A02",
    description: "'Kapala' means skull, 'bhati' means shining. Rapid forceful exhales with passive inhales cleanse the respiratory tract, stimulate the digestive system and fire up mental clarity.",
    benefit: "✨ Detox & Energy",
    phases: [
      { name: "Pump ×5",  duration: 5, type: "out",  color: "#FF8C42", instruction: "Rapid belly pumps — 5 sharp exhales through nose. Pull belly in with each breath out." },
      { name: "Inhale",   duration: 3, type: "in",   color: "#FFB880", instruction: "Deep slow inhale through nose — fill your lungs completely" },
      { name: "Pump ×5",  duration: 5, type: "out",  color: "#FF8C42", instruction: "5 more rapid pumps — stay rhythmic, keep the energy going!" },
      { name: "Inhale",   duration: 3, type: "in",   color: "#FFB880", instruction: "Deep recovery inhale — let belly expand fully" },
      { name: "Pump ×5",  duration: 5, type: "out",  color: "#FF8C42", instruction: "Final 5 pumps — strong and steady, belly contracts on each exhale" },
      { name: "Retain",   duration: 15, type: "hold", color: "#F0C860", instruction: "Hold breath after exhale — chin gently tucked. Feel warmth and stillness rising." },
      { name: "Recovery", duration: 8,  type: "in",   color: "#90D8B0", instruction: "Slow deep breath in — hold a moment, then release. One round complete." },
    ],
  },
];

// ─── CONDITION-BASED YOGA RECOMMENDATIONS ─────────────────────────────────────
const CONDITION_RECOMMENDATIONS = {
  "Hypothyroidism": {
    color: "#5B9BD5",
    icon: "moon",
    headline: "Thyroid-Supportive Yoga",
    description: "These poses stimulate the thyroid gland, boost metabolism and fight fatigue — ideal for hypothyroidism.",
    poses: [
      { name: "Sarvangasana", sanskrit: "Shoulder Stand", duration: "1–2 min", benefit: "Directly stimulates thyroid gland through chin lock", icon: "yoga", catId: "yoga" },
      { name: "Halasana", sanskrit: "Plow Pose", duration: "1 min", benefit: "Compresses throat, enhancing thyroid blood flow", icon: "yoga", catId: "yoga" },
      { name: "Matsyasana", sanskrit: "Fish Pose", duration: "30–60 sec", benefit: "Stretches throat, counter-pose to shoulder stand", icon: "yoga", catId: "yoga" },
      { name: "Setu Bandhasana", sanskrit: "Bridge Pose", duration: "1–2 min", benefit: "Gentle throat stimulation, boosts energy levels", icon: "yoga", catId: "yoga" },
      { name: "Ustrasana", sanskrit: "Camel Pose", duration: "30–45 sec", benefit: "Opens throat chakra, counters fatigue and sluggishness", icon: "yoga", catId: "yoga" },
    ]
  },
  "Hyperthyroidism": {
    color: "#C4A055",
    icon: "bolt",
    headline: "Calming Thyroid Yoga",
    description: "Gentle, cooling poses to reduce overactivity. Avoid inversions — focus on calming the nervous system.",
    poses: [
      { name: "Shavasana", sanskrit: "Corpse Pose", duration: "10 min", benefit: "Deep rest, reduces cortisol and calms overactive thyroid", icon: "yoga", catId: "yoga" },
      { name: "Balasana", sanskrit: "Child's Pose", duration: "2–3 min", benefit: "Activates parasympathetic system, eases anxiety & heat", icon: "yoga", catId: "yoga" },
      { name: "Viparita Karani", sanskrit: "Legs Up the Wall", duration: "5–10 min", benefit: "Gentle inversion, reduces stress hormones safely", icon: "yoga", catId: "yoga" },
      { name: "Supta Baddha Konasana", sanskrit: "Reclined Butterfly", duration: "3–5 min", benefit: "Opens chest gently, promotes stillness and calm", icon: "yoga", catId: "yoga" },
      { name: "Nadi Shodhana", sanskrit: "Alternate Nostril Breathing", duration: "5 min", benefit: "Balances nervous system, directly calms thyroid over-stimulation", icon: "wind", screen: "breathe" },
    ]
  },
  "Migraine": {
    color: "#7B5EA7",
    icon: "stress",
    headline: "Migraine Relief Yoga",
    description: "Gentle poses that reduce tension in neck and shoulders, improve circulation and reduce migraine triggers.",
    poses: [
      { name: "Balasana", sanskrit: "Child's Pose", duration: "3–5 min", benefit: "Relieves tension in head, neck and shoulders instantly", icon: "yoga", catId: "yoga" },
      { name: "Setu Bandhasana", sanskrit: "Bridge Pose", duration: "1–2 min", benefit: "Improves circulation to brain, reduces migraine intensity", icon: "yoga", catId: "yoga" },
      { name: "Paschimottanasana", sanskrit: "Seated Forward Bend", duration: "2 min", benefit: "Calms the nervous system and reduces headache triggers", icon: "yoga", catId: "yoga" },
      { name: "Viparita Karani", sanskrit: "Legs Up the Wall", duration: "5–10 min", benefit: "Reverses blood flow, powerful migraine relief pose", icon: "yoga", catId: "yoga" },
      { name: "4-7-8 Breathing", sanskrit: "Pranayama", duration: "5 min", benefit: "Activates vagus nerve, reduces migraine frequency", icon: "wind", screen: "breathe" },
    ]
  },
  "Joint Pain": {
    color: "#A8C5A0",
    icon: "stress",
    headline: "Joint-Friendly Movement",
    description: "Low-impact yoga to strengthen the muscles around joints, improve flexibility and reduce inflammation.",
    poses: [
      { name: "Tadasana", sanskrit: "Mountain Pose", duration: "1–2 min", benefit: "Aligns posture, reduces load on knee and hip joints", icon: "yoga", catId: "yoga" },
      { name: "Virabhadrasana I", sanskrit: "Warrior I", duration: "30 sec each side", benefit: "Strengthens legs and hips safely, improves joint stability", icon: "yoga", catId: "yoga" },
      { name: "Setu Bandhasana", sanskrit: "Bridge Pose", duration: "1–2 min", benefit: "Strengthens knee support muscles without joint stress", icon: "yoga", catId: "yoga" },
      { name: "Cat-Cow Stretch", sanskrit: "Marjaryasana-Bitilasana", duration: "2–3 min", benefit: "Lubricates spine joints, reduces stiffness", icon: "yoga", catId: "back" },
      { name: "Supta Padangusthasana", sanskrit: "Reclining Hand-to-Big-Toe", duration: "1 min each side", benefit: "Gentle hip and knee stretch, reduces inflammation", icon: "yoga", catId: "yoga" },
    ]
  },
  "Heart Condition": {
    color: "#E85D75",
    icon: "heart",
    headline: "Heart-Safe Yoga",
    description: "Gentle, doctor-approved poses that improve circulation and reduce stress on the heart. Always consult your doctor.",
    poses: [
      { name: "Shavasana", sanskrit: "Corpse Pose", duration: "15 min", benefit: "Reduces heart rate and blood pressure significantly", icon: "yoga", catId: "yoga" },
      { name: "Viparita Karani", sanskrit: "Legs Up the Wall", duration: "5–10 min", benefit: "Improves venous return, reduces strain on the heart", icon: "yoga", catId: "yoga" },
      { name: "Sukhasana", sanskrit: "Easy Seated Pose", duration: "5–10 min", benefit: "Slow deep breathing strengthens cardiac coherence", icon: "yoga", catId: "yoga" },
      { name: "Balasana", sanskrit: "Child's Pose", duration: "3–5 min", benefit: "Deeply restful, slows the nervous system gently", icon: "yoga", catId: "yoga" },
      { name: "Heart Coherence Breathing", sanskrit: "5-5 Rhythm", duration: "10 min", benefit: "Scientifically proven to improve heart rate variability", icon: "wind", screen: "breathe" },
    ]
  },
  "Diabetes": {
    color: "#E07840",
    icon: "bolt",
    headline: "Diabetes Management Yoga",
    description: "Poses that stimulate the pancreas, improve insulin sensitivity and regulate blood sugar levels.",
    poses: [
      { name: "Dhanurasana", sanskrit: "Bow Pose", duration: "30–45 sec", benefit: "Massages pancreas, stimulates insulin secretion", icon: "yoga", catId: "yoga" },
      { name: "Ardha Matsyendrasana", sanskrit: "Half Spinal Twist", duration: "1 min each side", benefit: "Compresses pancreas and abdominal organs, improves glucose regulation", icon: "yoga", catId: "yoga" },
      { name: "Paschimottanasana", sanskrit: "Seated Forward Bend", duration: "2 min", benefit: "Stretches spine, tones abdominal organs including pancreas", icon: "yoga", catId: "yoga" },
      { name: "Surya Namaskar", sanskrit: "Sun Salutation", duration: "5–10 min", benefit: "Full-body movement that lowers blood sugar effectively", icon: "yoga", catId: "yoga" },
      { name: "Kapalbhati", sanskrit: "Skull-Shining Breath", duration: "5 min", benefit: "Vigorous exhales massage pancreas, boost metabolism", icon: "wind", screen: "breathe" },
    ]
  },
  "PCOS": {
    color: "#D45B75",
    icon: "heart",
    headline: "PCOS-Supportive Yoga",
    description: "These poses balance hormones, reduce stress, and improve blood flow to the reproductive organs.",
    poses: [
      { name: "Baddha Konasana", sanskrit: "Butterfly Pose", duration: "3–5 min", benefit: "Opens hips, improves ovarian blood flow", icon: "yoga", catId: "yoga" },
      { name: "Supta Baddha Konasana", sanskrit: "Reclined Butterfly", duration: "5 min", benefit: "Deeply relaxing, balances reproductive hormones", icon: "yoga", catId: "yoga" },
      { name: "Setu Bandhasana", sanskrit: "Bridge Pose", duration: "1–2 min", benefit: "Stimulates ovaries and thyroid, reduces cortisol", icon: "yoga", catId: "yoga" },
      { name: "Nadi Shodhana", sanskrit: "Alternate Nostril Breathing", duration: "5–10 min", benefit: "Balances endocrine system and reduces stress hormones", icon: "wind", screen: "breathe" },
      { name: "Ustrasana", sanskrit: "Camel Pose", duration: "30–45 sec", benefit: "Opens chest and abdomen, regulates hormonal balance", icon: "yoga", catId: "yoga" },
    ]
  },
  "Back Pain": {
    color: "#9B8EC5",
    icon: "stress",
    headline: "Back Pain Relief Yoga",
    description: "Targeted poses to release tight muscles, decompress the spine and build core stability.",
    poses: [
      { name: "Cat-Cow Stretch", sanskrit: "Marjaryasana-Bitilasana", duration: "3–5 min", benefit: "Decompresses vertebrae, relieves acute back pain fast", icon: "yoga", catId: "back" },
      { name: "Balasana", sanskrit: "Child's Pose", duration: "3–5 min", benefit: "Lengthens the spine and releases lower back tension", icon: "yoga", catId: "yoga" },
      { name: "Setu Bandhasana", sanskrit: "Bridge Pose", duration: "1–2 min", benefit: "Strengthens lower back and glutes, key support muscles", icon: "yoga", catId: "yoga" },
      { name: "Supine Twist", sanskrit: "Supta Matsyendrasana", duration: "1–2 min each side", benefit: "Releases spinal rotation tension, relieves sciatica", icon: "yoga", catId: "yoga" },
      { name: "Bhujangasana", sanskrit: "Cobra Pose", duration: "30 sec", benefit: "Strengthens back extensors, reduces disc pressure", icon: "yoga", catId: "yoga" },
    ]
  },
  "Anxiety": {
    color: "#5B9BD5",
    icon: "wind",
    headline: "Anxiety-Calming Yoga",
    description: "Slow, grounding poses and breathing that activate the rest-and-digest response to quiet the mind.",
    poses: [
      { name: "Balasana", sanskrit: "Child's Pose", duration: "3–5 min", benefit: "Immediate grounding — activates parasympathetic nervous system", icon: "yoga", catId: "yoga" },
      { name: "4-7-8 Breathing", sanskrit: "Pranayama", duration: "5–10 min", benefit: "Fastest way to calm panic and reduce acute anxiety", icon: "wind", screen: "breathe" },
      { name: "Viparita Karani", sanskrit: "Legs Up the Wall", duration: "5–10 min", benefit: "Reverses stress hormones, deeply sedating effect", icon: "yoga", catId: "yoga" },
      { name: "Shavasana", sanskrit: "Corpse Pose", duration: "10–15 min", benefit: "Full nervous system reset, reduces cortisol levels", icon: "yoga", catId: "yoga" },
      { name: "Yoga Nidra", sanskrit: "Yogic Sleep", duration: "20 min", benefit: "Equivalent to 4 hours of sleep, eliminates anxiety", icon: "moon", catId: "yoga" },
    ]
  },
  "None": null,
  "Prefer not to say": null,
};

// ─── EXERCISE DATA ─────────────────────────────────────────────────────────────
const exerciseCategories = [
  {
    id: "legs", label: "Legs", emoji: "🦵", color: "#E85D75", desc: "Quads, Hamstrings & Glutes",
    exercises: [
      { name: "Squats", gif: "/gifs/squats.gif", sets: "4 × 12 reps", muscles: "Quads, Glutes, Hamstrings", difficulty: "Beginner", tip: "Keep knees behind toes, chest up, weight in heels.", cals: 60 },
      { name: "Lunges", gif: "/gifs/lunges.gif", sets: "3 × 10 each leg", muscles: "Quads, Glutes, Calves", difficulty: "Beginner", tip: "Step forward wide, lower back knee toward floor. Keep torso upright.", cals: 50 },
      { name: "Glute Bridge", gif: "/gifs/glute-bridge.gif", sets: "4 × 15 reps", muscles: "Glutes, Hamstrings, Core", difficulty: "Beginner", tip: "Drive through heels, squeeze glutes hard at top, hold 2 sec.", cals: 40 },
      { name: "Calf Raises", gif: "/gifs/calf-raises.gif", sets: "3 × 20 reps", muscles: "Calves, Ankles", difficulty: "Beginner", tip: "Rise as high as possible, full range of motion, slow descent.", cals: 25 },
      { name: "Sumo Squat", gif: "/gifs/sumo-squat.gif", sets: "4 × 12 reps", muscles: "Inner Thighs, Glutes, Quads", difficulty: "Beginner", tip: "Wide stance, toes pointed out 45°. Push knees out as you squat.", cals: 55 },
      { name: "Romanian Deadlift", gif: "/gifs/romanian-deadlift.gif", sets: "3 × 10 reps", muscles: "Hamstrings, Glutes, Lower Back", difficulty: "Intermediate", tip: "Hinge at hips, keep back flat. Deep hamstring stretch.", cals: 70 },
      { name: "Bulgarian Split Squat", gif: "/gifs/bulgarian-split-squat.gif", sets: "3 × 8 each leg", muscles: "Quads, Glutes, Balance", difficulty: "Advanced", tip: "Rear foot elevated, front foot far forward. Lower slowly.", cals: 80 },
      { name: "Standing Calf Raises", gif: "/gifs/standing-calf-raises.gif", sets: "4 × 20 reps", muscles: "Calves, Soleus", difficulty: "Beginner", tip: "Rise onto toes fully, pause at top, lower slowly for full stretch.", cals: 28 },
    ]
  },
  {
    id: "shoulders", label: "Shoulders", emoji: "🏋️", color: "#5D9BE8", desc: "Deltoids & Upper Body",
    exercises: [
      { name: "Shoulder Press", gif: "/gifs/shoulder-press.gif", sets: "4 × 10 reps", muscles: "Front & Side Delts, Triceps", difficulty: "Beginner", tip: "Engage core. Press straight overhead. Don't arch lower back.", cals: 45 },
      { name: "Lateral Raises", gif: "/gifs/lateral-raises.gif", sets: "3 × 15 reps", muscles: "Side Deltoids", difficulty: "Beginner", tip: "Lead with elbows, raise only to shoulder height, controlled descent.", cals: 30 },
      { name: "Front Raises", gif: "/gifs/front-raises.gif", sets: "3 × 12 reps", muscles: "Front Deltoids", difficulty: "Beginner", tip: "Raise to shoulder height. Don't swing body. Control the way down.", cals: 30 },
      { name: "Face Pulls", gif: "/gifs/face-pulls.gif", sets: "3 × 15 reps", muscles: "Rear Delts, Rotator Cuff", difficulty: "Beginner", tip: "Pull toward face with elbows high and wide. Great for posture.", cals: 25 },
      { name: "Arnold Press", gif: "/gifs/arnold-press.gif", sets: "3 × 10 reps", muscles: "All 3 Deltoid Heads", difficulty: "Intermediate", tip: "Start palms facing you, rotate as you press overhead.", cals: 50 },
      { name: "Upright Row", gif: "/gifs/upright-row.gif", sets: "3 × 12 reps", muscles: "Side Delts, Upper Traps", difficulty: "Intermediate", tip: "Keep bar close to body. Raise elbows above wrists.", cals: 40 },
    ]
  },
  {
    id: "abs", label: "Abs", emoji: "🔥", color: "#E8935D", desc: "Core & Midsection",
    exercises: [
      { name: "Crunches", gif: "/gifs/crunches.gif", sets: "4 × 20 reps", muscles: "Upper Abs", difficulty: "Beginner", tip: "Don't pull neck. Focus on crunching abs. Exhale as you rise.", cals: 35 },
      { name: "Plank Hold", gif: "/gifs/plank.gif", sets: "3 × 45 seconds", muscles: "Core, Shoulders, Glutes", difficulty: "Beginner", tip: "Keep body straight. Breathe steadily. Don't let hips sag.", cals: 30 },
      { name: "Leg Raises", gif: "/gifs/leg-raises.gif", sets: "3 × 12 reps", muscles: "Lower Abs, Hip Flexors", difficulty: "Intermediate", tip: "Press lower back firmly into floor. Lower legs slowly.", cals: 35 },
      { name: "Russian Twists", gif: "/gifs/russian-twists.webp", sets: "3 × 20 reps", muscles: "Obliques, Core", difficulty: "Beginner", tip: "Lean back 45°, lift feet slightly. Rotate from torso.", cals: 40 },
      { name: "Bicycle Crunches", gif: "/gifs/bicycle-crunches.gif", sets: "3 × 20 reps", muscles: "Obliques, Upper Abs", difficulty: "Intermediate", tip: "Slow and controlled. Touch opposite elbow to knee.", cals: 45 },
      { name: "Dead Bug", gif: "/gifs/dead-bug.gif", sets: "3 × 10 each side", muscles: "Deep Core, Stability", difficulty: "Intermediate", tip: "Press lower back to floor throughout. Extend opposite limbs.", cals: 30 },
      { name: "Dragon Flag", gif: "/gifs/dragon-flag.gif", sets: "3 × 6 reps", muscles: "Entire Core", difficulty: "Advanced", tip: "Keep body perfectly rigid. Lower yourself very slowly.", cals: 60 },
    ]
  },
  {
    id: "calisthenics", label: "Calisthenics", emoji: "🤸", color: "#8B5DE8", desc: "Bodyweight Strength",
    exercises: [
      { name: "Bench Press", gif: "/gifs/bench-press-male.gif", sets: "4 × 10 reps", muscles: "Chest, Triceps, Front Delts", difficulty: "Intermediate", tip: "Retract shoulder blades, bar to mid-chest, drive feet into floor.", cals: 65 },
      { name: "Push-Ups", gif: "/gifs/push-ups.gif", sets: "4 × 15 reps", muscles: "Chest, Triceps, Shoulders", difficulty: "Beginner", tip: "Keep core tight, elbows at 45°. Full range of motion.", cals: 55 },
      { name: "Pull-Ups", gif: "/gifs/pull-ups.gif", sets: "3 × 8 reps", muscles: "Back, Biceps, Core", difficulty: "Intermediate", tip: "Dead hang start. Pull until chin over bar. Lower slowly.", cals: 65 },
      { name: "Burpees", gif: "/gifs/burpees.gif", sets: "3 × 10 reps", muscles: "Full Body, Cardio", difficulty: "Intermediate", tip: "Squat down, jump feet back, push-up, jump feet in, jump up!", cals: 90 },
      { name: "Dips", gif: "/gifs/dips.gif", sets: "3 × 12 reps", muscles: "Triceps, Chest, Shoulders", difficulty: "Intermediate", tip: "Lower until elbows at 90°. Lean forward for more chest.", cals: 55 },
      { name: "Jump Squats", gif: "/gifs/jump-squats.gif", sets: "3 × 12 reps", muscles: "Quads, Glutes, Power", difficulty: "Intermediate", tip: "Squat deep, explode upward. Land with soft bent knees.", cals: 80 },
      { name: "Pike Push-Ups", gif: "/gifs/pike-push-ups.gif", sets: "3 × 10 reps", muscles: "Shoulders, Triceps", difficulty: "Intermediate", tip: "Inverted V shape, hips high, lower head toward floor.", cals: 45 },
      { name: "Muscle-Up", gif: "/gifs/muscle-up.gif", sets: "3 × 5 reps", muscles: "Full Upper Body", difficulty: "Advanced", tip: "Explosive pull, transition elbows above bar. Lean forward.", cals: 90 },
    ]
  },
  {
    id: "arms", label: "Arms", emoji: "💪", color: "#7C9E87", desc: "Biceps & Triceps",
    exercises: [
      { name: "Bicep Curls", gif: "/gifs/bicep-curls.gif", sets: "4 × 12 reps", muscles: "Biceps, Forearms", difficulty: "Beginner", tip: "Keep elbows pinned at sides. Supinate wrist at top.", cals: 30 },
      { name: "Hammer Curls", gif: "/gifs/hammer-curls.gif", sets: "3 × 12 reps", muscles: "Biceps, Brachialis", difficulty: "Beginner", tip: "Neutral grip (thumbs up). Slow eccentric phase.", cals: 30 },
      { name: "Tricep Dips", gif: "/gifs/tricep-dips.gif", sets: "4 × 15 reps", muscles: "Triceps", difficulty: "Beginner", tip: "Keep elbows close to body and pointing back.", cals: 35 },
      { name: "Diamond Push-Ups", gif: "/gifs/diamond-push-ups.gif", sets: "3 × 12 reps", muscles: "Triceps, Inner Chest", difficulty: "Intermediate", tip: "Diamond shape under chest. Elbows stay close.", cals: 40 },
      { name: "Concentration Curl", gif: "/gifs/concentration-curl.gif", sets: "3 × 10 each arm", muscles: "Bicep Peak", difficulty: "Beginner", tip: "Elbow on inner thigh. No swinging. Full squeeze at top.", cals: 25 },
      { name: "Skull Crushers", gif: "/gifs/skull-crushers.gif", sets: "3 × 12 reps", muscles: "Triceps Long Head", difficulty: "Intermediate", tip: "Keep upper arms vertical. Lower bar to forehead.", cals: 35 },
      { name: "Seated Curl", gif: "/gifs/seated-curl.gif", sets: "3 × 12 reps", muscles: "Biceps, Forearms", difficulty: "Beginner", tip: "Sit upright, curl both arms simultaneously, squeeze at top.", cals: 28 },
    ]
  },
  {
    id: "back", label: "Back", emoji: "🦾", color: "#4A6B55", desc: "Lats, Traps & Spine",
    exercises: [
      { name: "Superman Hold", gif: "/gifs/superman.gif", sets: "3 × 15 reps", muscles: "Lower Back, Glutes", difficulty: "Beginner", tip: "Lift arms and legs simultaneously. Squeeze at top.", cals: 25 },
      { name: "Bent Over Row", gif: "/gifs/bent-over-row.gif", sets: "4 × 10 reps", muscles: "Lats, Traps, Rear Delts", difficulty: "Intermediate", tip: "Hinge forward 45°. Pull elbows back and past hips.", cals: 55 },
      { name: "Lat Pulldown", gif: "/gifs/lat-pulldown.gif", sets: "4 × 12 reps", muscles: "Lats, Biceps", difficulty: "Beginner", tip: "Pull bar to upper chest. Retract shoulder blades.", cals: 45 },
      { name: "Seated Cable Row", gif: "/gifs/seated-row.gif", sets: "3 × 12 reps", muscles: "Mid Back, Rhomboids", difficulty: "Beginner", tip: "Drive elbows back, squeeze shoulder blades together.", cals: 40 },
      { name: "Cat-Cow Stretch", gif: "/gifs/cat-cow.gif", sets: "3 × 10 reps", muscles: "Spine Mobility, Core", difficulty: "Beginner", tip: "Breathe in on cow (arch), out on cat (round).", cals: 10 },
      { name: "T-Bar Row", gif: "/gifs/t-bar-row.gif", sets: "3 × 10 reps", muscles: "Mid Back, Lats", difficulty: "Intermediate", tip: "Keep back flat. Pull weight toward lower chest.", cals: 60 },
    ]
  },
  {
    id: "fullbody", label: "Full Body", emoji: "⚡", color: "#C8A96E", desc: "Total Body Burn",
    exercises: [
      { name: "Mountain Climbers", gif: "/gifs/mountain-climbers.gif", sets: "3 × 30 sec", muscles: "Core, Shoulders, Cardio", difficulty: "Intermediate", tip: "Keep hips level. Drive knees toward chest alternately.", cals: 70 },
      { name: "Jumping Jacks", gif: "/gifs/jumping-jacks.gif", sets: "3 × 45 sec", muscles: "Full Body, Cardio", difficulty: "Beginner", tip: "Stay light on feet. Arms fully extend overhead.", cals: 50 },
      { name: "Kettlebell Swing", gif: "/gifs/kettlebell-swing.gif", sets: "4 × 15 reps", muscles: "Glutes, Hamstrings, Core", difficulty: "Intermediate", tip: "Hip HINGE not squat. Power from explosively driving hips.", cals: 75 },
      { name: "Bear Crawl", gif: "/gifs/bear-crawl.gif", sets: "3 × 20m", muscles: "Core, Shoulders, Coordination", difficulty: "Intermediate", tip: "Keep hips low. Move opposite arm and leg together.", cals: 65 },
      { name: "Box Jumps", gif: "/gifs/box-jumps.gif", sets: "3 × 8 reps", muscles: "Legs, Explosive Power", difficulty: "Intermediate", tip: "Land softly with bent knees. Step down — don't jump down.", cals: 85 },
      { name: "Devil Press", gif: "/gifs/devil-press.gif", sets: "3 × 8 reps", muscles: "Full Body, Cardio", difficulty: "Advanced", tip: "Burpee combined with overhead dumbbell press.", cals: 100 },
    ]
  },
  {
    id: "pilates", label: "Pilates", emoji: "🔥", color: "#C4A882", desc: "Core, Posture & Flexibility",
    exercises: [
      { name: "Pelvic Curl",        gif: "/yoga-gifs/pilates/pelvic-curl.gif",          sets: "3 × 12 reps",       muscles: "Glutes, Hamstrings, Spine", difficulty: "Beginner",     tip: "Lie on back, feet flat. Peel spine off mat one vertebra at a time. Squeeze glutes at top.", cals: 25 },
      { name: "Single Leg Stretch", gif: "/yoga-gifs/pilates/single-leg-stretch.gif",    sets: "3 × 20 reps",       muscles: "Core, Hip Flexors",         difficulty: "Beginner",     tip: "Curl head up, one knee in, switch legs. Keep lower back firmly pressed into mat.",         cals: 30 },
      { name: "Double Leg Stretch", gif: "/yoga-gifs/pilates/double-leg-stretch.gif",    sets: "3 × 15 reps",       muscles: "Deep Core, Abs",            difficulty: "Intermediate", tip: "Both knees to chest, then extend arms & legs long simultaneously. Pull navel to spine.",   cals: 35 },
      { name: "Criss-Cross",        gif: "/yoga-gifs/pilates/criss-cross.gif",           sets: "3 × 20 reps",       muscles: "Obliques, Core",            difficulty: "Intermediate", tip: "Rotate to opposite knee. Don't pull your neck — support head lightly. Elbow leads.",       cals: 35 },
      { name: "Plank Hold",         gif: "/yoga-gifs/pilates/plank.gif",                 sets: "3 × 45 seconds",    muscles: "Core, Shoulders, Glutes",   difficulty: "Beginner",     tip: "Wrists under shoulders, body in a straight line. Breathe steadily. Hips level.",          cals: 30 },
      { name: "Swan Dive",          gif: "/yoga-gifs/pilates/swan-dive.gif",             sets: "3 × 10 reps",       muscles: "Back Extensors, Glutes",    difficulty: "Beginner",     tip: "Lie prone, hands under shoulders. Lift chest with back muscles only — not arms.",         cals: 20 },
      { name: "Roll Up",            gif: "/yoga-gifs/pilates/roll-up.gif",               sets: "3 × 10 reps",       muscles: "Abs, Spine Flexibility",    difficulty: "Intermediate", tip: "Lie flat, arms overhead. Roll up one vertebra at a time, then slowly roll back down.",     cals: 30 },
      { name: "Rest & Breathe",     gif: "/yoga-gifs/pilates/rest.gif",                  sets: "2 min",             muscles: "Recovery, Nervous System",  difficulty: "Beginner",     tip: "Lie in Savasana. Inhale deeply through nose, exhale fully through mouth. Let go.",        cals: 5  },
    ]
  },
  {
    id: "taichi", label: "Tai Chi", emoji: "☯️", color: "#9B8EC5", desc: "Flow, Balance & Qi",
    exercises: [
      { name: "Opening Stance",       gif: "/yoga-gifs/taichi/opening-stance.gif",  sets: "Hold 2 min",      muscles: "Balance, Posture, Focus",        difficulty: "Beginner", tip: "Feet shoulder-width, knees soft, weight even. Sink into your centre. Breathe naturally.",  cals: 10 },
      { name: "Ward Off",             gif: "/yoga-gifs/taichi/ward-off.gif",         sets: "3 × 8 each side", muscles: "Shoulders, Coordination",        difficulty: "Beginner", tip: "Round arms like holding a ball. Gentle outward press with relaxed shoulders and elbows.",  cals: 15 },
      { name: "Roll Back & Press",    gif: "/yoga-gifs/taichi/roll-back.gif",        sets: "3 × 8 reps",      muscles: "Core, Waist Rotation",           difficulty: "Beginner", tip: "Yield as opponent pushes, redirect force, then press forward from your centre. Rooted.", cals: 15 },
      { name: "Push & Single Whip",   gif: "/yoga-gifs/taichi/push-whip.gif",        sets: "3 × 8 each side", muscles: "Legs, Core, Coordination",       difficulty: "Beginner", tip: "Push forward with both palms, pivot and extend one arm wide. Weight shifts smoothly.",   cals: 20 },
      { name: "Grasp Sparrow's Tail", gif: "/yoga-gifs/taichi/sparrows-tail.gif",    sets: "3 × 6 flows",     muscles: "Full Body Coordination",         difficulty: "Intermediate", tip: "Four moves in sequence: Ward Off, Roll Back, Press, Push. Flow as one continuous motion.", cals: 25 },
      { name: "Cloud Hands",          gif: "/yoga-gifs/taichi/cloud-hands.gif",      sets: "3 × 10 each side",muscles: "Waist, Shoulders, Balance",      difficulty: "Beginner", tip: "Hands move in slow horizontal circles, weight shifts gently side to side. Stay low.",    cals: 20 },
      { name: "White Crane",          gif: "/yoga-gifs/taichi/white-crane.gif",      sets: "3 × 6 each side", muscles: "Balance, Hips, Coordination",    difficulty: "Intermediate", tip: "One arm rises, other lowers. Balance softly on one leg. Gaze forward. Stay completely calm.", cals: 20 },
      { name: "Closing Form",         gif: "/yoga-gifs/taichi/closing-form.gif",     sets: "Hold 2 min",      muscles: "Mindfulness, Recovery",          difficulty: "Beginner", tip: "Feet together, hands lower slowly to sides. Stand quietly and absorb the practice. Bow.", cals: 10 },
    ]
  },
  {
    id: "yoga", label: "Yoga Therapy", emoji: "🧘", color: "#7C9E87", desc: "Healing Poses & Asanas",
    exercises: [
      { name: "Sarvangasana",         gif: "/yoga-gifs/asanas/sarvangasana.gif",             sets: "Hold 1–2 min",        muscles: "Thyroid, Shoulders, Spine",         difficulty: "Intermediate", label: "Shoulder Stand",           tip: "Enter slowly from a supine position, support lower back with hands. Chin presses toward chest activating thyroid. Exit gently.", cals: 20 },
      { name: "Halasana",             gif: "/yoga-gifs/asanas/halasana.gif",                 sets: "Hold 1 min",          muscles: "Thyroid, Back, Hamstrings",         difficulty: "Intermediate", label: "Plow Pose",                tip: "From Shoulder Stand, slowly lower feet to floor behind head. Keep legs straight, breathe naturally. Use a blanket under shoulders.", cals: 18 },
      { name: "Matsyasana",           gif: "/yoga-gifs/asanas/matsyasana.webp",              sets: "Hold 30–60 sec",      muscles: "Throat, Chest, Hip Flexors",        difficulty: "Beginner",     label: "Fish Pose",                tip: "Lie on back, slide hands under hips. Arch back and rest crown of head on floor. Open chest wide. Breathe deeply.", cals: 12 },
      { name: "Setu Bandhasana",      gif: "/yoga-gifs/asanas/setu-bandhasana.gif",          sets: "Hold 1–2 min",        muscles: "Glutes, Spine, Thyroid",            difficulty: "Beginner",     label: "Bridge Pose",              tip: "Lie on back, feet flat, hip-width apart. Press down through feet to lift hips. Clasp hands under back. Chin moves toward chest.", cals: 22 },
      { name: "Ustrasana",            gif: "/yoga-gifs/asanas/ustrasana.gif",                sets: "Hold 30–45 sec",      muscles: "Throat, Chest, Hip Flexors",        difficulty: "Intermediate", label: "Camel Pose",               tip: "Kneel with hips over knees. Press hips forward, lean back to reach heels. Head follows spine — don't drop back abruptly.", cals: 20 },
      { name: "Shavasana",            gif: "/yoga-gifs/asanas/shavasana.gif",                sets: "10–15 min",           muscles: "Full Body, Nervous System",         difficulty: "Beginner",     label: "Corpse Pose",              tip: "Lie flat, arms 15° from body, palms up. Systematically release each muscle group starting from feet. Total conscious surrender.", cals: 8  },
      { name: "Balasana",             gif: "/yoga-gifs/asanas/balasana.gif",                 sets: "Hold 3–5 min",        muscles: "Back, Hips, Nervous System",        difficulty: "Beginner",     label: "Child's Pose",             tip: "Sit on heels, fold forward, arms extended or alongside body. Forehead to mat. Breathe into back ribs. Let gravity do the work.", cals: 10 },
      { name: "Viparita Karani",      gif: "/yoga-gifs/asanas/viparita-karani.gif",          sets: "Hold 5–10 min",       muscles: "Legs, Lower Back, Nervous System",  difficulty: "Beginner",     label: "Legs Up the Wall",         tip: "Sit close to wall, swing legs up. Adjust distance for comfort. Arms wide, palms up. Eyes closed. Breathe and completely let go.", cals: 10 },
      { name: "Supta Baddha Konasana",gif: "/yoga-gifs/asanas/supta-baddha-konasana.gif",   sets: "Hold 5 min",          muscles: "Hips, Groin, Chest",                difficulty: "Beginner",     label: "Reclined Butterfly",       tip: "Lie on back, soles of feet together, knees falling open to sides. Support knees with blocks or cushions if needed. Full release.", cals: 8  },
      { name: "Paschimottanasana",    gif: "/yoga-gifs/asanas/paschimottanasana.webp",       sets: "Hold 2 min",          muscles: "Hamstrings, Spine, Abdominals",     difficulty: "Beginner",     label: "Seated Forward Bend",      tip: "Sit tall, legs extended. Hinge from hips (not waist), reach toward feet. Keep spine long on the way in. Exhale deeper each breath.", cals: 15 },
      { name: "Tadasana",             gif: "/yoga-gifs/asanas/tadasana.gif",                 sets: "Hold 1–2 min",        muscles: "Posture, Balance, Full Body",       difficulty: "Beginner",     label: "Mountain Pose",            tip: "Stand feet hip-width, weight even. Engage thighs, lift sternum, relax shoulders. Crown reaches ceiling. Breathe tall.", cals: 8  },
      { name: "Virabhadrasana I",     gif: "/yoga-gifs/asanas/virabhadrasana-1.gif",         sets: "Hold 30 sec each side",muscles: "Legs, Hips, Chest, Shoulders",     difficulty: "Beginner",     label: "Warrior I",                tip: "Step back foot wide, turn 45°. Bend front knee over ankle. Raise arms, look up gently. Strong legs, open heart.", cals: 25 },
      { name: "Supta Padangusthasana",gif: "/yoga-gifs/asanas/supta-padangusthasana.gif",   sets: "Hold 1 min each side",muscles: "Hamstrings, Hips, Knees",          difficulty: "Beginner",     label: "Reclining Hand-to-Toe",    tip: "Lie on back, loop a strap or towel around one foot. Extend leg up. Keep grounded leg pressed firmly to the mat throughout.", cals: 12 },
      { name: "Dhanurasana",          gif: "/yoga-gifs/asanas/dhanurasana.gif",              sets: "Hold 30–45 sec",      muscles: "Back, Chest, Pancreas",             difficulty: "Intermediate", label: "Bow Pose",                 tip: "Lie prone, bend knees and reach back to hold ankles. On inhale, lift chest and thighs simultaneously. Rock gently to massage abdomen.", cals: 22 },
      { name: "Ardha Matsyendrasana", gif: "/yoga-gifs/asanas/ardha-matsyendrasana.webp",   sets: "Hold 1 min each side",muscles: "Spine, Pancreas, Abdominals",       difficulty: "Intermediate", label: "Half Spinal Twist",        tip: "Sit tall, cross one foot over opposite knee. Twist from base of spine upward. Exhale deeper into twist. Look over back shoulder.", cals: 18 },
      { name: "Surya Namaskar",       gif: "/yoga-gifs/asanas/surya-namaskar.gif",           sets: "5–12 rounds",         muscles: "Full Body, Cardiovascular",         difficulty: "Beginner",     label: "Sun Salutation",           tip: "12 poses linked with breath — inhale to expand, exhale to fold. Start with 3 slow rounds and build rhythm over time.", cals: 80 },
      { name: "Baddha Konasana",      gif: "/yoga-gifs/asanas/baddha-konasana.gif",          sets: "Hold 3–5 min",        muscles: "Hips, Groin, Pelvic Floor",         difficulty: "Beginner",     label: "Butterfly Pose",           tip: "Sit, soles of feet together. Hold feet with both hands. Sit tall and gently breathe into the inner hips. Knees drop naturally.", cals: 12 },
      { name: "Bhujangasana",         gif: "/yoga-gifs/asanas/bhujangasana.gif",             sets: "Hold 30 sec × 3",     muscles: "Spine Extensors, Chest, Back",      difficulty: "Beginner",     label: "Cobra Pose",               tip: "Lie prone, hands under shoulders. Inhale, lift chest — elbows slightly bent. Shoulders roll back and down. Look forward gently.", cals: 15 },
      { name: "Supine Twist",         gif: "/yoga-gifs/asanas/supine-twist.gif",             sets: "Hold 1–2 min each side",muscles: "Spine, Hips, Outer Thighs",        difficulty: "Beginner",     label: "Supta Matsyendrasana",     tip: "Lie on back, hug one knee to chest and guide it across body. Both shoulders stay grounded. Look opposite way. Breathe deeply.", cals: 10 },
      { name: "Yoga Nidra",           gif: "/yoga-gifs/asanas/yoga-nidra.gif",               sets: "20 min",              muscles: "Full Nervous System, Subconscious", difficulty: "Beginner",     label: "Yogic Sleep",              tip: "Lie flat, close eyes. Follow a guided rotation of awareness through the body. Stay fully conscious but completely still.", cals: 5  },
      { name: "Sukhasana",            gif: "/yoga-gifs/asanas/sukhasana.gif",                sets: "5–10 min",            muscles: "Hips, Spine, Mind",                 difficulty: "Beginner",     label: "Easy Seated Pose",         tip: "Sit cross-legged on a folded blanket. Hands on knees, spine tall, eyes closed. Simply breathe and be present.", cals: 5  },
    ]
  },
];

const yogaClasses = [
  { id: 1, title: "Morning Sun Flow", category: "yoga", duration: "20 min", durationMin: 20, level: "Beginner", instructor: "Aria Chen", rating: 4.9, emoji: "🌅", color: "#7C9E87", bg: "#0A1F14", about: "Awaken your body with this energising morning sequence. Perfect for starting the day with clarity and calm.", calories: 120, poses: 12,
    steps: ["Mountain Pose — 1:30", "Sun Salutation A × 3 — 5 min", "Warrior I & II flow — 3 min", "Triangle Pose — 2 min", "Seated Forward Fold — 2 min", "Savasana — 3 min"],
    poseGuide: [
      { name: "Mountain Pose",  dur: 90,  tip: "Stand tall, feet hip-width, arms at sides, spine long. Breathe deeply.",                             img: "https://images.unsplash.com/photo-1593810451137-5dc55105dace?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/mountain-pose.gif" },
      { name: "Sun Salutation", dur: 300, tip: "Flow through 12 poses linked with breath. Inhale to rise, exhale to fold.",                          img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/sun-salutation.gif", video: "/yoga-gifs/sun-salutation.mp4" },
      { name: "Warrior I & II", dur: 180, tip: "Warrior I: front knee 90°, arms overhead. Warrior II: wide stance, arms parallel, gaze forward.",    img: "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/warrior.gif" },
      { name: "Triangle Pose",  dur: 120, tip: "Straight legs, reach forward then lower hand to shin. Open chest to sky.",                           img: "https://images.unsplash.com/photo-1510894347713-fc3dc6166086?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/triangle-pose.gif" },
      { name: "Forward Fold",   dur: 120, tip: "Hinge from hips, soft knees, let head hang heavy. Hold for 5 breaths.",                              img: "https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/forward-fold.gif" },
      { name: "Savasana",       dur: 180, tip: "Lie flat, arms slightly away from body, eyes closed. Total surrender.",                              img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/savasana.gif" },
    ]
  },
  { id: 2, title: "Core Pilates Blast", category: "pilates", duration: "30 min", durationMin: 30, level: "Intermediate", instructor: "Marco Silva", rating: 4.8, emoji: "🔥", color: "#C4A882", bg: "#1A0E04", about: "Targeted core work using classic Pilates principles. Strengthen your deep stabilisers and improve posture.", calories: 180, poses: 10,
    steps: ["Pelvic Curl — 2 min", "Single Leg Stretch × 20 — 3 min", "Double Leg Stretch × 15 — 2:30", "Criss-Cross obliques × 20 — 3 min", "Plank hold — 45s", "Swan Dive — 1:30", "Roll Up × 10 — 2:30", "Rest & breathe — 2 min"],
    poseGuide: [
      { name: "Pelvic Curl",        dur: 120, tip: "Lie on back, feet flat. Peel spine off mat one vertebra at a time. Squeeze glutes at top.",      img: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/pilates/pelvic-curl.gif" },
      { name: "Single Leg Stretch", dur: 180, tip: "Curl head up, one knee in, switch legs. Keep lower back pressed firmly to mat.",                  img: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/pilates/single-leg-stretch.gif" },
      { name: "Double Leg Stretch", dur: 150, tip: "Both knees to chest, then extend arms & legs long. Pull navel to spine throughout.",              img: "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/pilates/double-leg-stretch.gif" },
      { name: "Criss-Cross",        dur: 180, tip: "Rotate to opposite knee. Don't pull neck — support head lightly. Elbow reaches, not head.",       img: "https://images.unsplash.com/photo-1566241142559-40e1dab266c6?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/pilates/criss-cross.gif" },
      { name: "Plank Hold",         dur: 90,  tip: "Wrists under shoulders, body in a straight line. Breathe steadily. Don't let hips sag.",         img: "https://images.unsplash.com/photo-1566241142559-40e1dab266c6?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/pilates/plank.gif" },
      { name: "Swan Dive",          dur: 90,  tip: "Lie prone, hands under shoulders. Lift chest using back muscles, not arms. Keep legs together.",  img: "https://images.unsplash.com/photo-1602827114685-efbb2717da90?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/pilates/swan-dive.gif" },
      { name: "Roll Up",            dur: 150, tip: "Lie flat, arms overhead. Slowly roll up one vertebra at a time, then roll back down. Control.",  img: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/pilates/roll-up.gif" },
      { name: "Rest & Breathe",     dur: 120, tip: "Lie in Savasana. Inhale deeply through nose, exhale fully through mouth. Let body recover.",     img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/pilates/rest.gif" },
    ]
  },
  { id: 3, title: "Wu Style Tai Chi", category: "taichi", duration: "45 min", durationMin: 45, level: "Beginner", instructor: "Lin Wei", rating: 5.0, emoji: "☯️", color: "#9B8EC5", bg: "#100E18", about: "Slow flowing movements that cultivate qi, improve balance, and calm the nervous system. No experience needed.", calories: 90, poses: 24,
    steps: ["Opening stance — 2 min", "Ward Off — 5 min", "Roll Back & Press — 5 min", "Push & Single Whip — 5 min", "Grasp Sparrow's Tail — 6 min", "Cloud Hands — 6 min", "White Crane — 4 min", "Closing form — 2 min"],
    poseGuide: [
      { name: "Opening Stance",       dur: 120, tip: "Feet shoulder-width, knees soft, weight even. Sink into your centre. Breathe naturally.",         img: "https://images.unsplash.com/photo-1554284126-aa88f22d8b74?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/taichi/opening-stance.gif" },
      { name: "Ward Off",             dur: 300, tip: "Round arms like holding a ball. Gentle outward press with relaxed shoulders and soft elbows.",     img: "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/taichi/ward-off.gif" },
      { name: "Roll Back & Press",    dur: 300, tip: "Yield as opponent pushes, redirect their force, then press forward from your centre. Stay rooted.", img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/taichi/roll-back.gif" },
      { name: "Push & Single Whip",   dur: 300, tip: "Push forward with both palms, then pivot and extend one arm out wide. Weight shifts smoothly.",    img: "https://images.unsplash.com/photo-1510894347713-fc3dc6166086?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/taichi/push-whip.gif" },
      { name: "Grasp Sparrow's Tail", dur: 360, tip: "Four movements: Ward Off, Roll Back, Press, Push — flowing together as one sequence.",            img: "https://images.unsplash.com/photo-1554284126-aa88f22d8b74?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/taichi/sparrows-tail.gif" },
      { name: "Cloud Hands",          dur: 360, tip: "Hands move in slow horizontal circles, weight shifts gently side to side. Stay low and rooted.",   img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/taichi/cloud-hands.gif" },
      { name: "White Crane",          dur: 240, tip: "One arm rises, other lowers. Balance on one leg softly. Gaze forward, stay calm.",                img: "https://images.unsplash.com/photo-1510894347713-fc3dc6166086?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/taichi/white-crane.gif" },
      { name: "Closing Form",         dur: 120, tip: "Bring feet together, hands lower slowly to sides. Stand quietly and absorb the practice. Bow.",   img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/taichi/closing-form.gif" },
    ]
  },
  { id: 5, title: "Power Vinyasa", category: "yoga", duration: "45 min", durationMin: 45, level: "Advanced", instructor: "Aria Chen", rating: 4.7, emoji: "⚡", color: "#7C9E87", bg: "#0A1F14", about: "A dynamic, sweat-inducing flow linking breath and movement. Build strength, flexibility and endurance.", calories: 280, poses: 20,
    steps: ["Sun Salutation B × 5 — 6 min", "Warrior III balance — 4 min", "Half Moon Pose — 3 min", "Crow Pose — hold 2:30", "Chaturanga flow — 4 min", "Wheel Pose × 3 — 3 min", "Inversions — 3 min", "Deep Savasana — 5 min"],
    poseGuide: [
      { name: "Sun Sal B",          dur: 360, tip: "Adds Chair Pose & Warrior I to Sun A. Stay strong — every movement links to breath.",             img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/sun-salutation.gif" },
      { name: "Warrior III",        dur: 240, tip: "Balance on one leg, body and arms parallel to floor. Engage core fiercely. Hold 5 breaths.",      img: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/warrior.gif" },
      { name: "Half Moon Pose",     dur: 180, tip: "Hand or block on floor, top hip stacked, both legs strong. Open chest. Look up bravely.",        img: "https://images.unsplash.com/photo-1510894347713-fc3dc6166086?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/triangle-pose.gif" },
      { name: "Crow Pose",          dur: 150, tip: "Knees on upper arms, shift weight forward until feet lift. Core is everything. Don't fear falling.", img: "https://images.unsplash.com/photo-1593810451137-5dc55105dace?w=400&h=300&fit=crop&auto=format", gif: "/gifs/pike-push-ups.gif" },
      { name: "Chaturanga Flow",    dur: 240, tip: "From plank, lower body in one line — elbows hug ribs. Upward Dog, then Downward Dog.",           img: "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=400&h=300&fit=crop&auto=format", gif: "/gifs/push-ups.gif" },
      { name: "Wheel Pose",         dur: 180, tip: "Press into hands and feet, lift hips sky-high. Open chest fully. Breathe into expansion.",       img: "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=400&h=300&fit=crop&auto=format", gif: "/gifs/glute-bridge.gif" },
      { name: "Inversions",         dur: 180, tip: "Headstand or shoulder stand: find a wall if needed. Core engaged. Breathe calmly upside down.",  img: "https://images.unsplash.com/photo-1593810451137-5dc55105dace?w=400&h=300&fit=crop&auto=format", gif: "/gifs/mountain-climbers.gif" },
      { name: "Deep Savasana",      dur: 300, tip: "5 full minutes of complete stillness. Let your nervous system absorb all the work. Surrender.",  img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/vinyasa/savasana.gif" },
    ]
  },
  { id: 6, title: "Stress Relief Flow", category: "pilates", duration: "25 min", durationMin: 25, level: "All Levels", instructor: "Marco Silva", rating: 4.8, emoji: "🌊", color: "#C4A882", bg: "#1A0E04", about: "Gentle movement paired with deep breathing to release tension from shoulders, hips and lower back.", calories: 100, poses: 8,
    steps: ["Box Breathing — 2 min", "Cat-Cow — 2 min", "Child's Pose — 1 min", "Supine Twist — 2 min", "Happy Baby — 2 min", "Legs-Up-The-Wall — 5 min", "Body Scan — 3 min"],
    poseGuide: [
      { name: "Box Breathing",  dur: 120, tip: "Inhale 4s, hold 4s, exhale 4s, hold 4s. Navy SEAL technique. Activates your calm nervous system.",  img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/mountain-pose.gif" },
      { name: "Cat-Cow",        dur: 120, tip: "On all fours. Inhale — drop belly, lift gaze (Cow). Exhale — round spine (Cat). Flow with breath.", img: "https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/stress/cat-cow.gif" },
      { name: "Child's Pose",   dur: 60,  tip: "Knees wide, forehead to mat, arms extended or alongside body. Melt here for 1 full minute.",        img: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/stress/childs-pose.gif" },
      { name: "Supine Twist",   dur: 120, tip: "Lie on back, draw one knee to chest then cross over. Keep shoulders flat on mat. Hold 30s each.",   img: "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/stress/supine-twist.gif" },
      { name: "Happy Baby",     dur: 120, tip: "Hold outer feet, knees toward armpits. Rock gently side to side to massage the lower back.",        img: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/stress/happy-baby.gif" },
      { name: "Legs Up Wall",   dur: 300, tip: "Lie on back, legs vertical against wall. Hold 5 min for total venous restoration. Pure bliss.",     img: "https://images.unsplash.com/photo-1602827114685-efbb2717da90?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/stress/legs-up-wall.webp" },
      { name: "Body Scan",      dur: 180, tip: "Close eyes. Slowly scan from feet to crown. Release tension from each part of your body. Let go.", img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop&auto=format", gif: "/yoga-gifs/savasana.gif" },
    ]
  },
];

const exploreCategories = [
  { id: "yoga", label: "Yoga", emoji: "🧘", color: "#7C9E87", desc: "Flexibility & Balance" },
  { id: "pilates", label: "Pilates", emoji: "💪", color: "#C4A882", desc: "Core & Strength" },
  { id: "taichi", label: "Tai Chi", emoji: "☯️", color: "#C5C0D8", desc: "Flow & Energy" },
];

const weekDays = ["M", "T", "W", "T", "F", "S", "S"];
const streakData = [true, true, true, false, true, true, false];

// ─── TRIAL ────────────────────────────────────────────────────────────────────
function getTrialStatus(joinDate) {
  const diffDays = Math.floor((new Date() - new Date(joinDate)) / 86400000);
  const remaining = 3 - diffDays;
  return { active: remaining > 0, remaining: Math.max(0, remaining), expired: remaining <= 0 };
}

// ─── AMBIENT MUSIC ────────────────────────────────────────────────────────────
function useAmbientMusic() {
  const ctxRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const start = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 2);
      master.connect(ctx.destination);
      [[110, "sine", 0.22], [165, "sine", 0.16], [220, "sine", 0.12], [132, "triangle", 0.09]].forEach(([f, t, v], i) => {
        const o = ctx.createOscillator(), g = ctx.createGain(), lfo = ctx.createOscillator(), lg = ctx.createGain();
        o.frequency.value = f; o.type = t; g.gain.value = v;
        lfo.frequency.value = 0.06 + i * 0.02; lg.gain.value = 1.2;
        lfo.connect(lg); lg.connect(o.frequency); o.connect(g); g.connect(master);
        o.start(); lfo.start();
      });
      setPlaying(true);
    } catch (e) {}
  };
  const stop = () => { if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null; setPlaying(false); } };
  return { playing, toggle: playing ? stop : start };
}

function MusicPlayer({ compact }) {
  const { playing, toggle } = useAmbientMusic();
  if (compact) return (
    <div onClick={toggle} style={{ width: 40, height: 40, borderRadius: "50%", background: playing ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
      <AppIconStyles/><AppIcon name={playing ? "speaker" : "music"} color="white" size={20}/>
    </div>
  );
  return (
    <div onClick={toggle} style={{ display: "flex", alignItems: "center", gap: 10, background: playing ? COLORS.deepSage : COLORS.card, border: `1.5px solid ${playing ? COLORS.deepSage : "rgba(255,255,255,0.08)"}`, borderRadius: 40, padding: "10px 16px", cursor: "pointer", transition: "all 0.3s" }}>
      <div style={{ display:"flex", alignItems:"center" }}><AppIcon name={playing ? "speaker" : "music"} color={playing ? "white" : COLORS.sage} size={20}/></div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: "white" }}>Breathing Ambient Music</div>
        <div style={{ fontFamily: FONT, fontSize: 11, color: playing ? "rgba(255,255,255,0.7)" : COLORS.muted }}>{playing ? "♪ Playing — Calming tones" : "Tap to play"}</div>
      </div>
      {playing && <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 18 }}>
        {[0.5,0.9,0.7,1,0.4,0.8].map((h,i) => <div key={i} style={{ width: 3, height: h*18, background: "rgba(255,255,255,0.8)", borderRadius: 2, animation: `wave ${0.5+i*0.1}s ease-in-out infinite alternate` }} />)}
      </div>}
    </div>
  );
}

// ─── ANIMATED WAVEFORM ICON ──────────────────────────────────────────────────
function SoundWaveIcon({ color, playing = false, size = 22 }) {
  const bars = [0.45, 0.75, 1.0, 0.6, 0.88, 0.4, 0.70];
  const bW = size * 0.10, gap = size * 0.055;
  const totalW = bars.length * (bW + gap) - gap;
  const maxH = size * 0.82;
  return (
    <svg width={totalW} height={size} style={{ display:"block", overflow:"visible" }}>
      {bars.map((h, i) => {
        const bH = playing ? maxH * h : maxH * h * 0.5;
        const x = i * (bW + gap);
        const y = (size - bH) / 2;
        const dur = (0.42 + i * 0.06).toFixed(2);
        const delay = (i * 0.09).toFixed(2);
        return (
          <rect key={i} x={x} y={y} width={bW} height={bH} rx={bW / 2} fill={color}
            opacity={playing ? 1 : 0.45}
            style={playing ? {
              transformOrigin: `${x + bW/2}px 50%`,
              animation: `sBar${i} ${dur}s ease-in-out infinite`,
              animationDelay: `${delay}s`,
              transition: "height 0.3s, y 0.3s, opacity 0.3s",
            } : { transition: "opacity 0.3s" }}
          />
        );
      })}
    </svg>
  );
}

// ─── MEDITATION MUSIC PLAYER ─────────────────────────────────────────────────
// Uses the 3 real MP3 tracks the user placed in /public/
const MUSIC_TRACKS = [
  { id: "track1", label: "Tranquil Tones",     emoji: "🎵", color: "#4A9B7F", desc: "Gentle & flowing" },
  { id: "track2", label: "Yoga Serenity",      emoji: "🎶", color: "#7B5EA7", desc: "Soft harmonics" },
  { id: "track3", label: "Deep Meditation",    emoji: "✨", color: "#C4A055", desc: "Ambient warmth" },
];
const TRACK_SRCS = {
  track1: "/meditation-1.mp3",
  track2: "/meditation-2.mp3",
  track3: "/meditation-3.mp3",
};

function NatureSoundControl() {
  const [trackId, setTrackId]   = useState(null);   // null = off
  const [muted, setMuted]       = useState(false);
  const [open, setOpen]         = useState(false);
  const audioRef                = useRef(null);

  const preset    = MUSIC_TRACKS.find(p => p.id === trackId);
  const isPlaying = trackId && !muted;

  // Swap / start / stop audio when track or mute changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (trackId && !muted) {
      const a = new Audio(TRACK_SRCS[trackId]);
      a.loop = true;
      a.volume = 0.55;
      a.play().catch(() => {});
      audioRef.current = a;
    }
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; audioRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId, muted]);

  const btnColor = preset?.color || "rgba(255,255,255,0.35)";

  return (
    <div style={{ position:"relative", zIndex:30 }}>
      {/* ── Track picker popup ── */}
      {open && (
        <div style={{
          position:"absolute", top:44, right:0,
          background:"rgba(10,9,6,0.95)", backdropFilter:"blur(24px)",
          border:"1px solid rgba(255,255,255,0.10)", borderRadius:20,
          padding:"10px 8px", minWidth:168,
          boxShadow:"0 20px 50px rgba(0,0,0,0.75), 0 0 0 0.5px rgba(255,255,255,0.04)",
          animation:"sPickerIn 0.22s cubic-bezier(0.22,1,0.36,1)",
        }}>
          {/* Silent option */}
          <div onClick={() => { setTrackId(null); setMuted(false); setOpen(false); }}
            style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 11px", borderRadius:12,
              background: !trackId ? "rgba(255,255,255,0.07)" : "transparent",
              cursor:"pointer", marginBottom:3, transition:"background 0.15s" }}>
            <span style={{ fontSize:17 }}>🔇</span>
            <div>
              <div style={{ color: !trackId ? "white" : "rgba(255,255,255,0.45)", fontSize:12, fontFamily:FONT, fontWeight:600, lineHeight:1.2 }}>Silent</div>
              <div style={{ color:"rgba(255,255,255,0.25)", fontSize:10, fontFamily:FONT }}>No music</div>
            </div>
            {!trackId && <div style={{ marginLeft:"auto", width:7, height:7, borderRadius:"50%", background:"white" }}/>}
          </div>
          {/* Tracks */}
          {MUSIC_TRACKS.map(p => (
            <div key={p.id} onClick={() => { setTrackId(p.id); setMuted(false); setOpen(false); }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 11px", borderRadius:12,
                background: trackId === p.id ? `${p.color}22` : "transparent",
                cursor:"pointer", marginBottom:2, transition:"background 0.15s" }}>
              <div style={{ width:30, height:30, borderRadius:10, background:`${p.color}18`,
                border:`1px solid ${p.color}35`, display:"flex", alignItems:"center",
                justifyContent:"center", flexShrink:0 }}>
                <SoundWaveIcon color={p.color} playing={trackId===p.id && !muted} size={18}/>
              </div>
              <div>
                <div style={{ color: trackId===p.id ? p.color : "rgba(255,255,255,0.6)", fontSize:12, fontFamily:FONT, fontWeight: trackId===p.id ? 700:400, lineHeight:1.2 }}>{p.label}</div>
                <div style={{ color:"rgba(255,255,255,0.25)", fontSize:10, fontFamily:FONT }}>{p.desc}</div>
              </div>
              {trackId===p.id && <div style={{ marginLeft:"auto", width:7, height:7, borderRadius:"50%", background:p.color }}/>}
            </div>
          ))}
        </div>
      )}

      {/* ── Controls row ── */}
      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
        {/* Mute toggle */}
        {trackId && (
          <button onClick={() => setMuted(m => !m)} style={{
            width:34, height:34, borderRadius:11,
            border:`1px solid ${muted ? "rgba(255,255,255,0.1)" : btnColor + "45"}`,
            background: muted ? "rgba(255,255,255,0.04)" : `${btnColor}18`,
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
            transition:"all 0.22s",
          }}>
            <span style={{ fontSize:15 }}>{muted ? "🔇" : "🔊"}</span>
          </button>
        )}
        {/* Main button */}
        <button onClick={() => setOpen(o => !o)} style={{
          height:36, borderRadius:13,
          border:`1.5px solid ${isPlaying ? btnColor + "60" : "rgba(255,255,255,0.12)"}`,
          background: isPlaying ? `${btnColor}22` : "rgba(255,255,255,0.05)",
          cursor:"pointer", display:"flex", alignItems:"center", gap:7, padding:"0 11px",
          transition:"all 0.22s",
          boxShadow: isPlaying ? `0 0 18px ${btnColor}35, inset 0 0 12px ${btnColor}10` : "none",
        }}>
          {isPlaying ? (
            <SoundWaveIcon color={btnColor} playing size={19}/>
          ) : (
            <svg width={19} height={19} viewBox="0 0 24 24" fill="none">
              <path d="M9 18V5l12-2v13" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="6" cy="18" r="3" stroke="rgba(255,255,255,0.4)" strokeWidth="2"/>
              <circle cx="18" cy="16" r="3" stroke="rgba(255,255,255,0.4)" strokeWidth="2"/>
            </svg>
          )}
          <span style={{ color: isPlaying ? btnColor : "rgba(255,255,255,0.38)", fontSize:11, fontFamily:FONT, fontWeight:600, letterSpacing:0.2 }}>
            {isPlaying ? preset?.emoji + " " + preset?.label : "Music"}
          </span>
        </button>
      </div>

      <style>{`
        @keyframes sPickerIn{from{opacity:0;transform:translateY(-10px) scale(0.93)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes sBar0{0%,100%{transform:scaleY(0.35)}50%{transform:scaleY(1)}}
        @keyframes sBar1{0%,100%{transform:scaleY(0.80)}50%{transform:scaleY(0.28)}}
        @keyframes sBar2{0%,100%{transform:scaleY(0.55)}50%{transform:scaleY(1)}}
        @keyframes sBar3{0%,100%{transform:scaleY(1)}50%{transform:scaleY(0.40)}}
        @keyframes sBar4{0%,100%{transform:scaleY(0.30)}50%{transform:scaleY(0.90)}}
        @keyframes sBar5{0%,100%{transform:scaleY(0.70)}50%{transform:scaleY(0.35)}}
        @keyframes sBar6{0%,100%{transform:scaleY(0.45)}50%{transform:scaleY(1)}}
      `}</style>
    </div>
  );
}

// ─── MEDITATION SCREEN ────────────────────────────────────────────────────────
function MeditationScreen({ onBack, onLogSession, isSubscribed, onUpgrade }) {
  const [selected, setSelected]     = useState(null);
  const [active, setActive]         = useState(false);
  const [stepIdx, setStepIdx]       = useState(0);
  const [elapsed, setElapsed]       = useState(0);
  const [angle, setAngle]           = useState(0);
  const [pulse, setPulse]           = useState(0);
  const [sessionDone, setSessionDone] = useState(false);
  const [eyesClosed, setEyesClosed] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(null); // user-chosen minutes (2-10), null = technique default
  const timerRef        = useRef(null);
  const animRef         = useRef(null);
  const guidedAudioRef  = useRef(null);
  const ambientRef      = useRef(null);
  const totalElapsedRef = useRef(0);   // total session seconds across all steps
  const intervalBellRef = useRef(0);   // count of 30-s interval bells fired

  // ── Guided MP3 audio tracks per technique ──────────────────────────────────
  const GUIDED_TRACKS = {
    bodyscan:       "/guided-bodyscan.mp3",
    lovingkindness: "/guided-lovingkindness.mp3",
  };

  const playGuidedAudio = useCallback((techId) => {
    const src = GUIDED_TRACKS[techId];
    if (!src) return;
    if (guidedAudioRef.current) { guidedAudioRef.current.pause(); guidedAudioRef.current = null; }
    const audio = new Audio(src);
    audio.volume = 0.88;
    audio.play().catch(() => {});
    guidedAudioRef.current = audio;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopGuidedAudio = useCallback(() => {
    if (guidedAudioRef.current) {
      guidedAudioRef.current.pause();
      guidedAudioRef.current.currentTime = 0;
      guidedAudioRef.current = null;
    }
  }, []);

  // ── Ambient calming drone (warm pad — plays during active meditation) ─────────
  const startAmbient = useCallback(() => {
    try {
      if (ambientRef.current) return;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const master = ctx.createGain();
      master.gain.setValueAtTime(0, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0.030, ctx.currentTime + 5); // slow 5-sec fade in
      master.connect(ctx.destination);
      // Warm layered drone: fundamental + harmonics with slight detuning for richness
      [[80, 1.0], [160, 0.40], [240, 0.16], [80.4, 0.45], [159.6, 0.25], [320, 0.08]].forEach(([freq, vol]) => {
        const osc = ctx.createOscillator();
        const g   = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = freq; g.gain.value = vol;
        osc.connect(g); g.connect(master); osc.start();
      });
      ambientRef.current = { ctx, master };
    } catch(e) {}
  }, []);

  const stopAmbient = useCallback(() => {
    if (!ambientRef.current) return;
    try {
      const { ctx, master } = ambientRef.current;
      master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 3); // 3-sec fade out
      setTimeout(() => { try { ctx.close(); } catch(e) {} }, 3800);
    } catch(e) {}
    ambientRef.current = null;
  }, []);

  // ── Audio bell (gentle singing bowl via Web Audio) ─────────────────────────
  const playBell = useCallback((type = "step") => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const master = ctx.createGain();
      master.connect(ctx.destination);
      const freq  = type === "done" ? 432 : type === "mid" ? 528 : 396;
      const decay = type === "done" ? 4.5  : type === "mid" ? 2.2  : 3.2;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.32, ctx.currentTime + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + decay);
      // Harmonic overtone for richness
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = "sine"; osc2.frequency.value = freq * 2.76;
      gain2.gain.setValueAtTime(0, ctx.currentTime);
      gain2.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.04);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + decay * 0.6);
      osc.connect(gain); gain.connect(master);
      osc2.connect(gain2); gain2.connect(master);
      osc.start(); osc2.start();
      osc.stop(ctx.currentTime + decay + 0.1);
      osc2.stop(ctx.currentTime + decay + 0.1);
      setTimeout(() => ctx.close(), (decay + 0.5) * 1000);
    } catch(e) {}
  }, []);

  const technique   = meditationTechniques.find(t => t.id === selected);

  // Steps with proportional scaling when user picks a custom duration
  const activeSteps = useMemo(() => {
    if (!technique) return [];
    if (!selectedDuration) return technique.steps;
    const totalOrig = technique.steps.reduce((s, st) => s + st.time, 0);
    const ratio = (selectedDuration * 60) / totalOrig;
    return technique.steps.map(s => ({ ...s, time: Math.max(12, Math.round(s.time * ratio)) }));
  }, [technique, selectedDuration]);

  const activeDuration  = selectedDuration || technique?.duration || 10;
  const currentStep     = activeSteps[stepIdx];
  const totalSteps      = activeSteps.length || 1;
  const stepProg        = currentStep ? Math.min(elapsed / currentStep.time, 1) : 0;
  const totalProg       = (stepIdx + stepProg) / totalSteps;
  const timeLeft        = currentStep ? Math.max(0, currentStep.time - elapsed) : 0;
  const formatTime      = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const stopSession = () => {
    clearInterval(timerRef.current); cancelAnimationFrame(animRef.current);
    stopGuidedAudio();
    stopAmbient();
    totalElapsedRef.current = 0;
    intervalBellRef.current = 0;
    setEyesClosed(false);
    setActive(false); setStepIdx(0); setElapsed(0); setAngle(0); setPulse(0);
  };

  // Set default duration pill when a technique is chosen
  useEffect(() => {
    if (technique) {
      setSelectedDuration(Math.min(technique.duration, 10));
    }
  }, [technique]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mandala animation — always runs; slow idle rotation, faster when active
  useEffect(() => {
    let last = null;
    const tick = ts => {
      if (last) {
        const dt = ts - last;
        setAngle(a => (a + dt * (active ? 0.009 : 0.003)) % 360);
        if (active) setPulse(0.5 + 0.38 * Math.sin(ts / 2800 * Math.PI));
      }
      last = ts;
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step countdown + bells
  useEffect(() => {
    if (!active || !technique) return;
    setElapsed(0);
    const step = activeSteps[stepIdx];
    if (!step) return;
    // 🔔 Singing bowl bell at step start
    setTimeout(() => playBell("step"), 200);
    let t = 0;
    timerRef.current = setInterval(() => {
      t++;
      totalElapsedRef.current += 1;
      setElapsed(t);
      // 🔔 Gentle interval bell every 30 seconds of total session time
      if (totalElapsedRef.current > 0 && totalElapsedRef.current % 30 === 0) {
        intervalBellRef.current += 1;
        playBell("mid");
      }
      if (t >= step.time) {
        clearInterval(timerRef.current);
        setStepIdx(prev => {
          const next = prev + 1;
          if (next >= activeSteps.length) {
            setActive(false); setSessionDone(true);
            stopAmbient();
            cancelAnimationFrame(animRef.current);
            setTimeout(() => playBell("done"), 300);
            onLogSession && onLogSession(makeSession({ type: "meditation", title: technique.name, category: "Meditation", emoji: technique.emoji, calories: 8, duration: `${activeDuration} min` }));
            return prev;
          }
          setElapsed(0);
          return next;
        });
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIdx, selected]);

  useEffect(() => () => {
    clearInterval(timerRef.current); cancelAnimationFrame(animRef.current);
    stopGuidedAudio();
    stopAmbient();
  }, [stopGuidedAudio, stopAmbient]);

  const handleStart = () => {
    totalElapsedRef.current = 0;
    intervalBellRef.current = 0;
    setStepIdx(0); setElapsed(0); setSessionDone(false); setAngle(0); setPulse(0.5);
    setTimeout(() => playBell("step"), 100);
    // Start gentle ambient drone after the initial bell settles
    setTimeout(() => startAmbient(), 900);
    // Start guided MP3 if this technique has one
    if (technique && GUIDED_TRACKS[technique.id]) {
      setTimeout(() => playGuidedAudio(technique.id), 600);
    }
    setActive(true);
  };

  // ════════════════════════════════════════════════
  // TECHNIQUE LIST — Headspace-inspired cards
  // ════════════════════════════════════════════════
  if (!selected) {
    const CARD_COLORS = {
      mindfulness:   { a: "#1A3A2A", b: "#0D2018", spot: "#4A9B7F" },
      bodyscan:      { a: "#1E1030", b: "#100818", spot: "#7B5EA7" },
      lovingkindness:{ a: "#2A1018", b: "#180810", spot: "#D45B75" },
      mantra:        { a: "#261408", b: "#160C04", spot: "#E07840" },
      vipassana:     { a: "#081828", b: "#040E18", spot: "#5B9BD5" },
      visualization: { a: "#201808", b: "#140E04", spot: "#C4A055" },
      peacefulplace: { a: "#051820", b: "#020D10", spot: "#4AABB5" },
      yoganidra:     { a: "#0D1130", b: "#060818", spot: "#6B7FD4" },
      chakra:        { a: "#1A0828", b: "#0D0415", spot: "#9B59B6" },
      tonglen:       { a: "#280A18", b: "#160408", spot: "#E07896" },
    };
    return (
      <div style={{ minHeight: "100vh", background: "#06050A", fontFamily: FONT, paddingBottom: 100 }}>
        {/* Header */}
        <div style={{ padding: "52px 22px 24px", position: "relative", overflow: "hidden" }}>
          {/* Subtle bg glow */}
          <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, #7B5EA715 0%, transparent 70%)", pointerEvents: "none" }} />
          <button onClick={onBack} style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.45)", padding: "7px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12, marginBottom: 20 }}>← Home</button>
          <div style={{ fontSize: 10, letterSpacing: 5, color: "#7B5EA7", marginBottom: 8, textTransform: "uppercase", fontWeight: 700 }}>CalmFit · Meditation</div>
          <h1 style={{ fontSize: 32, color: "white", margin: "0 0 10px", fontWeight: 300, lineHeight: 1.15, letterSpacing: -0.5 }}>
            Find your<br /><span style={{ fontWeight: 700, background: "linear-gradient(90deg, #C4A5E8, #7B5EA7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>stillness.</span>
          </h1>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <AppIconStyles/>
            <span style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(123,94,167,0.15)", border: "1px solid rgba(123,94,167,0.3)", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#A98BCF" }}>
              <AppIcon name="speaker" color="#A98BCF" size={14}/> Real voice guidance
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(74,155,127,0.12)", border: "1px solid rgba(74,155,127,0.25)", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#4A9B7F" }}>
              <AppIcon name="bell" color="#4A9B7F" size={14}/> Bell every 30s
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(80,80,160,0.12)", border: "1px solid rgba(80,80,160,0.25)", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#9090E0" }}>
              <AppIcon name="music" color="#9090E0" size={14}/> Ambient drone
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(196,160,85,0.12)", border: "1px solid rgba(196,160,85,0.25)", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#C4A055" }}>
              <AppIcon name="target" color="#C4A055" size={14}/> 2–10 min
            </span>
          </div>
        </div>

        {/* Featured (first) card — large */}
        {(() => {
          const t0 = meditationTechniques[0];
          const c0 = CARD_COLORS[t0.id];
          return (
            <div style={{ padding: "0 18px 14px" }}>
              <div onClick={() => { stopSession(); setSelected(t0.id); }} style={{
                background: `linear-gradient(135deg, ${c0.a} 0%, ${c0.b} 100%)`,
                border: `1px solid ${c0.spot}30`, borderRadius: 24, padding: "24px 20px 20px",
                cursor: "pointer", position: "relative", overflow: "hidden",
              }}>
                {/* Decorative circles */}
                <div style={{ position: "absolute", right: -30, top: -30, width: 160, height: 160, borderRadius: "50%", background: `radial-gradient(circle, ${c0.spot}18 0%, transparent 65%)`, pointerEvents: "none" }} />
                <div style={{ position: "absolute", right: 20, bottom: -20, width: 90, height: 90, borderRadius: "50%", border: `1px solid ${c0.spot}20`, pointerEvents: "none" }} />
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ background: `${c0.spot}18`, border: `1px solid ${c0.spot}35`, borderRadius: 16, padding: "10px 12px", display:"flex", alignItems:"center", justifyContent:"center", width:54, height:54 }}>
                  <AppIconStyles/><AppIcon name={MEDITATION_ICON_IDS[t0.id]} color={c0.spot} size={30}/>
                </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {GUIDED_TRACKS[t0.id] && (
                      <div style={{ background: "rgba(212,91,117,0.18)", border: "1px solid rgba(212,91,117,0.35)", borderRadius: 20, padding: "4px 10px", display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 11 }}>🎙</span>
                        <span style={{ color: "#D45B75", fontSize: 9, fontWeight: 700, letterSpacing: 0.5 }}>GUIDED</span>
                      </div>
                    )}
                    <div style={{ background: `${c0.spot}15`, border: `1px solid ${c0.spot}30`, borderRadius: 20, padding: "4px 12px", display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ color: c0.spot, fontSize: 10, fontWeight: 700 }}>TODAY'S PICK</span>
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 20, color: "white", fontWeight: 700, marginBottom: 4 }}>{t0.name}</div>
                <div style={{ fontSize: 12, color: c0.spot, marginBottom: 10, opacity: 0.9 }}>{t0.subtitle}</div>
                <div style={{ color: "rgba(255,255,255,0.38)", fontSize: 12, lineHeight: 1.6, marginBottom: 16 }}>{t0.description}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <span style={{ background: `${c0.spot}18`, border: `1px solid ${c0.spot}35`, borderRadius: 18, padding: "4px 12px", fontSize: 11, color: c0.spot, fontWeight: 600 }}>⏱ {t0.duration} min</span>
                  <span style={{ background: `${c0.spot}18`, border: `1px solid ${c0.spot}35`, borderRadius: 18, padding: "4px 12px", fontSize: 11, color: c0.spot }}>{t0.benefit}</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Grid of remaining techniques */}
        <div style={{ padding: "0 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {meditationTechniques.slice(1).map(tech => {
            const cc = CARD_COLORS[tech.id];
            const locked = !canAccess.meditation(tech.id, isSubscribed);
            return (
              <div key={tech.id} onClick={() => { if (locked) { onUpgrade && onUpgrade(); return; } stopSession(); setSelected(tech.id); }} style={{
                background: `linear-gradient(145deg, ${cc.a} 0%, ${cc.b} 100%)`,
                border: `1px solid ${locked ? "rgba(255,255,255,0.06)" : cc.spot+"28"}`, borderRadius: 20, padding: "16px 14px",
                cursor: "pointer", position: "relative", overflow: "hidden", minHeight: 150, opacity: locked ? 0.7 : 1,
              }}>
                <div style={{ position: "absolute", right: -20, bottom: -20, width: 90, height: 90, borderRadius: "50%", background: `radial-gradient(circle, ${cc.spot}15 0%, transparent 70%)`, pointerEvents: "none" }} />
                {locked && (
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.35)", borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
                    <div style={{ background: "rgba(0,0,0,0.7)", borderRadius: 16, padding: "6px 12px", display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 14 }}>🔒</span>
                      <span style={{ color: "#FFD700", fontSize: 10, fontFamily: FONT, fontWeight: 800 }}>PREMIUM</span>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 10, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ background: `${cc.spot}18`, border: `1px solid ${cc.spot}35`, borderRadius: 12, padding: "7px 8px", display:"flex", alignItems:"center" }}>
                    <AppIconStyles/><AppIcon name={MEDITATION_ICON_IDS[tech.id]} color={cc.spot} size={24}/>
                  </div>
                  {GUIDED_TRACKS[tech.id] && !locked && (
                    <div style={{ background: "rgba(212,91,117,0.18)", border: "1px solid rgba(212,91,117,0.3)", borderRadius: 10, padding: "2px 7px", display:"flex", alignItems:"center", gap: 3 }}>
                      <span style={{ fontSize: 9 }}>🎙</span>
                      <span style={{ color: "#D45B75", fontSize: 8, fontWeight: 700 }}>GUIDED</span>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "white", fontWeight: 700, lineHeight: 1.3, marginBottom: 3 }}>{tech.name}</div>
                <div style={{ fontSize: 10, color: cc.spot, marginBottom: 10, opacity: 0.9 }}>{tech.subtitle}</div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  <span style={{ background: `${cc.spot}15`, border: `1px solid ${cc.spot}30`, borderRadius: 12, padding: "2px 8px", fontSize: 9.5, color: cc.spot, fontWeight: 600 }}>{tech.duration} min</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <div style={{ padding: "18px 22px 0", textAlign: "center" }}>
          <div style={{ color: "rgba(255,255,255,0.18)", fontSize: 11, lineHeight: 1.7 }}>🎵 Pair with meditation music · 🧘 Guided step-by-step · 📊 Sessions tracked</div>
        </div>
        <style>{`@keyframes med-card-in{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}`}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════
  // ACTIVE SESSION — Headspace/Calm-inspired
  // ════════════════════════════════════════════════
  const col  = technique.color;
  const isDone = sessionDone;

  // Mandala SVG builder — multiple rotating rings
  const R = 130;
  const petalCount = 8;
  const petals = Array.from({ length: petalCount }, (_, i) => {
    const a = (360 / petalCount) * i;
    const r = a * Math.PI / 180;
    const px = 170 + 62 * Math.cos(r);
    const py = 170 + 62 * Math.sin(r);
    return { x: px, y: py, a };
  });

  // Circular progress path for step timer
  const cr = 148;
  const circumference = 2 * Math.PI * cr;
  const dashOffset = circumference * (1 - stepProg);

  return (
    <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, height: "100vh", zIndex: 101, background: `radial-gradient(ellipse at 50% 25%, ${technique.bg} 0%, #030206 75%)`, display: "flex", flexDirection: "column", fontFamily: FONT, overflowY: active ? "hidden" : "auto" }}>

      {/* ── Deep ambient glow layers ── */}
      <div style={{ position: "fixed", top: "5%", left: "50%", transform: "translateX(-50%)", width: 420, height: 420, borderRadius: "50%", background: `radial-gradient(circle, ${col}${active ? "20" : "10"} 0%, transparent 60%)`, pointerEvents: "none", transition: "background 2s", zIndex: 0 }} />
      <div style={{ position: "fixed", top: "18%", left: "50%", transform: `translateX(-50%) rotate(${angle * 0.3}deg)`, width: 280, height: 280, borderRadius: "50%", background: `radial-gradient(ellipse at 30% 30%, ${col}12 0%, transparent 70%)`, pointerEvents: "none", zIndex: 0 }} />

      {/* ── Eyes Closed Mode overlay ── */}
      {eyesClosed && active && (
        <div onClick={() => setEyesClosed(false)} style={{
          position: "fixed", inset: 0, zIndex: 50,
          background: "rgba(3,2,6,0.97)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
        }}>
          {/* Soft pulsing mandala glow */}
          <div style={{
            width: 200, height: 200, borderRadius: "50%",
            background: `radial-gradient(circle, ${col}18 0%, transparent 65%)`,
            animation: "med-float 3s ease-in-out infinite",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexDirection: "column", gap: 16,
          }}>
            <div style={{ fontSize: 10, letterSpacing: 4, color: `${col}80`, textTransform: "uppercase", fontFamily: FONT }}>
              {currentStep?.title || "Rest"}
            </div>
            <div style={{ fontSize: 40, fontWeight: 700, color: "rgba(255,255,255,0.9)", fontFamily: FONT }}>
              {active ? formatTime(timeLeft) : ""}
            </div>
            <div style={{ fontSize: 9, color: `${col}60`, fontFamily: FONT, letterSpacing: 1 }}>
              Step {stepIdx + 1} / {totalSteps}
            </div>
          </div>
          {/* Instruction text */}
          <div style={{
            marginTop: 40, padding: "0 40px", textAlign: "center",
            color: "rgba(255,255,255,0.45)", fontSize: 15, lineHeight: 1.9,
            fontFamily: FONT, fontWeight: 300, letterSpacing: 0.3,
            maxWidth: 340,
          }}>
            {currentStep?.instruction}
          </div>
          <div style={{ marginTop: 40, color: "rgba(255,255,255,0.15)", fontSize: 11, fontFamily: FONT, letterSpacing: 1 }}>
            tap anywhere to peek
          </div>
        </div>
      )}

      {/* ── Top bar ── */}
      <div style={{ padding: "50px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 20, position: "relative" }}>
        <button onClick={() => { stopSession(); setEyesClosed(false); setSelected(null); }}
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)", padding: "8px 16px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12 }}>
          ← Back
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Eyes Closed Mode button */}
          {active && (
            <button onClick={() => setEyesClosed(e => !e)} style={{
              height: 36, borderRadius: 13, padding: "0 12px",
              border: `1.5px solid ${eyesClosed ? col + "60" : "rgba(255,255,255,0.12)"}`,
              background: eyesClosed ? `${col}22` : "rgba(255,255,255,0.05)",
              color: eyesClosed ? col : "rgba(255,255,255,0.38)",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              fontSize: 11, fontFamily: FONT, fontWeight: 600, letterSpacing: 0.2,
              transition: "all 0.22s",
              boxShadow: eyesClosed ? `0 0 16px ${col}35` : "none",
            }}>
              <span style={{ fontSize: 14 }}>🌙</span>
              {eyesClosed ? "Eyes Open" : "Eyes Closed"}
            </button>
          )}
          <NatureSoundControl />
        </div>
      </div>

      {/* ── Technique name + guided audio badge ── */}
      <div style={{ textAlign: "center", padding: "14px 20px 0", zIndex: 20, position: "relative" }}>
        <div style={{ fontSize: 10, letterSpacing: 4, color: col, textTransform: "uppercase", fontWeight: 700, marginBottom: 5, opacity: 0.8 }}>{technique.subtitle}</div>
        <div style={{ fontSize: 22, color: "white", fontWeight: 400, letterSpacing: -0.3 }}>{technique.name}</div>
        {active && GUIDED_TRACKS[technique.id] && (
          <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(212,91,117,0.16)", border: "1px solid rgba(212,91,117,0.3)", borderRadius: 20, padding: "4px 12px" }}>
            <span style={{ display: "inline-flex", gap: 2, alignItems: "flex-end" }}>
              {[0,1,2,3].map(i => <span key={i} style={{ display:"inline-block", width:2.5, borderRadius:2, background:"#D45B75", animation:`mgWave 0.9s ease-in-out ${i*0.18}s infinite alternate`, height: [8,12,10,7][i] }} />)}
            </span>
            <span style={{ color: "#E8A0B0", fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>GUIDED AUDIO PLAYING</span>
          </div>
        )}
      </div>

      {/* ── MANDALA VISUALIZER ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 10, position: "relative" }}>

        <div style={{ position: "relative", width: 340, height: 340 }}>
          {/* Pulsing ambient ring behind mandala */}
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: `translate(-50%,-50%)`,
            width: 280 + pulse * 40, height: 280 + pulse * 40, borderRadius: "50%",
            background: `radial-gradient(circle, ${col}${active ? "18" : "08"} 0%, transparent 65%)`,
            transition: "width 1.2s ease-in-out, height 1.2s ease-in-out",
            pointerEvents: "none",
          }} />

          {/* SVG Mandala */}
          <svg viewBox="0 0 340 340" width="340" height="340" style={{ position: "absolute", top: 0, left: 0 }}>
            {/* Circular progress arc — step timer */}
            {active && (
              <>
                <circle cx={170} cy={170} r={cr} fill="none" stroke={`${col}15`} strokeWidth={2} />
                <circle cx={170} cy={170} r={cr} fill="none" stroke={col} strokeWidth={2.5}
                  strokeDasharray={circumference} strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                  transform="rotate(-90 170 170)"
                  style={{ transition: "stroke-dashoffset 1s linear" }} />
              </>
            )}
            {/* Outer rotation ring — dots */}
            <g transform={`rotate(${angle} 170 170)`}>
              {Array.from({ length: 12 }, (_, i) => {
                const a2 = (360 / 12) * i * Math.PI / 180;
                return <circle key={i} cx={170 + 120 * Math.cos(a2)} cy={170 + 120 * Math.sin(a2)}
                  r={active ? 2.5 : 1.8} fill={col} opacity={active ? 0.5 : 0.35} />;
              })}
            </g>
            {/* Middle ring — petals rotating opposite */}
            <g transform={`rotate(${-angle * 1.4} 170 170)`}>
              {petals.map((p, i) => (
                <ellipse key={i} cx={p.x} cy={p.y} rx={10} ry={22}
                  fill="none" stroke={col} strokeWidth={1}
                  opacity={active ? 0.35 : 0.28}
                  transform={`rotate(${p.a} ${p.x} ${p.y})`} />
              ))}
            </g>
            {/* Inner star — 6-pointed */}
            <g transform={`rotate(${angle * 0.6} 170 170)`} opacity={active ? 0.6 : 0.35}>
              {[0, 60, 120, 180, 240, 300].map((a3, i) => {
                const r3 = a3 * Math.PI / 180;
                const x1 = 170 + 34 * Math.cos(r3), y1 = 170 + 34 * Math.sin(r3);
                const x2 = 170 + 34 * Math.cos((a3 + 180) * Math.PI / 180);
                const y2 = 170 + 34 * Math.sin((a3 + 180) * Math.PI / 180);
                return i < 3 ? <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={col} strokeWidth={1.5} strokeLinecap="round" /> : null;
              })}
            </g>
            {/* Inner circle outline */}
            <circle cx={170} cy={170} r={28} fill="none" stroke={col} strokeWidth={1} opacity={active ? 0.5 : 0.38} />
            <circle cx={170} cy={170} r={18} fill={`${col}${active ? "20" : "14"}`} />
          </svg>

          {/* Center content overlay */}
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none" }}>
            {active ? (
              <>
                <div style={{ fontSize: 36, fontWeight: 700, color: "white", lineHeight: 1, letterSpacing: -1 }}>{formatTime(timeLeft)}</div>
                <div style={{ fontSize: 9, color: col, marginTop: 3, letterSpacing: 2, textTransform: "uppercase", opacity: 0.8 }}>{currentStep?.title}</div>
              </>
            ) : isDone ? (
              <>
                <div style={{ fontSize: 28 }}>🙏</div>
                <div style={{ fontSize: 11, color: col, marginTop: 4, fontWeight: 600 }}>Complete</div>
              </>
            ) : (
              <div style={{ animation: "med-float 3s ease-in-out infinite", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ position: "relative", width: 80, height: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {/* Outer glow halo */}
                  <div style={{ position: "absolute", width: 80, height: 80, borderRadius: "50%", background: `radial-gradient(circle, ${col}35 0%, transparent 70%)`, animation: "med-icon-pulse 2.5s ease-in-out infinite" }} />
                  {/* Pulsing outer ring */}
                  <div style={{ position: "absolute", width: 64, height: 64, borderRadius: "50%", border: `1px solid ${col}50`, animation: "med-icon-ring 3s ease-in-out infinite" }} />
                  {/* Inner ring */}
                  <div style={{ position: "absolute", width: 52, height: 52, borderRadius: "50%", border: `1px solid ${col}30`, animation: "med-icon-ring 3s ease-in-out infinite 0.4s" }} />
                  {/* Icon container */}
                  <div style={{ width: 46, height: 46, borderRadius: "50%", background: `${col}20`, border: `1.5px solid ${col}65`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 20px ${col}50, 0 0 40px ${col}20` }}>
                    <AppIconStyles/><AppIcon name={MEDITATION_ICON_IDS[technique.id]} color={col} size={26}/>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: `${col}90`, marginTop: 12, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>tap begin</div>
              </div>
            )}
          </div>
        </div>

        {/* Step instruction */}
        <div style={{ marginTop: 18, textAlign: "center", padding: "0 32px", minHeight: 56, zIndex: 6 }}>
          {active && currentStep ? (
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 14, lineHeight: 1.85, fontWeight: 300, letterSpacing: 0.2 }}>{currentStep.instruction}</div>
          ) : !active && !isDone ? (
            <div style={{ color: "rgba(255,255,255,0.28)", fontSize: 12, lineHeight: 1.75 }}>{technique.description}</div>
          ) : isDone ? (
            <div style={{ color: col, fontSize: 14, fontWeight: 500, lineHeight: 1.7 }}>Session complete 🙏<br /><span style={{ color: "rgba(255,255,255,0.38)", fontSize: 12, fontWeight: 300 }}>Logged to your progress</span></div>
          ) : null}
        </div>
      </div>

      {/* ── Overall progress bar ── */}
      <div style={{ padding: "0 20px 8px", zIndex: 20, position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)" }}>Step {active || isDone ? stepIdx + 1 : "—"} of {totalSteps}</span>
          <span style={{ fontSize: 10, color: col, opacity: 0.7 }}>{Math.round(totalProg * 100)}%</span>
        </div>
        <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", background: `linear-gradient(90deg, ${col}99, ${col})`, width: `${totalProg * 100}%`, transition: "width 1s linear", borderRadius: 3 }} />
        </div>
      </div>

      {/* ── Step pills ── */}
      <div style={{ padding: "0 18px 10px", zIndex: 20, position: "relative" }}>
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
          {technique.steps.map((s, i) => {
            const isCur = (active || isDone) && stepIdx === i;
            const isDoneS = active ? stepIdx > i : isDone ? i < totalSteps : false;
            return (
              <div key={i} style={{
                flex: "0 0 auto", minWidth: 60,
                background: isCur ? `${col}22` : isDoneS ? `${col}0C` : "rgba(255,255,255,0.03)",
                border: `1px solid ${isCur ? col : isDoneS ? col + "35" : "rgba(255,255,255,0.07)"}`,
                borderRadius: 14, padding: "9px 8px", textAlign: "center", transition: "all 0.4s",
              }}>
                {isDoneS && !isCur && <div style={{ fontSize: 9, marginBottom: 1 }}>✓</div>}
                <div style={{ fontSize: 8.5, color: isCur ? col : isDoneS ? col + "99" : "rgba(255,255,255,0.22)", letterSpacing: 0.3, textTransform: "uppercase", lineHeight: 1.4 }}>{s.title}</div>
                {isCur && (
                  <div style={{ marginTop: 5, height: 2, borderRadius: 2, background: `${col}25` }}>
                    <div style={{ height: "100%", background: col, width: `${stepProg * 100}%`, transition: "width 1s linear", borderRadius: 2 }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Benefit card (idle) ── */}
      {!active && !isDone && (
        <div style={{ margin: "0 18px 12px", zIndex: 20, position: "relative", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ background: `${col}0E`, border: `1px solid ${col}22`, borderRadius: 18, padding: "14px 16px" }}>
            <div style={{ color: col, fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{technique.benefit}</div>
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 11.5, lineHeight: 1.7 }}>{technique.description}</div>
          </div>
          {GUIDED_TRACKS[technique.id] && (
            <div style={{ background: "linear-gradient(135deg, rgba(212,91,117,0.13), rgba(123,94,167,0.13))", border: "1px solid rgba(212,91,117,0.26)", borderRadius: 16, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 26, flexShrink: 0 }}>🎙</div>
              <div>
                <div style={{ color: "#E8A0B0", fontSize: 12, fontWeight: 700, marginBottom: 2 }}>Real Voice Guidance Included</div>
                <div style={{ color: "rgba(255,255,255,0.38)", fontSize: 10.5, lineHeight: 1.55 }}>A calm guided audio will play automatically. Sit back, close your eyes, and follow along.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Duration picker (idle only) ── */}
      {!active && !isDone && (
        <div style={{ margin: "0 18px 14px", zIndex: 20, position: "relative" }}>
          <div style={{ marginBottom: 9, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, letterSpacing: 3, color: "rgba(255,255,255,0.28)", textTransform: "uppercase", fontWeight: 600 }}>Duration</span>
            <span style={{ fontSize: 10, color: col, opacity: 0.7, fontWeight: 600 }}>{activeDuration} min</span>
          </div>
          <AppIconStyles/>
          <div style={{ display: "flex", gap: 8 }}>
            {[2, 3, 5, 7, 10].map(min => {
              const isSel = selectedDuration === min;
              return (
                <button key={min} onClick={() => setSelectedDuration(min)} style={{
                  flex: "1 1 0",
                  padding: "9px 0",
                  borderRadius: 20,
                  border: `1.5px solid ${isSel ? col : "rgba(255,255,255,0.09)"}`,
                  background: isSel ? `${col}22` : "rgba(255,255,255,0.03)",
                  color: isSel ? col : "rgba(255,255,255,0.32)",
                  fontSize: 12, fontFamily: FONT, fontWeight: isSel ? 700 : 400,
                  cursor: "pointer", transition: "all 0.2s",
                  boxShadow: isSel ? `0 0 14px ${col}35` : "none",
                  letterSpacing: 0.2,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
                }}>
                  {isSel && <AppIcon name="target" color={col} size={11}/>}
                  {min}m
                </button>
              );
            })}
          </div>
          {/* Ambient sound + interval bell hint */}
          <div style={{ marginTop: 9, display: "flex", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: `${col}10`, border: `1px solid ${col}20`, borderRadius: 12, padding: "4px 10px" }}>
              <AppIcon name="music" color={col} size={14}/>
              <span style={{ fontSize: 9.5, color: `${col}BB`, fontWeight: 600, letterSpacing: 0.3 }}>Ambient Drone</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(196,160,85,0.10)", border: "1px solid rgba(196,160,85,0.22)", borderRadius: 12, padding: "4px 10px" }}>
              <AppIcon name="bell" color="#C4A055" size={14}/>
              <span style={{ fontSize: 9.5, color: "#C4A055BB", fontWeight: 600, letterSpacing: 0.3 }}>Bell every 30s</span>
            </div>
          </div>
        </div>
      )}

      {/* ── CTA button ── */}
      <div style={{ padding: "0 18px 40px", zIndex: 20, position: "relative" }}>
        {!active && !isDone ? (
          <button onClick={handleStart} style={{
            width: "100%", padding: "17px", borderRadius: 18, border: "none", cursor: "pointer",
            background: GUIDED_TRACKS[technique.id]
              ? "linear-gradient(135deg, #D45B75 0%, #7B5EA7 100%)"
              : `linear-gradient(135deg, ${col} 0%, ${col}BB 100%)`,
            color: "white", fontSize: 15, fontFamily: FONT, fontWeight: 700, letterSpacing: 0.3,
            boxShadow: GUIDED_TRACKS[technique.id]
              ? "0 8px 30px rgba(212,91,117,0.45), 0 2px 8px rgba(0,0,0,0.4)"
              : `0 8px 30px ${col}50, 0 2px 8px rgba(0,0,0,0.4)`,
            animation: "med-btn-shine 2.5s ease-in-out infinite",
          }}>
            {GUIDED_TRACKS[technique.id] ? `🎙 Begin ${activeDuration}-min Guide ▶` : `Begin ${activeDuration} min ▶`}
          </button>
        ) : isDone ? (
          <button onClick={() => { stopGuidedAudio(); totalElapsedRef.current = 0; intervalBellRef.current = 0; setStepIdx(0); setElapsed(0); setSessionDone(false); }} style={{
            width: "100%", padding: "17px", borderRadius: 18, border: `1px solid ${col}40`,
            background: `${col}15`, color: col, fontSize: 14, fontFamily: FONT, fontWeight: 700, cursor: "pointer",
          }}>
            Meditate Again ↺
          </button>
        ) : (
          <button onClick={stopSession} style={{
            width: "100%", padding: "17px", borderRadius: 18, cursor: "pointer",
            background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.4)",
            border: "1px solid rgba(255,255,255,0.08)", fontSize: 14, fontFamily: FONT,
          }}>
            ◼ End Session · {formatTime(totalElapsedRef.current)} elapsed
          </button>
        )}
      </div>

      <style>{`
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        @keyframes med-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes mgWave { from{transform:scaleY(0.35)} to{transform:scaleY(1)} }
        @keyframes med-icon-pulse { 0%,100%{transform:scale(1);opacity:0.7} 50%{transform:scale(1.22);opacity:1} }
        @keyframes med-icon-ring { 0%,100%{transform:scale(1);opacity:0.45} 50%{transform:scale(1.14);opacity:0.8} }
        @keyframes med-btn-shine { 0%,100%{filter:brightness(1)} 50%{filter:brightness(1.14)} }
      `}</style>
    </div>
  );
}

// ─── BREATHING VISUALIZER SCREEN ─────────────────────────────────────────────
function BreathingScreen({ onBack, onLogSession, isSubscribed, onUpgrade }) {
  const [selected, setSelected] = useState(null);
  const [active, setActive] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [rounds, setRounds] = useState(0);
  const [circleScale, setCircleScale] = useState(0.32);
  const timerRef = useRef(null);
  const animRef = useRef(null);
  const startRef = useRef(null);
  const startScaleRef = useRef(0.32);
  const audioCtxRef = useRef(null); // single AudioContext created on user tap

  const technique = breathingTechniques.find(t => t.id === selected);
  const phase = technique?.phases[phaseIdx];

  const stopSession = () => {
    clearInterval(timerRef.current); cancelAnimationFrame(animRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null; }
    setActive(false); setPhaseIdx(0); setCountdown(0); setCircleScale(0.32);
  };

  // Singing bowl sounds — same pattern as meditation bells, tuned per phase
  const playBreathSound = useCallback((type) => {
    try {
      const ctx = audioCtxRef.current;
      if (!ctx || ctx.state === "closed") return;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;

      // freq, overtone multiplier, peak volume, decay seconds
      const presets = {
        in:    { freq: 396, vol: 0.18, decay: 3.5 }, // soft G — beginning / opening
        out:   { freq: 528, vol: 0.15, decay: 4.2 }, // soft C — releasing / letting go
        hold:  { freq: 432, vol: 0.11, decay: 3.0 }, // soft A — stillness
        hold2: { freq: 432, vol: 0.11, decay: 3.0 },
      };
      const p = presets[type] || presets.hold;

      // Primary bowl tone
      const osc1 = ctx.createOscillator(), g1 = ctx.createGain();
      osc1.type = "sine"; osc1.frequency.value = p.freq;
      g1.gain.setValueAtTime(0, now);
      g1.gain.linearRampToValueAtTime(p.vol, now + 0.05);
      g1.gain.exponentialRampToValueAtTime(0.001, now + p.decay);
      osc1.connect(g1); g1.connect(ctx.destination);
      osc1.start(now); osc1.stop(now + p.decay + 0.1);

      // Harmonic overtone for warmth (same as meditation bell)
      const osc2 = ctx.createOscillator(), g2 = ctx.createGain();
      osc2.type = "sine"; osc2.frequency.value = p.freq * 2.76;
      g2.gain.setValueAtTime(0, now);
      g2.gain.linearRampToValueAtTime(p.vol * 0.28, now + 0.05);
      g2.gain.exponentialRampToValueAtTime(0.001, now + p.decay * 0.55);
      osc2.connect(g2); g2.connect(ctx.destination);
      osc2.start(now); osc2.stop(now + p.decay + 0.1);

    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!active || !technique) return;
    const p = technique.phases[phaseIdx];
    setCountdown(p.duration);
    playBreathSound(p.type);
    startRef.current = Date.now();
    const totalMs = p.duration * 1000;
    const startScale = circleScale;
    startScaleRef.current = startScale;
    const endScale = p.type === "in" ? 1.0 : p.type === "out" ? 0.28 : circleScale;

    const animate = () => {
      const t = Math.min((Date.now() - startRef.current) / totalMs, 1);
      const e = t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3)/2;
      if (p.type !== "hold") setCircleScale(startScaleRef.current + (endScale - startScaleRef.current) * e);
      if (t < 1) animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);

    let rem = p.duration;
    timerRef.current = setInterval(() => {
      rem--;
      setCountdown(rem);
      if (rem <= 0) {
        clearInterval(timerRef.current); cancelAnimationFrame(animRef.current);
        setPhaseIdx(prev => {
          const next = (prev + 1) % technique.phases.length;
          if (next === 0) {
            setRounds(r => {
              const newRounds = r + 1;
              if (newRounds === 3) {
                // Log session after 3 rounds
                onLogSession && onLogSession(makeSession({
                  type: "breathing",
                  title: `${technique.name} · 3 rounds`,
                  category: "Breathing",
                  emoji: technique.emoji,
                  calories: 5,
                  duration: `~${Math.round(technique.phases.reduce((a,p) => a + p.duration, 0) * 3 / 60)} min`,
                }));
              }
              return newRounds;
            });
          }
          return next;
        });
      }
    }, 1000);
    return () => { clearInterval(timerRef.current); cancelAnimationFrame(animRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, phaseIdx, selected, playBreathSound]);

  useEffect(() => () => {
    clearInterval(timerRef.current); cancelAnimationFrame(animRef.current);
  }, []);

  const handleStart = () => {
    // Create AudioContext on direct user tap — required by browser autoplay policy
    try {
      if (audioCtxRef.current) audioCtxRef.current.close();
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current.resume();
    } catch (e) {}
    setPhaseIdx(0); setRounds(0); setCircleScale(0.28);
    setCountdown(technique.phases[0].duration); setActive(true);
  };

  // ── Technique List ──
  if (!selected) return (
    <div style={{ minHeight: "100vh", background: "#06100A", fontFamily: FONT, paddingBottom: 90 }}>
      <div style={{ padding: "50px 22px 22px", background: "linear-gradient(180deg, #0E2016 0%, #06100A 100%)" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.08)", border: "none", color: "rgba(255,255,255,0.5)", padding: "7px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12, marginBottom: 18 }}>← Home</button>
        <div style={{ fontSize: 11, letterSpacing: 4, color: "#4A9B7F", marginBottom: 7, textTransform: "uppercase" }}>CalmFit</div>
        <h1 style={{ fontSize: 30, color: "white", margin: "0 0 4px", fontWeight: 400, lineHeight: 1.2 }}>
          Breathing<br /><em style={{ fontStyle: "italic", color: "#6BCBA8" }}>Techniques</em>
        </h1>
        <p style={{ color: "rgba(255,255,255,0.35)", margin: 0, fontSize: 13 }}>Choose · Visualize · Breathe.</p>
      </div>
      <div style={{ padding: "12px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
        {breathingTechniques.map(tech => {
          const locked = !canAccess.breathing(tech.id, isSubscribed);
          return (
          <div key={tech.id} onClick={() => { if (locked) { onUpgrade && onUpgrade(); return; } stopSession(); setSelected(tech.id); }}
            style={{ background: tech.bg, border: `1px solid ${locked ? "rgba(255,255,255,0.08)" : tech.color+"35"}`, borderRadius: 20, padding: "16px 18px", cursor: "pointer", position: "relative", overflow: "hidden", opacity: locked ? 0.72 : 1 }}>
            <div style={{ position: "absolute", right: -18, top: -18, width: 100, height: 100, borderRadius: "50%", background: `radial-gradient(circle, ${tech.color}20 0%, transparent 70%)`, pointerEvents: "none" }} />
            {locked && (
              <div style={{ position: "absolute", top: 10, right: 12, background: "rgba(0,0,0,0.55)", borderRadius: 20, padding: "3px 10px", display: "flex", alignItems: "center", gap: 4, zIndex: 2 }}>
                <span style={{ fontSize: 11 }}>🔒</span>
                <span style={{ color: "#FFD700", fontSize: 10, fontFamily: FONT, fontWeight: 700 }}>PREMIUM</span>
              </div>
            )}
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${tech.color}20`, border: `1.5px solid ${tech.color}50`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><AppIconStyles/><AppIcon name={BREATHING_ICON_IDS[tech.id]} color={locked ? "rgba(255,255,255,0.3)" : tech.color} size={28}/></div>
              <div style={{ flex: 1 }}>
                <div style={{ color: locked ? "rgba(255,255,255,0.5)" : "white", fontWeight: 700, fontSize: 15, marginBottom: 1 }}>{tech.name}</div>
                <div style={{ color: locked ? "rgba(255,255,255,0.25)" : tech.color, fontSize: 11, marginBottom: 8, opacity: 0.85 }}>{tech.subtitle}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {tech.phases.map((p, i) => (
                    <span key={i} style={{ background: `${tech.color}18`, border: `1px solid ${tech.color}38`, borderRadius: 18, padding: "2px 9px", fontSize: 10, color: locked ? "rgba(255,255,255,0.2)" : tech.color }}>{p.name} {p.duration}s</span>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
                <span style={{ color: locked ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.25)", fontSize: 18 }}>{locked ? "🔒" : "›"}</span>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.32)" }}>{tech.benefit}</span>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );

  // ── Active Session Visualizer ──
  const col = technique.color;
  const radius = 72 + (132 - 72) * circleScale;
  const glowHex = Math.round((0.1 + circleScale * 0.38) * 255).toString(16).padStart(2, "0");
  const pColor = active && phase ? phase.color : "rgba(255,255,255,0.22)";

  return (
    <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, height: "100vh", zIndex: 101, background: `radial-gradient(ellipse at 50% 38%, ${technique.bg}FF 0%, #030608 100%)`, display: "flex", flexDirection: "column", overflowY: "auto" }}>

      {/* Ambient glow that breathes with the circle */}
      <div style={{ position: "fixed", top: "10%", left: "50%", transform: "translateX(-50%)", width: 380, height: 380, borderRadius: "50%", background: `radial-gradient(circle, ${col}${glowHex} 0%, transparent 68%)`, pointerEvents: "none", transition: "background 0.12s", zIndex: 0 }} />

      {/* Top bar */}
      <div style={{ padding: "48px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 10, position: "relative" }}>
        <button onClick={() => { stopSession(); setSelected(null); }}
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)", padding: "7px 15px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12 }}>
          ← Techniques
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {active && rounds > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>{rounds}×</span>
              <div style={{ display: "flex", gap: 3 }}>
                {Array.from({ length: Math.min(rounds, 6) }).map((_, i) => (
                  <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: col, opacity: 0.65 }} />
                ))}
              </div>
            </div>
          )}
          <NatureSoundControl />
        </div>
      </div>

      {/* Title */}
      <div style={{ textAlign: "center", padding: "16px 28px 0", zIndex: 10, position: "relative" }}>
        <div style={{ marginBottom: 5, display:"flex", justifyContent:"center" }}><AppIcon name={BREATHING_ICON_IDS[technique.id]} color={technique.color} size={30}/></div>
        <h2 style={{ color: "white", fontSize: 20, margin: "0 0 2px", fontWeight: 400 }}>{technique.name}</h2>
        <p style={{ color: col, fontSize: 11, margin: 0, opacity: 0.8 }}>{technique.subtitle}</p>
      </div>

      {/* ══ BREATHING CIRCLE VISUALIZER ══ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", zIndex: 5 }}>

        {/* SVG: ripple rings + phase arc segments with second labels */}
        <svg viewBox="0 0 340 340" width="min(340px, 92vw)" height="min(340px, 92vw)" style={{ position: "absolute", pointerEvents: "none" }}>
          {/* Pulsing rings — active breathes with circle, idle shows gently */}
          {[1, 2, 3].map(i => (
            <circle key={i} cx={170} cy={170} r={radius + i * 22} fill="none" stroke={col}
              strokeWidth={active ? 1 : 0.8}
              opacity={active ? Math.max(0, 0.22 / i * circleScale) : 0.07 / i}
              style={{ transition: "r 0.08s ease-out, opacity 0.6s" }} />
          ))}
          {/* Phase arc segments — one arc per phase, lights up when active */}
          {technique.phases.map((p, i) => {
            const n = technique.phases.length;
            const seg = (360 / n) - 10;
            const start = (360 / n) * i - 90;
            const end = start + seg;
            const toRad = d => d * Math.PI / 180;
            const r = 162;
            const x1 = 170 + r * Math.cos(toRad(start)), y1 = 170 + r * Math.sin(toRad(start));
            const x2 = 170 + r * Math.cos(toRad(end)),   y2 = 170 + r * Math.sin(toRad(end));
            const isCur = active && phaseIdx === i;
            const large = seg > 180 ? 1 : 0;
            const mid = start + seg / 2;
            const lx = 170 + 148 * Math.cos(toRad(mid)), ly = 170 + 148 * Math.sin(toRad(mid));
            return (
              <g key={i}>
                <path d={`M${x1} ${y1} A${r} ${r} 0 ${large} 1 ${x2} ${y2}`} fill="none"
                  stroke={isCur ? p.color : "rgba(255,255,255,0.07)"}
                  strokeWidth={isCur ? 3 : 1.5} strokeLinecap="round"
                  style={{ transition: "stroke 0.4s, stroke-width 0.3s" }} />
                <text x={lx} y={ly + 4} textAnchor="middle"
                  fill={isCur ? p.color : "rgba(255,255,255,0.15)"}
                  fontSize={9} fontFamily="Georgia, serif"
                  style={{ transition: "fill 0.4s" }}>{p.duration}s</text>
              </g>
            );
          })}
        </svg>

        {/* Main breathing circle — expands smoothly on inhale, shrinks on exhale */}
        <div onClick={!active ? handleStart : undefined} style={{
          width: radius * 2, height: radius * 2, borderRadius: "50%",
          background: `radial-gradient(circle at 38% 32%, ${col}55 0%, ${col}1A 50%, transparent 100%)`,
          border: `1.5px solid ${col}65`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          transition: "width 0.1s ease-out, height 0.1s ease-out",
          position: "relative", zIndex: 6,
          boxShadow: `0 0 ${35 + circleScale * 55}px ${col}45, inset 0 0 ${15 + circleScale * 25}px ${col}12`,
          cursor: !active ? "pointer" : "default",
        }}>
          {active && phase ? (
            <>
              <div style={{ fontSize: 9, letterSpacing: 3, color: pColor, textTransform: "uppercase", transition: "color 0.5s", marginBottom: 5, textAlign: "center", padding: "0 12px", lineHeight: 1.4 }}>
                {phase.name}
              </div>
              {countdown > 0 && <div style={{ fontSize: 52, fontWeight: 700, color: "white", lineHeight: 1 }}>{countdown}</div>}
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.28)", marginTop: 3, letterSpacing: 1 }}>SEC</div>
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", animation: "brth-float 3s ease-in-out infinite" }}>
              <div style={{ position: "relative", width: 68, height: 68, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ position: "absolute", width: 68, height: 68, borderRadius: "50%", background: `radial-gradient(circle, ${col}32 0%, transparent 70%)`, animation: "brth-pulse 2.5s ease-in-out infinite" }} />
                <div style={{ position: "absolute", width: 52, height: 52, borderRadius: "50%", border: `1px solid ${col}45`, animation: "brth-ring 3s ease-in-out infinite" }} />
                <div style={{ position: "absolute", width: 40, height: 40, borderRadius: "50%", border: `1px solid ${col}28`, animation: "brth-ring 3s ease-in-out infinite 0.4s" }} />
                <AppIconStyles/><AppIcon name={BREATHING_ICON_IDS[technique.id]} color={col} size={24}/>
              </div>
              <div style={{ fontSize: 10, color: `${col}95`, marginTop: 10, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>tap to start</div>
            </div>
          )}
        </div>

        {/* Live breathing instruction */}
        <div style={{ marginTop: 26, textAlign: "center", minHeight: 46, padding: "0 32px", zIndex: 6, position: "relative" }}>
          {active && phase ? (
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, lineHeight: 1.75 }}>{phase.instruction}</div>
          ) : !active ? (
            <div style={{ color: "rgba(255,255,255,0.28)", fontSize: 12, lineHeight: 1.7 }}>{technique.description}</div>
          ) : null}
        </div>
      </div>

      {/* Phase strip — each card fills a progress bar as the phase counts down */}
      <div style={{ padding: "0 18px 12px", display: "flex", gap: 8, zIndex: 10, position: "relative" }}>
        {technique.phases.map((p, i) => {
          const isCur = active && phaseIdx === i;
          const progress = isCur ? ((p.duration - countdown) / p.duration) * 100 : 0;
          return (
            <div key={i} style={{ flex: 1, background: isCur ? `${p.color}20` : "rgba(255,255,255,0.04)", border: `1px solid ${isCur ? p.color : "rgba(255,255,255,0.07)"}`, borderRadius: 14, padding: "10px 6px", textAlign: "center", transition: "all 0.35s" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: isCur ? "white" : "rgba(255,255,255,0.2)", lineHeight: 1 }}>{p.duration}</div>
              <div style={{ fontSize: 8, color: isCur ? p.color : "rgba(255,255,255,0.18)", marginTop: 3, letterSpacing: 0.5, textTransform: "uppercase", lineHeight: 1.3 }}>{p.name}</div>
              <div style={{ marginTop: 6, height: 2, borderRadius: 2, background: `${p.color}22` }}>
                <div style={{ height: "100%", background: isCur ? p.color : "transparent", width: `${progress}%`, transition: "width 1s linear", borderRadius: 2 }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* About card shown when idle */}
      {!active && (
        <div style={{ margin: "0 18px 12px", background: `${col}12`, border: `1px solid ${col}28`, borderRadius: 16, padding: "12px 14px", zIndex: 10, position: "relative" }}>
          <div style={{ color: col, fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{technique.benefit}</div>
          <div style={{ color: "rgba(255,255,255,0.38)", fontSize: 11, lineHeight: 1.65 }}>{technique.description}</div>
        </div>
      )}

      {/* Start / Stop button */}
      <div style={{ padding: "0 18px 36px", zIndex: 10, position: "relative" }}>
        {!active ? (
          <button onClick={handleStart} style={{ width: "100%", padding: "16px", background: `linear-gradient(135deg, ${col}, ${col}AA)`, color: "white", border: "none", borderRadius: 16, fontSize: 15, fontFamily: FONT, cursor: "pointer", fontWeight: 700, boxShadow: `0 6px 22px ${col}45`, animation: "brth-btn-shine 2.5s ease-in-out infinite" }}>
            Begin {technique.name} ▶
          </button>
        ) : (
          <button onClick={stopSession} style={{ width: "100%", padding: "16px", background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.45)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 16, fontSize: 14, fontFamily: FONT, cursor: "pointer" }}>
            ◼ Stop &nbsp;·&nbsp; Round {rounds + 1}
          </button>
        )}
      </div>

      <style>{`
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        @keyframes wave { 0%{transform:scaleY(0.3)} 100%{transform:scaleY(1)} }
        @keyframes brth-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes brth-pulse { 0%,100%{transform:scale(1);opacity:0.7} 50%{transform:scale(1.22);opacity:1} }
        @keyframes brth-ring  { 0%,100%{transform:scale(1);opacity:0.45} 50%{transform:scale(1.14);opacity:0.8} }
        @keyframes brth-btn-shine { 0%,100%{filter:brightness(1)} 50%{filter:brightness(1.14)} }
        @keyframes ai-breathe { 0%,100%{transform:scaleX(1) scaleY(1)} 50%{transform:scaleX(1.13) scaleY(0.87)} }
        @keyframes ai-fire    { 0%,100%{transform:scaleY(1) scaleX(1)} 33%{transform:scaleY(1.08) scaleX(0.93)} 66%{transform:scaleY(0.93) scaleX(1.06)} }
        @keyframes ai-wave    { 0%,100%{transform:translateX(0)} 50%{transform:translateX(-5px)} }
        @keyframes ai-hbeat   { 0%,80%,100%{transform:scale(1)} 15%{transform:scale(1.24)} 30%{transform:scale(1)} 45%{transform:scale(1.16)} }
        @keyframes ai-swing   { 0%,100%{transform:rotate(-15deg)} 50%{transform:rotate(15deg)} }
        @keyframes ai-float   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
        @keyframes ai-pulse   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.14)} }
        @keyframes ai-twinkle { 0%,100%{opacity:0.15;transform:scale(0.6)} 50%{opacity:1;transform:scale(1)} }
        @keyframes ai-grow    { 0%,100%{transform:scaleY(0.5)} 50%{transform:scaleY(1)} }
      `}</style>
    </div>
  );
}

// ─── EXERCISE MODAL ──────────────────────────────────────────────────────────
function ExerciseModal({ exercise, category, onClose, onLogSession, onToggleFav, favorites = [] }) {
  const [gifLoaded, setGifLoaded] = useState(false);
  const [gifError, setGifError] = useState(false);
  const [marked, setMarked] = useState(false);
  const isFav = favorites.includes(exercise.name);
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);

  function handleMarkComplete() {
    if (marked) return;
    setMarked(true);
    onLogSession && onLogSession(makeSession({
      type: "workout",
      title: exercise.name,
      category: category.label,
      emoji: category.emoji,
      calories: exercise.cals || 0,
      duration: exercise.sets || "—",
    }));
  }
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)", zIndex: 999, display: "flex", alignItems: "flex-end", backdropFilter: "blur(8px)" }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: COLORS.card, borderRadius: "28px 28px 0 0", width: "100%", maxWidth: 430,
        margin: "0 auto", maxHeight: "92vh", overflowY: "auto", paddingBottom: 32,
        border: `1px solid ${COLORS.border}`,
      }}>
        {/* GIF area */}
        <div style={{ position: "relative", background: `${category.color}10`, height: 320, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: "28px 28px 0 0" }}>
          {!gifError ? (
            <>
              {!gifLoaded && (
                <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14, background:`${category.color}08` }}>
                  <div style={{ position:"relative", width:80, height:80 }}>
                    <div style={{ position:"absolute", inset:0, borderRadius:"50%", background:`${category.color}20`, animation:"gifShimmer 1.4s ease-in-out infinite" }}/>
                    <div style={{ position:"absolute", inset:8, borderRadius:"50%", background:`${category.color}15`, animation:"gifShimmer 1.4s ease-in-out infinite 0.2s" }}/>
                    <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:38 }}>{category.emoji}</div>
                  </div>
                  <div style={{ display:"flex", gap:5 }}>
                    {[0,1,2].map(i => <div key={i} style={{ width:6, height:6, borderRadius:"50%", background:`${category.color}60`, animation:`gifDot 1.2s ease-in-out infinite ${i*0.2}s` }}/>)}
                  </div>
                  <style>{`
                    @keyframes gifShimmer{0%,100%{opacity:0.4;transform:scale(1)}50%{opacity:0.9;transform:scale(1.08)}}
                    @keyframes gifDot{0%,100%{opacity:0.3;transform:translateY(0)}50%{opacity:1;transform:translateY(-4px)}}
                  `}</style>
                </div>
              )}
              <img src={exercise.gif} alt={exercise.name} onLoad={() => setGifLoaded(true)} onError={() => setGifError(true)}
                style={{ width: "100%", height: "100%", objectFit: "contain", display: gifLoaded ? "block" : "none" }} />
            </>
          ) : (
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:72 }}>{category.emoji}</div>
              <div style={{ fontFamily:FONT, color:COLORS.muted, fontSize:12, marginTop:8 }}>{exercise.name}</div>
              <div style={{ fontFamily:FONT, color:COLORS.subtle, fontSize:10, marginTop:4 }}>Follow the written guide below</div>
            </div>
          )}
          {/* Controls */}
          <div style={{ position: "absolute", top: 14, right: 14, display: "flex", gap: 8 }}>
            {onToggleFav && (
              <button onClick={() => onToggleFav(exercise.name)}
                style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: `1px solid ${COLORS.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <AppIconStyles/>{isFav ? <AppIcon name="heart" color={COLORS.red} size={18}/> : <AppIcon name="heartEmpty" color={COLORS.muted} size={18}/>}
              </button>
            )}
            <button onClick={onClose}
              style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: `1px solid ${COLORS.border}`, color: COLORS.text, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>✕</button>
          </div>
          {/* Difficulty badge */}
          <div style={{ position: "absolute", bottom: 14, left: 14, background: `${category.color}25`, border: `1px solid ${category.color}50`, borderRadius: 10, padding: "4px 10px" }}>
            <span style={{ color: category.color, fontSize: 11, fontFamily: FONT, fontWeight: 700 }}>{exercise.difficulty}</span>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "18px 20px" }}>
          <h2 style={{ fontFamily: FONT, color: COLORS.text, margin: "0 0 4px", fontSize: 22, fontWeight: 800 }}>{exercise.name}</h2>
          <p style={{ fontFamily: FONT, color: COLORS.muted, margin: "0 0 16px", fontSize: 13, display:"flex", alignItems:"center", gap: 5 }}><AppIconStyles/><AppIcon name="target" color={COLORS.muted} size={14}/> {exercise.muscles}</p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[["calendar", exercise.sets, "Sets & Reps", COLORS.blue], ["fire", `~${exercise.cals} kcal`, "Calories Burned", COLORS.coral]].map(([icon, val, lbl, col]) => (
              <div key={lbl} style={{ background: COLORS.surface, border: `1px solid ${col}25`, borderRadius: 15, padding: 14, textAlign: "center" }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom: 4 }}><AppIcon name={icon} color={col} size={20}/></div>
                <div style={{ fontFamily: FONT, fontWeight: 800, color: col, fontSize: 13 }}>{val}</div>
                <div style={{ fontFamily: FONT, color: COLORS.muted, fontSize: 11 }}>{lbl}</div>
              </div>
            ))}
          </div>

          {/* Form tip */}
          <div style={{ background: COLORS.sageDim, border: `1px solid ${COLORS.sage}30`, borderRadius: 14, padding: 14, marginBottom: 16 }}>
            <div style={{ fontFamily: FONT, fontWeight: 700, color: COLORS.sage, fontSize: 11, marginBottom: 5, letterSpacing: 0.5, textTransform: "uppercase", display:"flex", alignItems:"center", gap: 5 }}><AppIcon name="bulb" color={COLORS.sage} size={14}/> Form Tip</div>
            <div style={{ fontFamily: FONT, color: COLORS.text, fontSize: 13, lineHeight: 1.7 }}>{exercise.tip}</div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleMarkComplete}
              style={{ flex: 1, padding: "13px", background: marked ? COLORS.sageDim : COLORS.surface, color: marked ? COLORS.sage : COLORS.text, border: `1.5px solid ${marked ? COLORS.sage : COLORS.border}`, borderRadius: 14, fontSize: 13, fontFamily: FONT, cursor: marked ? "default" : "pointer", fontWeight: 700, transition: "all 0.2s" }}>
              {marked ? "✅ Logged!" : "Mark Done"}
            </button>
            <button onClick={onClose}
              style={{ flex: 1, padding: "13px", background: category.color, color: "white", border: "none", borderRadius: 14, fontSize: 13, fontFamily: FONT, cursor: "pointer", fontWeight: 700 }}>
              Let's go 💪
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ANIMATED WORKOUT ICONS ──────────────────────────────────────────────────
function WorkoutIconStyles() {
  return (
    <style>{`
      @keyframes wk-legL { 0%,100%{transform:rotate(-20deg)} 50%{transform:rotate(20deg)} }
      @keyframes wk-legR { 0%,100%{transform:rotate(20deg)} 50%{transform:rotate(-20deg)} }
      @keyframes wk-press { 0%,100%{transform:translateY(0)} 45%,55%{transform:translateY(-5px)} }
      @keyframes wk-abs1 { 0%,60%,100%{opacity:0.2} 15%,45%{opacity:1} }
      @keyframes wk-abs2 { 0%,20%,80%,100%{opacity:0.2} 35%,65%{opacity:1} }
      @keyframes wk-abs3 { 0%,40%,100%{opacity:0.2} 55%,85%{opacity:1} }
      @keyframes wk-bounce { 0%,100%{transform:translateY(0)} 42%{transform:translateY(-6px)} }
      @keyframes wk-flex { 0%,100%{transform:scale(1)} 50%{transform:scale(1.09)} }
      @keyframes wk-lat { 0%,100%{transform:scaleX(1)} 50%{transform:scaleX(1.11)} }
      @keyframes wk-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      @keyframes wk-pulse { 0%,100%{opacity:0.35;transform:scale(0.93)} 50%{opacity:0.95;transform:scale(1.07)} }
      @keyframes wk-ring { 0%{transform:scale(0.5);opacity:0.8} 100%{transform:scale(1.6);opacity:0} }
      @keyframes wk-curl { 0%,100%{transform:rotate(0deg)} 50%{transform:rotate(-28deg)} }
      @keyframes wk-legs-up { 0%,100%{transform:rotate(80deg)} 50%{transform:rotate(55deg)} }
      @keyframes wk-tai-arm1 { 0%,100%{transform:rotate(-30deg)} 50%{transform:rotate(30deg)} }
      @keyframes wk-tai-arm2 { 0%,100%{transform:rotate(30deg)} 50%{transform:rotate(-30deg)} }
      @keyframes wk-tai-body { 0%,100%{transform:translateX(-3px)} 50%{transform:translateX(3px)} }
      @keyframes wk-tai-circle { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      @keyframes wk-lotus-breathe { 0%,100%{transform:scale(1);opacity:0.92} 50%{transform:scale(1.05);opacity:1} }
      @keyframes wk-lotus-aura { 0%,100%{opacity:0.12;r:10} 50%{opacity:0.35;r:13} }
    `}</style>
  );
}

function WorkoutIcon({ catId, color, size = 36 }) {
  const icons = {
    legs: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="20" cy="9" rx="4.5" ry="5" fill={color} opacity="0.9"/>
        <g style={{transformOrigin:"16px 17px", animation:"wk-legL 0.95s ease-in-out infinite"}}>
          <path d="M16 17 L11 27 L9 36" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
          <ellipse cx="8.5" cy="36" rx="3.5" ry="2" fill={color} opacity="0.75"/>
        </g>
        <g style={{transformOrigin:"24px 17px", animation:"wk-legR 0.95s ease-in-out infinite"}}>
          <path d="M24 17 L29 27 L31 36" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
          <ellipse cx="31.5" cy="36" rx="3.5" ry="2" fill={color} opacity="0.75"/>
        </g>
      </svg>
    ),
    shoulders: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g style={{animation:"wk-press 1.1s ease-in-out infinite"}}>
          <rect x="6" y="11" width="28" height="4" rx="2" fill={color}/>
          <rect x="3" y="8" width="6" height="10" rx="2" fill={color} opacity="0.9"/>
          <rect x="31" y="8" width="6" height="10" rx="2" fill={color} opacity="0.9"/>
          <rect x="1" y="7" width="3" height="12" rx="1.5" fill={color} opacity="0.55"/>
          <rect x="36" y="7" width="3" height="12" rx="1.5" fill={color} opacity="0.55"/>
        </g>
        <path d="M15 15 L12 23 L10 30" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.65"/>
        <path d="M25 15 L28 23 L30 30" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.65"/>
        <ellipse cx="20" cy="34" rx="6" ry="4" fill={color} opacity="0.28"/>
        <circle cx="20" cy="34" r="3" fill={color} opacity="0.85"/>
      </svg>
    ),
    abs: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 5 C11 5 9 12 9 20 C9 29 12 36 14 37 L20 39 L26 37 C28 36 31 29 31 20 C31 12 29 5 26 5 Z" fill={color} opacity="0.1"/>
        <rect x="13" y="9" width="6" height="6" rx="3" fill={color} style={{animation:"wk-abs1 1.6s ease-in-out infinite 0s"}}/>
        <rect x="21" y="9" width="6" height="6" rx="3" fill={color} style={{animation:"wk-abs1 1.6s ease-in-out infinite 0.12s"}}/>
        <rect x="13" y="17" width="6" height="6" rx="3" fill={color} style={{animation:"wk-abs2 1.6s ease-in-out infinite 0.24s"}}/>
        <rect x="21" y="17" width="6" height="6" rx="3" fill={color} style={{animation:"wk-abs2 1.6s ease-in-out infinite 0.36s"}}/>
        <rect x="13" y="25" width="6" height="6" rx="3" fill={color} style={{animation:"wk-abs3 1.6s ease-in-out infinite 0.48s"}}/>
        <rect x="21" y="25" width="6" height="6" rx="3" fill={color} style={{animation:"wk-abs3 1.6s ease-in-out infinite 0.60s"}}/>
        <line x1="20" y1="7" x2="20" y2="33" stroke={color} strokeWidth="1" opacity="0.18"/>
      </svg>
    ),
    calisthenics: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g style={{animation:"wk-bounce 0.9s ease-in-out infinite"}}>
          <circle cx="20" cy="6" r="4.5" fill={color}/>
          <path d="M20 10.5 L20 22" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M20 14 L9 9" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M20 14 L31 9" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M20 22 L13 33" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M20 22 L27 33" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <circle cx="9" cy="9" r="2.5" fill={color} opacity="0.8"/>
          <circle cx="31" cy="9" r="2.5" fill={color} opacity="0.8"/>
          <circle cx="13" cy="34" r="2.5" fill={color} opacity="0.8"/>
          <circle cx="27" cy="34" r="2.5" fill={color} opacity="0.8"/>
        </g>
        <circle cx="20" cy="20" r="15" stroke={color} strokeWidth="1.5" strokeDasharray="3.5 3" opacity="0.22" style={{animation:"wk-spin 5s linear infinite", transformOrigin:"20px 20px"}}/>
      </svg>
    ),
    arms: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g style={{animation:"wk-flex 1.0s ease-in-out infinite", transformOrigin:"20px 22px"}}>
          <path d="M8 32 C7 20 11 13 17 11" stroke={color} strokeWidth="3.5" strokeLinecap="round"/>
          <path d="M17 11 C24 8 30 13 31 20" stroke={color} strokeWidth="3.5" strokeLinecap="round"/>
          <ellipse cx="16" cy="19" rx="8" ry="10" fill={color} opacity="0.88"/>
          <ellipse cx="15" cy="17" rx="4" ry="5" fill="white" opacity="0.2"/>
          <path d="M31 20 C34 25 33 31 30 34" stroke={color} strokeWidth="3" strokeLinecap="round"/>
          <ellipse cx="29" cy="36" rx="4.5" ry="3" fill={color} opacity="0.72"/>
        </g>
      </svg>
    ),
    back: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 3 C16 3 12.5 5.5 12.5 9.5 C12.5 13 15 15.5 20 16.5 C25 15.5 27.5 13 27.5 9.5 C27.5 5.5 24 3 20 3 Z" fill={color} opacity="0.95"/>
        <g style={{transformOrigin:"20px 22px", animation:"wk-lat 1.2s ease-in-out infinite"}}>
          <path d="M12.5 16.5 C7 18.5 5 23 5 27 L9 29 C9.5 25 12 22 16 20.5 Z" fill={color} opacity="0.85"/>
          <path d="M27.5 16.5 C33 18.5 35 23 35 27 L31 29 C30.5 25 28 22 24 20.5 Z" fill={color} opacity="0.85"/>
        </g>
        <rect x="14.5" y="20" width="11" height="14" rx="3" fill={color} opacity="0.62"/>
        <line x1="20" y1="15.5" x2="20" y2="35" stroke="rgba(0,0,0,0.22)" strokeWidth="1.5" strokeDasharray="2 2"/>
        <path d="M14.5 34 C14.5 36.5 16.5 38.5 20 38.5 C23.5 38.5 25.5 36.5 25.5 34 Z" fill={color} opacity="0.72"/>
      </svg>
    ),
    fullbody: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="15" stroke={color} strokeWidth="1.5" opacity="0" style={{animation:"wk-ring 1.8s ease-out infinite"}}/>
        <circle cx="20" cy="20" r="15" stroke={color} strokeWidth="1.5" opacity="0" style={{animation:"wk-ring 1.8s ease-out infinite 0.9s"}}/>
        <g style={{animation:"wk-pulse 1.0s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          <path d="M22 3 L11 22 L18.5 22 L18 37 L29 18 L21.5 18 Z" fill={color}/>
          <path d="M22 3 L18.5 14 L23 14 L22 3 Z" fill="white" opacity="0.28"/>
        </g>
      </svg>
    ),
    pilates: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Person lying on back doing crunch */}
        <ellipse cx="20" cy="30" rx="15" ry="2.5" fill={color} opacity="0.12"/>
        {/* torso curling up */}
        <g style={{transformOrigin:"20px 26px", animation:"wk-curl 1.4s ease-in-out infinite"}}>
          <rect x="15" y="18" width="10" height="9" rx="4" fill={color} opacity="0.9"/>
          {/* head */}
          <circle cx="20" cy="15" r="4" fill={color}/>
        </g>
        {/* legs raised */}
        <g style={{transformOrigin:"16px 27px", animation:"wk-legs-up 1.4s ease-in-out infinite"}}>
          <path d="M16 27 L10 22 L7 17" stroke={color} strokeWidth="3.5" strokeLinecap="round"/>
        </g>
        <g style={{transformOrigin:"24px 27px", animation:"wk-legs-up 1.4s ease-in-out infinite 0.05s"}}>
          <path d="M24 27 L30 22 L33 17" stroke={color} strokeWidth="3.5" strokeLinecap="round"/>
        </g>
        {/* mat */}
        <rect x="5" y="30" width="30" height="3" rx="1.5" fill={color} opacity="0.2"/>
      </svg>
    ),
    yoga: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Soft aura glow behind figure */}
        <circle cx="20" cy="26" r="12" fill={color} style={{animation:"wk-lotus-aura 3s ease-in-out infinite", opacity:0.12, transformOrigin:"20px 26px"}}/>
        <g style={{animation:"wk-lotus-breathe 3.5s ease-in-out infinite", transformOrigin:"20px 22px"}}>
          {/* Head */}
          <circle cx="20" cy="6" r="4" fill={color} opacity="0.95"/>
          {/* Third eye dot */}
          <circle cx="20" cy="4.8" r="1" fill="white" opacity="0.55"/>
          {/* Torso */}
          <path d="M20 10 L20 21" stroke={color} strokeWidth="2.8" strokeLinecap="round"/>
          {/* Left arm → resting on left knee */}
          <path d="M20 14 L10 21 L8 27" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
          {/* Right arm → resting on right knee */}
          <path d="M20 14 L30 21 L32 27" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
          {/* Lotus seat arc (crossed legs) */}
          <path d="M6 31 Q13 24 20 28 Q27 24 34 31" stroke={color} strokeWidth="2.6" strokeLinecap="round" fill="none"/>
          {/* Left foot */}
          <ellipse cx="7.5" cy="31.5" rx="3.5" ry="1.8" fill={color} opacity="0.78" transform="rotate(-12 7.5 31.5)"/>
          {/* Right foot */}
          <ellipse cx="32.5" cy="31.5" rx="3.5" ry="1.8" fill={color} opacity="0.78" transform="rotate(12 32.5 31.5)"/>
          {/* Hands open (mudra circles) */}
          <circle cx="8" cy="27" r="2" fill={color} opacity="0.7"/>
          <circle cx="32" cy="27" r="2" fill={color} opacity="0.7"/>
        </g>
      </svg>
    ),
    taichi: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Yin-yang inspired flowing circle */}
        <circle cx="20" cy="20" r="13" stroke={color} strokeWidth="1.2" opacity="0.2" style={{animation:"wk-tai-circle 8s linear infinite", transformOrigin:"20px 20px"}} strokeDasharray="5 3"/>
        {/* figure body flowing side to side */}
        <g style={{animation:"wk-tai-body 1.8s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          {/* head */}
          <circle cx="20" cy="7" r="3.5" fill={color} opacity="0.95"/>
          {/* torso */}
          <path d="M20 10.5 L20 22" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          {/* left arm flowing up */}
          <g style={{transformOrigin:"20px 14px", animation:"wk-tai-arm1 1.8s ease-in-out infinite"}}>
            <path d="M20 14 L9 10" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="8" cy="9.5" r="2" fill={color} opacity="0.7"/>
          </g>
          {/* right arm flowing down */}
          <g style={{transformOrigin:"20px 16px", animation:"wk-tai-arm2 1.8s ease-in-out infinite"}}>
            <path d="M20 16 L31 20" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="32" cy="20.5" r="2" fill={color} opacity="0.7"/>
          </g>
          {/* legs in stance */}
          <path d="M20 22 L14 33" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M20 22 L26 33" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
        </g>
      </svg>
    ),
  };
  return icons[catId] || <span style={{fontSize: size * 0.78}}>💪</span>;
}

// ─── ANIMATED CONDITION ICONS ────────────────────────────────────────────────
function ConditionIconStyles() {
  return (
    <style>{`
      @keyframes ci-pulse   { 0%,100%{transform:scale(1);opacity:0.85} 50%{transform:scale(1.12);opacity:1} }
      @keyframes ci-throb   { 0%,100%{transform:scale(1)} 30%{transform:scale(1.18)} 60%{transform:scale(0.95)} }
      @keyframes ci-orbit   { from{transform:rotate(0deg) translateX(7px) rotate(0deg)} to{transform:rotate(360deg) translateX(7px) rotate(-360deg)} }
      @keyframes ci-orbit2  { from{transform:rotate(180deg) translateX(7px) rotate(-180deg)} to{transform:rotate(540deg) translateX(7px) rotate(-540deg)} }
      @keyframes ci-wave    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
      @keyframes ci-wave2   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(4px)} }
      @keyframes ci-spin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      @keyframes ci-spinslow{ from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      @keyframes ci-flicker { 0%,90%,100%{opacity:1} 92%,98%{opacity:0.3} }
      @keyframes ci-drip    { 0%,100%{transform:translateY(0) scaleY(1)} 50%{transform:translateY(3px) scaleY(1.15)} }
      @keyframes ci-breathe { 0%,100%{transform:scaleX(1) scaleY(1)} 50%{transform:scaleX(1.08) scaleY(0.95)} }
      @keyframes ci-glow    { 0%,100%{opacity:0.15} 50%{opacity:0.45} }
      @keyframes ci-rock    { 0%,100%{transform:rotate(-8deg)} 50%{transform:rotate(8deg)} }
      @keyframes ci-zigzag  { 0%,100%{transform:translateX(0)} 25%{transform:translateX(3px)} 75%{transform:translateX(-3px)} }
    `}</style>
  );
}

function ConditionIcon({ condition, color, size = 36 }) {
  const s = size;
  const icons = {
    "Hypothyroidism": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Moon with orbiting energy particle */}
        <circle cx="20" cy="20" r="13" fill={color} opacity="0.12" style={{animation:"ci-glow 2.5s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        <path d="M26 10 A10 10 0 1 1 10 26 A14 14 0 0 0 26 10Z" fill={color} opacity="0.9" style={{animation:"ci-pulse 3s ease-in-out infinite", transformOrigin:"18px 18px"}}/>
        <circle cx="27" cy="14" r="2" fill={color} opacity="0.6"/>
        <circle cx="30" cy="20" r="1.3" fill={color} opacity="0.4"/>
        {/* Small stars */}
        <circle cx="14" cy="8" r="1.2" fill={color} opacity="0.7" style={{animation:"ci-flicker 2s ease-in-out infinite"}}/>
        <circle cx="32" cy="12" r="0.9" fill={color} opacity="0.5" style={{animation:"ci-flicker 2s ease-in-out infinite 0.6s"}}/>
        <circle cx="10" cy="30" r="1" fill={color} opacity="0.4" style={{animation:"ci-flicker 2s ease-in-out infinite 1.2s"}}/>
      </svg>
    ),
    "Hyperthyroidism": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Lightning bolt with pulse rings */}
        <circle cx="20" cy="20" r="13" fill={color} opacity="0.1" style={{animation:"ci-pulse 1.2s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        <circle cx="20" cy="20" r="9" stroke={color} strokeWidth="1.5" opacity="0.2" style={{animation:"ci-pulse 1.2s ease-in-out infinite 0.3s", transformOrigin:"20px 20px"}} fill="none"/>
        {/* Bolt */}
        <path d="M23 7 L14 22 L20 22 L17 33 L26 18 L20 18 Z" fill={color} opacity="0.95" style={{animation:"ci-flicker 1.4s ease-in-out infinite"}}/>
      </svg>
    ),
    "Migraine": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Brain with pulsing pain waves */}
        <circle cx="20" cy="20" r="12" fill={color} opacity="0.12" style={{animation:"ci-glow 2s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        {/* Head outline */}
        <ellipse cx="20" cy="18" rx="9" ry="10" fill={color} opacity="0.2"/>
        <ellipse cx="20" cy="18" rx="9" ry="10" stroke={color} strokeWidth="2" opacity="0.85" fill="none"/>
        {/* Brain wrinkle lines */}
        <path d="M14 15 Q17 13 20 15 Q23 17 26 15" stroke={color} strokeWidth="1.8" opacity="0.9" strokeLinecap="round" fill="none" style={{animation:"ci-wave 1.8s ease-in-out infinite"}}/>
        <path d="M14 19 Q17 17 20 19 Q23 21 26 19" stroke={color} strokeWidth="1.8" opacity="0.9" strokeLinecap="round" fill="none" style={{animation:"ci-wave 1.8s ease-in-out infinite 0.3s"}}/>
        {/* Pain sparks */}
        <line x1="8" y1="10" x2="11" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.7" style={{animation:"ci-flicker 1s ease-in-out infinite"}}/>
        <line x1="32" y1="10" x2="29" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.7" style={{animation:"ci-flicker 1s ease-in-out infinite 0.5s"}}/>
        <circle cx="20" cy="30" r="3" fill={color} opacity="0.5" style={{animation:"ci-drip 2s ease-in-out infinite"}}/>
      </svg>
    ),
    "Joint Pain": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Bone/joint with relief glow */}
        <circle cx="20" cy="20" r="12" fill={color} opacity="0.1" style={{animation:"ci-glow 2.5s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        {/* Bone */}
        <line x1="12" y1="28" x2="28" y2="12" stroke={color} strokeWidth="3.5" strokeLinecap="round" opacity="0.9"/>
        <circle cx="11" cy="29" r="4" fill={color} opacity="0.85" style={{animation:"ci-pulse 2s ease-in-out infinite"}}/>
        <circle cx="29" cy="11" r="4" fill={color} opacity="0.85" style={{animation:"ci-pulse 2s ease-in-out infinite 0.4s"}}/>
        <circle cx="9" cy="27" r="2.5" fill={color} opacity="0.5"/>
        <circle cx="31" cy="13" r="2.5" fill={color} opacity="0.5"/>
        {/* Glow at joint center */}
        <circle cx="20" cy="20" r="3.5" fill={color} opacity="0.35" style={{animation:"ci-throb 2s ease-in-out infinite"}}/>
      </svg>
    ),
    "Heart Condition": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Beating heart with ECG line */}
        <circle cx="20" cy="20" r="13" fill={color} opacity="0.1" style={{animation:"ci-throb 1s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        <path d="M20 30 C20 30 8 22 8 14 C8 10 11 7 15 7 C17.5 7 19.5 8.5 20 10 C20.5 8.5 22.5 7 25 7 C29 7 32 10 32 14 C32 22 20 30 20 30Z" fill={color} opacity="0.9" style={{animation:"ci-throb 1s ease-in-out infinite"}}/>
        {/* ECG pulse line */}
        <polyline points="9,22 13,22 15,17 17,26 19,20 21,20 23,22 31,22" stroke="white" strokeWidth="1.5" opacity="0.8" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{animation:"ci-wave 1s ease-in-out infinite"}}/>
      </svg>
    ),
    "Diabetes": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Blood drop with sugar level indicator */}
        <circle cx="20" cy="22" r="12" fill={color} opacity="0.1" style={{animation:"ci-glow 2s ease-in-out infinite", transformOrigin:"20px 22px"}}/>
        {/* Drop shape */}
        <path d="M20 7 C20 7 10 18 10 24 C10 29.5 14.5 33 20 33 C25.5 33 30 29.5 30 24 C30 18 20 7 20 7Z" fill={color} opacity="0.88" style={{animation:"ci-drip 2s ease-in-out infinite"}}/>
        {/* Level line */}
        <line x1="13" y1="26" x2="27" y2="26" stroke="white" strokeWidth="1.8" opacity="0.8" strokeLinecap="round" style={{animation:"ci-wave 2s ease-in-out infinite"}}/>
        <circle cx="20" cy="22" r="2.5" fill="white" opacity="0.6" style={{animation:"ci-pulse 2s ease-in-out infinite"}}/>
      </svg>
    ),
    "PCOS": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Flower / ovary with orbiting dots */}
        <circle cx="20" cy="20" r="8" fill={color} opacity="0.25" style={{animation:"ci-breathe 2.5s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        <circle cx="20" cy="20" r="8" stroke={color} strokeWidth="2" opacity="0.8" fill="none"/>
        {/* Petals */}
        {[0,60,120,180,240,300].map((deg, i) => {
          const rad = deg * Math.PI / 180;
          const px = 20 + Math.cos(rad) * 13;
          const py = 20 + Math.sin(rad) * 13;
          return <circle key={i} cx={px} cy={py} r="3.5" fill={color} opacity="0.7" style={{animation:`ci-pulse 2.5s ease-in-out infinite ${i*0.15}s`}}/>;
        })}
        <circle cx="20" cy="20" r="3" fill={color} opacity="0.9" style={{animation:"ci-throb 2.5s ease-in-out infinite"}}/>
      </svg>
    ),
    "Back Pain": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Spine column with pain indicator */}
        <circle cx="20" cy="20" r="12" fill={color} opacity="0.1" style={{animation:"ci-glow 2s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        {/* Spine */}
        {[8,13,18,23,28,33].map((y, i) => (
          <rect key={i} x="15" y={y} width="10" height="4" rx="2" fill={color} opacity={0.5 + i*0.05} style={{animation:`ci-wave 2s ease-in-out infinite ${i*0.1}s`}}/>
        ))}
        {/* Pain zone highlight */}
        <rect x="14" y="18" width="12" height="8" rx="3" fill={color} opacity="0.4" style={{animation:"ci-pulse 1.5s ease-in-out infinite"}}/>
        {/* Zigzag pain lines */}
        <polyline points="30,20 33,18 36,22" stroke={color} strokeWidth="2" opacity="0.8" fill="none" strokeLinecap="round" style={{animation:"ci-flicker 1.2s ease-in-out infinite"}}/>
        <polyline points="30,24 33,22 36,26" stroke={color} strokeWidth="2" opacity="0.6" fill="none" strokeLinecap="round" style={{animation:"ci-flicker 1.2s ease-in-out infinite 0.3s"}}/>
      </svg>
    ),
    "Anxiety": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Breathing waves */}
        <ellipse cx="20" cy="20" rx="13" ry="10" fill={color} opacity="0.1" style={{animation:"ci-breathe 3s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        {/* Wavy lines representing breath */}
        <path d="M6 16 Q9 12 12 16 Q15 20 18 16 Q21 12 24 16 Q27 20 30 16 Q33 12 36 16" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" opacity="0.9" style={{animation:"ci-wave 2s ease-in-out infinite"}}/>
        <path d="M6 22 Q9 18 12 22 Q15 26 18 22 Q21 18 24 22 Q27 26 30 22 Q33 18 36 22" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" opacity="0.7" style={{animation:"ci-wave 2s ease-in-out infinite 0.3s"}}/>
        <path d="M6 28 Q9 24 12 28 Q15 32 18 28 Q21 24 24 28 Q27 32 30 28 Q33 24 36 28" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" opacity="0.4" style={{animation:"ci-wave 2s ease-in-out infinite 0.6s"}}/>
      </svg>
    ),
    "Prefer not to say": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Sparkle / wellness glow */}
        <circle cx="20" cy="20" r="12" fill={color} opacity="0.15" style={{animation:"ci-glow 3s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        {/* Lotus / star shape */}
        <g style={{animation:"ci-spinslow 8s linear infinite", transformOrigin:"20px 20px"}}>
          {[0,45,90,135,180,225,270,315].map((deg, i) => {
            const rad = deg * Math.PI / 180;
            const x1 = 20 + Math.cos(rad) * 5;
            const y1 = 20 + Math.sin(rad) * 5;
            const x2 = 20 + Math.cos(rad) * 13;
            const y2 = 20 + Math.sin(rad) * 13;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={i%2===0?2:1.2} opacity={i%2===0?0.8:0.4} strokeLinecap="round"/>;
          })}
        </g>
        <circle cx="20" cy="20" r="4" fill={color} opacity="0.9" style={{animation:"ci-pulse 2s ease-in-out infinite"}}/>
      </svg>
    ),
  };
  return icons[condition] || <span style={{fontSize: s*0.65}}>✨</span>;
}

// ─── ANIMATED MEAL ICONS ─────────────────────────────────────────────────────
function MealIcon({ meal, color, size = 32 }) {
  const s = size;
  const icons = {
    "Breakfast": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Horizon line */}
        <line x1="4" y1="30" x2="36" y2="30" stroke={color} strokeWidth="2" opacity="0.4" strokeLinecap="round"/>
        {/* Rising sun disc */}
        <circle cx="20" cy="27" r="9" fill={color} opacity="0.9" style={{animation:"ci-pulse 2.5s ease-in-out infinite", transformOrigin:"20px 27px"}}/>
        {/* Rays fanning above horizon */}
        {[-50,-30,-10,10,30,50].map((deg, i) => {
          const rad = (deg - 90) * Math.PI / 180;
          const x1 = 20 + Math.cos(rad) * 12;
          const y1 = 27 + Math.sin(rad) * 12;
          const x2 = 20 + Math.cos(rad) * 20;
          const y2 = 27 + Math.sin(rad) * 20;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={i===2||i===3?2.5:1.8} opacity={i===2||i===3?0.9:0.55} strokeLinecap="round"
            style={{animation:`ci-pulse 2.5s ease-in-out infinite ${i*0.12}s`, transformOrigin:"20px 27px"}}/>;
        })}
        {/* Glow under sun */}
        <ellipse cx="20" cy="30" rx="12" ry="3" fill={color} opacity="0.18" style={{animation:"ci-glow 2.5s ease-in-out infinite"}}/>
      </svg>
    ),
    "Mid-Morning": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Apple body */}
        <path d="M20 12 C13 12 9 17 9 23 C9 30 13 35 20 35 C27 35 31 30 31 23 C31 17 27 12 20 12Z" fill={color} opacity="0.88" style={{animation:"ci-pulse 2.8s ease-in-out infinite", transformOrigin:"20px 23px"}}/>
        {/* Stem */}
        <path d="M20 12 C20 8 23 6 25 5" stroke={color} strokeWidth="2" opacity="0.7" strokeLinecap="round" style={{animation:"ci-wave 2s ease-in-out infinite"}}/>
        {/* Leaf */}
        <path d="M22 9 C25 7 28 9 26 12 C24 14 21 12 22 9Z" fill={color} opacity="0.6" style={{animation:"ci-rock 2s ease-in-out infinite", transformOrigin:"24px 10px"}}/>
        {/* Shine */}
        <ellipse cx="15" cy="18" rx="3" ry="2" fill="white" opacity="0.25" transform="rotate(-30 15 18)"/>
      </svg>
    ),
    "Lunch": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Outer glow */}
        <circle cx="20" cy="20" r="15" fill={color} opacity="0.1" style={{animation:"ci-glow 2s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        {/* Sun disc */}
        <circle cx="20" cy="20" r="8" fill={color} opacity="0.95" style={{animation:"ci-pulse 2s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        {/* Spinning rays */}
        <g style={{animation:"ci-spinslow 6s linear infinite", transformOrigin:"20px 20px"}}>
          {[0,45,90,135,180,225,270,315].map((deg, i) => {
            const rad = deg * Math.PI / 180;
            const x1 = 20 + Math.cos(rad) * 11;
            const y1 = 20 + Math.sin(rad) * 11;
            const x2 = 20 + Math.cos(rad) * (i%2===0 ? 18 : 15);
            const y2 = 20 + Math.sin(rad) * (i%2===0 ? 18 : 15);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={i%2===0?2.5:1.5} opacity={i%2===0?0.9:0.5} strokeLinecap="round"/>;
          })}
        </g>
      </svg>
    ),
    "Pre-Workout": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Concentric energy rings */}
        <circle cx="20" cy="20" r="15" stroke={color} strokeWidth="1.5" opacity="0.2" fill="none" style={{animation:"ci-pulse 0.9s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        <circle cx="20" cy="20" r="10" stroke={color} strokeWidth="1.5" opacity="0.35" fill="none" style={{animation:"ci-pulse 0.9s ease-in-out infinite 0.15s", transformOrigin:"20px 20px"}}/>
        {/* Bolt */}
        <path d="M24 6 L14 22 L21 22 L16 34 L27 17 L20 17 Z" fill={color} opacity="0.95" style={{animation:"ci-flicker 1.1s ease-in-out infinite"}}/>
        {/* Spark dots */}
        <circle cx="8" cy="14" r="1.5" fill={color} opacity="0.6" style={{animation:"ci-flicker 1.1s ease-in-out infinite 0.2s"}}/>
        <circle cx="32" cy="26" r="1.5" fill={color} opacity="0.6" style={{animation:"ci-flicker 1.1s ease-in-out infinite 0.5s"}}/>
        <circle cx="10" cy="28" r="1" fill={color} opacity="0.4" style={{animation:"ci-flicker 1.1s ease-in-out infinite 0.8s"}}/>
      </svg>
    ),
    "Dinner": (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Soft night glow */}
        <circle cx="20" cy="20" r="14" fill={color} opacity="0.1" style={{animation:"ci-glow 3s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        {/* Crescent moon */}
        <path d="M28 10 A12 12 0 1 1 10 28 A16 16 0 0 0 28 10Z" fill={color} opacity="0.9" style={{animation:"ci-pulse 3s ease-in-out infinite", transformOrigin:"19px 19px"}}/>
        {/* Stars */}
        <circle cx="30" cy="8" r="1.8" fill={color} opacity="0.8" style={{animation:"ci-flicker 2s ease-in-out infinite"}}/>
        <circle cx="34" cy="18" r="1.2" fill={color} opacity="0.55" style={{animation:"ci-flicker 2s ease-in-out infinite 0.5s"}}/>
        <circle cx="8" cy="12" r="1.4" fill={color} opacity="0.5" style={{animation:"ci-flicker 2s ease-in-out infinite 1s"}}/>
        <circle cx="28" cy="30" r="1" fill={color} opacity="0.4" style={{animation:"ci-flicker 2s ease-in-out infinite 1.5s"}}/>
      </svg>
    ),
  };
  return icons[meal] || <span style={{fontSize: s*0.6}}>🍽</span>;
}

// ─── YOGA CLASS ANIMATED ICONS ───────────────────────────────────────────────
function YogaClassIconStyles() {
  return (
    <style>{`
      @keyframes yc-ray   { 0%,100%{opacity:0.5;transform:scaleY(0.7)} 50%{opacity:1;transform:scaleY(1.15)} }
      @keyframes yc-sun   { 0%,100%{transform:scale(0.93)} 50%{transform:scale(1.07)} }
      @keyframes yc-flame { 0%,100%{transform:scaleY(1) scaleX(1)} 33%{transform:scaleY(1.12) scaleX(0.9)} 66%{transform:scaleY(0.92) scaleX(1.08)} }
      @keyframes yc-flick { 0%,100%{transform:translateX(0) scaleX(1)} 40%{transform:translateX(-2px) scaleX(0.88)} 70%{transform:translateX(2px) scaleX(1.1)} }
      @keyframes yc-spin  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      @keyframes yc-bolt  { 0%,70%,100%{opacity:1;transform:scale(1)} 35%{opacity:0.4;transform:scale(0.88)} }
      @keyframes yc-glow  { 0%,100%{opacity:0.2;transform:scale(0.85)} 50%{opacity:0.55;transform:scale(1.15)} }
      @keyframes yc-wave  { 0%,100%{transform:translateX(0)} 50%{transform:translateX(-6px)} }
      @keyframes yc-drop  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
    `}</style>
  );
}

function YogaClassIcon({ classId, color, size = 36 }) {
  const c = color;
  const icons = {
    1: ( // Morning Sun Flow — animated sunrise
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        {[0,45,90,135,180,225,270,315].map((deg,i) => (
          <line key={i}
            x1="20" y1="6" x2="20" y2="2"
            stroke={c} strokeWidth="2" strokeLinecap="round" opacity="0.8"
            style={{transformOrigin:"20px 20px", transform:`rotate(${deg}deg)`, animation:`yc-ray 2s ease-in-out infinite ${i*0.25}s`}}
          />
        ))}
        <g style={{animation:"yc-sun 2s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          <circle cx="20" cy="20" r="7" fill={c}/>
        </g>
        {/* horizon line */}
        <rect x="6" y="29" width="28" height="2.5" rx="1.25" fill={c} opacity="0.35"/>
        {/* rising arc */}
        <path d="M8 29 Q20 14 32 29" stroke={c} strokeWidth="1.5" fill="none" opacity="0.4"/>
      </svg>
    ),
    2: ( // Core Pilates Blast — animated flame
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <g style={{transformOrigin:"20px 30px", animation:"yc-flame 1.1s ease-in-out infinite"}}>
          <path d="M20 5 C14 12 10 17 10 24 C10 31 14.5 37 20 37 C25.5 37 30 31 30 24 C30 17 26 12 20 5 Z" fill={c} opacity="0.9"/>
        </g>
        <g style={{transformOrigin:"20px 28px", animation:"yc-flick 0.9s ease-in-out infinite 0.15s"}}>
          <path d="M20 14 C17 19 15 22 15 26 C15 30 17 33 20 33 C23 33 25 30 25 26 C25 22 23 19 20 14 Z" fill="white" opacity="0.22"/>
        </g>
      </svg>
    ),
    3: ( // Wu Style Tai Chi — spinning yin-yang
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"yc-spin 5s linear infinite", transformOrigin:"20px 20px"}}>
          <circle cx="20" cy="20" r="14" fill={c} opacity="0.15" stroke={c} strokeWidth="1.5"/>
          {/* yin half */}
          <path d="M20 6 A14 14 0 0 1 20 34 A7 7 0 0 1 20 20 A7 7 0 0 0 20 6 Z" fill={c} opacity="0.9"/>
          {/* small dots */}
          <circle cx="20" cy="13" r="2.5" fill="white" opacity="0.9"/>
          <circle cx="20" cy="27" r="2.5" fill={c} opacity="0.9"/>
        </g>
      </svg>
    ),
    5: ( // Power Vinyasa — pulsing lightning bolt
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="15" fill={c} opacity="0" style={{animation:"yc-glow 1.2s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        <circle cx="20" cy="20" r="15" stroke={c} strokeWidth="1.5" opacity="0" style={{animation:"yc-glow 1.2s ease-in-out infinite 0.6s", transformOrigin:"20px 20px"}}/>
        <g style={{animation:"yc-bolt 1.2s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          <path d="M23 4 L11 22 L19 22 L17 36 L29 18 L21 18 Z" fill={c}/>
          <path d="M23 4 L19 14 L24 14 L23 4 Z" fill="white" opacity="0.3"/>
        </g>
      </svg>
    ),
    6: ( // Stress Relief Flow — flowing wave
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"yc-wave 2s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          <path d="M4 20 Q8 13 12 20 Q16 27 20 20 Q24 13 28 20 Q32 27 36 20" stroke={c} strokeWidth="3" fill="none" strokeLinecap="round"/>
        </g>
        <g style={{animation:"yc-wave 2s ease-in-out infinite 0.5s", transformOrigin:"20px 26px"}}>
          <path d="M4 26 Q9 19 14 26 Q19 33 24 26 Q29 19 36 26" stroke={c} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.55"/>
        </g>
        <g style={{animation:"yc-drop 2s ease-in-out infinite 0.9s", transformOrigin:"20px 10px"}}>
          <path d="M20 5 C18 8 15 10 15 13 C15 15.8 17.2 18 20 18 C22.8 18 25 15.8 25 13 C25 10 22 8 20 5Z" fill={c} opacity="0.7"/>
        </g>
      </svg>
    ),
  };
  return (
    <>
      <YogaClassIconStyles/>
      {icons[classId] || <span style={{fontSize: size * 0.78}}>{["🧘","🔥","☯️","⚡","🌊"][classId % 5]}</span>}
    </>
  );
}

// ─── APP-WIDE ANIMATED ICONS ─────────────────────────────────────────────────
function AppIconStyles() {
  return (
    <style>{`
      @keyframes ai-breathe { 0%,100%{transform:scaleX(1) scaleY(1)} 50%{transform:scaleX(1.13) scaleY(0.87)} }
      @keyframes ai-fire    { 0%,100%{transform:scaleY(1) scaleX(1)} 33%{transform:scaleY(1.08) scaleX(0.93)} 66%{transform:scaleY(0.93) scaleX(1.06)} }
      @keyframes ai-wave    { 0%,100%{transform:translateX(0)} 50%{transform:translateX(-5px)} }
      @keyframes ai-hbeat   { 0%,80%,100%{transform:scale(1)} 15%{transform:scale(1.24)} 30%{transform:scale(1)} 45%{transform:scale(1.16)} }
      @keyframes ai-swing   { 0%,100%{transform:rotate(-15deg)} 50%{transform:rotate(15deg)} }
      @keyframes ai-float   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
      @keyframes ai-pulse   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.14)} }
      @keyframes ai-twinkle { 0%,100%{opacity:0.15;transform:scale(0.6)} 50%{opacity:1;transform:scale(1)} }
      @keyframes ai-grow    { 0%,100%{transform:scaleY(0.5)} 50%{transform:scaleY(1)} }
      @keyframes ai-dot1    { 0%,60%,100%{opacity:0.2;transform:translateY(0)} 25%{opacity:1;transform:translateY(-3px)} }
      @keyframes ai-dot2    { 0%,15%,75%,100%{opacity:0.2;transform:translateY(0)} 45%{opacity:1;transform:translateY(-3px)} }
      @keyframes ai-dot3    { 0%,30%,90%,100%{opacity:0.2;transform:translateY(0)} 65%{opacity:1;transform:translateY(-3px)} }
      @keyframes ai-kbray   { 0%,100%{opacity:1;transform:scaleX(1)} 50%{opacity:0.25;transform:scaleX(0.35)} }
      @keyframes ai-kbcore  { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(0.78);opacity:0.7} }
    `}</style>
  );
}

// ─── ANIMATED EMOJI COMPONENT ─────────────────────────────────────────────────
// Usage: <Ico a="float">🌙</Ico>  or  <Ico a="bounce" d="0.3s">⭐</Ico>
// a = animation name (float|bounce|pulse|spin|swing|twinkle|ring|shake|rubber|heartbeat|fire|tada)
// d = delay (css string)  s = speed multiplier (default 1)
const ICO_ANIMS = {
  float:     "ico-float 2.6s ease-in-out infinite",
  bounce:    "ico-bounce 1.4s ease-in-out infinite",
  pulse:     "ico-pulse  2.0s ease-in-out infinite",
  spin:      "ico-spin   4.0s linear infinite",
  spinslow:  "ico-spin   8.0s linear infinite",
  swing:     "ico-swing  2.5s ease-in-out infinite",
  twinkle:   "ico-twinkle 1.8s ease-in-out infinite",
  ring:      "ico-ring   1.5s ease-in-out infinite",
  shake:     "ico-shake  2.4s ease-in-out infinite",
  rubber:    "ico-rubber 2.0s ease-in-out infinite",
  heartbeat: "ico-heartbeat 1.4s ease-in-out infinite",
  fire:      "ico-fire   0.9s ease-in-out infinite",
  tada:      "ico-tada   2.5s ease-in-out infinite",
  wave:      "ico-wave   2.0s ease-in-out infinite",
  nod:       "ico-nod    3.0s ease-in-out infinite",
};
function Ico({ a = "float", d = "0s", children, style = {} }) {
  return (
    <span style={{ display:"inline-block", animation: ICO_ANIMS[a] || ICO_ANIMS.float, animationDelay: d, transformOrigin:"center bottom", willChange:"transform", ...style }}>
      {children}
    </span>
  );
}
function IcoStyles() {
  return (
    <style>{`
      @keyframes ico-float     { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
      @keyframes ico-bounce    { 0%,20%,50%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-8px)} 60%{transform:translateY(-4px)} }
      @keyframes ico-pulse     { 0%,100%{transform:scale(1)} 50%{transform:scale(1.18)} }
      @keyframes ico-spin      { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      @keyframes ico-swing     { 0%,100%{transform:rotate(-14deg)} 50%{transform:rotate(14deg)} }
      @keyframes ico-twinkle   { 0%,100%{transform:scale(1);opacity:0.85} 50%{transform:scale(1.22);opacity:1} }
      @keyframes ico-ring      { 0%,100%{transform:rotate(0deg)} 10%{transform:rotate(-14deg)} 20%{transform:rotate(14deg)} 30%{transform:rotate(-10deg)} 40%{transform:rotate(10deg)} 50%{transform:rotate(0deg)} }
      @keyframes ico-shake     { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-4px) rotate(-3deg)} 40%{transform:translateX(4px) rotate(3deg)} 60%{transform:translateX(-3px)} 80%{transform:translateX(3px)} }
      @keyframes ico-rubber    { 0%,100%{transform:scale(1,1)} 30%{transform:scale(1.18,0.82)} 55%{transform:scale(0.88,1.12)} 75%{transform:scale(1.05,0.95)} }
      @keyframes ico-heartbeat { 0%,100%{transform:scale(1)} 14%{transform:scale(1.3)} 28%{transform:scale(1)} 42%{transform:scale(1.2)} 70%{transform:scale(1)} }
      @keyframes ico-fire      { 0%,100%{transform:scaleY(1) rotate(-1deg)} 33%{transform:scaleY(1.09) rotate(1deg)} 66%{transform:scaleY(0.94) rotate(-0.5deg)} }
      @keyframes ico-tada      { 0%,100%{transform:scale(1) rotate(0deg)} 10%,30%{transform:scale(0.9) rotate(-3deg)} 20%,40%,60%,80%{transform:scale(1.1) rotate(3deg)} 50%,70%{transform:scale(1.1) rotate(-3deg)} 90%{transform:scale(1.03) rotate(1deg)} }
      @keyframes ico-wave      { 0%,100%{transform:rotate(0deg)} 20%{transform:rotate(14deg)} 40%{transform:rotate(-8deg)} 60%{transform:rotate(14deg)} 80%{transform:rotate(-4deg)} }
      @keyframes ico-nod       { 0%,100%{transform:scaleY(1) scaleX(1)} 50%{transform:scaleY(1.1) scaleX(0.92)} }
    `}</style>
  );
}

const BREATHING_ICON_IDS = { box:"box", "478":"moon", physiological:"wind", coherence:"heart", calm:"wave", energize:"bolt", nadi:"wind", wimhof:"bolt", resonance:"heart", kapalbhati:"kapalbhati" };
const MEDITATION_ICON_IDS = { mindfulness:"leaf", bodyscan:"heart", lovingkindness:"heartEmpty", mantra:"sparkles", vipassana:"target", visualization:"moon", peacefulplace:"wave", yoganidra:"moon", chakra:"bolt", tonglen:"lungs" };
const MOOD_ICON_IDS = { Energized:"bolt", Calm:"wave", Focused:"target", Stressed:"stress", Tired:"moon", Happy:"sparkles" };

const MOOD_PLANS = {
  Energized: {
    headline: "Let's channel that energy!", sub: "You're primed — time to move.",
    accent: "#E85D75",
    recs: [
      { icon:"dumbbell", title:"Calisthenics Blast", desc:"Bodyweight power circuit", time:"20 min", screen:"workout", badge:"Workout" },
      { icon:"bolt",     title:"Energizing Breath", desc:"Morning activation boost",  time:"5 min",  screen:"breathe", badge:"Breathing" },
      { icon:"yoga",     title:"Power Yoga Flow",   desc:"Energetic sun salutation",   time:"25 min", screen:"explore", badge:"Yoga" },
    ],
  },
  Calm: {
    headline: "Stay in that stillness.", sub: "Let's deepen your peace.",
    accent: "#5B9BD5",
    recs: [
      { icon:"wave",  title:"Calm Breathing",   desc:"Gentle 4-4-6 anxiety relief", time:"8 min",  screen:"breathe", badge:"Breathing" },
      { icon:"yoga",  title:"Restorative Yoga", desc:"Deep rest & recovery",         time:"25 min", screen:"explore", badge:"Yoga" },
      { icon:"moon",  title:"4-7-8 Breathing",  desc:"Deepen your relaxation",       time:"5 min",  screen:"breathe", badge:"Breathing" },
    ],
  },
  Focused: {
    headline: "Sharp mind — let's build.", sub: "Channel that focus into results.",
    accent: "#4A9B7F",
    recs: [
      { icon:"box",      title:"Box Breathing",  desc:"Navy SEAL focus technique",    time:"5 min",  screen:"breathe", badge:"Breathing" },
      { icon:"dumbbell", title:"Full Body",       desc:"Structured strength circuit",  time:"30 min", screen:"workout", badge:"Workout" },
      { icon:"target",   title:"Balance Flow",   desc:"Mind-body connection yoga",    time:"20 min", screen:"explore", badge:"Yoga" },
    ],
  },
  Stressed: {
    headline: "You're safe here.", sub: "Science-backed relief in minutes.",
    accent: "#9B7BC4",
    recs: [
      { icon:"wind",  title:"Physiological Sigh", desc:"Stanford's fastest reset",   time:"2 min",  screen:"breathe", badge:"Fastest" },
      { icon:"wave",  title:"Calm Breathing",     desc:"Gentle anxiety relief",       time:"8 min",  screen:"breathe", badge:"Breathing" },
      { icon:"yoga",  title:"Gentle Yoga",        desc:"Slow release & grounding",    time:"20 min", screen:"explore", badge:"Yoga" },
    ],
  },
  Tired: {
    headline: "Rest is progress too.", sub: "A gentle session is still a win.",
    accent: "#C4A055",
    recs: [
      { icon:"moon",     title:"4-7-8 Breathing", desc:"Dr. Weil's sleep protocol",  time:"5 min",  screen:"breathe", badge:"Breathing" },
      { icon:"yoga",     title:"Yin Yoga",         desc:"Deep passive holds & rest",  time:"20 min", screen:"explore", badge:"Yoga" },
      { icon:"dumbbell", title:"Light Mobility",   desc:"Gentle stretching & release",time:"10 min", screen:"workout", badge:"Workout" },
    ],
  },
  Happy: {
    headline: "That joy is contagious!", sub: "Let's celebrate with movement.",
    accent: "#E07840",
    recs: [
      { icon:"dumbbell", title:"Calisthenics",    desc:"Bodyweight fun circuit",       time:"25 min", screen:"workout", badge:"Workout" },
      { icon:"yoga",     title:"Sun Flow",         desc:"Classic energizing sequence", time:"20 min", screen:"explore", badge:"Yoga" },
      { icon:"sparkles", title:"Energizing Breath",desc:"Keep the momentum going",    time:"5 min",  screen:"breathe", badge:"Breathing" },
    ],
  },
};

function AppIcon({ name, color = "#7FB89A", size = 24 }) {
  const s = size;
  const icons = {
    lungs: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-breathe 2.4s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          <path d="M18 9 L18 20 C18 20 13 20 11 24 C9 28 9 32 12 34 C15 36 17 34 17 31 L17 23 C16 23 15 21 13 21 C11 21 10 19 11 16.5 C12 13.5 15 10 18 9 Z" fill={color} opacity="0.9"/>
          <path d="M22 9 L22 20 C22 20 27 20 29 24 C31 28 31 32 28 34 C25 36 23 34 23 31 L23 23 C24 23 25 21 27 21 C29 21 30 19 29 16.5 C28 13.5 25 10 22 9 Z" fill={color} opacity="0.9"/>
          <path d="M18 9 C18 9 19 6 20 5 C21 6 22 9 22 9" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round"/>
          <line x1="20" y1="5" x2="20" y2="3" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        </g>
      </svg>
    ),
    fire: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{transformOrigin:"20px 30px", animation:"ai-fire 0.85s ease-in-out infinite"}}>
          <path d="M20 4 C20 4 27 14 27 22 C27 27.5 24 32 20 33 C16 32 13 27.5 13 22 C13 14 20 4 20 4 Z" fill={color}/>
          <path d="M20 16 C20 16 24 21 24 26 C24 28.8 22.2 31 20 31.5 C17.8 31 16 28.8 16 26 C16 21 20 16 20 16 Z" fill="white" opacity="0.28"/>
        </g>
      </svg>
    ),
    bolt: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-pulse 1.0s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          <path d="M23 2 L10 22 L19 22 L17 38 L30 18 L21 18 Z" fill={color}/>
          <path d="M23 2 L19 13 L24 13 L23 2 Z" fill="white" opacity="0.35"/>
        </g>
        <circle cx="20" cy="20" r="16" stroke={color} strokeWidth="1" opacity="0.22" style={{animation:"ai-pulse 1.4s ease-in-out infinite 0.5s", transformOrigin:"20px 20px"}}/>
      </svg>
    ),
    wave: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 1.6s ease-in-out infinite"}}>
          <path d="M3 14 C7 10 11 10 15 14 C19 18 23 18 27 14 C31 10 35 10 38 14" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.5"/>
          <path d="M3 22 C7 18 11 18 15 22 C19 26 23 26 27 22 C31 18 35 18 38 22" stroke={color} strokeWidth="3.5" fill="none" strokeLinecap="round"/>
          <path d="M3 30 C7 26 11 26 15 30 C19 34 23 34 27 30 C31 26 35 26 38 30" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.6"/>
        </g>
      </svg>
    ),
    heart: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-hbeat 1.2s ease-in-out infinite", transformOrigin:"20px 21px"}}>
          <path d="M20 33 C20 33 7 23 7 15 C7 10 10.5 7 14.5 7 C17 7 19 8.5 20 10.5 C21 8.5 23 7 25.5 7 C29.5 7 33 10 33 15 C33 23 20 33 20 33 Z" fill={color}/>
        </g>
      </svg>
    ),
    heartEmpty: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-pulse 1.8s ease-in-out infinite", transformOrigin:"20px 21px"}}>
          <path d="M20 33 C20 33 7 23 7 15 C7 10 10.5 7 14.5 7 C17 7 19 8.5 20 10.5 C21 8.5 23 7 25.5 7 C29.5 7 33 10 33 15 C33 23 20 33 20 33 Z" stroke={color} strokeWidth="2.5" fill="none"/>
        </g>
      </svg>
    ),
    leaf: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-swing 2.8s ease-in-out infinite", transformOrigin:"20px 36px"}}>
          <path d="M20 36 C20 36 20 20 20 17 C20 10 14 5 7 4 C7 4 20 4 29 13 C34 18 34 25 29 29 C26 32 23 35 20 36 Z" fill={color}/>
          <path d="M20 36 C20 36 20 22 20 17" stroke="rgba(0,0,0,0.2)" strokeWidth="1.5" strokeLinecap="round"/>
        </g>
      </svg>
    ),
    wind: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 1.4s ease-in-out infinite"}}>
          <path d="M6 14 L22 14 C26 14 29 11 28 8.5 C27 6.5 24.5 5.5 22 6.5 C20 7.3 19.5 9.5 21.5 10.5" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round"/>
          <path d="M6 20 L28 20" stroke={color} strokeWidth="3.5" strokeLinecap="round"/>
          <path d="M6 26 L24 26 C28 26 30 29 28.5 31.5 C27 33.5 24.5 34 22.5 33" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
        </g>
        <circle cx="6" cy="20" r="2" fill={color} style={{animation:"ai-pulse 1.4s ease-in-out infinite 0.3s", transformOrigin:"6px 20px"}} opacity="0.7"/>
      </svg>
    ),
    moon: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-swing 3.5s ease-in-out infinite", transformOrigin:"20px 22px"}}>
          <path d="M30 22 C30 30 24 36 16 36 C14 36 12 35.6 10 34.8 C15.5 33.5 19.5 28.5 19.5 22.5 C19.5 16 14 10.5 7 10 C10.5 7 15.5 5 22 7 C27 9 30 15 30 22 Z" fill={color}/>
        </g>
        <circle cx="32" cy="8" r="1.8" fill={color} style={{animation:"ai-twinkle 2.2s ease-in-out infinite 0s"}}/>
        <circle cx="36" cy="17" r="1.2" fill={color} style={{animation:"ai-twinkle 2.2s ease-in-out infinite 0.8s"}}/>
        <circle cx="35" cy="5" r="1.4" fill={color} style={{animation:"ai-twinkle 2.2s ease-in-out infinite 1.5s"}}/>
      </svg>
    ),
    sparkles: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <path d="M20 7 L22 17.2 L32 19 L22 20.8 L20 31 L18 20.8 L8 19 L18 17.2 Z" fill={color} style={{animation:"ai-pulse 1.3s ease-in-out infinite", transformOrigin:"20px 19px"}}/>
        <path d="M33 9 L33.9 12.6 L37.5 13.5 L33.9 14.4 L33 18 L32.1 14.4 L28.5 13.5 L32.1 12.6 Z" fill={color} opacity="0.6" style={{animation:"ai-twinkle 1.6s ease-in-out infinite 0.35s", transformOrigin:"33px 13.5px"}}/>
        <path d="M9 25 L9.7 27.8 L12.5 28.5 L9.7 29.2 L9 32 L8.3 29.2 L5.5 28.5 L8.3 27.8 Z" fill={color} opacity="0.5" style={{animation:"ai-twinkle 1.6s ease-in-out infinite 0.7s", transformOrigin:"9px 28.5px"}}/>
      </svg>
    ),
    target: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="16" stroke={color} strokeWidth="1.5" fill="none" opacity="0.27" style={{animation:"ai-pulse 1.6s ease-in-out infinite 0.4s", transformOrigin:"20px 20px"}}/>
        <circle cx="20" cy="20" r="11" stroke={color} strokeWidth="2" fill="none" opacity="0.5" style={{animation:"ai-pulse 1.6s ease-in-out infinite 0.2s", transformOrigin:"20px 20px"}}/>
        <circle cx="20" cy="20" r="6" stroke={color} strokeWidth="2" fill="none" opacity="0.78"/>
        <circle cx="20" cy="20" r="2.5" fill={color} style={{animation:"ai-pulse 1.6s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        <line x1="20" y1="2" x2="20" y2="7" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        <line x1="20" y1="33" x2="20" y2="38" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        <line x1="2" y1="20" x2="7" y2="20" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        <line x1="33" y1="20" x2="38" y2="20" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    stress: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 0.55s ease-in-out infinite"}}>
          <path d="M5 20 L10 12 L15 22 L20 9 L25 22 L30 13 L35 20" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <path d="M5 29 L9 23 L13 29 L17 19 L22 30 L26 23 L32 29" stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.42" style={{animation:"ai-float 0.55s ease-in-out infinite 0.275s"}}/>
      </svg>
    ),
    kapalbhati: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        {/* Core — pulsing fast like rapid belly pumps */}
        <circle cx="20" cy="20" r="7.5" fill={color} style={{animation:"ai-kbcore 0.38s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
        <circle cx="20" cy="20" r="5" fill="white" opacity="0.22" style={{animation:"ai-kbcore 0.38s ease-in-out infinite 0.19s", transformOrigin:"20px 20px"}}/>
        {/* 8 rays — each flashes in sequence like rapid exhale bursts */}
        <line x1="20" y1="10.5" x2="20" y2="4"   stroke={color} strokeWidth="2.8" strokeLinecap="round" style={{animation:"ai-kbray 0.38s ease-in-out infinite 0s",     transformOrigin:"20px 10.5px"}}/>
        <line x1="26" y1="12.5" x2="31" y2="7"   stroke={color} strokeWidth="2.2" strokeLinecap="round" style={{animation:"ai-kbray 0.38s ease-in-out infinite 0.047s", transformOrigin:"26px 12.5px"}}/>
        <line x1="29.5" y1="20" x2="36" y2="20"  stroke={color} strokeWidth="2.8" strokeLinecap="round" style={{animation:"ai-kbray 0.38s ease-in-out infinite 0.095s", transformOrigin:"29.5px 20px"}}/>
        <line x1="26" y1="27.5" x2="31" y2="33"  stroke={color} strokeWidth="2.2" strokeLinecap="round" style={{animation:"ai-kbray 0.38s ease-in-out infinite 0.142s", transformOrigin:"26px 27.5px"}}/>
        <line x1="20" y1="29.5" x2="20" y2="36"  stroke={color} strokeWidth="2.8" strokeLinecap="round" style={{animation:"ai-kbray 0.38s ease-in-out infinite 0.19s",  transformOrigin:"20px 29.5px"}}/>
        <line x1="14" y1="27.5" x2="9"  y2="33"  stroke={color} strokeWidth="2.2" strokeLinecap="round" style={{animation:"ai-kbray 0.38s ease-in-out infinite 0.237s", transformOrigin:"14px 27.5px"}}/>
        <line x1="10.5" y1="20" x2="4"  y2="20"  stroke={color} strokeWidth="2.8" strokeLinecap="round" style={{animation:"ai-kbray 0.38s ease-in-out infinite 0.285s", transformOrigin:"10.5px 20px"}}/>
        <line x1="14" y1="12.5" x2="9"  y2="7"   stroke={color} strokeWidth="2.2" strokeLinecap="round" style={{animation:"ai-kbray 0.38s ease-in-out infinite 0.332s", transformOrigin:"14px 12.5px"}}/>
        {/* Outer glow ring */}
        <circle cx="20" cy="20" r="15" stroke={color} strokeWidth="1.2" fill="none" opacity="0.28" style={{animation:"ai-pulse 0.76s ease-in-out infinite", transformOrigin:"20px 20px"}}/>
      </svg>
    ),
    box: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-pulse 3s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          <rect x="9" y="9" width="22" height="22" rx="3.5" stroke={color} strokeWidth="2.5" fill="none"/>
          <circle cx="9" cy="9" r="2.5" fill={color}/>
          <circle cx="31" cy="9" r="2.5" fill={color}/>
          <circle cx="9" cy="31" r="2.5" fill={color}/>
          <circle cx="31" cy="31" r="2.5" fill={color}/>
          <rect x="15.5" y="15.5" width="9" height="9" rx="2" fill={color} opacity="0.38"/>
        </g>
      </svg>
    ),
    yoga: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-pulse 2.8s ease-in-out infinite", transformOrigin:"20px 20px"}}>
          <circle cx="20" cy="7" r="4.5" fill={color}/>
          <path d="M20 11.5 L20 22" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M20 22 C14 22 9 24.5 7 29 L10 30 C11.5 26.5 15 25 20 24.5 C25 25 28.5 26.5 30 30 L33 29 C31 24.5 26 22 20 22 Z" fill={color} opacity="0.85"/>
          <path d="M20 15.5 C16 14.5 11.5 15.5 9 20" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" opacity="0.75"/>
          <path d="M20 15.5 C24 14.5 28.5 15.5 31 20" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" opacity="0.75"/>
          <circle cx="9" cy="20" r="2" fill={color} opacity="0.65"/>
          <circle cx="31" cy="20" r="2" fill={color} opacity="0.65"/>
        </g>
      </svg>
    ),
    dumbbell: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 1s ease-in-out infinite"}}>
          <rect x="8" y="17" width="24" height="6" rx="3" fill={color}/>
          <rect x="4" y="13" width="7" height="14" rx="3.5" fill={color} opacity="0.9"/>
          <rect x="29" y="13" width="7" height="14" rx="3.5" fill={color} opacity="0.9"/>
          <rect x="1" y="12" width="4" height="16" rx="2" fill={color} opacity="0.6"/>
          <rect x="35" y="12" width="4" height="16" rx="2" fill={color} opacity="0.6"/>
        </g>
      </svg>
    ),
    music: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 1.1s ease-in-out infinite"}}>
          <path d="M16 30 L16 14 L32 11 L32 26" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="13.5" cy="30" r="4" fill={color}/>
          <circle cx="29.5" cy="26" r="4" fill={color}/>
        </g>
        <circle cx="36" cy="7" r="2" fill={color} opacity="0" style={{animation:"wk-ring 2.2s ease-out infinite"}}/>
      </svg>
    ),
    speaker: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <path d="M7 14 L15 14 L24 8 L24 32 L15 26 L7 26 Z" fill={color} opacity="0.9"/>
        <path d="M28 15 C32 17.5 32 22.5 28 25" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" style={{animation:"ai-pulse 1s ease-in-out infinite"}}/>
        <path d="M30 10 C37 14 37 26 30 30" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5" style={{animation:"ai-pulse 1s ease-in-out infinite 0.3s"}}/>
      </svg>
    ),
    bell: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-swing 1.3s ease-in-out infinite", transformOrigin:"20px 7px"}}>
          <circle cx="20" cy="7" r="3" fill={color} opacity="0.85"/>
          <path d="M11 28 C11 21 14 13 20 11 C26 13 29 21 29 28 Z" fill={color}/>
          <path d="M9 28 L31 28" stroke={color} strokeWidth="3" strokeLinecap="round"/>
          <ellipse cx="20" cy="31" rx="4" ry="2.5" fill={color} opacity="0.8"/>
        </g>
      </svg>
    ),
    chart: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <rect x="6" y="24" width="8" height="11" rx="2" fill={color} opacity="0.55" style={{transformOrigin:"10px 35px", animation:"ai-grow 1.9s ease-in-out infinite 0s"}}/>
        <rect x="16" y="14" width="8" height="21" rx="2" fill={color} style={{transformOrigin:"20px 35px", animation:"ai-grow 1.9s ease-in-out infinite 0.3s"}}/>
        <rect x="26" y="19" width="8" height="16" rx="2" fill={color} opacity="0.8" style={{transformOrigin:"30px 35px", animation:"ai-grow 1.9s ease-in-out infinite 0.6s"}}/>
        <line x1="4" y1="35" x2="36" y2="35" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
      </svg>
    ),
    calendar: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 2.2s ease-in-out infinite"}}>
          <rect x="5" y="9" width="30" height="27" rx="5" stroke={color} strokeWidth="2.5" fill="none"/>
          <line x1="5" y1="17" x2="35" y2="17" stroke={color} strokeWidth="2" opacity="0.45"/>
          <line x1="13" y1="5" x2="13" y2="12" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="27" y1="5" x2="27" y2="12" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
          <circle cx="13" cy="24" r="2" fill={color} opacity="0.5"/>
          <circle cx="20" cy="24" r="2.5" fill={color} style={{animation:"ai-pulse 1.1s ease-in-out infinite"}}/>
          <circle cx="27" cy="24" r="2" fill={color} opacity="0.5"/>
          <circle cx="13" cy="31" r="2" fill={color} opacity="0.3"/>
          <circle cx="20" cy="31" r="2" fill={color} opacity="0.5"/>
        </g>
      </svg>
    ),
    chat: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 2s ease-in-out infinite"}}>
          <path d="M6 8 C6 5.8 7.8 4 10 4 L30 4 C32.2 4 34 5.8 34 8 L34 24 C34 26.2 32.2 28 30 28 L22 28 L18 35 L14 28 L10 28 C7.8 28 6 26.2 6 24 Z" fill={color} opacity="0.18" stroke={color} strokeWidth="2"/>
          <circle cx="14" cy="16" r="2.5" fill={color} style={{animation:"ai-dot1 1.3s ease-in-out infinite"}}/>
          <circle cx="20" cy="16" r="2.5" fill={color} style={{animation:"ai-dot2 1.3s ease-in-out infinite"}}/>
          <circle cx="26" cy="16" r="2.5" fill={color} style={{animation:"ai-dot3 1.3s ease-in-out infinite"}}/>
        </g>
      </svg>
    ),
    door: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <rect x="5" y="3" width="22" height="34" rx="3" stroke={color} strokeWidth="2.5" fill="none"/>
        <rect x="5" y="3" width="22" height="34" rx="3" fill={color} opacity="0.12"/>
        <circle cx="23" cy="20" r="2" fill={color} opacity="0.8"/>
        <g style={{animation:"ai-float 1s ease-in-out infinite"}}>
          <path d="M29 16 L38 20 L29 24" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="27" y1="20" x2="38" y2="20" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
        </g>
      </svg>
    ),
    sunrise: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 2.5s ease-in-out infinite"}}>
          <path d="M20 18 C13 18 7 22.8 7 28.5 L33 28.5 C33 22.8 27 18 20 18 Z" fill={color}/>
          <circle cx="20" cy="17" r="5" fill={color}/>
        </g>
        <line x1="20" y1="4" x2="20" y2="9" stroke={color} strokeWidth="2.5" strokeLinecap="round" style={{animation:"ai-pulse 1.8s ease-in-out infinite 0s"}}/>
        <line x1="30" y1="8" x2="27.5" y2="11.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" style={{animation:"ai-pulse 1.8s ease-in-out infinite 0.3s"}}/>
        <line x1="10" y1="8" x2="12.5" y2="11.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" style={{animation:"ai-pulse 1.8s ease-in-out infinite 0.6s"}}/>
        <line x1="36" y1="17" x2="31" y2="18.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" style={{animation:"ai-pulse 1.8s ease-in-out infinite 0.9s"}}/>
        <line x1="4" y1="17" x2="9" y2="18.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" style={{animation:"ai-pulse 1.8s ease-in-out infinite 1.2s"}}/>
        <line x1="4" y1="28.5" x2="36" y2="28.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.35"/>
      </svg>
    ),
    gift: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-float 1.6s ease-in-out infinite"}}>
          <rect x="5" y="17" width="30" height="20" rx="3" fill={color} opacity="0.88"/>
          <rect x="3" y="12" width="34" height="8" rx="3" fill={color}/>
          <rect x="18" y="12" width="4" height="25" rx="2" fill="white" opacity="0.22"/>
          <path d="M20 12 C17 8 11.5 7 11.5 10.5 C11.5 13.5 16 13 20 12 Z" fill={color}/>
          <path d="M20 12 C23 8 28.5 7 28.5 10.5 C28.5 13.5 24 13 20 12 Z" fill={color}/>
        </g>
      </svg>
    ),
    bulb: (
      <svg width={s} height={s} viewBox="0 0 40 40" fill="none">
        <g style={{animation:"ai-pulse 1.6s ease-in-out infinite", transformOrigin:"20px 18px"}}>
          <path d="M20 4 C13.4 4 8 9.4 8 16 C8 20.4 10.4 24.2 14 26.4 L14 30 C14 31.1 14.9 32 16 32 L24 32 C25.1 32 26 31.1 26 30 L26 26.4 C29.6 24.2 32 20.4 32 16 C32 9.4 26.6 4 20 4 Z" fill={color} opacity="0.9"/>
          <rect x="16" y="32" width="8" height="2.5" rx="1.25" fill={color} opacity="0.6"/>
          <rect x="17" y="35.5" width="6" height="2" rx="1" fill={color} opacity="0.45"/>
          <line x1="20" y1="1" x2="20" y2="4" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
          <line x1="32" y1="6" x2="30" y2="8" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
          <line x1="8" y1="6" x2="10" y2="8" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
        </g>
      </svg>
    ),
  };
  return icons[name] ? <>{icons[name]}</> : <span style={{fontSize: s * 0.75}}>{name}</span>;
}

// ─── AI CHAT COACH ────────────────────────────────────────────────────────────
function getLocalAIResponse(msg, user, sessions) {
  const m = msg.toLowerCase();
  const name = user?.name?.split(" ")[0] || "there";
  const count = sessions?.length || 0;
  const types = { workout:0, breathing:0, yoga:0 };
  sessions?.forEach(s => { if(s.type in types) types[s.type]++; });
  const leastType = Object.entries(types).sort((a,b)=>a[1]-b[1])[0][0];

  // Health profile context
  const condition = user?.condition || "";
  const bmi = user?.bmi || 0;
  const activity = user?.activity || "";
  const isHypo = /hypothyroid/i.test(condition);
  const isHyper = /hyperthyroid/i.test(condition);
  const isJoint = /joint/i.test(condition);
  const isHeart = /heart/i.test(condition);

  // Thyroid-specific queries
  if (/thyroid|hypothyroid|hyperthyroid/.test(m)) {
    if (isHypo) return `With hypothyroidism, fatigue is real — not laziness. Focus on Energizing Breath in the morning to counter that sluggishness, and moderate workouts (not overtraining). Box Breathing helps regulate cortisol which directly affects thyroid function. Start slow and build. 💙`;
    if (isHyper) return `With hyperthyroidism, your heart rate is already elevated — high-intensity cardio can overstress your system. Heart Coherence breathing is your best friend (5s in, 5s out) — it actually slows heart rate. Yoga and Calm Breathing are ideal. Avoid Energizing Breath. 🌿`;
    return `Thyroid health is closely linked to stress and cortisol. Breathing exercises — especially Box Breathing and Heart Coherence — help regulate the HPA axis which affects thyroid function. Worth discussing with your doctor too. 💙`;
  }

  // Health-aware responses
  if (isHypo && /tired|exhaust|fatigue|energy/.test(m))
    return `Thyroid fatigue is different from regular tiredness — it's metabolic. Start with Energizing Breath (5 min) which activates your sympathetic system without taxing your thyroid. Then light movement: Arms or Back workout. Consistency over intensity is everything for you, ${name}. ⚡`;

  if (isHyper && /workout|exercise|train|intense|cardio/.test(m))
    return `With hyperthyroidism, I'd steer you toward Yoga and moderate-pace workouts (Back, Shoulders) over high-intensity circuits. Your heart is already working harder — let's not push it further. Heart Coherence breathing before any session is a great stabiliser. 🧘`;

  if (isJoint && /workout|exercise|legs|squat/.test(m))
    return `With joint pain, low-impact is key. Back workout and light Shoulders are good. For legs, swap Squats for Glute Bridges (no knee stress) and Calf Raises. Breathing before sessions reduces systemic inflammation. Always warm up properly. 🌿`;

  if (isHeart && /intense|hard|cardio|fast/.test(m))
    return `With a heart condition, I'd recommend building slowly — moderate workouts and Heart Coherence breathing (it's literally designed to optimise heart rhythm). Always check with your cardiologist before intense sessions. Start with 4-7-8 breathing to calm the nervous system first. 💙`;

  // BMI-aware workout advice
  if (/what.*do|today|recommend|suggest/.test(m) && bmi > 0) {
    if (bmi >= 30) return `Based on your profile, starting with breathing sessions and gentle yoga builds your foundation without joint stress. Once consistent for 2 weeks, add Back and Arms workouts. Physiological Sigh daily resets cortisol which directly affects weight. Small steps, real results. 🌿`;
    if (bmi < 18.5) return `For building mass, Full Body and Calisthenics are your priority — focus on progressive resistance. Protein timing matters. Calm Breathing post-workout speeds recovery. You've got ${count} sessions logged — keep building that habit. 💪`;
  }

  if (/stress|anxious|anxiety|overwhelm|panic|nervous|tense|freak/.test(m))
    return `The fastest reset is Physiological Sigh — just 2 minutes. Double inhale through your nose, then one long slow exhale. Stanford research shows even 1–2 cycles rapidly drops cortisol. Tap Breathing → Physiological Sigh right now. 💙`;

  if (/sleep|insomnia|can't sleep|cant sleep|fall asleep|bed/.test(m))
    return `4-7-8 Breathing is the gold standard for sleep. Inhale 4s → Hold 7s → Exhale 8s. The extended hold activates your parasympathetic nervous system deeply. Do 4 cycles in bed tonight with your eyes closed. You'll feel the shift. 🌙`;

  if (/tired|exhaust|fatigue|no energy|drained/.test(m) && !/morning/.test(m))
    return `If it's physical fatigue, rest is the right call — maybe a gentle Yin Yoga session instead. If it's mental fog, Energizing Breath (5 min) actually works faster than coffee. Sharp short inhales fire the sympathetic system. Try it! ⚡`;

  if (/energy|energi|morning|wake up|sluggish|low energy|groggy/.test(m))
    return `Morning activation: Energizing Breath first (5 min) — sharp inhales fire your sympathetic system. Follow with Calisthenics for a full-body wake-up. That combo carries your energy for 3–4 hours. Way better than just scrolling. ⚡`;

  if (/focus|concentrat|study|work.*out|distract|productiv|clarity|sharp/.test(m))
    return `Box Breathing is used by Navy SEALs before high-pressure situations — 4 counts in → 4 hold → 4 out → 4 hold. Just 5 minutes resets your prefrontal cortex. Tap Breathing → Box Breathing before any deep work session. 🎯`;

  if (/calm|relax|peace|unwind|chill|settle/.test(m))
    return `Heart Coherence breathing is incredible for this — equal 5-second inhale and exhale. It syncs your heart and brain rhythms and measurably lowers cortisol. Try 5 cycles in the Breathing section. 🌊`;

  if (/5\s*min|five\s*min|quick|short.*session|no time|busy/.test(m))
    return `5 minutes is perfect for Physiological Sigh (stress reset) or Box Breathing (focus) — both complete in under 5 mins with immediate effects. If you want physical: the Abs circuit takes 3 sets and about 8 minutes. Every session counts. 🕐`;

  if (/10\s*min|ten\s*min|half.*hour/.test(m))
    return `10 minutes: try 4-7-8 Breathing (5 min) + a quick Shoulders circuit. Or Energizing Breath + core activation. Short focused sessions compound over time — your body adapts to the consistency, not just the duration. 💪`;

  if (/back\s*pain|back\s*hurt|lower\s*back|spine|posture/.test(m))
    return `For back pain, the Back workout in CalmFit focuses on mobility and posterior chain strengthening — go light and focus on form. Pair with diaphragmatic breathing (breathe into your belly, not chest) which directly reduces lower back tension. 🧘`;

  if (/leg|quad|hamstring|glute|squat/.test(m))
    return `Legs section: Squats (4×12) → Lunges (3×10 each) → Glute Bridge (4×15) → Calf Raises (3×20). Rest 60s between sets. Key tip: full depth on squats, weight in heels. Your glutes should burn, not your knees. 🦵`;

  if (/shoulder|overhead|deltoid|press/.test(m))
    return `Shoulders workout covers overhead press, lateral raises, and rear delts. Classic sequence: Overhead Press → Lateral Raises → Arnold Press. Moderate weight, full range. Rear delts are usually the weak link — don't skip them. 💪`;

  if (/arm|bicep|tricep|curl|pull/.test(m))
    return `Arms: superset biceps + triceps for max efficiency. Barbell curl superset with tricep extension, 3×12 each, 45s rest. The pump is real and the time is half. Check the Arms category in Workouts. 💪`;

  if (/abs|core|stomach|six.?pack|plank/.test(m))
    return `Core circuit: Plank 3×45s → Crunches 3×20 → Leg Raises 3×15 → Bicycle 3×20. About 12 minutes. Key: full exhale at the top of each crunch to activate the deep transverse abdominis — that's what actually flattens your stomach. 🔥`;

  if (/full.?body|everything|whole body/.test(m))
    return `Full Body circuit in CalmFit is your best bet — it hits all major muscle groups in one session. Great for busy weeks when you can only train 2-3x. Pair with Box Breathing after for recovery. 💪`;

  if (/plan|schedule|week|routine|program|day.*split/.test(m))
    return `Here's a solid weekly split: Mon → Legs, Tue → Breathing (Box + 4-7-8), Wed → Shoulders + Arms, Thu → Yoga/Explore, Fri → Full Body, Weekend → rest or Physiological Sigh for recovery. Adjust based on energy — the best plan is one you'll actually follow. 📅`;

  if (/haven.t|havent|long time|week.*no|not.*workout|lazy|motivat|guilt|procrastinat/.test(m))
    return `The hardest part is starting again — and you just did by asking. Tonight: one breathing session, just 5 minutes. Tomorrow: any 15-min workout. The gap closes fast once you move. Every comeback starts with one rep. You've got this, ${name}. 🌱`;

  if (/weight|calor|fat|lose|burn|lean/.test(m))
    return `For calorie burn, Full Body and Calisthenics are highest intensity. But don't overlook breathing — it regulates cortisol, which directly drives fat storage especially around the belly. Stress management + training together is the most effective combo. 🔥`;

  if (/why|science|how does|explain|benefit|proof/.test(m))
    return `Breathing directly stimulates the vagus nerve which controls your parasympathetic "rest and digest" system. Even 5 minutes of paced breathing measurably lowers heart rate variability and cortisol. Exercise does the same over time — both build resilience. 🧠`;

  if (/what.*do|today|suggest|recommend|should i|where.*start/.test(m)) {
    if (count === 0) return `Since you're just starting, try Box Breathing first — 5 minutes and you'll immediately feel the difference. Then come back tomorrow and we'll plan your first workout. Small steps, real results. 🌿`;
    return `Based on your ${count} sessions, you've done less ${leastType} recently. I'd balance it out today — ${leastType === "breathing" ? "try Calm Breathing (8 min), great for daily stress." : leastType === "yoga" ? "Explore section has yoga flows, even 20 min makes a difference." : "pick any muscle group you haven't hit this week."} Mix it up for full-body progress. 🎯`;
  }

  if (/hello|hi |hey|what can|who are you|help/.test(m))
    return `I'm your CalmFit AI Coach — I know all the workouts, breathing techniques, and your session history (${count} sessions logged). Ask me what to do today, how to fix a problem, or anything wellness-related. I'm here. 🌿`;

  if (/diet|eat|food|nutrition|protein|carb|meal|calorie.*eat/.test(m))
    return `Nutrition amplifies everything. For workouts: protein at each meal (eggs, dal, paneer, chicken). For breathing/stress: avoid caffeine 4h before bed — it raises cortisol by 30%. Hydration matters too — even mild dehydration tanks workout performance by 10-20%. Small consistent meals beat skipping. 🥗`;

  if (/streak|days.*row|consecutive|habit|consistent/.test(m))
    return `${count > 0 ? `Your ${count}-session history shows you're building something real.` : "Streaks start with a single session."} Science shows 21 days to form a habit, 66 days to make it automatic. Even a 5-min breathing session counts — never break the chain. Your future self will thank you. 🔥`;

  if (/chest|pec|bench|push.?up/.test(m))
    return `Chest: Bench Press 4×10 → Push-ups 3×max → Diamond Push-ups 3×12. Key tip — control the descent (3 seconds down) which is where most chest growth happens. Don't rush the negatives. 💪`;

  if (/meditat|mindful|zen|still|quiet/.test(m))
    return `Meditation in CalmFit pairs beautifully with breathing — try a 5-min breathing session first to settle the mind, then go into Mindfulness or Body Scan. Even 10 minutes daily measurably thickens the prefrontal cortex after 8 weeks. Start with 5 min, don't force it. 🧘`;

  if (/stretch|flexib|tight|stiff|mobility/.test(m))
    return `For flexibility, the Yoga section has a Stress Relief Flow and Yin Yoga which target tight hips, hamstrings and lower back — the 3 most common areas. Hold each pose at least 90 seconds for real connective tissue change. Post-workout is the best time, muscles are warm. 🌿`;

  if (/beginner|new|start|first.*time|never.*workout/.test(m))
    return `Perfect starting point: Day 1 — Box Breathing (5 min). Day 2 — Arms workout, just the first 3 exercises. Day 3 — rest. Week 2 add one more. The nervous system adapts first, muscles follow. Don't go all-in on day 1 — that's how people quit. You've got this ${name}. 🌱`;

  if (/pain|hurt|sore|injur|recover/.test(m))
    return `If something hurts (not just sore), stop and rest it. DOMS (soreness 24-48h later) is normal — active recovery helps: gentle yoga, walking, or Calm Breathing which increases blood flow. Never train through sharp or joint pain. When in doubt, rest > regret. 💙`;

  const fallbacks = [
    `Good question, ${name}! With ${count} sessions logged, you're ahead of most people who just think about it. Pick one thing to work on this week — either daily 5-min breathing or 3x workouts. Small daily wins compound into big results. What sounds doable? 💚`,
    `${name}, the most impactful thing right now is reducing decision fatigue. Pick your session, open CalmFit, and start. Motivation comes after you begin, not before. What do you feel like today — breathe, move, or meditate? 🔥`,
    `Worth knowing: ${count > 0 ? `your ${count} sessions have already built real neural pathways for this habit.` : "every expert was once a beginner."} Breathing + movement together is the most powerful wellness combination. What's your biggest challenge right now? 🌿`,
  ];
  return fallbacks[Math.floor(Math.random() * fallbacks.length)];
}

// Smart AI response engine — returns { text, cards, chips }
function getSmartAIResponse(msg, user, sessions) {
  const m = msg.toLowerCase();
  const name = user?.name?.split(" ")[0] || "there";
  const count = sessions?.length || 0;
  const lastSession = sessions?.[0];
  const streak = (() => { let s=0,d=new Date(); for(let i=0;i<30;i++){const ds=d.toDateString();if(sessions?.some(x=>new Date(x.date||x.timestamp).toDateString()===ds)){s++;}else if(i>0)break;d=new Date(d-86400000);} return s; })();

  const SESSIONS = {
    breathing: [
      { label:"Physiological Sigh", sub:"2 min · Instant stress reset", color:COLORS.lavender, screen:"breathe", icon:"wind" },
      { label:"Box Breathing",      sub:"5 min · Focus & calm",         color:COLORS.blue,    screen:"breathe", icon:"box"  },
      { label:"4-7-8 Breathing",    sub:"5 min · Fall asleep fast",     color:"#9B8EC5",      screen:"breathe", icon:"moon" },
      { label:"Energizing Breath",  sub:"3 min · Morning activation",   color:COLORS.gold,    screen:"breathe", icon:"bolt" },
      { label:"Calm Breathing",     sub:"8 min · Anxiety relief",       color:COLORS.sage,    screen:"breathe", icon:"wave" },
      { label:"Heart Coherence",    sub:"10 min · HRV & balance",       color:COLORS.coral,   screen:"breathe", icon:"heart"},
    ],
    yoga: [
      { label:"Morning Sun Flow",  sub:"20 min · Beginner",      color:"#7C9E87", screen:"explore" },
      { label:"Core Pilates Blast",sub:"30 min · Intermediate",  color:"#C4A882", screen:"explore" },
      { label:"Wu Style Tai Chi",  sub:"45 min · Beginner",      color:"#9B8EC5", screen:"explore" },
      { label:"Power Vinyasa",     sub:"45 min · Advanced",      color:"#7C9E87", screen:"explore" },
      { label:"Stress Relief Flow",sub:"25 min · All levels",    color:"#C4A882", screen:"explore" },
    ],
    workout: [
      { label:"Full Body Workout", sub:"30 min · Total burn",    color:COLORS.gold,   screen:"workout" },
      { label:"Abs & Core",        sub:"20 min · Midsection",    color:"#E8935D",     screen:"workout" },
      { label:"Legs Day",          sub:"25 min · Quads & glutes",color:"#E85D75",     screen:"workout" },
      { label:"Arms",              sub:"20 min · Biceps & triceps",color:COLORS.sage,  screen:"workout" },
      { label:"Pilates",           sub:"30 min · Core & posture",color:"#C4A882",     screen:"workout" },
    ],
  };

  const chips_general = ["What to do today? 🎯","I'm stressed 😤","Best for sleep 🌙","I have 5 minutes ⚡","Motivate me 🔥","Build my plan 📅"];
  const chips_breathe  = ["Start Box Breathing","Try 4-7-8 for sleep","Physiological Sigh now","Heart Coherence"];
  const chips_workout  = ["What's best today?","Skip leg day? 😅","Short workout pls","Which burns most?"];
  const chips_yoga     = ["Morning routine?","Stress relief yoga","Beginner yoga","Tai chi vs yoga?"];

  // STRESS / ANXIETY
  if (/stress|anxious|anxiety|overwhelm|panic|nervous|tense/.test(m)) return {
    text: `Stress hits fast — but so does the reset. Physiological Sigh is the fastest scientifically-proven stress relief (takes 2 minutes). Follow it with Stress Relief Flow and you'll feel like a different person. Tap below to start 👇`,
    cards: [SESSIONS.breathing[0], SESSIONS.yoga[4]],
    chips: chips_breathe,
  };

  // SLEEP
  if (/sleep|insomnia|can't sleep|bed|tired|rest|relax before/.test(m)) return {
    text: `For sleep, 4-7-8 Breathing is the gold standard — it activates your parasympathetic system in under 5 minutes. Pair it with Stress Relief Flow earlier in the evening. Try this tonight 🌙`,
    cards: [SESSIONS.breathing[2], SESSIONS.yoga[4]],
    chips: ["Does it really work?", "Any yoga for sleep?", "Best time to do it?"],
  };

  // ENERGY / MORNING
  if (/energy|morning|wake|tired|sluggish|sleepy|motivat|kick.?start/.test(m)) return {
    text: `Morning activation = Energizing Breath first (3 min, transforms your nervous system), then Morning Sun Flow. This combo is the most powerful way to start your day. Ready? ☀️`,
    cards: [SESSIONS.breathing[3], SESSIONS.yoga[0]],
    chips: ["I only have 5 min", "I hate mornings 😅", "Show me a workout"],
  };

  // TODAY / WHAT TO DO
  if (/today|what.*do|what.*next|suggest|recommend|plan|where.*start/.test(m)) {
    const todayCards = count === 0
      ? [SESSIONS.breathing[1], SESSIONS.yoga[0]]
      : streak >= 3
        ? [SESSIONS.workout[0], SESSIONS.breathing[3]]
        : [SESSIONS.yoga[0], SESSIONS.breathing[1]];
    const todayText = count === 0
      ? `Welcome! Start with Box Breathing (5 min) to feel the CalmFit difference, then try Morning Sun Flow. These two will show you exactly what this app can do. 🌿`
      : streak >= 3
        ? `${streak}-day streak — you're on fire 🔥 Today: Full Body to keep the momentum, then Energizing Breath post-workout for recovery. You've earned this.`
        : `You've got ${count} session${count>1?"s":""} logged. Today's pick: Morning Sun Flow to reset + Box Breathing for focus. Simple and effective. Let's go! 💪`;
    return { text: todayText, cards: todayCards, chips: chips_general };
  }

  // 5 MINUTES
  if (/5 min|five min|quick|short|no time|busy|fast/.test(m)) return {
    text: `5 minutes is actually enough to reset your entire nervous system. Physiological Sigh: 2 double-inhales through the nose, long exhale. Done. Or Box Breathing for focus. Pick one 👇`,
    cards: [SESSIONS.breathing[0], SESSIONS.breathing[1]],
    chips: ["What about 10 min?", "I want yoga instead", "I want a quick workout"],
  };

  // SKIPPED / MISSED
  if (/skip|skipped|missed|week off|haven.t|havent|long time|restart|back to/.test(m)) return {
    text: `Welcome back! The gap doesn't matter — the fact you're here does. Don't go hard on day 1 back. Start with Calm Breathing to reconnect, then Morning Sun Flow. Easy re-entry wins every time. 🌱`,
    cards: [SESSIONS.breathing[4], SESSIONS.yoga[0]],
    chips: ["Give me a 7-day plan", "I feel guilty 😞", "How do I stay consistent?"],
  };

  // WORKOUT SPECIFIC
  if (/workout|exercise|train|gym|strength|muscle|weight|lift/.test(m)) return {
    text: `${count > 0 ? `You've logged ${count} sessions so far — solid foundation.` : "Let's build your foundation."} Here are the best options right now. Full Body gives the most bang for your time. Abs if you want to go targeted. 💪`,
    cards: [SESSIONS.workout[0], SESSIONS.workout[1]],
    chips: chips_workout,
  };

  // YOGA SPECIFIC
  if (/yoga|flow|pilates|tai chi|vinyasa|pose|stretch|flexibility/.test(m)) return {
    text: `CalmFit has 5 classes from Beginner to Advanced, all with animated GIF pose guides so you know exactly what to do. What's your level and time today?`,
    cards: [SESSIONS.yoga[0], SESSIONS.yoga[4]],
    chips: chips_yoga,
  };

  // STREAK / PROGRESS
  if (/streak|progress|history|how.*doing|stats|achievement/.test(m)) return {
    text: streak >= 1
      ? `You're on a ${streak}-day streak — that puts you in the top 20% of CalmFit users. ${count} total sessions logged. Keep building. Today's recommendation to extend the streak 👇`
      : `You have ${count} sessions logged. Build your first streak by hitting 3 days in a row — it changes your relationship with exercise entirely. Start here 👇`,
    cards: streak >= 3 ? [SESSIONS.workout[0], SESSIONS.breathing[3]] : [SESSIONS.yoga[0], SESSIONS.breathing[1]],
    chips: ["How do I improve?", "What's my best day?", "Set a reminder"],
  };

  // MOTIVATION
  if (/motivat|lazy|can't|cant|don't want|dont want|hard|difficult|push|struggle/.test(m)) return {
    text: `Motivation is unreliable — systems beat feelings every time. But here's the truth: 5 minutes in, you'll always be glad you started. The hardest part is just tapping Start. So do that right now. ⚡`,
    cards: [SESSIONS.breathing[0], SESSIONS.workout[0]],
    chips: ["I really can't today", "Give me the shortest one", "Why even bother?"],
  };

  // DEFAULT
  return {
    text: `${lastSession ? `Last session: ${lastSession.type || "workout"} — nice work. ` : ""}I know everything in CalmFit — workouts, breathing, yoga, your history. What are you trying to fix or improve today? I'll give you the exact session to start. 🌿`,
    cards: count > 0 ? [SESSIONS.workout[0], SESSIONS.breathing[1]] : [SESSIONS.breathing[1], SESSIONS.yoga[0]],
    chips: chips_general,
  };
}

function AIChatCoach({ user, sessions, setScreen }) {
  const [open, setOpen] = useState(false);
  const name = user?.name?.split(" ")[0] || "there";
  const [msgs, setMsgs] = useState([{
    role: "assistant",
    content: `Hey ${name}! 👋 I'm your CalmFit AI Coach. Tap a quick action below or ask me anything — I'll give you the exact session to start right now.`,
    chips: ["What to do today? 🎯","I'm stressed 😤","Best for sleep 🌙","I have 5 minutes ⚡","Motivate me 🔥","I skipped a week 😅"],
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiKey] = useState(() => { try { return localStorage.getItem("zf_key") || ""; } catch { return ""; } });
  const [pulse, setPulse] = useState(false);
  const msgsEndRef = useRef(null);

  useEffect(() => {
    if (msgsEndRef.current) msgsEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  useEffect(() => {
    if (!open) { const t = setTimeout(() => setPulse(true), 4000); return () => clearTimeout(t); }
    setPulse(false);
  }, [open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    const userMsg = { role: "user", content: msg };
    setMsgs(prev => [...prev, userMsg]);
    setLoading(true);

    await new Promise(r => setTimeout(r, 600 + Math.random() * 400));

    // Try Claude API if key available
    if (apiKey) {
      try {
        const history = msgs.concat(userMsg).map(m => ({ role: m.role, content: m.content }));
        const res = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { "Content-Type":"application/json", "x-api-key":apiKey, "anthropic-version":"2023-06-01", "anthropic-dangerous-direct-browser-access":"true" },
          body: JSON.stringify({
            model: "claude-haiku-4-5-20251001", max_tokens: 300,
            system: `You are CalmFit AI Coach. User: ${user?.name}, ${sessions?.length||0} sessions logged. CalmFit has yoga classes, pilates, tai chi, breathing exercises, and workouts. Be warm, concise (2-3 sentences max), practical. Always end with a specific action recommendation.`,
            messages: history,
          }),
        });
        const data = await res.json();
        const reply = data.content?.[0]?.text;
        if (reply) {
          const smart = getSmartAIResponse(msg, user, sessions);
          setMsgs(prev => [...prev, { role:"assistant", content: reply, cards: smart.cards, chips: smart.chips }]);
          setLoading(false); return;
        }
      } catch(_) {}
    }

    // Local smart engine
    const { text: replyText, cards, chips } = getSmartAIResponse(msg, user, sessions);
    setMsgs(prev => [...prev, { role:"assistant", content: replyText, cards, chips }]);
    setLoading(false);
  };

  const handleCardTap = (card) => {
    setOpen(false);
    if (setScreen) setScreen(card.screen || "workout");
  };

  // Last AI chips for quick replies
  const lastAiMsg = [...msgs].reverse().find(m => m.role === "assistant" && m.chips);
  const activeChips = lastAiMsg?.chips || ["What to do today? 🎯","I'm stressed 😤","I have 5 minutes ⚡","Motivate me 🔥"];

  return (
    <>
      <AppIconStyles/>
      <style>{`
        @keyframes chatSlideIn{from{transform:translateX(-50%) translateY(100%)}to{transform:translateX(-50%) translateY(0)}}
        @keyframes cardPop{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {/* Floating button */}
      {!open && (
        <div onClick={() => setOpen(true)} style={{
          position:"fixed", bottom:86, right:18, zIndex:998,
          width:54, height:54, borderRadius:18,
          background:`linear-gradient(135deg,${COLORS.sage},${COLORS.deepSage})`,
          boxShadow:`0 6px 24px ${COLORS.sage}60`,
          display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer",
        }}>
          <AppIcon name="chat" color="white" size={24}/>
          {pulse && <div style={{ position:"absolute", top:-3, right:-3, width:13, height:13, borderRadius:"50%", background:COLORS.coral, border:`2px solid ${COLORS.bg}`, animation:"ai-pulse 1s ease-in-out infinite" }}/>}
        </div>
      )}

      {open && <div onClick={() => setOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:999 }}/>}

      {open && (
        <div style={{
          position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
          width:"100%", maxWidth:430, height:"88vh",
          background:"rgba(14,12,8,0.95)", backdropFilter:"blur(30px) saturate(1.5)",
          borderRadius:"24px 24px 0 0",
          border:`1px solid rgba(127,184,154,0.15)`, borderBottom:"none",
          zIndex:1000, display:"flex", flexDirection:"column",
          boxShadow:"0 -16px 60px rgba(0,0,0,0.7)",
          animation:"chatSlideIn 0.32s cubic-bezier(0.22,1,0.36,1)",
        }}>
          {/* Handle */}
          <div style={{ display:"flex", justifyContent:"center", paddingTop:10, flexShrink:0 }}>
            <div style={{ width:40, height:4, borderRadius:2, background:COLORS.border }}/>
          </div>

          {/* Header */}
          <div style={{ padding:"10px 16px 12px", borderBottom:`1px solid ${COLORS.border}`, display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
            <div style={{ width:44, height:44, borderRadius:15, background:`linear-gradient(135deg,${COLORS.sage}30,${COLORS.sage}10)`, border:`1.5px solid ${COLORS.sage}50`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <AppIcon name="sparkles" color={COLORS.sage} size={22}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ color:COLORS.text, fontWeight:800, fontFamily:FONT, fontSize:15, letterSpacing:-0.3 }}>CalmFit AI Coach</div>
              <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:1 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:COLORS.sage, animation:"ai-pulse 2s ease-in-out infinite" }}/>
                <span style={{ color:COLORS.sage, fontSize:11, fontFamily:FONT }}>Tap a session card to start instantly</span>
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background:"none", border:`1px solid ${COLORS.border}`, color:COLORS.muted, borderRadius:11, width:34, height:34, cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
          </div>

          {/* Messages */}
          <div style={{ flex:1, overflowY:"auto", padding:"14px 14px 8px", display:"flex", flexDirection:"column", gap:12 }}>
            {msgs.map((m, i) => (
              <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:m.role==="user"?"flex-end":"flex-start", gap:6 }}>
                {/* bubble row */}
                <div style={{ display:"flex", alignItems:"flex-end", gap:8, maxWidth:"90%", alignSelf:m.role==="user"?"flex-end":"flex-start" }}>
                  {m.role === "assistant" && (
                    <div style={{ width:30, height:30, borderRadius:11, background:`${COLORS.sage}18`, border:`1px solid ${COLORS.sage}35`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginBottom:2 }}>
                      <AppIcon name="sparkles" color={COLORS.sage} size={14}/>
                    </div>
                  )}
                  <div style={{
                    padding:"11px 15px",
                    borderRadius: m.role==="user" ? "18px 18px 4px 18px" : "4px 18px 18px 18px",
                    background: m.role==="user" ? `linear-gradient(135deg,${COLORS.sage},${COLORS.deepSage})` : COLORS.card,
                    color: m.role==="user" ? COLORS.bg : COLORS.text,
                    fontFamily:FONT, fontSize:13, lineHeight:1.65,
                    border: m.role==="assistant" ? `1px solid ${COLORS.border}` : "none",
                    boxShadow: m.role==="user" ? `0 3px 12px ${COLORS.sage}30` : "none",
                  }}>
                    {m.content}
                  </div>
                </div>

                {/* Session cards */}
                {m.role === "assistant" && m.cards && m.cards.length > 0 && (
                  <div style={{ width:"100%", paddingLeft:38, display:"flex", flexDirection:"column", gap:7 }}>
                    {m.cards.map((card, ci) => (
                      <div key={ci} onClick={() => handleCardTap(card)}
                        style={{ background:`${card.color}18`, border:`1.5px solid ${card.color}40`, borderRadius:14, padding:"11px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:12, animation:`cardPop 0.25s ease ${ci*0.08}s both` }}>
                        <div style={{ width:36, height:36, borderRadius:10, background:`${card.color}25`, border:`1px solid ${card.color}50`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {card.icon ? <AppIcon name={card.icon} color={card.color} size={18}/> : <span style={{fontSize:16}}>▶</span>}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, color:COLORS.text, fontSize:13, fontFamily:FONT }}>{card.label}</div>
                          <div style={{ color:COLORS.muted, fontSize:11, fontFamily:FONT, marginTop:1 }}>{card.sub}</div>
                        </div>
                        <div style={{ background:card.color, color:"white", borderRadius:9, padding:"7px 13px", fontSize:12, fontWeight:800, fontFamily:FONT, flexShrink:0, boxShadow:`0 3px 10px ${card.color}50` }}>
                          Start ▶
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div style={{ display:"flex", alignItems:"flex-end", gap:8 }}>
                <div style={{ width:30, height:30, borderRadius:11, background:`${COLORS.sage}18`, border:`1px solid ${COLORS.sage}35`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <AppIcon name="sparkles" color={COLORS.sage} size={14}/>
                </div>
                <div style={{ padding:"13px 17px", background:COLORS.card, borderRadius:"4px 18px 18px 18px", border:`1px solid ${COLORS.border}`, display:"flex", gap:5, alignItems:"center" }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:COLORS.sage, animation:"ai-dot1 1s ease-in-out infinite" }}/>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:COLORS.sage, animation:"ai-dot2 1s ease-in-out infinite" }}/>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:COLORS.sage, animation:"ai-dot3 1s ease-in-out infinite" }}/>
                </div>
              </div>
            )}
            <div ref={msgsEndRef}/>
          </div>

          {/* Quick reply chips — always visible, contextual */}
          {!loading && (
            <div style={{ padding:"8px 14px 6px", borderTop:`1px solid ${COLORS.border}20`, flexShrink:0 }}>
              <div style={{ display:"flex", gap:7, overflowX:"auto", paddingBottom:4, scrollbarWidth:"none" }}>
                {activeChips.map((chip, i) => (
                  <div key={i} onClick={() => send(chip)}
                    style={{ padding:"7px 13px", borderRadius:22, border:`1px solid ${COLORS.sage}35`, color:COLORS.sage, background:`${COLORS.sage}0D`, fontSize:12, fontFamily:FONT, cursor:"pointer", fontWeight:600, whiteSpace:"nowrap", flexShrink:0 }}>
                    {chip}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div style={{ padding:"6px 14px 30px", display:"flex", gap:9, alignItems:"center", flexShrink:0 }}>
            <input value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key==="Enter" && !loading && send()}
              placeholder="Or type anything…"
              style={{ flex:1, padding:"12px 16px", borderRadius:15, border:`1.5px solid ${input?COLORS.sage+"70":COLORS.border}`, background:COLORS.surface, color:COLORS.text, fontFamily:FONT, fontSize:13, outline:"none", transition:"border-color 0.2s" }}/>
            <button onClick={() => send()} disabled={loading || !input.trim()}
              style={{ width:46, height:46, borderRadius:15, background:input.trim()?COLORS.sage:COLORS.surface, border:"none", display:"flex", alignItems:"center", justifyContent:"center", cursor:input.trim()?"pointer":"default", transition:"all 0.2s", flexShrink:0, boxShadow:input.trim()?`0 4px 14px ${COLORS.sage}40`:"none" }}>
              <AppIcon name="bolt" color={input.trim()?COLORS.bg:COLORS.muted} size={19}/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── WORKOUT SCREEN ───────────────────────────────────────────────────────────
function WorkoutScreen({ setScreen, trialExpired, onLogSession, onToggleFav, favorites, deepLink, onDeepLinkConsumed, isSubscribed, onUpgrade }) {
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedEx, setSelectedEx] = useState(null);
  const [filter, setFilter] = useState("all");
  const currentCat = exerciseCategories.find(c => c.id === selectedCat);

  // Handle deep link navigation — auto-open a category and optionally an exercise
  useEffect(() => {
    if (!deepLink) return;
    const cat = exerciseCategories.find(c => c.id === deepLink.catId);
    if (!cat) return;
    setSelectedCat(cat.id);
    setFilter("all");
    if (deepLink.exName) {
      const ex = cat.exercises.find(e => e.name === deepLink.exName);
      if (ex) setTimeout(() => setSelectedEx(ex), 180); // short delay for smooth animation
    }
    if (onDeepLinkConsumed) onDeepLinkConsumed();
  }, [deepLink]); // eslint-disable-line react-hooks/exhaustive-deps

  if (trialExpired) return (
    <div style={{ background: "transparent", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 24px 100px" }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>⏰</div>
      <h2 style={{ fontFamily: FONT, color: COLORS.text, fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Trial Ended</h2>
      <p style={{ color: COLORS.muted, fontFamily: FONT, marginBottom: 28, textAlign: "center", fontSize: 14, lineHeight: 1.6 }}>Subscribe to unlock all workouts — from just ₹299/month.</p>
      <button onClick={() => setScreen("profile")}
        style={{ padding: "14px 32px", background: `linear-gradient(135deg, ${COLORS.gold}, #C4902A)`, color: COLORS.bg, border: "none", borderRadius: 16, fontSize: 15, fontFamily: FONT, cursor: "pointer", fontWeight: 800 }}>
        View Plans ✦
      </button>
    </div>
  );

  if (selectedCat && currentCat) {
    const filtered = filter === "all" ? currentCat.exercises : currentCat.exercises.filter(e => e.difficulty.toLowerCase() === filter);
    return (
      <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100 }}>
        {/* Category header */}
        <div style={{ background: `linear-gradient(160deg, ${currentCat.color}30 0%, ${COLORS.bgWarm} 100%)`, padding: "54px 22px 22px", borderBottom: `1px solid ${currentCat.color}20` }}>
          <button onClick={() => setSelectedCat(null)}
            style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: "6px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", marginBottom: 16, fontSize: 12, fontWeight: 600 }}>
            ← Back
          </button>
          <div style={{ marginBottom: 8 }}><WorkoutIcon catId={currentCat.id} color={currentCat.color} size={44}/></div>
          <h1 style={{ color: COLORS.text, fontSize: 23, margin: "0 0 4px", fontFamily: FONT, fontWeight: 800 }}>{currentCat.label} Workout</h1>
          <p style={{ color: COLORS.muted, margin: 0, fontFamily: FONT, fontSize: 13 }}>{currentCat.exercises.length} exercises · {currentCat.desc}</p>
        </div>

        <div style={{ padding: "16px" }}>
          {/* Filter pills */}
          <div style={{ display: "flex", gap: 7, marginBottom: 16, overflowX: "auto" }}>
            {[{ id: "all", l: "All" }, { id: "beginner", l: "Beginner" }, { id: "intermediate", l: "Intermediate" }, { id: "advanced", l: "Advanced" }].map(f => (
              <div key={f.id} onClick={() => setFilter(f.id)}
                style={{
                  padding: "7px 14px", borderRadius: 20, whiteSpace: "nowrap",
                  background: filter === f.id ? currentCat.color : COLORS.surface,
                  color: filter === f.id ? "white" : COLORS.muted,
                  border: `1px solid ${filter === f.id ? "transparent" : COLORS.border}`,
                  cursor: "pointer", fontSize: 12, fontFamily: FONT, fontWeight: filter === f.id ? 700 : 400,
                }}>{f.l}</div>
            ))}
          </div>

          {/* Free preview notice */}
          {!isSubscribed && (
            <div style={{ background: COLORS.goldDim, border: `1px solid ${COLORS.gold}35`, borderRadius: 14, padding: "10px 16px", marginBottom: 14, fontSize: 12, color: COLORS.gold, fontFamily: FONT, fontWeight: 600, display:"flex", alignItems:"center", gap:8 }}>
              🎁 First {FREE_EXERCISES} exercises free · <span style={{ fontWeight:400, color:COLORS.muted }}>Go Premium for full access</span>
            </div>
          )}

          {/* Exercise list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((ex, i) => {
              // Find original index in full list (for lock check) since filter may reorder
              const origIdx = currentCat.exercises.indexOf(ex);
              const locked = !canAccess.exercise(origIdx, isSubscribed);
              return (
              <div key={i} onClick={() => { if (locked) { onUpgrade && onUpgrade(); return; } setSelectedEx(ex); }}
                style={{
                  background: COLORS.card, borderRadius: 18, padding: 13, display: "flex", gap: 13, alignItems: "center",
                  cursor: "pointer", border: `1px solid ${locked ? COLORS.border : currentCat.color+"25"}`,
                  transition: "all 0.18s cubic-bezier(0.22,1,0.36,1)",
                  opacity: locked ? 0.75 : 1, position: "relative", overflow: "hidden",
                }}
                onMouseEnter={e => { if (!locked) { e.currentTarget.style.border=`1px solid ${currentCat.color}60`; e.currentTarget.style.boxShadow=`0 4px 20px ${currentCat.color}18`; e.currentTarget.style.transform="translateY(-1px)"; }}}
                onMouseLeave={e => { e.currentTarget.style.border=`1px solid ${locked ? COLORS.border : currentCat.color+"25"}`; e.currentTarget.style.boxShadow="none"; e.currentTarget.style.transform="translateY(0)"; }}
              >
                <div style={{ width: 68, height: 68, borderRadius: 14, background: `${currentCat.color}12`, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", border:`1px solid ${currentCat.color}20` }}>
                  <img src={ex.gif} alt={ex.name} style={{ width: "100%", height: "100%", objectFit: "contain", filter: locked ? "blur(2px) grayscale(0.6)" : "none" }}
                    onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                  <div style={{ display: "none", position: "absolute", inset: 0, alignItems: "center", justifyContent: "center" }}><WorkoutIcon catId={currentCat.id} color={currentCat.color} size={34}/></div>
                  {locked && (
                    <div style={{ position:"absolute", inset:0, background:"rgba(10,18,15,0.55)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ fontSize:22 }}>🔒</span>
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: FONT, fontWeight: 700, color: locked ? COLORS.muted : COLORS.text, fontSize: 14, marginBottom: 3 }}>{ex.name}</div>
                  {ex.label && <div style={{ fontFamily: FONT, color: currentCat.color, fontSize: 10, fontStyle:"italic", marginBottom:3, fontWeight:600 }}>{ex.label}</div>}
                  <div style={{ fontFamily: FONT, color: COLORS.muted, fontSize: 11, marginBottom: 5 }}>{locked ? "Premium exercise" : ex.muscles}</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {locked ? (
                      <span style={{ fontSize: 10, color: COLORS.gold, fontFamily: FONT, fontWeight: 700 }}>✦ PREMIUM</span>
                    ) : (
                      <>
                        <span style={{ fontSize: 10, color: COLORS.muted, fontFamily: FONT }}>📋 {ex.sets}</span>
                        <span style={{ fontSize: 10, color: COLORS.coral, fontFamily: FONT }}>🔥 {ex.cals} kcal</span>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: locked ? COLORS.subtle : currentCat.color, fontFamily: FONT, fontWeight: 700, background: locked ? "rgba(255,255,255,0.06)" : `${currentCat.color}18`, border: `1px solid ${locked ? COLORS.border : currentCat.color+"30"}`, padding: "3px 9px", borderRadius: 8 }}>{locked ? "🔒" : ex.difficulty}</div>
              </div>
              );
            })}
          </div>
        </div>
        {selectedEx && <ExerciseModal exercise={selectedEx} category={currentCat} onClose={() => setSelectedEx(null)} onLogSession={onLogSession} onToggleFav={onToggleFav} favorites={favorites} />}
      </div>
    );
  }

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100 }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 22px 20px" }}>
        <h1 style={{ color: COLORS.text, fontSize: 26, margin: "0 0 4px", fontFamily: FONT, fontWeight: 800, letterSpacing: -0.5 }}>Workout Library</h1>
        <p style={{ color: COLORS.muted, margin: "0 0 16px", fontFamily: FONT, fontSize: 13 }}>
          {exerciseCategories.reduce((a, c) => a + c.exercises.length, 0)} exercises · {exerciseCategories.length} muscle groups
        </p>
        <MusicPlayer />
      </div>

      <div style={{ padding: "0 16px" }}>
        <div style={{ color: COLORS.muted, fontFamily: FONT, fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 12 }}>Choose Your Target</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
          {exerciseCategories.map(cat => (
            <div key={cat.id} onClick={() => { setSelectedCat(cat.id); setFilter("all"); }}
              style={{
                background: COLORS.card, borderRadius: 20, padding: 16, cursor: "pointer",
                border: `1.5px solid ${cat.color}20`, position: "relative", overflow: "hidden",
                transition: "border-color 0.2s",
              }}>
              <WorkoutIconStyles />
              <div style={{ position: "absolute", right: -4, bottom: -4, opacity: 0.1, pointerEvents: "none" }}><WorkoutIcon catId={cat.id} color={cat.color} size={54} /></div>
              <div style={{ marginBottom: 10 }}><WorkoutIcon catId={cat.id} color={cat.color} size={38} /></div>
              <div style={{ fontFamily: FONT, fontWeight: 800, color: COLORS.text, fontSize: 14, marginBottom: 3 }}>{cat.label}</div>
              <div style={{ fontFamily: FONT, color: COLORS.muted, fontSize: 11, marginBottom: 10, lineHeight: 1.4 }}>{cat.desc}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 10, background: `${cat.color}18`, color: cat.color, padding: "3px 9px", borderRadius: 10, fontFamily: FONT, fontWeight: 700 }}>{cat.exercises.length} ex</span>
                <span style={{ color: cat.color, fontSize: 16 }}>→</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────
const NOTIF_MESSAGES = [
  { title:"Time to flow, {n}! 🌿", body:"Your body's ready. 10 minutes changes everything." },
  { title:"Hey {n} — check in 💪", body:"Keep that streak alive. One session at a time." },
  { title:"Breathe. Move. Thrive. ⚡", body:"Your CalmFit session is waiting for you." },
  { title:"{n}, stress check 💙", body:"2-min Physiological Sigh resets your whole day." },
  { title:"Morning activated ☀️", body:"Energizing Breath + any workout = unstoppable day." },
  { title:"Recovery mode ON 🧘", body:"Even 5 minutes of breathing counts. Open CalmFit." },
  { title:"Consistency wins 🔥", body:"The best version of you shows up today." },
  { title:"Your coach is waiting 🌿", body:"Ask anything or start a quick session now." },
  { title:"You promised yourself 💚", body:"10 minutes. That's all. Let's go {n}." },
  { title:"Science says: move now 🧠", body:"Exercise is the fastest mood booster. Period." },
];

// ─── HEALTH PROFILE SCREEN (shown after login) ────────────────────────────────
function HealthProfileScreen({ user, onComplete }) {
  const TOTAL = 4;
  const [step, setStep]         = useState(0);
  const [animDir, setAnimDir]   = useState(1);
  const [age, setAge]           = useState(25);
  const [height, setHeight]     = useState(168);
  const [weight, setWeight]     = useState(68);
  const [condition, setCondition] = useState("");
  const [goal, setGoal]         = useState("");
  const [notifEnabled, setNotif] = useState(false);
  const [notifHour, setNotifHour] = useState(7);
  const [notifPermission, setNPerm] = useState("default");

  const bmi = +(weight / ((height / 100) ** 2)).toFixed(1);
  const bmiLabel = bmi < 18.5 ? "Underweight" : bmi < 25 ? "Healthy" : bmi < 30 ? "Overweight" : "Obese";
  const bmiColor = bmi < 18.5 ? COLORS.blue : bmi < 25 ? COLORS.sage : bmi < 30 ? COLORS.gold : COLORS.coral;

  const goTo = (s) => { setAnimDir(s > step ? 1 : -1); setStep(s); };
  const next = () => goTo(step + 1);

  const conditions = [
    { label:"Hypothyroidism",    emoji:"🌙", color:COLORS.blue },
    { label:"Hyperthyroidism",   emoji:"⚡", color:COLORS.gold },
    { label:"Migraine",          emoji:"🧠", color:COLORS.lavender },
    { label:"Joint Pain",        emoji:"🦴", color:"#A8C5A0" },
    { label:"Heart Condition",   emoji:"❤️", color:COLORS.coral },
    { label:"Diabetes",          emoji:"🩸", color:COLORS.orange },
    { label:"PCOS",              emoji:"🌸", color:"#D45B75" },
    { label:"Back Pain",         emoji:"🔵", color:"#9B8EC5" },
    { label:"Anxiety",           emoji:"🌬️", color:COLORS.blue },
    { label:"Prefer not to say", emoji:"✨", color:COLORS.muted },
  ];
  const goals = [
    { label:"Reduce Stress",   icon:"wind",     color:COLORS.lavender },
    { label:"Build Strength",  icon:"dumbbell", color:COLORS.coral },
    { label:"Flexibility",     icon:"yoga",     color:COLORS.sage },
    { label:"Better Sleep",    icon:"moon",     color:COLORS.blue },
    { label:"Mental Clarity",  icon:"target",   color:COLORS.gold },
    { label:"Weight Loss",     icon:"fire",     color:COLORS.orange },
  ];
  const notifTimes = [
    { label:"7 AM",  hour:7,  icon:"sunrise", desc:"Morning activation" },
    { label:"12 PM", hour:12, icon:"sun",     desc:"Midday reset" },
    { label:"6 PM",  hour:18, icon:"moon",    desc:"Evening wind-down" },
    { label:"9 PM",  hour:21, icon:"moon",    desc:"Night routine" },
  ];

  const requestNotif = async () => {
    if (!("Notification" in window)) { setNPerm("denied"); return; }
    const p = await Notification.requestPermission();
    setNPerm(p);
    if (p === "granted") setNotif(true);
  };

  const finish = () => {
    const profileData = { age, height, weight, bmi, condition, goal, notifEnabled, notifHour };
    localStorage.setItem("zf_health_profile", JSON.stringify(profileData));
    onComplete(profileData);
  };

  const cardStyle = (active, color = COLORS.sage) => ({
    borderRadius: 16, border: `1.5px solid ${active ? color : COLORS.border}`,
    background: active ? `${color}18` : COLORS.card,
    cursor: "pointer", transition: "all 0.18s",
    boxShadow: active ? `0 4px 16px ${color}25` : "none",
    transform: active ? "scale(1.02)" : "scale(1)",
  });

  const Btn = ({ label, disabled, onClick, style = {} }) => (
    <button onClick={onClick} disabled={disabled} style={{
      width:"100%", padding:"15px", borderRadius:14, fontSize:15, fontFamily:FONT, fontWeight:800,
      background: disabled ? COLORS.surface : COLORS.sage, color: disabled ? COLORS.subtle : COLORS.bg,
      border: `1px solid ${disabled ? COLORS.border : "transparent"}`,
      cursor: disabled ? "default" : "pointer", transition:"all 0.2s",
      boxShadow: disabled ? "none" : `0 6px 20px ${COLORS.sage}40`, ...style,
    }}>{label}</button>
  );

  const firstName = user?.name?.split(" ")[0] || "there";

  const steps = [
    /* 0 — Age & BMI */
    <div style={{ maxWidth:390, width:"100%" }}>
      <div style={{ textAlign:"center", marginBottom:20 }}>
        <div style={{ width:72, height:72, borderRadius:24, background:`${COLORS.sage}18`, border:`1.5px solid ${COLORS.sage}40`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <AppIconStyles/><AppIcon name="target" color={COLORS.sage} size={36}/>
        </div>
        <h2 style={{ fontSize:26, color:COLORS.text, fontWeight:800, letterSpacing:-0.5, margin:"0 0 6px" }}>
          Hi {firstName}! 👋<br/><span style={{ color:COLORS.sage }}>Let's set up your profile</span>
        </h2>
        <p style={{ color:COLORS.muted, fontSize:13, lineHeight:1.6 }}>This helps us tailor your yoga & wellness plan</p>
      </div>

      {/* Age */}
      <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:20, padding:"18px 18px", marginBottom:14 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <span style={{ fontFamily:FONT, fontWeight:700, fontSize:14, color:COLORS.text }}>Your Age</span>
          <span style={{ fontFamily:FONT, fontWeight:800, fontSize:22, color:COLORS.sage }}>{age} <span style={{ fontSize:13, color:COLORS.muted }}>yrs</span></span>
        </div>
        <input type="range" min={13} max={80} value={age} onChange={e=>setAge(+e.target.value)} style={{ width:"100%", accentColor:COLORS.sage, cursor:"pointer" }}/>
        <div style={{ display:"flex", justifyContent:"space-between", color:COLORS.subtle, fontSize:11, fontFamily:FONT, marginTop:4 }}><span>13</span><span>80 yrs</span></div>
      </div>

      {/* Height & Weight */}
      <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:20, padding:"18px 18px", marginBottom:14 }}>
        <div style={{ marginBottom:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontFamily:FONT, fontWeight:700, fontSize:14, color:COLORS.text }}>Height</span>
            <span style={{ fontFamily:FONT, fontWeight:800, fontSize:18, color:COLORS.sage }}>{height} <span style={{ fontSize:12, color:COLORS.muted }}>cm</span></span>
          </div>
          <input type="range" min={140} max={220} value={height} onChange={e=>setHeight(+e.target.value)} style={{ width:"100%", accentColor:COLORS.sage, cursor:"pointer" }}/>
          <div style={{ display:"flex", justifyContent:"space-between", color:COLORS.subtle, fontSize:11, fontFamily:FONT, marginTop:4 }}><span>140 cm</span><span>220 cm</span></div>
        </div>
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontFamily:FONT, fontWeight:700, fontSize:14, color:COLORS.text }}>Weight</span>
            <span style={{ fontFamily:FONT, fontWeight:800, fontSize:18, color:COLORS.sage }}>{weight} <span style={{ fontSize:12, color:COLORS.muted }}>kg</span></span>
          </div>
          <input type="range" min={35} max={180} value={weight} onChange={e=>setWeight(+e.target.value)} style={{ width:"100%", accentColor:COLORS.sage, cursor:"pointer" }}/>
          <div style={{ display:"flex", justifyContent:"space-between", color:COLORS.subtle, fontSize:11, fontFamily:FONT, marginTop:4 }}><span>35 kg</span><span>180 kg</span></div>
        </div>
      </div>

      {/* BMI */}
      <div style={{ background:`${bmiColor}15`, border:`1px solid ${bmiColor}40`, borderRadius:14, padding:"12px 16px", marginBottom:20, display:"flex", alignItems:"center", gap:12 }}>
        <AppIcon name="target" color={bmiColor} size={22}/>
        <div>
          <div style={{ fontFamily:FONT, fontWeight:800, fontSize:15, color:bmiColor }}>BMI {bmi} · {bmiLabel}</div>
          <div style={{ fontFamily:FONT, fontSize:12, color:COLORS.muted, marginTop:2 }}>
            {bmi<18.5?"Focus on strength building & nourishing meals.":bmi<25?"Great range — all workouts are suitable for you.":bmi<30?"Mix of cardio, strength & breathing recommended.":"Start gentle — breathing + low-impact movement first."}
          </div>
        </div>
      </div>
      <Btn label="Continue →" onClick={next}/>
    </div>,

    /* 1 — Health Conditions */
    <div style={{ maxWidth:390, width:"100%" }}>
      <h2 style={{ fontSize:26, color:COLORS.text, textAlign:"center", marginBottom:4, fontWeight:800, letterSpacing:-0.5 }}>Any health conditions?</h2>
      <p style={{ color:COLORS.muted, textAlign:"center", marginBottom:8, fontSize:13 }}>We'll personalise your yoga recommendations</p>
      <div style={{ background:`${COLORS.gold}12`, border:`1px solid ${COLORS.gold}30`, borderRadius:12, padding:"8px 14px", marginBottom:16, fontSize:12, color:COLORS.gold, fontFamily:FONT }}>🔒 Stays on your device — never shared</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        {conditions.map(c => {
          const isSelected = condition === c.label;
          return (
          <div key={c.label} onClick={()=>setCondition(c.label)} style={{
            borderRadius: 16,
            border: `2px solid ${isSelected ? c.color : COLORS.border}`,
            background: isSelected ? `linear-gradient(135deg, ${c.color}28, ${c.color}15)` : COLORS.card,
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.22,1,0.36,1)",
            boxShadow: isSelected ? `0 6px 24px ${c.color}35` : "none",
            transform: isSelected ? "scale(1.04)" : "scale(1)",
            padding:"14px 10px", textAlign:"center",
            position:"relative",
          }}>
            {/* Checkmark badge */}
            {isSelected && (
              <div style={{ position:"absolute", top:6, right:6, width:18, height:18, borderRadius:"50%", background:c.color, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:`0 2px 8px ${c.color}60` }}>
                <span style={{ color:"#fff", fontSize:10, fontWeight:900, lineHeight:1 }}>✓</span>
              </div>
            )}
            <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}>
              <div style={{ width:44, height:44, borderRadius:13, background: isSelected ? `${c.color}25` : COLORS.surface, border:`1.5px solid ${isSelected ? c.color+"90" : COLORS.border}`, display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.2s", boxShadow: isSelected ? `0 4px 14px ${c.color}50` : "none" }}>
                <ConditionIconStyles/><ConditionIcon condition={c.label} color={c.color} size={28}/>
              </div>
            </div>
            <div style={{ fontSize:12, fontWeight: isSelected ? 800 : 500, color: isSelected ? c.color : COLORS.text, fontFamily:FONT, lineHeight:1.3, transition:"all 0.2s" }}>{c.label}</div>
          </div>
          );
        })}
      </div>
      {condition && CONDITION_RECOMMENDATIONS[condition] && (
        <div style={{ background:`${CONDITION_RECOMMENDATIONS[condition].color}15`, border:`1.5px solid ${CONDITION_RECOMMENDATIONS[condition].color}40`, borderRadius:14, padding:"12px 16px", marginBottom:16, fontFamily:FONT, lineHeight:1.6, display:"flex", alignItems:"flex-start", gap:10 }}>
          <span style={{ fontSize:18, flexShrink:0 }}>✨</span>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:CONDITION_RECOMMENDATIONS[condition].color, marginBottom:2 }}>{CONDITION_RECOMMENDATIONS[condition].headline}</div>
            <div style={{ fontSize:12, color:COLORS.muted }}>We'll show 5 personalised yoga poses for <strong style={{ color:COLORS.text }}>{condition}</strong> on your Home screen</div>
          </div>
        </div>
      )}
      <Btn label="Continue →" disabled={!condition} onClick={()=>condition&&next()}/>
    </div>,

    /* 2 — Goal */
    <div style={{ maxWidth:390, width:"100%" }}>
      <h2 style={{ fontSize:26, color:COLORS.text, textAlign:"center", marginBottom:4, fontWeight:800, letterSpacing:-0.5 }}>What's your main goal?</h2>
      <p style={{ color:COLORS.muted, textAlign:"center", marginBottom:20, fontSize:13 }}>We'll build your program around this</p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:22 }}>
        {goals.map(g => (
          <div key={g.label} onClick={()=>setGoal(g.label)} style={{ ...cardStyle(goal===g.label, g.color), padding:"14px 10px", textAlign:"center" }}>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}><AppIconStyles/><AppIcon name={g.icon} color={goal===g.label?g.color:COLORS.muted} size={24}/></div>
            <div style={{ fontSize:12, fontWeight:goal===g.label?700:500, color:goal===g.label?g.color:COLORS.text, fontFamily:FONT }}>{g.label}</div>
          </div>
        ))}
      </div>
      <Btn label="Continue →" disabled={!goal} onClick={()=>goal&&next()}/>
    </div>,

    /* 3 — Notifications */
    <div style={{ maxWidth:390, width:"100%" }}>
      <div style={{ textAlign:"center", marginBottom:20 }}>
        <div style={{ width:72, height:72, borderRadius:24, background:`${COLORS.coral}18`, border:`1.5px solid ${COLORS.coral}40`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <AppIconStyles/><AppIcon name="bell" color={COLORS.coral} size={36}/>
        </div>
        <h2 style={{ fontSize:26, color:COLORS.text, fontWeight:800, letterSpacing:-0.5, margin:"0 0 6px" }}>Stay on track</h2>
        <p style={{ color:COLORS.muted, fontSize:13 }}>Get daily nudges that bring you back when life gets busy</p>
      </div>
      {notifPermission !== "granted" ? (
        <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:20, padding:"20px 18px", marginBottom:16 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:18 }}>
            {["⏰ Workout reminders at your chosen time","🔥 Streak alerts so you never break the chain","💬 Motivational nudges on tough days"].map(t=>(
              <div key={t} style={{ display:"flex", alignItems:"center", gap:10, fontFamily:FONT, fontSize:13, color:COLORS.text }}><span style={{ fontSize:18 }}>{t.split(" ")[0]}</span>{t.slice(2)}</div>
            ))}
          </div>
          <button onClick={requestNotif} style={{ width:"100%", padding:"14px", borderRadius:14, background:`linear-gradient(135deg,${COLORS.coral},${COLORS.orange})`, color:"#fff", border:"none", fontFamily:FONT, fontWeight:800, fontSize:14, cursor:"pointer", boxShadow:`0 6px 20px ${COLORS.coral}40` }}>Enable Notifications 🔔</button>
          <button onClick={finish} style={{ width:"100%", padding:"10px", borderRadius:14, background:"none", border:"none", color:COLORS.muted, fontFamily:FONT, fontSize:12, cursor:"pointer", marginTop:8 }}>Skip for now</button>
        </div>
      ) : (
        <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:20, padding:"20px 18px", marginBottom:16 }}>
          <div style={{ color:COLORS.sage, fontFamily:FONT, fontWeight:700, fontSize:13, marginBottom:14, display:"flex", alignItems:"center", gap:6 }}>✓ Notifications enabled — choose your time:</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:9, marginBottom:16 }}>
            {notifTimes.map(t => (
              <div key={t.hour} onClick={()=>setNotifHour(t.hour)} style={{ ...cardStyle(notifHour===t.hour, COLORS.coral), padding:"12px 10px", textAlign:"center" }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:6 }}><AppIcon name={t.icon} color={notifHour===t.hour?COLORS.coral:COLORS.muted} size={18}/></div>
                <div style={{ fontFamily:FONT, fontWeight:700, fontSize:14, color:notifHour===t.hour?COLORS.coral:COLORS.text }}>{t.label}</div>
                <div style={{ fontFamily:FONT, fontSize:11, color:COLORS.muted, marginTop:2 }}>{t.desc}</div>
              </div>
            ))}
          </div>
          <Btn label="All set! →" disabled={false} onClick={finish} style={{ background:`linear-gradient(135deg,${COLORS.coral},${COLORS.orange})`, boxShadow:`0 6px 20px ${COLORS.coral}40` }}/>
        </div>
      )}
    </div>,
  ];

  return (
    <div style={{ minHeight:"100vh", background:"transparent", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px 20px", fontFamily:FONT, position:"relative", overflow:"hidden" }}>
      {/* bg orbs */}
      <div style={{ position:"absolute", top:-100, left:-100, width:300, height:300, borderRadius:"50%", background:`radial-gradient(circle,${COLORS.sage}18 0%,transparent 70%)`, pointerEvents:"none" }}/>
      <div style={{ position:"absolute", bottom:-80, right:-80, width:250, height:250, borderRadius:"50%", background:`radial-gradient(circle,${COLORS.coral}12 0%,transparent 70%)`, pointerEvents:"none" }}/>

      {/* Progress bar */}
      <div style={{ display:"flex", gap:6, marginBottom:32 }}>
        {Array.from({length:TOTAL},(_,i)=>(
          <div key={i} style={{ width:i===step?32:i<step?14:8, height:6, borderRadius:3, background:i<=step?COLORS.sage:COLORS.surface, transition:"all 0.35s cubic-bezier(0.22,1,0.36,1)" }}/>
        ))}
      </div>

      {/* Step content */}
      <div key={step} style={{ width:"100%", display:"flex", justifyContent:"center", animation:"hpIn 0.3s ease" }}>
        <style>{`@keyframes hpIn{from{opacity:0;transform:translateX(${animDir*24}px)}to{opacity:1;transform:translateX(0)}}`}</style>
        {steps[step]}
      </div>

      {/* Back */}
      {step > 0 && (
        <button onClick={()=>goTo(step-1)} style={{ marginTop:16, background:"none", border:"none", color:COLORS.muted, fontFamily:FONT, fontSize:13, cursor:"pointer" }}>← Back</button>
      )}
      <style>{`*{-webkit-tap-highlight-color:transparent;box-sizing:border-box;}`}</style>
    </div>
  );
}

function Onboarding({ onComplete }) {
  const TOTAL = 6;
  const [step, setStep]             = useState(0);
  const [animDir, setAnimDir]       = useState(1);
  const [name, setName]             = useState("");
  const [goal, setGoal]             = useState("");
  const [activity, setActivity]     = useState("");
  const [height, setHeight]         = useState(168);
  const [weight, setWeight]         = useState(68);
  const [condition, setCondition]   = useState("");
  const [notifEnabled, setNotif]    = useState(false);
  const [notifHour, setNotifHour]   = useState(7);
  const [notifPermission, setNPerm] = useState("default");

  const bmi = +(weight / ((height / 100) ** 2)).toFixed(1);
  const bmiLabel = bmi < 18.5 ? "Underweight" : bmi < 25 ? "Healthy" : bmi < 30 ? "Overweight" : "Obese";
  const bmiColor = bmi < 18.5 ? COLORS.blue : bmi < 25 ? COLORS.sage : bmi < 30 ? COLORS.gold : COLORS.coral;

  const goTo = (s) => { setAnimDir(s > step ? 1 : -1); setStep(s); };
  const next = () => goTo(step + 1);

  const goals = [
    { label:"Reduce Stress",      icon:"wind",     color:COLORS.lavender },
    { label:"Build Strength",     icon:"dumbbell", color:COLORS.coral },
    { label:"Flexibility",        icon:"yoga",     color:COLORS.sage },
    { label:"Better Sleep",       icon:"moon",     color:COLORS.blue },
    { label:"Mental Clarity",     icon:"target",   color:COLORS.gold },
    { label:"Weight Loss",        icon:"fire",     color:COLORS.orange },
  ];
  const activities = [
    { label:"Just starting",   desc:"Little to no exercise", icon:"leaf" },
    { label:"Lightly active",  desc:"1–2 days/week",         icon:"yoga" },
    { label:"Moderately active",desc:"3–4 days/week",        icon:"dumbbell" },
    { label:"Very active",     desc:"5+ days/week",          icon:"bolt" },
  ];
  const conditions = [
    { label:"Hypothyroidism",    emoji:"🌙", color:COLORS.blue },
    { label:"Hyperthyroidism",   emoji:"⚡", color:COLORS.gold },
    { label:"Migraine",          emoji:"🧠", color:"#B09FE0" },
    { label:"Joint Pain",        emoji:"🦴", color:COLORS.lavender },
    { label:"Heart Condition",   emoji:"❤️", color:COLORS.coral },
    { label:"Diabetes",          emoji:"🩸", color:COLORS.orange },
    { label:"PCOS",              emoji:"🌸", color:"#D45B75" },
    { label:"Back Pain",         emoji:"🔵", color:"#9B8EC5" },
    { label:"Anxiety",           emoji:"🌬️", color:COLORS.blue },
    { label:"Prefer not to say", emoji:"✨", color:COLORS.muted },
  ];
  const notifTimes = [
    { label:"7 AM",  hour:7,  icon:"sunrise", desc:"Morning activation" },
    { label:"12 PM", hour:12, icon:"sun",     desc:"Midday reset" },
    { label:"6 PM",  hour:18, icon:"moon",    desc:"Evening wind-down" },
    { label:"9 PM",  hour:21, icon:"moon",    desc:"Night routine" },
  ];

  const requestNotif = async () => {
    if (!("Notification" in window)) { setNPerm("denied"); return; }
    const p = await Notification.requestPermission();
    setNPerm(p);
    if (p === "granted") setNotif(true);
  };

  const finish = () => {
    onComplete({ name, goal, activity, height, weight, bmi, condition, notifEnabled, notifHour, joinDate: new Date().toISOString() });
  };

  const cardStyle = (active, color = COLORS.sage) => ({
    borderRadius: 16, border: `1.5px solid ${active ? color : COLORS.border}`,
    background: active ? `${color}18` : COLORS.card,
    cursor: "pointer", transition: "all 0.18s",
    boxShadow: active ? `0 4px 16px ${color}25` : "none",
    transform: active ? "scale(1.02)" : "scale(1)",
  });

  const Btn = ({ label, disabled, onClick, style = {} }) => (
    <button onClick={onClick} disabled={disabled} style={{
      width:"100%", padding:"15px", borderRadius:14, fontSize:15, fontFamily:FONT, fontWeight:800,
      background: disabled ? COLORS.surface : COLORS.sage, color: disabled ? COLORS.subtle : COLORS.bg,
      border: `1px solid ${disabled ? COLORS.border : "transparent"}`,
      cursor: disabled ? "default" : "pointer", transition:"all 0.2s",
      boxShadow: disabled ? "none" : `0 6px 20px ${COLORS.sage}40`, ...style,
    }}>{label}</button>
  );

  const steps = [
    /* 0 — Welcome */
    <div style={{ textAlign:"center", maxWidth:360, width:"100%" }}>
      <div style={{ width:82, height:82, borderRadius:28, background:`linear-gradient(135deg,${COLORS.sageDim},${COLORS.surface})`, border:`1.5px solid ${COLORS.sage}50`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", boxShadow:`0 8px 32px ${COLORS.sage}30` }}>
        <AppIconStyles/><AppIcon name="leaf" color={COLORS.sage} size={44}/>
      </div>
      <h1 style={{ fontSize:34, fontWeight:900, color:COLORS.text, marginBottom:6, lineHeight:1.15, letterSpacing:-1.2 }}>Welcome to<br/><span style={{ color:COLORS.sage }}>CalmFit</span></h1>
      <p style={{ color:COLORS.muted, fontSize:14, lineHeight:1.7, marginBottom:18 }}>Your personal fitness, breathing & wellness coach.</p>
      <div style={{ background:COLORS.sageDim, border:`1px solid ${COLORS.sage}30`, borderRadius:14, padding:"10px 16px", marginBottom:22, fontSize:13, color:COLORS.sage, fontWeight:600 }}>🎁 Free forever — upgrade for full access</div>
      <input value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&name&&next()} placeholder="What's your name?" style={{ width:"100%", padding:"14px 18px", border:`1.5px solid ${name?COLORS.sage+"80":COLORS.border}`, borderRadius:14, fontSize:15, fontFamily:FONT, background:COLORS.surface, color:COLORS.text, outline:"none", boxSizing:"border-box", marginBottom:14 }}/>
      <Btn label="Let's start →" disabled={!name} onClick={()=>name&&next()}/>
    </div>,

    /* 1 — Goal */
    <div style={{ maxWidth:390, width:"100%" }}>
      <h2 style={{ fontSize:26, color:COLORS.text, textAlign:"center", marginBottom:4, fontWeight:800, letterSpacing:-0.5 }}>What's your main goal,<br/><span style={{ color:COLORS.sage }}>{name}</span>?</h2>
      <p style={{ color:COLORS.muted, textAlign:"center", marginBottom:20, fontSize:13 }}>We'll build your program around this</p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:22 }}>
        {goals.map(g => (
          <div key={g.label} onClick={()=>setGoal(g.label)} style={{ ...cardStyle(goal===g.label, g.color), padding:"14px 10px", textAlign:"center" }}>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}><AppIconStyles/><AppIcon name={g.icon} color={goal===g.label?g.color:COLORS.muted} size={24}/></div>
            <div style={{ fontSize:12, fontWeight:goal===g.label?700:500, color:goal===g.label?g.color:COLORS.text, fontFamily:FONT }}>{g.label}</div>
          </div>
        ))}
      </div>
      <Btn label="Continue →" disabled={!goal} onClick={()=>goal&&next()}/>
    </div>,

    /* 2 — Activity */
    <div style={{ maxWidth:390, width:"100%" }}>
      <h2 style={{ fontSize:26, color:COLORS.text, textAlign:"center", marginBottom:4, fontWeight:800, letterSpacing:-0.5 }}>How active are you<br/>right now?</h2>
      <p style={{ color:COLORS.muted, textAlign:"center", marginBottom:20, fontSize:13 }}>Honest answer = better plan</p>
      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:22 }}>
        {activities.map(a => (
          <div key={a.label} onClick={()=>setActivity(a.label)} style={{ ...cardStyle(activity===a.label), padding:"14px 16px", display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:13, background:activity===a.label?`${COLORS.sage}25`:COLORS.surface, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <AppIcon name={a.icon} color={activity===a.label?COLORS.sage:COLORS.muted} size={22}/>
            </div>
            <div>
              <div style={{ fontFamily:FONT, fontWeight:700, fontSize:14, color:activity===a.label?COLORS.sage:COLORS.text }}>{a.label}</div>
              <div style={{ fontFamily:FONT, fontSize:12, color:COLORS.muted, marginTop:2 }}>{a.desc}</div>
            </div>
            {activity===a.label && <div style={{ marginLeft:"auto", width:20, height:20, borderRadius:"50%", background:COLORS.sage, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><span style={{ color:COLORS.bg, fontSize:12, fontWeight:800 }}>✓</span></div>}
          </div>
        ))}
      </div>
      <Btn label="Continue →" disabled={!activity} onClick={()=>activity&&next()}/>
    </div>,

    /* 3 — Body stats */
    <div style={{ maxWidth:390, width:"100%" }}>
      <h2 style={{ fontSize:26, color:COLORS.text, textAlign:"center", marginBottom:4, fontWeight:800, letterSpacing:-0.5 }}>Your body stats</h2>
      <p style={{ color:COLORS.muted, textAlign:"center", marginBottom:20, fontSize:13 }}>Helps calibrate workouts & calorie tracking</p>
      <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:20, padding:"20px 18px", marginBottom:16 }}>
        <div style={{ marginBottom:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontFamily:FONT, fontWeight:700, fontSize:14, color:COLORS.text }}>Height</span>
            <span style={{ fontFamily:FONT, fontWeight:800, fontSize:18, color:COLORS.sage }}>{height} <span style={{ fontSize:12, color:COLORS.muted }}>cm</span></span>
          </div>
          <input type="range" min={140} max={220} value={height} onChange={e=>setHeight(+e.target.value)} style={{ width:"100%", accentColor:COLORS.sage, cursor:"pointer" }}/>
          <div style={{ display:"flex", justifyContent:"space-between", color:COLORS.subtle, fontSize:11, fontFamily:FONT, marginTop:4 }}><span>140 cm</span><span>220 cm</span></div>
        </div>
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontFamily:FONT, fontWeight:700, fontSize:14, color:COLORS.text }}>Weight</span>
            <span style={{ fontFamily:FONT, fontWeight:800, fontSize:18, color:COLORS.sage }}>{weight} <span style={{ fontSize:12, color:COLORS.muted }}>kg</span></span>
          </div>
          <input type="range" min={35} max={180} value={weight} onChange={e=>setWeight(+e.target.value)} style={{ width:"100%", accentColor:COLORS.sage, cursor:"pointer" }}/>
          <div style={{ display:"flex", justifyContent:"space-between", color:COLORS.subtle, fontSize:11, fontFamily:FONT, marginTop:4 }}><span>35 kg</span><span>180 kg</span></div>
        </div>
      </div>
      {/* BMI display */}
      <div style={{ background:`${bmiColor}15`, border:`1px solid ${bmiColor}40`, borderRadius:14, padding:"12px 16px", marginBottom:20, display:"flex", alignItems:"center", gap:12 }}>
        <AppIcon name="target" color={bmiColor} size={22}/>
        <div>
          <div style={{ fontFamily:FONT, fontWeight:800, fontSize:15, color:bmiColor }}>BMI {bmi} · {bmiLabel}</div>
          <div style={{ fontFamily:FONT, fontSize:12, color:COLORS.muted, marginTop:2 }}>
            {bmi<18.5?"Focus on strength building & nourishing meals.":bmi<25?"Great range — all workouts are suitable for you.":bmi<30?"Mix of cardio, strength & breathing recommended.":"Start gentle — breathing + low-impact movement first."}
          </div>
        </div>
      </div>
      <Btn label="Continue →" disabled={false} onClick={next}/>
    </div>,

    /* 4 — Health conditions */
    <div style={{ maxWidth:390, width:"100%" }}>
      <h2 style={{ fontSize:26, color:COLORS.text, textAlign:"center", marginBottom:4, fontWeight:800, letterSpacing:-0.5 }}>Any health conditions?</h2>
      <p style={{ color:COLORS.muted, textAlign:"center", marginBottom:8, fontSize:13 }}>We'll personalise your recommendations accordingly</p>
      <div style={{ background:`${COLORS.gold}12`, border:`1px solid ${COLORS.gold}30`, borderRadius:12, padding:"8px 14px", marginBottom:18, fontSize:12, color:COLORS.gold, fontFamily:FONT }}>🔒 This stays on your device — never shared</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:22 }}>
        {conditions.map(c => {
          const isSel = condition === c.label;
          return (
          <div key={c.label} onClick={()=>setCondition(c.label)} style={{
            borderRadius:16, border:`2px solid ${isSel?c.color:COLORS.border}`,
            background: isSel ? `linear-gradient(135deg, ${c.color}28, ${c.color}15)` : COLORS.card,
            cursor:"pointer", transition:"all 0.2s cubic-bezier(0.22,1,0.36,1)",
            boxShadow: isSel ? `0 6px 24px ${c.color}35` : "none",
            transform: isSel ? "scale(1.04)" : "scale(1)",
            padding:"14px 10px", textAlign:"center", position:"relative",
          }}>
            {isSel && <div style={{ position:"absolute", top:6, right:6, width:18, height:18, borderRadius:"50%", background:c.color, display:"flex", alignItems:"center", justifyContent:"center" }}><span style={{ color:"#fff", fontSize:10, fontWeight:900 }}>✓</span></div>}
            <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}>
              <div style={{ width:44, height:44, borderRadius:13, background:isSel?`${c.color}25`:COLORS.surface, border:`1.5px solid ${isSel?c.color+"90":COLORS.border}`, display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.2s", boxShadow: isSel ? `0 4px 14px ${c.color}50` : "none" }}>
                <ConditionIconStyles/><ConditionIcon condition={c.label} color={c.color} size={28}/>
              </div>
            </div>
            <div style={{ fontSize:12, fontWeight:isSel?800:500, color:isSel?c.color:COLORS.text, fontFamily:FONT, lineHeight:1.3 }}>{c.label}</div>
          </div>
          );
        })}
      </div>
      {(condition==="Hypothyroidism"||condition==="Hyperthyroidism") && (
        <div style={{ background:`${COLORS.blue}12`, border:`1px solid ${COLORS.blue}35`, borderRadius:13, padding:"10px 14px", marginBottom:16, fontSize:12, color:COLORS.blue, fontFamily:FONT, lineHeight:1.5 }}>
          {condition==="Hypothyroidism"?"💙 We'll prioritise energy-boosting sessions & avoid overtraining. Your AI coach knows thyroid fatigue patterns.":"⚡ We'll recommend calming exercises & avoid high-intensity triggers. Heart Coherence breathing is especially beneficial for you."}
        </div>
      )}
      <Btn label="Continue →" disabled={!condition} onClick={()=>condition&&next()}/>
    </div>,

    /* 5 — Notifications */
    <div style={{ maxWidth:390, width:"100%" }}>
      <div style={{ textAlign:"center", marginBottom:20 }}>
        <div style={{ width:72, height:72, borderRadius:24, background:`${COLORS.coral}18`, border:`1.5px solid ${COLORS.coral}40`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <AppIconStyles/><AppIcon name="bell" color={COLORS.coral} size={36}/>
        </div>
        <h2 style={{ fontSize:26, color:COLORS.text, fontWeight:800, letterSpacing:-0.5, margin:"0 0 6px" }}>Stay on track</h2>
        <p style={{ color:COLORS.muted, fontSize:13 }}>Get daily nudges that bring you back when life gets busy</p>
      </div>
      {notifPermission !== "granted" ? (
        <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:20, padding:"20px 18px", marginBottom:16 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:18 }}>
            {["⏰ Workout reminders at your chosen time","🔥 Streak alerts so you never break the chain","💬 Motivational nudges on tough days"].map(t=>(
              <div key={t} style={{ display:"flex", alignItems:"center", gap:10, fontFamily:FONT, fontSize:13, color:COLORS.text }}><span style={{ fontSize:18 }}>{t.split(" ")[0]}</span>{t.slice(2)}</div>
            ))}
          </div>
          <button onClick={requestNotif} style={{ width:"100%", padding:"14px", borderRadius:14, background:`linear-gradient(135deg,${COLORS.coral},${COLORS.orange})`, color:"#fff", border:"none", fontFamily:FONT, fontWeight:800, fontSize:14, cursor:"pointer", boxShadow:`0 6px 20px ${COLORS.coral}40` }}>Enable Notifications 🔔</button>
          <button onClick={finish} style={{ width:"100%", padding:"10px", borderRadius:14, background:"none", border:"none", color:COLORS.muted, fontFamily:FONT, fontSize:12, cursor:"pointer", marginTop:8 }}>Skip for now</button>
        </div>
      ) : (
        <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:20, padding:"20px 18px", marginBottom:16 }}>
          <div style={{ color:COLORS.sage, fontFamily:FONT, fontWeight:700, fontSize:13, marginBottom:14, display:"flex", alignItems:"center", gap:6 }}>✓ Notifications enabled — choose your time:</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:9, marginBottom:16 }}>
            {notifTimes.map(t => (
              <div key={t.hour} onClick={()=>setNotifHour(t.hour)} style={{ ...cardStyle(notifHour===t.hour, COLORS.coral), padding:"12px 10px", textAlign:"center" }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:6 }}><AppIcon name={t.icon} color={notifHour===t.hour?COLORS.coral:COLORS.muted} size={18}/></div>
                <div style={{ fontFamily:FONT, fontWeight:700, fontSize:14, color:notifHour===t.hour?COLORS.coral:COLORS.text }}>{t.label}</div>
                <div style={{ fontFamily:FONT, fontSize:11, color:COLORS.muted, marginTop:2 }}>{t.desc}</div>
              </div>
            ))}
          </div>
          <Btn label="All set! →" disabled={false} onClick={finish} style={{ background:`linear-gradient(135deg,${COLORS.coral},${COLORS.orange})`, boxShadow:`0 6px 20px ${COLORS.coral}40` }}/>
        </div>
      )}
    </div>,
  ];

  return (
    <div style={{ minHeight:"100vh", background:"transparent", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px 20px", fontFamily:FONT, position:"relative", overflow:"hidden" }}>
      {/* bg orbs */}
      <div style={{ position:"absolute", top:-100, left:-100, width:300, height:300, borderRadius:"50%", background:`radial-gradient(circle,${COLORS.sage}18 0%,transparent 70%)`, pointerEvents:"none" }}/>
      <div style={{ position:"absolute", bottom:-80, right:-80, width:250, height:250, borderRadius:"50%", background:`radial-gradient(circle,${COLORS.coral}12 0%,transparent 70%)`, pointerEvents:"none" }}/>

      {/* Progress bar */}
      <div style={{ display:"flex", gap:6, marginBottom:36 }}>
        {Array.from({length:TOTAL},(_,i)=>(
          <div key={i} style={{ width:i===step?32:i<step?14:8, height:6, borderRadius:3, background:i<=step?COLORS.sage:COLORS.surface, transition:"all 0.35s cubic-bezier(0.22,1,0.36,1)" }}/>
        ))}
      </div>

      {/* Step content with fade */}
      <div key={step} style={{ width:"100%", display:"flex", justifyContent:"center", animation:"obIn 0.3s ease" }}>
        <style>{`@keyframes obIn{from{opacity:0;transform:translateX(${animDir*24}px)}to{opacity:1;transform:translateX(0)}}`}</style>
        {steps[step]}
      </div>

      {/* Back button */}
      {step > 0 && (
        <button onClick={()=>goTo(step-1)} style={{ marginTop:16, background:"none", border:"none", color:COLORS.muted, fontFamily:FONT, fontSize:13, cursor:"pointer" }}>← Back</button>
      )}
      <style>{`*{-webkit-tap-highlight-color:transparent;box-sizing:border-box;}`}</style>
    </div>
  );
}

// ─── BOTTOM NAV ───────────────────────────────────────────────────────────────
function BottomNav({ active, setActive }) {
  const tabs = [
    { id: "home",     icon: "sunrise",  label: "Home",     color: "#4A9B7F" },
    { id: "breathe",  icon: "wind",     label: "Breathe",  color: "#5B9BD5" },
    { id: "meditate", icon: "moon",     label: "Meditate", color: "#7B5EA7" },
    { id: "workout",  icon: "dumbbell", label: "Workout",  color: "#E07840" },
    { id: "progress", icon: "target",   label: "Progress", color: "#D45B75" },
    { id: "profile",  icon: "sparkles", label: "Profile",  color: "#C4A055" },
  ];
  return (
    <div style={{
      position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
      width: "100%", maxWidth: 430,
      background: "rgba(8,8,6,0.88)", backdropFilter: "blur(30px) saturate(1.6)",
      borderTop: "1px solid rgba(255,255,255,0.07)",
      display: "flex", padding: "8px 0 0", paddingBottom: "calc(18px + env(safe-area-inset-bottom, 0px))", zIndex: 100,
    }}>
      <AppIconStyles/>
      {tabs.map(t => {
        const isActive = active === t.id;
        return (
          <div key={t.id} onClick={() => setActive(t.id)}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "2px 0", position: "relative", transition: "opacity 0.2s" }}>
            {/* Active indicator pill */}
            {isActive && (
              <div style={{
                position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
                width: 28, height: 3, borderRadius: 2,
                background: `linear-gradient(90deg, ${t.color}88, ${t.color})`,
                boxShadow: `0 0 10px ${t.color}80`,
              }} />
            )}
            {/* Icon container */}
            <div style={{
              width: 38, height: 38, borderRadius: 13,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: isActive ? `${t.color}20` : "transparent",
              border: isActive ? `1px solid ${t.color}35` : "1px solid transparent",
              boxShadow: isActive ? `0 0 16px ${t.color}30, inset 0 0 8px ${t.color}10` : "none",
              transition: "all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
              transform: isActive ? "scale(1.08)" : "scale(1)",
              animation: isActive ? "ico-bounce 2.2s ease-in-out infinite" : "none",
            }}>
              <AppIcon
                name={t.icon}
                color={isActive ? t.color : "rgba(255,255,255,0.28)"}
                size={isActive ? 22 : 20}
              />
            </div>
            {/* Label */}
            <div style={{
              fontSize: 8.5, fontFamily: FONT, letterSpacing: 0.2, fontWeight: isActive ? 700 : 400,
              color: isActive ? t.color : "rgba(255,255,255,0.28)",
              transition: "all 0.2s",
              textShadow: isActive ? `0 0 12px ${t.color}60` : "none",
            }}>{t.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── GOOGLE ADSENSE BANNER ────────────────────────────────────────────────────
// Only renders for free / trial users — never for premium
function AdBanner({ isPremium }) {
  const ref = useRef(null);
  useEffect(() => {
    if (isPremium) return;
    try {
      // Push the AdSense unit after mount
      if (window.adsbygoogle && ref.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (_) {}
  }, [isPremium]);

  if (isPremium) return null;

  return (
    <div style={{ width:"100%", background:"rgba(0,0,0,0.35)", borderTop:"1px solid rgba(255,255,255,0.07)", padding:"6px 0", textAlign:"center", flexShrink:0 }}>
      {/* Google AdSense — Banner-To unit (keys from env vars) */}
      <ins className="adsbygoogle"
        style={{ display:"block" }}
        data-ad-client={process.env.REACT_APP_ADSENSE_CLIENT}
        data-ad-slot={process.env.REACT_APP_ADSENSE_SLOT}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
      <div style={{ color:"rgba(255,255,255,0.18)", fontFamily:FONT, fontSize:9, marginTop:2, letterSpacing:0.5 }}>ADVERTISEMENT · Upgrade to remove ads</div>
    </div>
  );
}

// ─── SOCIAL SHARE ─────────────────────────────────────────────────────────────
function ShareButton({ sessions, streak }) {
  const [showMenu, setShowMenu] = useState(false);

  const text = `🧘 I've completed ${sessions} sessions on CalmFit!\n🔥 ${streak}-day streak and counting.\nJoin me → https://calmfitapp.in`;

  const shareWhatsApp = () => {
    logEvent("social_share", { platform: "whatsapp" });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    setShowMenu(false);
  };
  const shareInstagram = () => {
    logEvent("social_share", { platform: "instagram" });
    // Copy text to clipboard then open Instagram (Instagram doesn't support direct share URL)
    try { navigator.clipboard.writeText(text); } catch (_) {}
    alert("Caption copied! 📋 Open Instagram Stories and paste it.");
    window.open("https://www.instagram.com/", "_blank");
    setShowMenu(false);
  };
  const shareNative = () => {
    if (navigator.share) {
      navigator.share({ title:"CalmFit Streak 🔥", text, url:"https://calmfitapp.in" })
        .then(() => logEvent("social_share", { platform: "native" }))
        .catch(() => {});
    } else {
      setShowMenu(v => !v);
    }
  };

  return (
    <div style={{ position:"relative", display:"inline-block" }}>
      <button onClick={shareNative}
        style={{ background:"rgba(74,155,127,0.15)", border:"1px solid rgba(74,155,127,0.4)", borderRadius:12, padding:"8px 14px", color:COLORS.sage, fontFamily:FONT, fontWeight:700, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
        <svg width={14} height={14} viewBox="0 0 40 40" fill="none">
          <circle cx="30" cy="8" r="5" fill={COLORS.sage} opacity="0.9"/>
          <circle cx="10" cy="20" r="5" fill={COLORS.sage} opacity="0.9"/>
          <circle cx="30" cy="32" r="5" fill={COLORS.sage} opacity="0.9"/>
          <line x1="25" y1="11" x2="15" y2="17" stroke={COLORS.sage} strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="25" y1="29" x2="15" y2="23" stroke={COLORS.sage} strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
        Share Streak
      </button>
      {showMenu && (
        <div style={{ position:"absolute", bottom:"calc(100% + 8px)", left:0, background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14, overflow:"hidden", minWidth:180, boxShadow:"0 8px 28px rgba(0,0,0,0.5)", zIndex:200 }}>
          <div onClick={shareWhatsApp} style={{ padding:"12px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:10, borderBottom:`1px solid ${COLORS.border}` }}>
            <span style={{ fontSize:18 }}>💬</span><span style={{ color:COLORS.text, fontFamily:FONT, fontSize:13, fontWeight:600 }}>Share on WhatsApp</span>
          </div>
          <div onClick={shareInstagram} style={{ padding:"12px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:18 }}>📸</span><span style={{ color:COLORS.text, fontFamily:FONT, fontSize:13, fontWeight:600 }}>Copy for Instagram</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HOME SCREEN ─────────────────────────────────────────────────────────────
function HomeScreen({ user, setScreen, trialStatus, isSubscribed, sessions, onNavigateToExercise, onConditionChange }) {
  const [mood, setMood] = useState(null);
  const [showConditionPicker, setShowConditionPicker] = useState(false);
  const hour = new Date().getHours();

  // Conditions for the switcher
  const conditionList = [
    { label:"Hypothyroidism",    icon:"moon",     color:"#6AAED5", emoji:"🌙", desc:"Thyroid support" },
    { label:"Hyperthyroidism",   icon:"bolt",     color:"#F5C842", emoji:"⚡", desc:"Calming therapy" },
    { label:"Migraine",          icon:"stress",   color:"#B09FE0", emoji:"🧠", desc:"Tension relief" },
    { label:"Joint Pain",        icon:"stress",   color:"#7EC8A0", emoji:"🦴", desc:"Low-impact moves" },
    { label:"Heart Condition",   icon:"heart",    color:"#E8856A", emoji:"❤️", desc:"Heart-safe yoga" },
    { label:"Diabetes",          icon:"bolt",     color:"#F09A3E", emoji:"🩸", desc:"Sugar regulation" },
    { label:"PCOS",              icon:"heart",    color:"#D45B75", emoji:"🌸", desc:"Hormone balance" },
    { label:"Back Pain",         icon:"stress",   color:"#9B8EC5", emoji:"🔵", desc:"Spine & core" },
    { label:"Anxiety",           icon:"wind",     color:"#5BA4CF", emoji:"🌬️", desc:"Calm the mind" },
    { label:"Prefer not to say", icon:"sparkles", color:"#7C9E87", emoji:"✨", desc:"General wellness" },
  ];

  // Local-date helper — avoids UTC slice timezone bug
  const localDateStr = (iso) => { const d = new Date(iso); return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); };

  // Dynamic streak from actual sessions (timezone-safe)
  const homeStreak = (() => {
    if (!sessions?.length) return 0;
    const days = [...new Set(sessions.map(s => s.date ? localDateStr(s.date) : null).filter(Boolean))].sort((a, b) => b.localeCompare(a));
    const now = new Date();
    let count = 0; let cur = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (const day of days) {
      const [y,m,dd] = day.split('-').map(Number);
      const d = new Date(y, m-1, dd);
      const diff = Math.round((cur - d) / 86400000);
      if (diff === 0 || diff === 1) { count++; cur = d; } else break;
    }
    return count;
  })();

  // Dynamic last-7-days streak data from actual sessions (timezone-safe)
  const homeStreakData = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    const ds = d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
    return sessions?.some(s => s.date ? localDateStr(s.date) === ds : false) ?? false;
  });

  // Analytics: log screen view on mount
  useEffect(() => {
    logEvent("screen_view", { screen_name: "home", user_status: isSubscribed ? "premium" : "free" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const greeting = hour < 5 ? "Good Night" : hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  const greetingIconName = hour < 5 ? "moon" : hour < 17 ? "sunrise" : "moon";
  const greetingIconColor = hour < 5 ? COLORS.lavender : hour < 12 ? COLORS.gold : hour < 17 ? COLORS.orange : COLORS.lavender;
  const moods = [
    { label: "Energized", emoji: "⚡", color: COLORS.coral },
    { label: "Calm", emoji: "🌊", color: COLORS.blue },
    { label: "Focused", emoji: "🎯", color: COLORS.sage },
    { label: "Stressed", emoji: "😤", color: COLORS.lavender },
    { label: "Tired", emoji: "😴", color: COLORS.gold },
    { label: "Happy", emoji: "✨", color: COLORS.orange },
  ];

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100 }}>
      {/* ── Header ── */}
      <div style={{ padding: "54px 22px 20px", background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <p style={{ color: COLORS.muted, fontSize: 12, margin: 0, fontFamily: FONT, letterSpacing: 0.3, display:"flex", alignItems:"center", gap: 5 }}>
              <AppIconStyles/><AppIcon name={greetingIconName} color={greetingIconColor} size={14}/>{greeting}
            </p>
            <h1 style={{ color: COLORS.text, fontSize: 26, margin: "4px 0 0", fontFamily: FONT, fontWeight: 800, letterSpacing: -0.5, display:"flex", alignItems:"center", gap: 8 }}>
              {user.name.split(" ")[0]} <AppIcon name="wave" color={COLORS.gold} size={24}/>
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MusicPlayer compact />
            {isSubscribed ? (
              <div style={{ background: COLORS.sageDim, border: `1px solid ${COLORS.sage}50`, borderRadius: 14, padding: "6px 12px", textAlign: "right" }}>
                <div style={{ color: COLORS.sage, fontSize: 9, fontFamily: FONT, fontWeight: 700, letterSpacing: 0.5 }}>PREMIUM</div>
                <div style={{ color: COLORS.sage, fontSize: 11, fontWeight: 800, fontFamily: FONT }}>✦ Active</div>
              </div>
            ) : (
              <div onClick={() => setScreen("profile")} style={{ background: COLORS.goldDim, border: `1px solid ${COLORS.gold}50`, borderRadius: 14, padding: "6px 12px", cursor: "pointer", textAlign: "right" }}>
                <div style={{ color: COLORS.gold, fontSize: 9, fontFamily: FONT, fontWeight: 700, letterSpacing: 0.5 }}>FREE</div>
                <div style={{ color: COLORS.gold, fontSize: 11, fontFamily: FONT, fontWeight: 700 }}>Go Pro →</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Upgrade Promo Banner (only for free users) ── */}
        {!isSubscribed && (
          <div onClick={() => setScreen("profile")} style={{
            background:"linear-gradient(135deg, rgba(232,93,117,0.15) 0%, rgba(212,168,83,0.12) 100%)",
            border:"1px solid rgba(232,93,117,0.35)", borderRadius:18, padding:"12px 14px",
            display:"flex", alignItems:"center", gap:12, cursor:"pointer", marginBottom:0,
            boxShadow:"0 4px 18px rgba(232,93,117,0.15)",
            animation:"promoBounce 3s ease-in-out infinite",
          }}>
            {/* Mini mascot */}
            <div style={{ flexShrink:0 }}>
              <YogaGirlMascot size={54}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                <span style={{ background:"linear-gradient(135deg,#E85D75,#C04050)", color:"white", fontSize:9, fontFamily:FONT, fontWeight:900, padding:"2px 8px", borderRadius:8 }}>50% OFF</span>
                <span style={{ color:COLORS.coral, fontSize:9, fontFamily:FONT, fontWeight:700 }}>🔥 LAUNCH OFFER</span>
              </div>
              <div style={{ color:COLORS.text, fontFamily:FONT, fontSize:13, fontWeight:800, lineHeight:1.2 }}>Unlock Everything — Go Premium!</div>
              <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:10, marginTop:1 }}>All classes, yoga & workouts · From ₹299/mo</div>
            </div>
            <div style={{ flexShrink:0, fontSize:18 }}>→</div>
          </div>
        )}

        {/* Streak strip */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.borderWarm}`, borderRadius: 18, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 10, fontFamily: FONT, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}>Current Streak</div>
            <div style={{ color: COLORS.coral, fontSize: 22, fontWeight: 800, fontFamily: FONT, marginTop: 2, display:"flex", alignItems:"center", gap: 6 }}><AppIconStyles/><AppIcon name="fire" color={COLORS.coral} size={22}/> {homeStreak} {homeStreak === 1 ? "Day" : "Days"}</div>
          </div>
          <div style={{ display: "flex", gap: 5 }}>
            {weekDays.map((d, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 8,
                  background: homeStreakData[i] ? COLORS.coral : COLORS.surface,
                  border: `1px solid ${homeStreakData[i] ? COLORS.coral : COLORS.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10,
                  color: homeStreakData[i] ? "white" : COLORS.subtle,
                }}>{homeStreakData[i] ? "✓" : ""}</div>
                <div style={{ color: COLORS.subtle, fontSize: 9, fontFamily: FONT, fontWeight: 600 }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: "0 18px" }}>
        {/* ── Mood Check ── */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: "16px", marginBottom: 16 }}>
          <div style={{ color: COLORS.text, fontWeight: 700, fontFamily: FONT, fontSize: 14, marginBottom: 12 }}>How are you feeling today?</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {moods.map(m => (
              <div key={m.label} onClick={() => setMood(m.label)}
                style={{
                  padding: "8px 14px", borderRadius: 24,
                  border: `1.5px solid ${mood === m.label ? m.color : m.color + "45"}`,
                  background: mood === m.label ? m.color : `${m.color}12`,
                  color: mood === m.label ? "#fff" : m.color,
                  fontSize: 12, cursor: "pointer", fontFamily: FONT, fontWeight: mood === m.label ? 700 : 500,
                  transition: "all 0.22s", display: "flex", alignItems: "center", gap: 5,
                  boxShadow: mood === m.label ? `0 4px 18px ${m.color}55` : "none",
                  transform: mood === m.label ? "scale(1.07)" : "scale(1)",
                }}>
                <AppIcon name={MOOD_ICON_IDS[m.label]} color={mood === m.label ? "#fff" : m.color} size={15}/> {m.label}
              </div>
            ))}
          </div>
        </div>

        {/* ── For You Panel (mood-based recommendations) ── */}
        {mood && (() => {
          const plan = MOOD_PLANS[mood];
          if (!plan) return null;
          return (
            <div style={{ marginBottom: 16, animation: "forYouIn 0.38s cubic-bezier(0.22,1,0.36,1)" }}>
              <style>{`@keyframes forYouIn{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}`}</style>
              {/* Header */}
              <div style={{ display:"flex", alignItems:"center", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLORS.text, fontWeight: 800, fontFamily: FONT, fontSize: 15, letterSpacing: -0.3 }}>{plan.headline}</div>
                  <div style={{ color: COLORS.muted, fontFamily: FONT, fontSize: 12, marginTop: 2 }}>{plan.sub}</div>
                </div>
                <div style={{ background: `${plan.accent}22`, border: `1px solid ${plan.accent}50`, borderRadius: 20, padding: "4px 10px", display:"flex", alignItems:"center", gap: 5 }}>
                  <AppIcon name={MOOD_ICON_IDS[mood]} color={plan.accent} size={13}/>
                  <span style={{ color: plan.accent, fontSize: 11, fontFamily: FONT, fontWeight: 700 }}>{mood}</span>
                </div>
              </div>
              {/* Recommendation cards */}
              <div style={{ display:"flex", flexDirection:"column", gap: 9 }}>
                {plan.recs.map((rec, i) => (
                  <div key={i} onClick={() => setScreen(rec.screen)}
                    style={{
                      background: `linear-gradient(135deg, ${COLORS.card} 0%, ${plan.accent}10 100%)`,
                      border: `1px solid ${plan.accent}30`,
                      borderRadius: 16, padding: "13px 14px",
                      display:"flex", alignItems:"center", gap: 12,
                      cursor:"pointer", transition:"transform 0.15s, box-shadow 0.15s",
                      animation: `forYouIn ${0.38 + i * 0.08}s cubic-bezier(0.22,1,0.36,1)`,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform="scale(1.02)"; e.currentTarget.style.boxShadow=`0 6px 20px ${plan.accent}25`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform="scale(1)"; e.currentTarget.style.boxShadow="none"; }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 13, background: `${plan.accent}20`, border: `1px solid ${plan.accent}40`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink: 0 }}>
                      <AppIcon name={rec.icon} color={plan.accent} size={22}/>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap: 7, marginBottom: 2 }}>
                        <span style={{ color: COLORS.text, fontFamily: FONT, fontWeight: 700, fontSize: 13 }}>{rec.title}</span>
                        <span style={{ background: `${plan.accent}25`, color: plan.accent, fontSize: 9, fontFamily: FONT, fontWeight: 800, padding: "2px 7px", borderRadius: 8, letterSpacing: 0.3 }}>{rec.badge}</span>
                      </div>
                      <div style={{ color: COLORS.muted, fontFamily: FONT, fontSize: 11 }}>{rec.desc}</div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap: 4, flexShrink: 0 }}>
                      <span style={{ color: COLORS.muted, fontSize: 10, fontFamily: FONT }}>{rec.time}</span>
                      <div style={{ background: plan.accent, borderRadius: 10, padding: "5px 11px", color:"#fff", fontSize: 11, fontFamily: FONT, fontWeight: 700 }}>Start →</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Personalised Health Recommendations (condition-based) ── */}
        {user?.condition && CONDITION_RECOMMENDATIONS[user.condition] && (() => {
          const rec = CONDITION_RECOMMENDATIONS[user.condition];
          // find exercise data (for GIF) from exerciseCategories
          const yogaCat = exerciseCategories.find(c => c.id === "yoga");
          const getExGif = (poseName) => {
            const ex = yogaCat?.exercises.find(e => e.name === poseName);
            return ex?.gif || null;
          };
          return (
            <div style={{ marginBottom: 18, animation: "forYouIn 0.4s cubic-bezier(0.22,1,0.36,1)" }}>
              <style>{`
                @keyframes forYouIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
                .pose-card:active{transform:scale(0.98)!important;}
              `}</style>

              {/* ── Condition Picker Modal ── */}
              {showConditionPicker && (
                <div style={{ position:"fixed", inset:0, zIndex:9999, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}
                  onClick={e => { if(e.target===e.currentTarget) setShowConditionPicker(false); }}>
                  <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.72)", backdropFilter:"blur(8px)" }} onClick={() => setShowConditionPicker(false)}/>
                  <div style={{ position:"relative", background:"linear-gradient(180deg,#141E18 0%,#0E1812 100%)", borderRadius:"28px 28px 0 0", padding:"0 0 36px", maxHeight:"85vh", overflowY:"auto", boxShadow:"0 -12px 60px rgba(0,0,0,0.6)", animation:"forYouIn 0.3s cubic-bezier(0.22,1,0.36,1)" }}>
                    {/* Handle bar */}
                    <div style={{ display:"flex", justifyContent:"center", padding:"14px 0 4px" }}>
                      <div style={{ width:40, height:4, borderRadius:2, background:"rgba(255,255,255,0.2)" }}/>
                    </div>
                    {/* Header */}
                    <div style={{ padding:"10px 22px 18px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                        <div style={{ width:34, height:34, borderRadius:10, background:"linear-gradient(135deg,#7C9E8730,#7C9E8715)", border:"1px solid #7C9E8740", display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <span style={{ fontSize:16 }}>🔄</span>
                        </div>
                        <div>
                          <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:800, fontSize:18, letterSpacing:-0.4 }}>Switch Condition</div>
                          <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:11, marginTop:1 }}>Recommendations update instantly</div>
                        </div>
                      </div>
                    </div>
                    {/* Condition grid */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:"16px 14px" }}>
                      <AppIconStyles/>
                      {conditionList.map((c, idx) => {
                        const isSelected = user.condition === c.label;
                        return (
                          <div key={c.label} onClick={() => { if(onConditionChange) onConditionChange(c.label); setShowConditionPicker(false); }}
                            style={{
                              background: isSelected
                                ? `linear-gradient(135deg, ${c.color}28 0%, ${c.color}14 100%)`
                                : "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)",
                              border: isSelected ? `2px solid ${c.color}80` : "1.5px solid rgba(255,255,255,0.07)",
                              borderRadius: 18, padding:"14px 13px 13px", cursor:"pointer", position:"relative", overflow:"hidden",
                              transition:"all 0.18s cubic-bezier(0.22,1,0.36,1)",
                              transform: isSelected ? "scale(1.03)" : "scale(1)",
                              boxShadow: isSelected ? `0 6px 24px ${c.color}25` : "none",
                              animation: `forYouIn ${0.3 + idx * 0.04}s cubic-bezier(0.22,1,0.36,1)`,
                            }}
                            onMouseEnter={e => { if(!isSelected){ e.currentTarget.style.border=`1.5px solid ${c.color}40`; e.currentTarget.style.background=`linear-gradient(135deg,${c.color}14,${c.color}08)`; }}}
                            onMouseLeave={e => { if(!isSelected){ e.currentTarget.style.border="1.5px solid rgba(255,255,255,0.07)"; e.currentTarget.style.background="linear-gradient(135deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))"; }}}
                          >
                            {/* Background glow blob */}
                            <div style={{ position:"absolute", top:-20, right:-20, width:70, height:70, borderRadius:"50%", background:`radial-gradient(circle,${c.color}20 0%,transparent 70%)`, pointerEvents:"none" }}/>
                            {/* Checkmark badge */}
                            {isSelected && (
                              <div style={{ position:"absolute", top:8, right:8, width:20, height:20, borderRadius:"50%", background:`linear-gradient(135deg,${c.color},${c.color}CC)`, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:`0 2px 8px ${c.color}50` }}>
                                <span style={{ color:"#fff", fontSize:10, fontWeight:900 }}>✓</span>
                              </div>
                            )}
                            {/* Animated condition icon */}
                            <div style={{ width:44, height:44, borderRadius:13, background:`linear-gradient(135deg,${c.color}35,${c.color}18)`, border:`1.5px solid ${c.color}50`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10, boxShadow:`0 4px 12px ${c.color}30` }}>
                              <ConditionIconStyles/><ConditionIcon condition={c.label} color={c.color} size={28}/>
                            </div>
                            {/* Label */}
                            <div style={{ color: isSelected ? "#F0F0EE" : COLORS.text, fontFamily:FONT, fontWeight:800, fontSize:12, lineHeight:1.25, marginBottom:3 }}>{c.label}</div>
                            {/* Sub-desc */}
                            <div style={{ color: isSelected ? c.color : COLORS.subtle, fontFamily:FONT, fontSize:10, fontWeight:600, letterSpacing:0.1 }}>{c.desc}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Section label ── */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:4, height:18, borderRadius:2, background:`linear-gradient(180deg,${rec.color},${rec.color}80)` }}/>
                  <span style={{ color:COLORS.text, fontFamily:FONT, fontWeight:800, fontSize:15, letterSpacing:-0.3 }}>Recommended For You</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ background:`${rec.color}20`, border:`1px solid ${rec.color}35`, borderRadius:20, padding:"3px 10px", display:"flex", alignItems:"center", gap:5 }}>
                    <AppIconStyles/><AppIcon name={rec.icon} color={rec.color} size={11}/>
                    <span style={{ color:rec.color, fontSize:10, fontFamily:FONT, fontWeight:700 }}>{user.condition}</span>
                  </div>
                  <div onClick={() => setShowConditionPicker(true)} style={{
                    background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)",
                    borderRadius:20, padding:"3px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:4,
                    transition:"all 0.15s ease",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.06)"; }}
                  >
                    <span style={{ color:COLORS.muted, fontSize:10 }}>✏️</span>
                    <span style={{ color:COLORS.muted, fontSize:10, fontFamily:FONT, fontWeight:600 }}>Change</span>
                  </div>
                </div>
              </div>

              {/* ── Hero header card ── */}
              <div style={{
                background: "linear-gradient(135deg, #0D1A14 0%, #111B17 50%, #0A1410 100%)",
                border: `1.5px solid ${rec.color}35`,
                borderRadius: 22, padding: "16px 16px 14px", marginBottom: 10,
                position:"relative", overflow:"hidden",
              }}>
                {/* Glow orb */}
                <div style={{ position:"absolute", top:-40, right:-40, width:130, height:130, borderRadius:"50%", background:`radial-gradient(circle, ${rec.color}22 0%, transparent 70%)`, pointerEvents:"none" }}/>
                <div style={{ display:"flex", alignItems:"flex-start", gap:12, position:"relative" }}>
                  <div style={{ width:46, height:46, borderRadius:14, background:`linear-gradient(135deg,${rec.color}30,${rec.color}18)`, border:`1.5px solid ${rec.color}50`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, boxShadow:`0 4px 14px ${rec.color}30` }}>
                    <AppIcon name={rec.icon} color={rec.color} size={22}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
                      <span style={{ background:`linear-gradient(135deg,${rec.color},${rec.color}CC)`, color:"#fff", fontSize:9, fontFamily:FONT, fontWeight:900, padding:"3px 9px", borderRadius:8, letterSpacing:0.5 }}>✦ FOR YOU</span>
                      <span style={{ color:COLORS.subtle, fontSize:9, fontFamily:FONT }}>{rec.poses.length} poses</span>
                    </div>
                    <div style={{ color:"#F0F0EE", fontFamily:FONT, fontWeight:800, fontSize:16, letterSpacing:-0.4, lineHeight:1.2, marginBottom:5 }}>{rec.headline}</div>
                    <div style={{ color:"#7A8A82", fontFamily:FONT, fontSize:11, lineHeight:1.55 }}>{rec.description}</div>
                  </div>
                </div>
              </div>

              {/* ── Pose cards ── */}
              <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                {rec.poses.map((pose, i) => {
                  const isBreathe = !!pose.screen;
                  const gif = getExGif(pose.name);
                  const handleClick = () => {
                    if (isBreathe) { setScreen("breathe"); }
                    else if (pose.catId && onNavigateToExercise) { onNavigateToExercise(pose.catId, pose.name); }
                    else { setScreen("workout"); }
                  };
                  return (
                    <div key={i} className="pose-card" onClick={handleClick} style={{
                      background: "linear-gradient(135deg, #111917 0%, #0E1712 100%)",
                      border: "1px solid rgba(255,255,255,0.07)",
                      borderRadius: 16, padding: "11px 13px",
                      display:"flex", alignItems:"center", gap:12,
                      cursor:"pointer",
                      transition:"all 0.18s cubic-bezier(0.22,1,0.36,1)",
                      animation: `forYouIn ${0.3 + i * 0.06}s cubic-bezier(0.22,1,0.36,1)`,
                    }}
                      onMouseEnter={e => { e.currentTarget.style.border=`1px solid ${rec.color}45`; e.currentTarget.style.background=`linear-gradient(135deg, #162118 0%, #111A14 100%)`; e.currentTarget.style.boxShadow=`0 4px 20px ${rec.color}18`; }}
                      onMouseLeave={e => { e.currentTarget.style.border="1px solid rgba(255,255,255,0.07)"; e.currentTarget.style.background="linear-gradient(135deg, #111917 0%, #0E1712 100%)"; e.currentTarget.style.boxShadow="none"; }}
                    >
                      {/* GIF / icon thumbnail */}
                      <div style={{ width:58, height:58, borderRadius:13, overflow:"hidden", flexShrink:0, background:`linear-gradient(135deg,${rec.color}18,${rec.color}0A)`, border:`1px solid ${rec.color}30`, display:"flex", alignItems:"center", justifyContent:"center", position:"relative" }}>
                        {gif ? (
                          <>
                            <img src={gif} alt={pose.name} style={{ width:"100%", height:"100%", objectFit:"cover" }}
                              onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }}/>
                            <div style={{ display:"none", position:"absolute", inset:0, alignItems:"center", justifyContent:"center" }}>
                              <AppIcon name={isBreathe?"wind":"yoga"} color={rec.color} size={24}/>
                            </div>
                          </>
                        ) : (
                          <AppIcon name={isBreathe?"wind":"yoga"} color={rec.color} size={24}/>
                        )}
                        {/* Number badge */}
                        <div style={{ position:"absolute", bottom:3, right:3, width:16, height:16, borderRadius:"50%", background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <span style={{ color:"#fff", fontSize:8, fontFamily:FONT, fontWeight:800 }}>{i+1}</span>
                        </div>
                      </div>

                      {/* Text content */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3, flexWrap:"wrap" }}>
                          <span style={{ color:"#E8EDE9", fontFamily:FONT, fontWeight:700, fontSize:13 }}>{pose.name}</span>
                          <span style={{ background:`${rec.color}22`, color:rec.color, fontSize:9, fontFamily:FONT, fontWeight:800, padding:"2px 7px", borderRadius:6, letterSpacing:0.2 }}>{pose.duration}</span>
                          {isBreathe && <span style={{ background:"#4A8BC520", color:"#6AAED5", fontSize:9, fontFamily:FONT, fontWeight:700, padding:"2px 7px", borderRadius:6 }}>BREATHE</span>}
                        </div>
                        <div style={{ color:"#6B7A70", fontFamily:FONT, fontSize:11, lineHeight:1.45, marginBottom:3 }}>{pose.benefit}</div>
                        <div style={{ color:rec.color, fontFamily:FONT, fontSize:10, fontWeight:600, fontStyle:"italic", opacity:0.85 }}>{pose.sanskrit}</div>
                      </div>

                      {/* CTA arrow */}
                      <div style={{ flexShrink:0, width:30, height:30, borderRadius:9, background:`${rec.color}20`, border:`1px solid ${rec.color}35`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <span style={{ color:rec.color, fontSize:13, fontWeight:800 }}>{isBreathe ? "💨" : "→"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── Mind & Breathe CTAs ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div onClick={() => setScreen("breathe")} style={{
            background: `linear-gradient(135deg, #0A1A14 0%, #14251C 100%)`,
            border: `1px solid ${COLORS.sage}25`,
            borderRadius: 18, padding: "16px 14px", cursor: "pointer",
            position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", right: -8, top: -8, fontSize: 60, opacity: 0.07 }}>◎</div>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: COLORS.sageDim, border: `1px solid ${COLORS.sage}30`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}><AppIcon name="lungs" color={COLORS.sage} size={22}/></div>
            <div style={{ color: COLORS.text, fontWeight: 700, fontFamily: FONT, fontSize: 13 }}>Breathe</div>
            <div style={{ color: COLORS.muted, fontFamily: FONT, fontSize: 11, marginTop: 3 }}>Box · 4-7-8 · Sigh</div>
            <div style={{ color: COLORS.sage, fontSize: 11, marginTop: 6, fontWeight: 600 }}>6 techniques →</div>
          </div>
          <div onClick={() => setScreen("meditate")} style={{
            background: `linear-gradient(135deg, #0D0814 0%, #180D22 100%)`,
            border: `1px solid #7B5EA725`,
            borderRadius: 18, padding: "16px 14px", cursor: "pointer",
            position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", right: -8, top: -8, opacity: 0.08, pointerEvents:"none" }}><AppIcon name="moon" color="#7B5EA7" size={60}/></div>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: "#7B5EA715", border: `1px solid #7B5EA730`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}><AppIconStyles/><AppIcon name="moon" color="#7B5EA7" size={22}/></div>
            <div style={{ color: COLORS.text, fontWeight: 700, fontFamily: FONT, fontSize: 13 }}>Meditate</div>
            <div style={{ color: COLORS.muted, fontFamily: FONT, fontSize: 11, marginTop: 3 }}>Mindfulness · Metta · Vipassana</div>
            <div style={{ color: "#A98BCF", fontSize: 11, marginTop: 6, fontWeight: 600 }}>6 techniques →</div>
          </div>
        </div>

        {/* ── Today's Yoga ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ color: COLORS.text, fontWeight: 700, fontFamily: FONT, fontSize: 15 }}>Today's Yoga Class</div>
          <div onClick={() => setScreen("explore")} style={{ fontSize: 12, color: COLORS.sage, fontFamily: FONT, cursor: "pointer", fontWeight: 600 }}>See all →</div>
        </div>
        <div onClick={() => setScreen("explore")} style={{
          background: `linear-gradient(135deg, #0E1E16 0%, #152A1E 60%, #1E3828 100%)`,
          border: `1px solid ${COLORS.sage}30`,
          borderRadius: 20, padding: 20, marginBottom: 16, cursor: "pointer", position: "relative", overflow: "hidden",
        }}>
          <div style={{ position: "absolute", right: -10, bottom: -10, opacity: 0.07, pointerEvents:"none" }}><AppIcon name="sunrise" color={COLORS.sage} size={90}/></div>
          <div style={{ background: `${COLORS.sage}20`, border: `1px solid ${COLORS.sage}30`, display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 20, fontSize: 10, color: COLORS.sage, marginBottom: 10, fontFamily: FONT, fontWeight: 700, letterSpacing: 0.5 }}>BEGINNER · 20 MIN</div>
          <h2 style={{ color: COLORS.text, fontSize: 19, margin: "0 0 4px", fontFamily: FONT, fontWeight: 800, display:"flex", alignItems:"center", gap: 8 }}><AppIcon name="sunrise" color={COLORS.sage} size={22}/> Morning Sun Flow</h2>
          <p style={{ color: COLORS.muted, fontSize: 12, margin: "0 0 14px", fontFamily: FONT }}>with Aria Chen · Hatha Yoga</p>
          <div style={{ background: COLORS.sage, color: COLORS.bg, display: "inline-block", padding: "8px 16px", borderRadius: 12, fontSize: 12, fontWeight: 700, fontFamily: FONT }}>Browse All Classes →</div>
        </div>

        {/* ── 30-Day Challenge Banner ── */}
        {(() => {
          const done = (() => { try { return JSON.parse(localStorage.getItem("cf_challenge_done") || "[]"); } catch { return []; } })();
          const count = done.length;
          const pct = Math.min(1, count / 30);
          const nextDay = CHALLENGE_DAYS.find(d => !done.includes(d.day)) || CHALLENGE_DAYS[29];
          const circ = 2 * Math.PI * 22;
          return (
            <div onClick={() => setScreen("challenge")} style={{
              background: "linear-gradient(135deg, #041A12 0%, #071F17 50%, #0C2A1D 100%)",
              border: "1.5px solid rgba(74,155,127,0.45)",
              borderRadius: 22, marginBottom: 16, cursor: "pointer", overflow: "hidden", position: "relative",
              boxShadow: "0 8px 32px rgba(74,155,127,0.18)",
            }}>
              {/* Decorative bg glow */}
              <div style={{ position:"absolute", right:-30, top:-30, width:160, height:160, borderRadius:"50%", background:"rgba(74,155,127,0.07)", pointerEvents:"none" }}/>

              {/* Top: title + ring */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 18px 12px" }}>
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4 }}>
                    <span style={{ background:"linear-gradient(135deg,#4A9B7F,#2D7A5F)", color:"white", fontSize:9, fontFamily:FONT, fontWeight:900, padding:"3px 9px", borderRadius:8, letterSpacing:0.5 }}>30-DAY CHALLENGE</span>
                    {count > 0 && count < 30 && <span style={{ color:COLORS.sage, fontSize:9, fontFamily:FONT, fontWeight:700 }}>🔥 IN PROGRESS</span>}
                    {count === 0 && <span style={{ color:"#C4A055", fontSize:9, fontFamily:FONT, fontWeight:700 }}>✦ START TODAY</span>}
                    {count === 30 && <span style={{ color:"#C4A055", fontSize:9, fontFamily:FONT, fontWeight:700 }}>🏆 COMPLETE!</span>}
                  </div>
                  <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:900, fontSize:20, letterSpacing:-0.5, lineHeight:1.2 }}>Stay Active</div>
                  <div style={{ color:COLORS.sage, fontFamily:FONT, fontWeight:900, fontSize:20, letterSpacing:-0.5, lineHeight:1.2 }}>30 Day Challenge</div>
                  <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:11, marginTop:4 }}>Yoga · Pilates · Tai Chi · Breathwork</div>
                </div>

                {/* Mini ring */}
                <div style={{ position:"relative", width:58, height:58, flexShrink:0 }}>
                  <svg width={58} height={58} style={{ transform:"rotate(-90deg)" }}>
                    <circle cx={29} cy={29} r={22} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={5}/>
                    <circle cx={29} cy={29} r={22} fill="none" stroke={COLORS.sage} strokeWidth={5}
                      strokeDasharray={circ} strokeDashoffset={circ*(1-pct)} strokeLinecap="round"
                      style={{ transition:"stroke-dashoffset 0.8s ease" }}/>
                  </svg>
                  <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                    <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:900, fontSize:14, lineHeight:1 }}>{count}</div>
                    <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:9 }}>/ 30</div>
                  </div>
                </div>
              </div>

              {/* Week dots row */}
              <div style={{ display:"flex", gap:0, padding:"0 18px 10px" }}>
                {[1,2,3,4,5].map(wk => {
                  const wkDays = CHALLENGE_DAYS.filter(d => d.week === wk);
                  const wkDone = wkDays.filter(d => done.includes(d.day)).length;
                  const wkPct  = wkDone / wkDays.length;
                  return (
                    <div key={wk} style={{ flex:1, paddingRight: wk < 5 ? 5 : 0 }}>
                      <div style={{ height:4, borderRadius:3, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                        <div style={{ width:`${wkPct*100}%`, height:"100%", background:COLORS.sage, borderRadius:3, transition:"width 0.5s" }}/>
                      </div>
                      <div style={{ color:COLORS.subtle, fontFamily:FONT, fontSize:8, marginTop:3, textAlign:"center" }}>W{wk}</div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom: next day pill */}
              {count < 30 && (
                <div style={{ margin:"0 14px 14px", background:"rgba(74,155,127,0.12)", border:"1px solid rgba(74,155,127,0.3)", borderRadius:14, padding:"10px 14px", display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:36, height:36, borderRadius:11, background:"rgba(74,155,127,0.2)", border:"1px solid rgba(74,155,127,0.4)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <AppIconStyles/><AppIcon name={CHALLENGE_DAYS[0] && nextDay ? ({"breathe":"lungs","yoga":"yoga","pilates":"dumbbell","meditate":"moon","taichi":"wave","vinyasa":"bolt","rest":"leaf","celebrate":"sparkles"}[nextDay.type] || "leaf") : "leaf"} color={COLORS.sage} size={20}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                      <span style={{ color:COLORS.sage, fontFamily:FONT, fontWeight:800, fontSize:10 }}>Day {nextDay.day}</span>
                      <span style={{ color:COLORS.muted, fontFamily:FONT, fontSize:10 }}>· {nextDay.dur}</span>
                    </div>
                    <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:700, fontSize:13 }}>{nextDay.label}</div>
                  </div>
                  <div style={{ background:COLORS.sage, color:"white", fontFamily:FONT, fontWeight:800, fontSize:11, padding:"8px 14px", borderRadius:12, flexShrink:0 }}>Open →</div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Workout Quick Access ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ color: COLORS.text, fontWeight: 700, fontFamily: FONT, fontSize: 15 }}>Train by Muscle Group</div>
          <div onClick={() => setScreen("workout")} style={{ fontSize: 12, color: COLORS.coral, fontFamily: FONT, cursor: "pointer", fontWeight: 600 }}>All →</div>
        </div>
        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
          {exerciseCategories.map(cat => (
            <div key={cat.id} onClick={() => setScreen("workout")}
              style={{
                flexShrink: 0, background: COLORS.card, borderRadius: 16, padding: "12px 10px",
                width: 80, textAlign: "center", cursor: "pointer",
                border: `1px solid ${cat.color}25`,
              }}>
              <WorkoutIconStyles />
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 5 }}><WorkoutIcon catId={cat.id} color={cat.color} size={30} /></div>
              <div style={{ fontFamily: FONT, fontWeight: 700, color: COLORS.text, fontSize: 11, marginBottom: 2 }}>{cat.label}</div>
              <div style={{ fontFamily: FONT, color: cat.color, fontSize: 10, fontWeight: 600 }}>{cat.exercises.length} ex</div>
            </div>
          ))}
        </div>

        {/* ── Share streak ── */}
        <div style={{ display:"flex", justifyContent:"center", marginTop:20, marginBottom:4 }}>
          <ShareButton sessions={(sessions||[]).length} streak={homeStreak}/>
        </div>
      </div>

      {/* ── AdSense banner (hidden for premium users) ── */}
      <AdBanner isPremium={isSubscribed}/>
    </div>
  );
}

// ─── 30-DAY CHALLENGE SCREEN ──────────────────────────────────────────────────
const CHALLENGE_DAYS = [
  // Week 1 — Foundation
  { day:1,  week:1, type:"breathe",  label:"Morning Breathwork",       sub:"Box Breathing · Energise",            dur:"5 min",  screen:"breathe",  emoji:"🌬️",  color:"#4A9B7F" },
  { day:2,  week:1, type:"yoga",     label:"Mountain & Forward Fold",   sub:"Foundation Poses",                    dur:"10 min", screen:"explore",  emoji:"🧘",  color:"#5B9BD5" },
  { day:3,  week:1, type:"rest",     label:"Rest & Recover",            sub:"Hydrate · Sleep · Reflect",           dur:"REST",   screen:null,       emoji:"☕",  color:"#7B5EA7" },
  { day:4,  week:1, type:"yoga",     label:"Sun Salutation × 3",        sub:"Morning Sun Flow",                    dur:"15 min", screen:"explore",  emoji:"🌅",  color:"#C4A055" },
  { day:5,  week:1, type:"pilates",  label:"Core Foundations",          sub:"Pelvic Curl · Single Leg",            dur:"15 min", screen:"explore",  emoji:"💪",  color:"#D45B75" },
  { day:6,  week:1, type:"meditate", label:"Loving-Kindness",           sub:"Metta Meditation · 12 min",           dur:"12 min", screen:"meditate", emoji:"❤️", color:"#D45B75" },
  { day:7,  week:1, type:"rest",     label:"Rest & Restore",            sub:"You completed Week 1! 🎉",            dur:"REST",   screen:null,       emoji:"🌿",  color:"#4A9B7F" },
  // Week 2 — Building
  { day:8,  week:2, type:"yoga",     label:"Full Morning Flow",         sub:"Morning Sun Flow · 20 min",           dur:"20 min", screen:"explore",  emoji:"🌅",  color:"#5B9BD5" },
  { day:9,  week:2, type:"pilates",  label:"Core Pilates Blast",        sub:"Double Leg · Criss-Cross",            dur:"20 min", screen:"explore",  emoji:"🔥",  color:"#E07840" },
  { day:10, week:2, type:"rest",     label:"Active Rest",               sub:"Walk · Stretch · Breathe",            dur:"REST",   screen:null,       emoji:"🚶",  color:"#7B5EA7" },
  { day:11, week:2, type:"breathe",  label:"Breathwork + Stretch",      sub:"4-7-8 · Alternate Nostril",           dur:"15 min", screen:"breathe",  emoji:"🌬️",  color:"#4A9B7F" },
  { day:12, week:2, type:"taichi",   label:"Tai Chi Introduction",      sub:"Wu Style · Opening Forms",            dur:"25 min", screen:"explore",  emoji:"🌊",  color:"#4AABB5" },
  { day:13, week:2, type:"meditate", label:"Body Scan",                 sub:"MBSR · Deep Relaxation",              dur:"18 min", screen:"meditate", emoji:"🫁",  color:"#7B5EA7" },
  { day:14, week:2, type:"rest",     label:"Rest Day",                  sub:"Week 2 done! Halfway there 💫",       dur:"REST",   screen:null,       emoji:"🌙",  color:"#5B9BD5" },
  // Week 3 — Deepening
  { day:15, week:3, type:"yoga",     label:"Sun Flow + Warriors",       sub:"Warrior I & II · Triangle",           dur:"20 min", screen:"explore",  emoji:"⚔️",  color:"#C4A055" },
  { day:16, week:3, type:"pilates",  label:"Obliques & Core",           sub:"Criss-Cross · Roll Up · Swan",        dur:"22 min", screen:"explore",  emoji:"💪",  color:"#D45B75" },
  { day:17, week:3, type:"rest",     label:"Rest & Nourish",            sub:"Focus on nutrition today 🥗",         dur:"REST",   screen:null,       emoji:"🥗",  color:"#4A9B7F" },
  { day:18, week:3, type:"taichi",   label:"Tai Chi Flow",              sub:"Wu Style · 8-Form Sequence",          dur:"35 min", screen:"explore",  emoji:"🌊",  color:"#4AABB5" },
  { day:19, week:3, type:"yoga",     label:"Stress Relief Flow",        sub:"Legs Up Wall · Savasana",             dur:"25 min", screen:"explore",  emoji:"😮‍💨", color:"#5B9BD5" },
  { day:20, week:3, type:"meditate", label:"Guided Visualization",      sub:"Inner Cinema · 15 min",               dur:"15 min", screen:"meditate", emoji:"🎬",  color:"#C4A055" },
  { day:21, week:3, type:"rest",     label:"Halfway Celebration! 🎊",   sub:"21 days — you're unstoppable!",       dur:"REST",   screen:null,       emoji:"🏅",  color:"#C4A055" },
  // Week 4 — Power
  { day:22, week:4, type:"vinyasa",  label:"Power Vinyasa Intro",       sub:"Chair · Warrior III · Half Moon",     dur:"30 min", screen:"explore",  emoji:"⚡",  color:"#E07840" },
  { day:23, week:4, type:"pilates",  label:"Full Pilates Blast",        sub:"All 8 poses · Full sequence",         dur:"30 min", screen:"explore",  emoji:"🔥",  color:"#D45B75" },
  { day:24, week:4, type:"rest",     label:"Deep Recovery",             sub:"Foam roll · Cold shower · Sleep",     dur:"REST",   screen:null,       emoji:"🛁",  color:"#7B5EA7" },
  { day:25, week:4, type:"vinyasa",  label:"Power Vinyasa Flow",        sub:"Crow · Chaturanga · Wheel",           dur:"40 min", screen:"explore",  emoji:"⚡",  color:"#E07840" },
  { day:26, week:4, type:"taichi",   label:"Full Tai Chi Practice",     sub:"Complete Wu Style 35-min",            dur:"40 min", screen:"explore",  emoji:"🌊",  color:"#4AABB5" },
  { day:27, week:4, type:"meditate", label:"Peaceful Place",            sub:"Body Scan + Sanctuary · 18 min",      dur:"18 min", screen:"meditate", emoji:"🏝️",  color:"#4AABB5" },
  { day:28, week:4, type:"rest",     label:"Peak Week Complete!",       sub:"Week 4 done — final push ahead 💥",   dur:"REST",   screen:null,       emoji:"🌟",  color:"#C4A055" },
  // Week 5 — Completion
  { day:29, week:5, type:"yoga",     label:"Master Flow",               sub:"Sun Flow + Power Vinyasa combo",      dur:"45 min", screen:"explore",  emoji:"🧘",  color:"#5B9BD5" },
  { day:30, week:5, type:"celebrate",label:"30-Day Complete! 🏆",        sub:"You did it — take a bow 🙏",          dur:"DONE!",  screen:"explore",  emoji:"🏆",  color:"#C4A055" },
];

function ChallengeScreen({ onBack, setScreen, isSubscribed, onUpgrade }) {
  const [completedDays, setCompletedDays] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cf_challenge_done") || "[]"); } catch { return []; }
  });
  const [expandedWeek, setExpandedWeek] = useState(1);

  const completedCount = completedDays.length;
  const progressPct    = Math.min(1, completedCount / 30);
  const nextDay        = CHALLENGE_DAYS.find(d => !completedDays.includes(d.day)) || CHALLENGE_DAYS[29];

  const markDone = (dayNum) => {
    if (completedDays.includes(dayNum)) return;
    const next = [...completedDays, dayNum];
    setCompletedDays(next);
    try { localStorage.setItem("cf_challenge_done", JSON.stringify(next)); } catch {}
  };

  const isDone      = (d) => completedDays.includes(d.day);
  const isUnlocked  = (d) => d.day === 1 || completedDays.includes(d.day - 1);
  const isCurrent   = (d) => d.day === nextDay.day && !isDone(d);

  const weeks = [1,2,3,4,5];
  const circumference = 2 * Math.PI * 44;

  const typeColors = {
    breathe:"#4A9B7F", yoga:"#5B9BD5", pilates:"#D45B75",
    meditate:"#7B5EA7", taichi:"#4AABB5", vinyasa:"#E07840",
    rest:"#555E6A", celebrate:"#C4A055",
  };
  const typeLabelMap = {
    breathe:"BREATHE", yoga:"YOGA", pilates:"PILATES",
    meditate:"MEDITATE", taichi:"TAI CHI", vinyasa:"VINYASA",
    rest:"REST", celebrate:"FINALE",
  };
  const typeIconMap = {
    breathe:"lungs", yoga:"yoga", pilates:"dumbbell",
    meditate:"moon", taichi:"wave", vinyasa:"bolt",
    rest:"leaf", celebrate:"sparkles",
  };

  /* Inline SVG icons — match app's dark aesthetic exactly */
  const LockIcon = ({ size = 20, color = "rgba(255,255,255,0.28)" }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect x="10" y="20" width="20" height="15" rx="4.5" fill={color} opacity="0.18" stroke={color} strokeWidth="2"/>
      <path d="M14 20 L14 14.5 C14 10.5 26 10.5 26 14.5 L26 20" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="20" cy="27.5" r="2.5" fill={color} opacity="0.75"/>
      <line x1="20" y1="27.5" x2="20" y2="30" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
    </svg>
  );

  const CheckIcon = ({ size = 22, col: c = COLORS.sage }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="20" r="15" fill={c} opacity="0.22"/>
      <circle cx="20" cy="20" r="15" stroke={c} strokeWidth="2.5" fill="none"/>
      <path d="M13 20.5 L18 26 L28 13.5" stroke={c} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div style={{ background: COLORS.bg, minHeight: "100vh", paddingBottom: 100 }}>
      <style>{`
        @keyframes challengePulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.04)} }
        @keyframes challengeSlideIn { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes starPop { 0%{transform:scale(0) rotate(-30deg);opacity:0} 60%{transform:scale(1.2) rotate(5deg);opacity:1} 100%{transform:scale(1) rotate(0deg);opacity:1} }
      `}</style>

      {/* ── Header ── */}
      <div style={{ background: "linear-gradient(180deg,#020D0A 0%,#041410 50%,transparent 100%)", padding: "52px 20px 20px", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, background:"radial-gradient(ellipse 80% 60% at 50% 0%, #4A9B7F18 0%, transparent 70%)", pointerEvents:"none" }} />
        <button onClick={onBack} style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:12, padding:"7px 14px", color:COLORS.text, fontFamily:FONT, fontSize:12, fontWeight:600, cursor:"pointer", marginBottom:18 }}>← Back</button>

        {/* Title row */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
          <div>
            <div style={{ color:COLORS.sage, fontSize:10, fontFamily:FONT, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>CalmFit × 30 Days</div>
            <h1 style={{ color:COLORS.text, fontSize:26, fontFamily:FONT, fontWeight:900, margin:0, letterSpacing:-0.8, lineHeight:1.15 }}>Stay Active<br/><span style={{ color:COLORS.sage }}>30-Day Challenge</span></h1>
            <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:12, marginTop:6 }}>Yoga · Pilates · Tai Chi · Breathwork · Meditation</div>
          </div>

          {/* Progress ring */}
          <div style={{ position:"relative", width:100, height:100, flexShrink:0 }}>
            <svg width={100} height={100} style={{ transform:"rotate(-90deg)" }}>
              <circle cx={50} cy={50} r={44} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={7}/>
              <circle cx={50} cy={50} r={44} fill="none" stroke={COLORS.sage} strokeWidth={7}
                strokeDasharray={circumference} strokeDashoffset={circumference * (1 - progressPct)}
                strokeLinecap="round" style={{ transition:"stroke-dashoffset 0.8s ease" }}/>
            </svg>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
              <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:900, fontSize:20, lineHeight:1 }}>{completedCount}</div>
              <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:10, fontWeight:600 }}>/ 30</div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          {[
            { label:"Completed", value:`${completedCount}`, unit:"days", icon:"target",  color:COLORS.sage },
            { label:"Remaining", value:`${30-completedCount}`, unit:"days", icon:"calendar", color:COLORS.blue  },
            { label:"Progress",  value:`${Math.round(progressPct*100)}`, unit:"%", icon:"chart",    color:COLORS.gold  },
          ].map((s,i) => (
            <div key={i} style={{ background:"rgba(255,255,255,0.05)", border:`1px solid ${s.color}25`, borderRadius:14, padding:"11px 10px", textAlign:"center" }}>
              <div style={{ display:"flex", justifyContent:"center", marginBottom:6 }}>
                <AppIconStyles/><AppIcon name={s.icon} color={s.color} size={22}/>
              </div>
              <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:900, fontSize:16, lineHeight:1 }}>{s.value}<span style={{ color:COLORS.muted, fontSize:10, fontWeight:500, marginLeft:2 }}>{s.unit}</span></div>
              <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:10, marginTop:3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding:"0 16px" }}>

        {/* ── Today's Day CTA ── */}
        {completedCount < 30 && (
          <div style={{
            background:`linear-gradient(135deg, ${nextDay.color}22 0%, ${nextDay.color}10 100%)`,
            border:`1.5px solid ${nextDay.color}55`, borderRadius:22, padding:"16px 18px",
            marginBottom:18, animation:"challengePulse 3s ease-in-out infinite",
            boxShadow:`0 8px 32px ${nextDay.color}25`,
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:58, height:58, borderRadius:18, background:`${nextDay.color}28`, border:`1.5px solid ${nextDay.color}60`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, boxShadow:`0 4px 18px ${nextDay.color}35` }}>
                <AppIconStyles/><AppIcon name={typeIconMap[nextDay.type] || "leaf"} color={nextDay.color} size={30}/>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                  <span style={{ background:nextDay.color, color:"white", fontSize:9, fontFamily:FONT, fontWeight:900, padding:"2px 8px", borderRadius:8, letterSpacing:0.5 }}>DAY {nextDay.day}</span>
                  <span style={{ color:nextDay.color, fontSize:9, fontFamily:FONT, fontWeight:700 }}>▶ UP NEXT</span>
                </div>
                <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:800, fontSize:15, marginBottom:2 }}>{nextDay.label}</div>
                <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:11 }}>{nextDay.sub}</div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0 }}>
                {nextDay.screen && (
                  <div onClick={() => { setScreen(nextDay.screen); markDone(nextDay.day); }}
                    style={{ background:nextDay.color, color:"white", fontFamily:FONT, fontWeight:800, fontSize:12, padding:"9px 16px", borderRadius:14, cursor:"pointer", textAlign:"center", boxShadow:`0 4px 16px ${nextDay.color}55` }}>Start →</div>
                )}
                {!nextDay.screen && (
                  <div onClick={() => markDone(nextDay.day)}
                    style={{ background:`${nextDay.color}30`, border:`1px solid ${nextDay.color}60`, color:nextDay.color, fontFamily:FONT, fontWeight:800, fontSize:12, padding:"9px 16px", borderRadius:14, cursor:"pointer", textAlign:"center" }}>Mark ✓</div>
                )}
                <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:10, textAlign:"center" }}>{nextDay.dur}</div>
              </div>
            </div>
          </div>
        )}

        {/* 30-day complete banner */}
        {completedCount === 30 && (
          <div style={{ background:"linear-gradient(135deg,#1A1200,#261A00)", border:"1.5px solid #C4A05580", borderRadius:22, padding:"24px 18px", marginBottom:18, textAlign:"center", animation:"starPop 0.6s cubic-bezier(0.22,1,0.36,1)", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 70% 50% at 50% 0%, rgba(196,160,85,0.12) 0%, transparent 70%)", pointerEvents:"none" }}/>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:10 }}>
              <AppIconStyles/><AppIcon name="sparkles" color="#C4A055" size={52}/>
            </div>
            <div style={{ color:"#C4A055", fontFamily:FONT, fontWeight:900, fontSize:22, marginBottom:6, letterSpacing:-0.5 }}>Challenge Complete!</div>
            <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:13 }}>You completed all 30 days. You're incredible!</div>
            <div style={{ display:"flex", justifyContent:"center", marginTop:12 }}>
              <AppIconStyles/><AppIcon name="heart" color="#C4A055" size={22}/>
            </div>
          </div>
        )}

        {/* Premium weeks notice */}
        {!isSubscribed && (
          <div onClick={() => onUpgrade && onUpgrade()} style={{ background:"linear-gradient(135deg,rgba(196,160,85,0.14),rgba(74,155,127,0.1))", border:`1px solid ${COLORS.gold}40`, borderRadius:16, padding:"12px 16px", marginBottom:14, cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:22 }}>🔒</span>
            <div style={{ flex:1 }}>
              <div style={{ color:COLORS.gold, fontFamily:FONT, fontWeight:800, fontSize:12, marginBottom:1 }}>Week 1 Free · Weeks 2–5 Premium</div>
              <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:10 }}>Complete Days 1–7 free. Upgrade to unlock all 30 days.</div>
            </div>
            <div style={{ color:COLORS.gold, fontSize:14 }}>→</div>
          </div>
        )}

        {/* ── Week sections ── */}
        {weeks.map(wk => {
          const wkDays = CHALLENGE_DAYS.filter(d => d.week === wk);
          const wkDone = wkDays.filter(d => isDone(d)).length;
          const wkOpen = expandedWeek === wk;
          const wkNames = ["Foundation","Building","Deepening","Power","Completion"];
          const allWkDone = wkDone === wkDays.length;
          const wkPremiumLocked = !isSubscribed && wk > 1;

          return (
            <div key={wk} style={{ marginBottom:12, animation:`challengeSlideIn ${0.15 + wk*0.05}s ease` }}>
              {/* Week header */}
              <div onClick={() => { if (wkPremiumLocked) { onUpgrade && onUpgrade(); return; } setExpandedWeek(wkOpen ? 0 : wk); }}
                style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", background:COLORS.card, border:`1px solid ${wkPremiumLocked ? COLORS.gold+"30" : allWkDone ? COLORS.sage+"60" : COLORS.border}`, borderRadius: wkOpen ? "16px 16px 0 0" : 16, cursor:"pointer", opacity: wkPremiumLocked ? 0.7 : 1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:36, height:36, borderRadius:12, background: wkPremiumLocked ? `${COLORS.gold}14` : allWkDone ? `${COLORS.sage}28` : "rgba(255,255,255,0.06)", border:`1.5px solid ${wkPremiumLocked ? COLORS.gold+"50" : allWkDone ? COLORS.sage+"70" : "rgba(255,255,255,0.1)"}`, display:"flex", alignItems:"center", justifyContent:"center", boxShadow: allWkDone ? `0 2px 10px ${COLORS.sage}30` : "none" }}>
                    {wkPremiumLocked ? (
                      <span style={{ fontSize:16 }}>🔒</span>
                    ) : allWkDone ? (
                      <svg width={18} height={18} viewBox="0 0 40 40" fill="none">
                        <circle cx="20" cy="20" r="15" fill={COLORS.sage} opacity="0.3"/>
                        <path d="M12 20.5 L17.5 26.5 L29 13" stroke={COLORS.sage} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <div style={{ color: wkDone > 0 ? COLORS.sage : COLORS.muted, fontFamily:FONT, fontWeight:900, fontSize:12, letterSpacing:0.5 }}>W{wk}</div>
                    )}
                  </div>
                  <div>
                    <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:700, fontSize:13 }}>Week {wk} <span style={{ color:COLORS.muted, fontWeight:500 }}>—</span> {wkNames[wk-1]}</div>
                    <div style={{ color: wkDone > 0 ? COLORS.sage : COLORS.muted, fontFamily:FONT, fontSize:10, fontWeight: wkDone > 0 ? 600 : 400 }}>{wkDone}/{wkDays.length} days {wkDone === wkDays.length ? "✦ complete" : "done"}</div>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  {/* mini progress bar */}
                  <div style={{ width:56, height:5, borderRadius:3, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                    <div style={{ width:`${(wkDone/wkDays.length)*100}%`, height:"100%", background:COLORS.sage, borderRadius:3, transition:"width 0.5s ease" }}/>
                  </div>
                  <div style={{ color:COLORS.muted, fontSize:13, transform: wkOpen ? "rotate(90deg)" : "rotate(0deg)", transition:"transform 0.2s" }}>›</div>
                </div>
              </div>

              {/* Day cards */}
              {wkOpen && (
                <div style={{ background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderTop:"none", borderRadius:"0 0 16px 16px", overflow:"hidden" }}>
                  {wkDays.map((d, idx) => {
                    const done      = isDone(d);
                    const unlocked  = isUnlocked(d);
                    const current   = isCurrent(d);
                    const col       = typeColors[d.type] || COLORS.sage;

                    const premiumLocked = !canAccess.challenge(d.day, isSubscribed);
                    return (
                      <div key={d.day}
                        onClick={() => {
                          if (premiumLocked) { onUpgrade && onUpgrade(); return; }
                          if (!unlocked) return;
                          if (d.type === "rest" || d.type === "celebrate") { markDone(d.day); return; }
                          if (d.screen) { setScreen(d.screen); markDone(d.day); }
                        }}
                        style={{
                          display:"flex", alignItems:"center", gap:13, padding:"13px 14px",
                          borderBottom: idx < wkDays.length - 1 ? `1px solid ${COLORS.border}` : "none",
                          cursor: (unlocked && !premiumLocked) ? "pointer" : "default",
                          background: current && !premiumLocked ? `${col}12` : "transparent",
                          opacity: (!unlocked || premiumLocked) ? 0.42 : 1,
                          transition:"background 0.18s",
                        }}
                      >
                        {/* ── Day icon bubble ── */}
                        <div style={{
                          width:48, height:48, borderRadius:15, flexShrink:0,
                          background: done
                            ? `linear-gradient(135deg, ${col}35 0%, ${col}18 100%)`
                            : current
                              ? `linear-gradient(135deg, ${col}30 0%, ${col}15 100%)`
                              : !unlocked
                                ? "rgba(255,255,255,0.04)"
                                : `${col}12`,
                          border: `1.5px solid ${done ? col+"90" : current ? col+"70" : !unlocked ? "rgba(255,255,255,0.09)" : col+"40"}`,
                          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                          boxShadow: done
                            ? `0 3px 14px ${col}35`
                            : current
                              ? `0 4px 18px ${col}45`
                              : "none",
                          position:"relative", overflow:"hidden",
                        }}>
                          {/* shimmer line on done/current */}
                          {(done || current) && (
                            <div style={{ position:"absolute", top:0, left:"-60%", width:"40%", height:"100%", background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.12),transparent)", transform:"skewX(-20deg)", animation:"shimmer 2.5s ease-in-out infinite" }}/>
                          )}

                          {done && !premiumLocked ? (
                            /* ✓ Completed — clean SVG check */
                            <CheckIcon size={24} col={col}/>
                          ) : (!unlocked || premiumLocked) ? (
                            /* 🔒 Locked — SVG padlock */
                            <LockIcon size={20} color={premiumLocked ? COLORS.gold+"80" : undefined}/>
                          ) : (
                            /* Active/unlocked — activity icon + day number */
                            <>
                              <AppIconStyles/>
                              <AppIcon name={typeIconMap[d.type] || "leaf"} color={col} size={20}/>
                              <div style={{ color:col, fontFamily:FONT, fontWeight:900, fontSize:9, lineHeight:1, marginTop:2, letterSpacing:0.3 }}>DAY {d.day}</div>
                            </>
                          )}
                        </div>

                        {/* ── Label + sub ── */}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                            <span style={{ color: !unlocked ? COLORS.subtle : COLORS.text, fontFamily:FONT, fontWeight: current ? 800 : 700, fontSize:13, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{d.label}</span>
                            {current && (
                              <span style={{ background:`linear-gradient(90deg,${col},${col}CC)`, color:"white", fontSize:8, fontFamily:FONT, fontWeight:900, padding:"2px 7px", borderRadius:6, letterSpacing:0.5, flexShrink:0, boxShadow:`0 2px 8px ${col}55` }}>TODAY</span>
                            )}
                            {done && (
                              <span style={{ color:col, fontSize:9, fontFamily:FONT, fontWeight:700, flexShrink:0 }}>✦ done</span>
                            )}
                          </div>
                          <div style={{ color: !unlocked ? "rgba(255,255,255,0.2)" : COLORS.muted, fontFamily:FONT, fontSize:10, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{d.sub}</div>
                        </div>

                        {/* ── Right: type badge + duration ── */}
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:5, flexShrink:0 }}>
                          {/* Type badge with icon */}
                          <div style={{ display:"flex", alignItems:"center", gap:4, background:`${col}18`, border:`1px solid ${col}45`, borderRadius:8, padding:"3px 8px" }}>
                            <AppIconStyles/>
                            <AppIcon name={typeIconMap[d.type] || "leaf"} color={col} size={10}/>
                            <span style={{ color:col, fontFamily:FONT, fontSize:9, fontWeight:700, letterSpacing:0.4 }}>{typeLabelMap[d.type]}</span>
                          </div>
                          <div style={{ color: d.type==="rest"||d.type==="celebrate" ? col+"CC" : COLORS.subtle, fontFamily:FONT, fontSize:10, fontWeight: d.type==="rest"||d.type==="celebrate" ? 700 : 400 }}>{d.dur}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Reset link */}
        {completedCount > 0 && (
          <div style={{ textAlign:"center", marginTop:8, marginBottom:16 }}>
            <span onClick={() => { setCompletedDays([]); try { localStorage.removeItem("cf_challenge_done"); } catch {} }}
              style={{ color:COLORS.subtle, fontFamily:FONT, fontSize:11, cursor:"pointer", textDecoration:"underline" }}>Reset Challenge Progress</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SAVASANA CARTOON SVG ─────────────────────────────────────────────────────
function SavasanaIllustration({ width = "100%", height = 220 }) {
  return (
    <svg viewBox="0 0 360 240" xmlns="http://www.w3.org/2000/svg"
      style={{ width, height, display: "block" }}>
      {/* Sky gradient */}
      <defs>
        <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#56CCF2"/>
          <stop offset="100%" stopColor="#A8E6CF"/>
        </linearGradient>
        <linearGradient id="grassGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#56B870"/>
          <stop offset="100%" stopColor="#3A9A52"/>
        </linearGradient>
        <linearGradient id="mtGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#B8D4E8"/>
          <stop offset="100%" stopColor="#7BAAC8"/>
        </linearGradient>
      </defs>
      {/* Sky */}
      <rect width="360" height="240" fill="url(#skyGrad)"/>
      {/* Clouds */}
      <ellipse cx="60" cy="42" rx="30" ry="16" fill="white" opacity="0.95"/>
      <ellipse cx="85" cy="36" rx="22" ry="14" fill="white" opacity="0.95"/>
      <ellipse cx="40" cy="46" rx="18" ry="12" fill="white" opacity="0.95"/>
      <ellipse cx="290" cy="36" rx="26" ry="14" fill="white" opacity="0.95"/>
      <ellipse cx="310" cy="30" rx="18" ry="12" fill="white" opacity="0.95"/>
      <ellipse cx="270" cy="42" rx="16" ry="10" fill="white" opacity="0.95"/>
      {/* Mountain — back center large */}
      <polygon points="180,30 280,160 80,160" fill="url(#mtGrad)"/>
      {/* Snow cap center */}
      <polygon points="180,30 210,75 150,75" fill="white" opacity="0.9"/>
      {/* Mountain left */}
      <polygon points="80,70 155,160 5,160" fill="#8AAFC5"/>
      <polygon points="80,70 100,100 60,100" fill="white" opacity="0.85"/>
      {/* Mountain right */}
      <polygon points="280,60 360,160 200,160" fill="#8AAFC5"/>
      <polygon points="280,60 305,95 255,95" fill="white" opacity="0.85"/>
      {/* Grass meadow */}
      <rect x="0" y="155" width="360" height="85" fill="url(#grassGrad)"/>
      {/* Small grass tufts */}
      <ellipse cx="30" cy="158" rx="18" ry="8" fill="#4DB86A"/>
      <ellipse cx="330" cy="158" rx="18" ry="8" fill="#4DB86A"/>
      <ellipse cx="140" cy="157" rx="10" ry="5" fill="#5AC474" opacity="0.6"/>
      <ellipse cx="220" cy="157" rx="10" ry="5" fill="#5AC474" opacity="0.6"/>
      {/* Pine tree LEFT */}
      <polygon points="35,155 60,110 85,155" fill="#2E7D4F"/>
      <polygon points="35,135 60,90 85,135" fill="#38994F"/>
      <polygon points="40,115 60,78 80,115" fill="#43A85E"/>
      <rect x="55" y="155" width="10" height="18" fill="#6B3A2A"/>
      {/* Pine tree RIGHT */}
      <polygon points="275,155 300,110 325,155" fill="#2E7D4F"/>
      <polygon points="275,135 300,90 325,135" fill="#38994F"/>
      <polygon points="280,115 300,78 320,115" fill="#43A85E"/>
      <rect x="295" y="155" width="10" height="18" fill="#6B3A2A"/>
      {/* Yoga mat */}
      <rect x="80" y="182" width="200" height="48" rx="8" fill="#2DAE6A" opacity="0.85"/>
      <rect x="82" y="184" width="196" height="44" rx="6" fill="none" stroke="#56CC7A" strokeWidth="2" opacity="0.5"/>
      {/* === GIRL lying in Savasana === */}
      {/* Body/torso — blue top */}
      <ellipse cx="180" cy="208" rx="26" ry="10" fill="#4A90D9"/>
      <rect x="154" y="200" width="52" height="16" rx="7" fill="#4A90D9"/>
      {/* Arms spread wide */}
      {/* Left arm */}
      <line x1="154" y1="207" x2="108" y2="215" stroke="#F2C4A0" strokeWidth="9" strokeLinecap="round"/>
      {/* Right arm */}
      <line x1="206" y1="207" x2="252" y2="215" stroke="#F2C4A0" strokeWidth="9" strokeLinecap="round"/>
      {/* Palms */}
      <circle cx="108" cy="215" r="7" fill="#F2C4A0"/>
      <circle cx="252" cy="215" r="7" fill="#F2C4A0"/>
      {/* Legs — pink yoga pants */}
      <rect x="161" y="210" width="16" height="28" rx="7" fill="#E8609A"/>
      <rect x="183" y="210" width="16" height="28" rx="7" fill="#E8609A"/>
      {/* Feet */}
      <ellipse cx="169" cy="238" rx="9" ry="6" fill="#F2C4A0"/>
      <ellipse cx="191" cy="238" rx="9" ry="6" fill="#F2C4A0"/>
      {/* White t-shirt strip */}
      <rect x="158" y="200" width="44" height="6" rx="3" fill="#FAFAFA" opacity="0.5"/>
      {/* Head (viewed from above — top of head toward viewer) */}
      <circle cx="180" cy="193" r="17" fill="#F2C4A0"/>
      {/* Face — closed eyes, peaceful smile */}
      <path d="M173,193 Q176,190 179,193" stroke="#5C3317" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <path d="M181,193 Q184,190 187,193" stroke="#5C3317" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <path d="M175,198 Q180,202 185,198" stroke="#D4805A" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      {/* Rosy cheeks */}
      <circle cx="172" cy="197" r="4" fill="#F4A7A7" opacity="0.6"/>
      <circle cx="188" cy="197" r="4" fill="#F4A7A7" opacity="0.6"/>
      {/* Hair — brown pigtails spread out */}
      <ellipse cx="180" cy="179" rx="18" ry="10" fill="#7B3F0E"/>
      {/* Left pigtail */}
      <path d="M163,182 Q140,172 125,188 Q118,200 130,205" stroke="#7B3F0E" strokeWidth="8" fill="none" strokeLinecap="round"/>
      {/* Right pigtail */}
      <path d="M197,182 Q220,172 235,188 Q242,200 230,205" stroke="#7B3F0E" strokeWidth="8" fill="none" strokeLinecap="round"/>
      {/* Hair ties */}
      <circle cx="130" cy="205" r="5" fill="#E8609A"/>
      <circle cx="230" cy="205" r="5" fill="#E8609A"/>
      {/* Little stars/sparkles around for zen feel */}
      <text x="88" y="178" fontSize="14" textAnchor="middle" opacity="0.7">✨</text>
      <text x="270" y="178" fontSize="14" textAnchor="middle" opacity="0.7">✨</text>
      <text x="180" y="172" fontSize="10" textAnchor="middle" opacity="0.5">😌</text>
    </svg>
  );
}

// ─── CLASS SESSION SCREEN ─────────────────────────────────────────────────────
function PoseCard({ pose, color }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div onClick={() => setFlipped(f => !f)}
      style={{ flexShrink: 0, width: 130, borderRadius: 14, overflow: "hidden", cursor: "pointer", border: `1.5px solid ${flipped ? color : COLORS.border}`, transition: "border-color 0.2s" }}>
      {flipped ? (
        <div style={{ background: color, padding: "12px 10px", minHeight: 130, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ color: "white", fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{pose.name}</div>
          <div style={{ color: "rgba(255,255,255,0.88)", fontSize: 10.5, lineHeight: 1.5 }}>{pose.tip}</div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 9, marginTop: 8 }}>Tap to flip back</div>
        </div>
      ) : (
        <div>
          {pose.cartoon ? (
            <div style={{ width: "100%", height: 110, overflow: "hidden" }}>
              <SavasanaIllustration width="100%" height={110} />
            </div>
          ) : (
            <>
              <img src={pose.gif || pose.img} alt={pose.name}
                style={{ width: "100%", height: 110, objectFit: pose.gif ? "contain" : "cover", background: pose.gif ? "#0a1a0e" : "transparent", display: "block" }}
                onError={e => {
                  if (pose.gif && e.target.src !== pose.img) { e.target.src = pose.img; e.target.style.objectFit = "cover"; e.target.style.background = "transparent"; }
                  else { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }
                }}
              />
              <div style={{ display: "none", width: "100%", height: 110, background: `${color}18`, alignItems: "center", justifyContent: "center", fontSize: 28 }}>🧘</div>
            </>
          )}
          <div style={{ padding: "7px 8px", background: COLORS.card }}>
            <div style={{ color: COLORS.text, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pose.name}</div>
            <div style={{ color: color, fontSize: 9.5, marginTop: 2 }}>Tap for tip →</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClassSessionScreen({ cls, onBack, onLogSession }) {
  const [phase, setPhase] = useState("detail"); // detail | active | done
  const [elapsed, setElapsed] = useState(0);    // global session elapsed (seconds)
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // ── Per-pose duration data ──────────────────────────────────────────────────
  const poses = cls.poseGuide || [];
  const defaultDur = Math.floor((cls.durationMin * 60) / Math.max(poses.length, 1));
  const poseDurations = poses.map(p => p.dur || defaultDur);
  // cumulative seconds at END of each pose: [dur0, dur0+dur1, ...]
  const cumDurs = poseDurations.reduce((acc, d) => {
    acc.push((acc.length ? acc[acc.length - 1] : 0) + d);
    return acc;
  }, []);
  const totalSetSec = cumDurs[cumDurs.length - 1] || 1;

  // Derive current step from elapsed
  const rawStepIdx = cumDurs.findIndex(c => elapsed < c);
  const stepIdx    = rawStepIdx === -1 ? poses.length - 1 : rawStepIdx;
  const prevCum    = stepIdx > 0 ? cumDurs[stepIdx - 1] : 0;
  const poseElapsed   = elapsed - prevCum;
  const currentPoseDur = poseDurations[stepIdx] || defaultDur;
  const poseTimeLeft   = Math.max(0, currentPoseDur - poseElapsed);
  const totalTimeLeft  = Math.max(0, totalSetSec - elapsed);
  const poseProgress   = Math.min(1, poseElapsed / currentPoseDur);
  const currentPose    = poses[stepIdx];
  const nextPose       = poses[stepIdx + 1] || null;
  const hasNext        = stepIdx + 1 < poses.length;

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2,"0")}:${String(s % 60).padStart(2,"0")}`;
  const fmtMin = s => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return sec ? `${m}m ${sec}s` : `${m} min`;
  };
  const col = cls.color;

  // ── Controls ──────────────────────────────────────────────────────────────
  const startSession = () => { setPhase("active"); setElapsed(0); setPaused(false); };

  const stopSession = () => {
    clearInterval(timerRef.current);
    setPhase("detail"); setElapsed(0);
  };

  const skipToPose = (idx) => {
    const newElapsed = idx > 0 ? cumDurs[idx - 1] : 0;
    setElapsed(newElapsed);
  };

  const nextPoseClick = () => {
    if (!hasNext) return;
    skipToPose(stepIdx + 1);
  };

  // ── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "active" || paused) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      setElapsed(e => {
        const next = e + 1;
        if (next >= totalSetSec) {
          clearInterval(timerRef.current);
          setPhase("done");
          onLogSession && onLogSession(makeSession({
            type: "yoga", title: cls.title, category: cls.category || "Yoga",
            emoji: cls.emoji, calories: cls.calories, duration: cls.duration,
          }));
          return next;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, paused]);

  // ── Done screen ─────────────────────────────────────────────────────────────
  if (phase === "done") return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(160deg, ${cls.bg}, #030608)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28, fontFamily: FONT }}>
      <div style={{ fontSize: 72, marginBottom: 16 }}><Ico a="tada">🎉</Ico></div>
      <h2 style={{ color: "white", fontSize: 26, margin: "0 0 6px", fontWeight: 400, textAlign: "center" }}>Class Complete!</h2>
      <p style={{ color: col, margin: "0 0 30px", fontSize: 14 }}>{cls.title}</p>
      <div style={{ display: "flex", gap: 14, marginBottom: 32 }}>
        {[["target", fmt(totalSetSec), "Duration"], ["fire", `${cls.calories} kcal`, "Burned"], ["bolt", `${poses.length}`, "Poses"]].map(([iconName, val, lbl]) => (
          <div key={lbl} style={{ background: "rgba(255,255,255,0.08)", borderRadius: 16, padding: "14px 16px", textAlign: "center", flex: 1 }}>
            <div style={{ marginBottom: 4 }}><AppIconStyles/><AppIcon name={iconName} color="white" size={22}/></div>
            <div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{val}</div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 10 }}>{lbl}</div>
          </div>
        ))}
      </div>
      <button onClick={() => setPhase("detail")} style={{ width: "100%", padding: 15, background: `linear-gradient(135deg, ${col}, ${col}AA)`, color: "white", border: "none", borderRadius: 16, fontSize: 15, cursor: "pointer", fontWeight: 700 }}>Back to Class ›</button>
    </div>
  );

  // ── Active session ──────────────────────────────────────────────────────────
  if (phase === "active") return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 50% 20%, ${cls.bg} 0%, #030608 100%)`, display: "flex", flexDirection: "column", fontFamily: FONT }}>

      {/* ── Top bar ── */}
      <div style={{ padding: "48px 18px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <button onClick={stopSession} style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.5)", padding: "7px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12 }}>✕ End</button>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 600 }}>{cls.emoji} {cls.title}</div>
          <div style={{ color: col, fontSize: 11, marginTop: 2, fontWeight: 700 }}>⏱ {fmt(totalTimeLeft)} remaining in set</div>
        </div>
        <button onClick={() => setPaused(p => !p)} style={{ background: paused ? col : "rgba(255,255,255,0.07)", border: `1px solid ${paused ? col : "rgba(255,255,255,0.12)"}`, color: paused ? "white" : "rgba(255,255,255,0.5)", padding: "7px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12 }}>
          {paused ? "▶ Resume" : "⏸ Pause"}
        </button>
      </div>

      {/* ── Video / GIF / Image — large, prominent ── */}
      <div style={{ margin: "0 18px 12px", borderRadius: 22, overflow: "hidden", boxShadow: `0 6px 28px ${col}35`, position: "relative", flexShrink: 0 }}>
        {currentPose ? (
          currentPose.video ? (
            /* ── MP4 video: autoplay, loop, muted ── */
            <video
              key={`vid-${stepIdx}`}
              src={currentPose.video}
              autoPlay
              loop
              muted
              playsInline
              style={{ width: "100%", height: 260, objectFit: "contain", background: `${cls.bg}CC`, display: "block" }}
            />
          ) : (
            /* ── GIF / static image ── */
            <img
              key={stepIdx}
              src={currentPose.gif || currentPose.img}
              alt={currentPose.name}
              style={{ width: "100%", height: 260, objectFit: currentPose.gif ? "contain" : "cover", background: currentPose.gif ? `${cls.bg}CC` : "transparent", display: "block" }}
              onError={e => {
                if (currentPose.gif && e.target.src !== currentPose.img) { e.target.src = currentPose.img; e.target.style.objectFit = "cover"; e.target.style.background = "transparent"; }
                else { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }
              }}
            />
          )
        ) : null}
        <div style={{ display: currentPose ? "none" : "flex", width: "100%", height: 260, background: `${col}20`, alignItems: "center", justifyContent: "center", fontSize: 72 }}>🧘</div>

        {/* Gradient overlay */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 110, background: "linear-gradient(transparent, rgba(3,6,8,0.92))", pointerEvents: "none" }} />

        {/* Pose label overlay */}
        <div style={{ position: "absolute", bottom: 12, left: 16, right: 16 }}>
          <div style={{ color: col, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 2 }}>
            POSE {stepIdx + 1} of {poses.length}
          </div>
          <div style={{ color: "white", fontSize: 19, fontWeight: 800, letterSpacing: -0.3 }}>{cls.steps[stepIdx] || currentPose?.name}</div>
        </div>

        {/* Paused badge */}
        {paused && (
          <div style={{ position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.75)", border: `1px solid ${col}`, borderRadius: 20, padding: "5px 16px", color: col, fontSize: 12, fontWeight: 700 }}>⏸ PAUSED</div>
        )}

        {/* Media badge: VIDEO GUIDE or GIF GUIDE */}
        {currentPose?.video ? (
          <div style={{ position: "absolute", top: 10, right: 12, background: "rgba(0,0,0,0.60)", border: `1px solid ${col}60`, borderRadius: 10, padding: "3px 9px", fontSize: 9, color: col, fontWeight: 700, letterSpacing: 0.5 }}>▶ VIDEO GUIDE</div>
        ) : currentPose?.gif ? (
          <div style={{ position: "absolute", top: 10, right: 12, background: "rgba(0,0,0,0.55)", border: `1px solid ${col}50`, borderRadius: 10, padding: "3px 8px", fontSize: 9, color: col, fontWeight: 700, letterSpacing: 0.5 }}>GIF GUIDE</div>
        ) : null}
      </div>

      {/* ── Pose timer + tip ── */}
      <div style={{ margin: "0 18px 10px", background: "rgba(255,255,255,0.05)", border: `1px solid ${col}35`, borderRadius: 18, padding: "14px 16px", flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 10, marginBottom: 3, letterSpacing: 0.5, textTransform: "uppercase" }}>This pose</div>
            <div style={{ color: col, fontSize: 30, fontWeight: 900, letterSpacing: -1, lineHeight: 1 }}>
              {fmt(poseTimeLeft)}
              <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 12, fontWeight: 400, marginLeft: 4 }}>left</span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 10, marginBottom: 3, letterSpacing: 0.5, textTransform: "uppercase" }}>Set remaining</div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 16, fontWeight: 700 }}>{fmt(totalTimeLeft)}</div>
          </div>
        </div>
        {/* Pose progress bar */}
        <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 6, height: 5, overflow: "hidden", marginBottom: 8 }}>
          <div style={{ width: `${poseProgress * 100}%`, height: "100%", background: `linear-gradient(90deg, ${col}AA, ${col})`, borderRadius: 6, transition: "width 1s linear" }} />
        </div>
        {currentPose && (
          <div style={{ color: "rgba(255,255,255,0.42)", fontSize: 11, lineHeight: 1.55 }}>💡 {currentPose.tip}</div>
        )}
      </div>

      {/* ── Next up + Next Pose button ── */}
      {hasNext ? (
        <div style={{ margin: "0 18px 10px", display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: "10px 12px", flexShrink: 0 }}>
          <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, flexShrink: 0, minWidth: 40 }}>Next</div>
          {nextPose && (
            <img src={nextPose.gif || nextPose.img} alt={nextPose.name}
              style={{ width: 42, height: 42, borderRadius: 10, objectFit: "cover", flexShrink: 0 }}
              onError={e => { if (nextPose.gif && e.target.src !== nextPose.img) { e.target.src = nextPose.img; } else { e.target.style.display="none"; } }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cls.steps[stepIdx + 1] || nextPose?.name}</div>
            <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, marginTop: 1 }}>{nextPose ? fmtMin(nextPose.dur || defaultDur) : ""} · Pose {stepIdx + 2}</div>
          </div>
          <button onClick={nextPoseClick} style={{ background: `linear-gradient(135deg, ${col}, ${col}BB)`, border: "none", borderRadius: 12, padding: "8px 14px", color: "white", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT, flexShrink: 0, boxShadow: `0 3px 10px ${col}50`, letterSpacing: 0.3 }}>
            Next →
          </button>
        </div>
      ) : (
        <div style={{ margin: "0 18px 10px", background: `${col}18`, border: `1px solid ${col}40`, borderRadius: 16, padding: "10px 14px", textAlign: "center", flexShrink: 0 }}>
          <div style={{ color: col, fontSize: 12, fontWeight: 700 }}>🏁 Final pose — great work!</div>
        </div>
      )}

      {/* ── Segmented progress strip ── */}
      <div style={{ padding: "0 18px 8px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 3 }}>
          {poses.map((p, i) => {
            const segWidth = (poseDurations[i] / totalSetSec) * 100;
            const isDone   = i < stepIdx;
            const isCur    = i === stepIdx;
            return (
              <div key={i} onClick={() => skipToPose(i)} style={{ flex: `${segWidth} 0 0`, cursor: "pointer" }}>
                <div style={{ height: 4, borderRadius: 3, background: isDone ? col : isCur ? col : "rgba(255,255,255,0.12)", opacity: isDone ? 0.5 : 1, overflow: "hidden", position: "relative" }}>
                  {isCur && (
                    <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: `${poseProgress * 100}%`, background: col, borderRadius: 3, transition: "width 1s linear" }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ color: "rgba(255,255,255,0.2)", fontSize: 9, marginTop: 4, letterSpacing: 0.5 }}>TAP SEGMENT TO JUMP TO POSE</div>
      </div>

      {/* ── Thumbnail scrubber ── */}
      <div style={{ padding: "0 18px 24px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
          {poses.map((p, i) => {
            const isActive = i === stepIdx;
            const isDone   = i < stepIdx;
            return (
              <div key={i} onClick={() => skipToPose(i)} style={{ flexShrink: 0, width: 58, cursor: "pointer", opacity: isDone ? 0.45 : 1 }}>
                <div style={{ width: 58, height: 58, borderRadius: 12, overflow: "hidden", border: isActive ? `2.5px solid ${col}` : "1.5px solid rgba(255,255,255,0.1)", boxShadow: isActive ? `0 0 12px ${col}55` : "none", position: "relative", background: `${col}18` }}>
                  <img src={p.gif || p.img} alt={p.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={e => { if (p.gif && e.target.src !== p.img) { e.target.src = p.img; } else { e.target.style.display = "none"; } }} />
                  {isDone && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: col }}>✓</div>}
                  {isActive && <div style={{ position: "absolute", bottom: 2, left: "50%", transform: "translateX(-50%)", width: 5, height: 5, borderRadius: "50%", background: col }} />}
                </div>
                <div style={{ color: isActive ? col : "rgba(255,255,255,0.3)", fontSize: 8.5, marginTop: 3, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                <div style={{ color: "rgba(255,255,255,0.2)", fontSize: 7.5, textAlign: "center" }}>{fmtMin(p.dur || defaultDur)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── Detail screen ──────────────────────────────────────────────────────────
  const totalMinDisplay = Math.round(totalSetSec / 60);
  return (
    <div style={{ minHeight: "100vh", background: "transparent", fontFamily: FONT, paddingBottom: 100 }}>
      {/* Hero */}
      <div style={{ background: `linear-gradient(160deg, ${cls.bg} 0%, ${COLORS.bg} 100%)`, padding: "54px 22px 28px", borderBottom: `1px solid ${cls.color}20`, position: "relative" }}>
        <button onClick={onBack} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: "7px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12, marginBottom: 20, fontWeight: 600 }}>← Explore</button>
        <div style={{ marginBottom: 10 }}><YogaClassIcon classId={cls.id} color={cls.color} size={52}/></div>
        <div style={{ display: "inline-block", background: `${cls.color}20`, border: `1px solid ${cls.color}50`, borderRadius: 18, padding: "4px 12px", fontSize: 10, color: cls.color, marginBottom: 10, fontWeight: 700, letterSpacing: 0.5 }}>{cls.level.toUpperCase()}</div>
        <h1 style={{ color: COLORS.text, fontSize: 24, margin: "0 0 4px", fontWeight: 800 }}>{cls.title}</h1>
        <p style={{ color: COLORS.muted, margin: 0, fontSize: 13 }}>with {cls.instructor}</p>
        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          {[["⏱", `${totalMinDisplay} min total`, COLORS.blue], ["🔥", `${cls.calories} kcal`, COLORS.coral], ["★", `${cls.rating}`, COLORS.gold], ["🎯", `${poses.length} poses`, cls.color]].map(([icon, val, c]) => (
            <div key={val} style={{ background: `${c}15`, border: `1px solid ${c}30`, borderRadius: 10, padding: "5px 10px" }}>
              <div style={{ color: c, fontSize: 12, fontWeight: 700 }}>{icon} {val}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: "18px 20px" }}>
        {/* Set timing overview */}
        <div style={{ background: `linear-gradient(135deg, ${col}18, ${col}08)`, border: `1px solid ${col}35`, borderRadius: 18, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ color: COLORS.text, fontWeight: 800, fontSize: 13, marginBottom: 10 }}>⏱ This set will take ~{totalMinDisplay} minutes</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {poses.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 22, height: 22, borderRadius: 7, background: `${col}20`, border: `1px solid ${col}35`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ color: col, fontSize: 9, fontWeight: 800 }}>{i + 1}</span>
                </div>
                <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.07)", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${((p.dur || defaultDur) / totalSetSec) * 100}%`, background: `linear-gradient(90deg, ${col}99, ${col})`, borderRadius: 3 }} />
                </div>
                <div style={{ color: COLORS.text, fontSize: 11, fontWeight: 600, minWidth: 90 }}>{cls.steps[i] || p.name}</div>
                <div style={{ color: col, fontSize: 10, fontWeight: 700, minWidth: 34, textAlign: "right" }}>{fmtMin(p.dur || defaultDur)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* About */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: 16, marginBottom: 14 }}>
          <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, marginBottom: 8 }}>About this class</div>
          <p style={{ color: COLORS.muted, margin: 0, fontSize: 13, lineHeight: 1.7 }}>{cls.about}</p>
        </div>

        {/* Pose Guide */}
        {poses.length > 0 && (
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: 16, marginBottom: 14 }}>
            <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>📸 Pose Guide</div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 12 }}>Tap a pose to see the form tip</div>
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6, scrollbarWidth: "none" }}>
              {poses.map((pose, i) => (
                <PoseCard key={i} pose={pose} color={cls.color} />
              ))}
            </div>
          </div>
        )}

        {/* Start button */}
        <button onClick={startSession} style={{ width: "100%", padding: "17px", background: `linear-gradient(135deg, ${col}, ${col}BB)`, color: "white", border: "none", borderRadius: 18, fontSize: 15, fontFamily: FONT, cursor: "pointer", fontWeight: 800, boxShadow: `0 8px 28px ${col}50`, letterSpacing: 0.3 }}>
          Begin Class · {totalMinDisplay} min ▶
        </button>
      </div>
    </div>
  );
}


// ─── EXPLORE SCREEN ───────────────────────────────────────────────────────────
function ExploreScreen({ onLogSession, isSubscribed, onUpgrade }) {
  const [filter, setFilter] = useState("all");
  const [selectedClass, setSelectedClass] = useState(null);
  const filtered = filter === "all" ? yogaClasses : yogaClasses.filter(c => c.category === filter);

  if (selectedClass) {
    if (!canAccess.yoga(isSubscribed)) { onUpgrade && onUpgrade(); return null; }
    return <ClassSessionScreen cls={selectedClass} onBack={() => setSelectedClass(null)} onLogSession={onLogSession} />;
  }

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100, fontFamily: FONT }}>
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 18px 16px" }}>
        <h1 style={{ fontSize: 26, color: COLORS.text, margin: "0 0 3px", fontWeight: 800, letterSpacing: -0.5 }}>Explore Classes</h1>
        <p style={{ color: COLORS.muted, margin: "0 0 16px", fontSize: 13 }}>{yogaClasses.length} classes · Yoga, Pilates & more</p>
        <div style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 4 }}>
          {[{ id: "all", label: "All", emoji: "✦" }, ...exploreCategories].map(c => (
            <div key={c.id} onClick={() => setFilter(c.id)}
              style={{
                padding: "7px 14px", borderRadius: 20, whiteSpace: "nowrap", fontSize: 12,
                background: filter === c.id ? COLORS.sage : COLORS.surface,
                color: filter === c.id ? COLORS.bg : COLORS.muted,
                border: `1px solid ${filter === c.id ? "transparent" : COLORS.border}`,
                cursor: "pointer", fontWeight: filter === c.id ? 700 : 400, transition: "all 0.2s",
              }}>
              {c.emoji} {c.label}
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: "0 18px" }}>
        {/* Premium gate notice */}
        {!isSubscribed && (
          <div onClick={() => onUpgrade && onUpgrade()} style={{ background:"linear-gradient(135deg,rgba(196,160,85,0.14),rgba(74,155,127,0.1))", border:`1px solid ${COLORS.gold}40`, borderRadius:16, padding:"14px 16px", marginBottom:14, cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ fontSize:28 }}>🔒</div>
            <div style={{ flex:1 }}>
              <div style={{ color:COLORS.gold, fontFamily:FONT, fontWeight:800, fontSize:13, marginBottom:2 }}>Premium Classes</div>
              <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:11 }}>All yoga, pilates & tai chi classes require Premium. Tap to upgrade.</div>
            </div>
            <div style={{ color:COLORS.gold, fontSize:16 }}>→</div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(cls => {
            const locked = !canAccess.yoga(isSubscribed);
            return (
            <div key={cls.id} onClick={() => { if (locked) { onUpgrade && onUpgrade(); return; } setSelectedClass(cls); }}
              style={{
                background: COLORS.card, borderRadius: 18, padding: 14,
                display: "flex", gap: 14, alignItems: "center",
                border: `1px solid ${locked ? COLORS.border : cls.color+"20"}`, cursor: "pointer",
                opacity: locked ? 0.72 : 1, position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 62, height: 62, borderRadius: 16, background: `${cls.color}15`, border: `1px solid ${cls.color}25`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position:"relative" }}>
                <YogaClassIcon classId={cls.id} color={locked ? COLORS.subtle : cls.color} size={36}/>
                {locked && (
                  <div style={{ position:"absolute", inset:0, background:"rgba(10,18,15,0.6)", borderRadius:16, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <span style={{ fontSize:18 }}>🔒</span>
                  </div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: locked ? COLORS.muted : COLORS.text, fontWeight: 700, marginBottom: 2 }}>{cls.title}</div>
                <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 5 }}>{cls.instructor}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  {locked ? (
                    <span style={{ fontSize: 10, color: COLORS.gold, fontWeight: 700 }}>✦ PREMIUM</span>
                  ) : (
                    <>
                      <span style={{ fontSize: 10, color: COLORS.muted }}>⏱ {cls.duration}</span>
                      <span style={{ fontSize: 10, color: COLORS.muted }}>· {cls.level}</span>
                      <span style={{ fontSize: 10, color: COLORS.gold, fontWeight: 700 }}>★ {cls.rating}</span>
                    </>
                  )}
                </div>
              </div>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: locked ? "rgba(255,255,255,0.06)" : `${cls.color}18`, border: `1.5px solid ${locked ? COLORS.border : cls.color+"40"}`, display: "flex", alignItems: "center", justifyContent: "center", color: locked ? COLORS.subtle : cls.color, fontSize: 14, flexShrink: 0 }}>{locked ? "🔒" : "▶"}</div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── SESSIONS SCREEN ──────────────────────────────────────────────────────────
function SessionsScreen({ sessions, onBack }) {
  const [filter, setFilter] = useState("all");
  const filters = [
    { id: "all",       label: "All",       icon: null },
    { id: "workout",   label: "Workout",   icon: "dumbbell" },
    { id: "breathing", label: "Breathing", icon: "lungs" },
    { id: "yoga",      label: "Yoga",      icon: "yoga" },
  ];
  const filtered = filter === "all" ? sessions : sessions.filter(s => s.type === filter);

  // Stats
  const totalSessions = sessions.length;
  const totalCalories = sessions.reduce((a, s) => a + (parseInt(s.calories) || 0), 0);
  const workoutCount = sessions.filter(s => s.type === "workout").length;

  // Streak calculation (timezone-safe — uses local date, not UTC slice)
  const _lds = (iso) => { const d = new Date(iso); return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); };
  const streak = (() => {
    if (!sessions.length) return 0;
    const days = [...new Set(sessions.map(s => s.date ? _lds(s.date) : null).filter(Boolean))].sort((a, b) => b.localeCompare(a));
    const now = new Date();
    let count = 0; let cur = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (const day of days) {
      const [y,m,dd] = day.split('-').map(Number);
      const d = new Date(y, m-1, dd);
      const diff = Math.round((cur - d) / 86400000);
      if (diff === 0 || diff === 1) { count++; cur = d; }
      else break;
    }
    return count;
  })();

  const statColors = [COLORS.coral, COLORS.gold, COLORS.sage, COLORS.blue];
  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100, fontFamily: FONT }}>
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 22px 20px" }}>
        <button onClick={onBack} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: "6px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12, marginBottom: 16, fontWeight: 600 }}>← Back</button>
        <h1 style={{ color: COLORS.text, fontSize: 24, margin: "0 0 3px", fontWeight: 800, letterSpacing: -0.5 }}>Session History</h1>
        <p style={{ color: COLORS.muted, margin: 0, fontSize: 13 }}>{totalSessions} activities logged</p>
      </div>

      <div style={{ padding: "0 18px" }}>
        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
          {[
            { icon: "dumbbell", val: workoutCount, lbl: "Workouts" },
            { icon: "fire",     val: `${streak}d`, lbl: "Streak" },
            { icon: "bolt",     val: `${totalCalories}`, lbl: "Calories" },
            { icon: "calendar", val: totalSessions, lbl: "Total" },
          ].map((s, idx) => (
            <div key={s.lbl} style={{ background: COLORS.card, border: `1px solid ${statColors[idx]}25`, borderRadius: 13, padding: "11px 8px", textAlign: "center" }}>
              <div style={{ display:"flex", justifyContent:"center", marginBottom: 3 }}><AppIconStyles/><AppIcon name={s.icon} color={statColors[idx]} size={18}/></div>
              <div style={{ fontSize: 16, fontWeight: 800, color: statColors[idx] }}>{s.val}</div>
              <div style={{ fontSize: 9, color: COLORS.muted }}>{s.lbl}</div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
          {filters.map(f => (
            <div key={f.id} onClick={() => setFilter(f.id)}
              style={{ padding: "6px 13px", borderRadius: 20, background: filter === f.id ? COLORS.sage : COLORS.surface, color: filter === f.id ? COLORS.bg : COLORS.muted, border: `1px solid ${filter === f.id ? "transparent" : COLORS.border}`, cursor: "pointer", whiteSpace: "nowrap", fontSize: 12, fontWeight: filter === f.id ? 700 : 400, transition: "all 0.2s", display:"flex", alignItems:"center", gap: 5 }}>
              {f.icon && <AppIcon name={f.icon} color={filter === f.id ? COLORS.bg : COLORS.muted} size={13}/>}{f.label}
            </div>
          ))}
        </div>

        {/* Session list */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ color: COLORS.text, fontWeight: 700, marginBottom: 6, fontSize: 15 }}>No sessions yet</div>
            <div style={{ color: COLORS.muted, fontSize: 12, lineHeight: 1.6 }}>Complete a workout or breathing session to see your history here.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map((s, i) => {
              const typeColor = s.type === "workout" ? COLORS.coral : s.type === "breathing" ? COLORS.blue : COLORS.sage;
              return (
                <div key={s.id || i} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 15, padding: "13px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: `${typeColor}18`, border: `1px solid ${typeColor}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                    {s.emoji || (s.type === "workout" ? "💪" : s.type === "breathing" ? "🫁" : "🧘")}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title}</div>
                    <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 1 }}>{s.category} · {formatSessionDate(s.date)}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {s.calories > 0 && <div style={{ fontSize: 11, color: typeColor, fontWeight: 700 }}>🔥 {s.calories} kcal</div>}
                    <div style={{ fontSize: 10, color: COLORS.muted }}>{s.duration}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PROGRESS SCREEN ──────────────────────────────────────────────────────────
function ProgressScreen({ sessions, onViewSessions }) {
  // ── Water intake state (persisted per day) ──
  const todayKey = new Date().toISOString().slice(0, 10);
  const [waterGlasses, setWaterGlasses] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("cf_water") || "{}");
      return stored.date === todayKey ? (stored.glasses || 0) : 0;
    } catch { return 0; }
  });
  const WATER_GOAL = 8;
  const addWater = (delta) => {
    const next = Math.max(0, Math.min(WATER_GOAL + 2, waterGlasses + delta));
    setWaterGlasses(next);
    try { localStorage.setItem("cf_water", JSON.stringify({ date: todayKey, glasses: next })); } catch {}
  };

  // ── Nutrition tip of the day (cycles daily) ──
  const NUTRITION_TIPS = [
    { icon: "🥗", title: "Eat the Rainbow", tip: "Include 5 different colored fruits or veggies today. Each color provides unique antioxidants and micronutrients your body needs.", tag: "Micronutrients", color: "#4A9B7F" },
    { icon: "🥩", title: "Protein at Every Meal", tip: "Aim for 20–30g of protein per meal. It preserves muscle, keeps you full longer, and stabilizes blood sugar.", tag: "Protein", color: "#D45B75" },
    { icon: "🌾", title: "Choose Complex Carbs", tip: "Swap white rice for quinoa, oats, or sweet potato. Complex carbs release energy slowly — no mid-day crashes.", tag: "Energy", color: "#C4A055" },
    { icon: "🥑", title: "Healthy Fats Matter", tip: "Add a handful of nuts, avocado, or olive oil today. Healthy fats support brain function and reduce inflammation.", tag: "Fats", color: "#7B5EA7" },
    { icon: "⏰", title: "Eat Within a Window", tip: "Try eating your meals within a 10–12 hour window. Time-restricted eating improves digestion and sleep quality.", tag: "Timing", color: "#5B9BD5" },
    { icon: "🚶", title: "Walk After Meals", tip: "A 10-minute walk after eating lowers blood sugar by up to 30%. It also aids digestion and boosts mood instantly.", tag: "Post-Meal", color: "#E07840" },
    { icon: "🥜", title: "Snack Smart", tip: "When hunger strikes between meals, reach for protein + fiber: Greek yogurt with berries, or apple with peanut butter.", tag: "Snacking", color: "#4AABB5" },
  ];
  const tipIdx = new Date().getDate() % NUTRITION_TIPS.length;
  const dailyTip = NUTRITION_TIPS[tipIdx];

  // ── Macros guide ──
  const MACROS = [
    { label: "Protein", pct: 30, color: "#D45B75", ex: "Chicken, eggs, dal, tofu" },
    { label: "Carbs",   pct: 45, color: "#C4A055", ex: "Rice, oats, fruits, veggies" },
    { label: "Fats",    pct: 25, color: "#7B5EA7", ex: "Nuts, ghee, avocado, fish" },
  ];

  // ── Session stats ──
  const _lds = (iso) => { const d = new Date(iso); return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); };
  const totalSessions = sessions.length;
  const totalCals = sessions.reduce((a, s) => a + (parseInt(s.calories) || 0), 0);
  const weekBars = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    const ds = d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
    return sessions.filter(s => s.date ? _lds(s.date) === ds : false).length;
  });
  const maxBar = Math.max(...weekBars, 1);
  const streak = (() => {
    if (!sessions.length) return 0;
    const days = [...new Set(sessions.map(s => s.date ? _lds(s.date) : null).filter(Boolean))].sort((a, b) => b.localeCompare(a));
    const now = new Date();
    let count = 0; let cur = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (const day of days) {
      const [y,m,dd] = day.split('-').map(Number);
      const d = new Date(y, m-1, dd);
      const diff = Math.round((cur - d) / 86400000);
      if (diff === 0 || diff === 1) { count++; cur = d; } else break;
    }
    return count;
  })();
  const recent = sessions.slice(0, 4);
  const statColors = [COLORS.sage, COLORS.coral, COLORS.gold, COLORS.lavender];
  const stats = [
    { label: "Sessions", value: totalSessions || 0, icon: "yoga",   sub: "total logged" },
    { label: "Streak",   value: `${streak}d`,       icon: "fire",   sub: streak > 0 ? "keep it up 🔥" : "start today!" },
    { label: "Calories", value: totalCals > 999 ? `${(totalCals/1000).toFixed(1)}k` : totalCals, icon: "bolt", sub: "kcal burned" },
    { label: "Types",    value: [...new Set(sessions.map(s => s.type))].length || 0, icon: "target", sub: "activity types" },
  ];

  const today = new Date();
  const greetHour = today.getHours();
  const greeting = greetHour < 12 ? "Good morning" : greetHour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 110, fontFamily: FONT }}>

      {/* ── Header ── */}
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 22px 20px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -30, right: -30, width: 180, height: 180, borderRadius: "50%", background: `radial-gradient(circle, ${COLORS.sage}18 0%, transparent 65%)`, pointerEvents: "none" }} />
        <div style={{ fontSize: 11, color: COLORS.sage, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 4 }}>CalmFit</div>
        <h1 style={{ color: COLORS.text, fontSize: 28, margin: "0 0 4px", fontWeight: 900, letterSpacing: -0.8 }}>{greeting} 👋</h1>
        <p style={{ color: COLORS.muted, margin: 0, fontSize: 13 }}>
          {totalSessions > 0 ? `${totalSessions} sessions logged · ${streak > 0 ? `${streak}-day streak 🔥` : "Start your streak!"}` : "Start your wellness journey today!"}
        </p>
      </div>

      <div style={{ padding: "0 18px" }}>

        {/* ── Stats grid ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {stats.map((s, idx) => (
            <div key={s.label} style={{
              background: `linear-gradient(135deg, ${COLORS.card} 0%, ${statColors[idx]}0D 100%)`,
              border: `1px solid ${statColors[idx]}30`, borderRadius: 20, padding: "16px 14px",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 12, background: `${statColors[idx]}18`, border: `1px solid ${statColors[idx]}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AppIconStyles/><AppIcon name={s.icon} color={statColors[idx]} size={20}/>
                </div>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: statColors[idx], opacity: 0.6 }} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: statColors[idx], letterSpacing: -1, lineHeight: 1 }}>{s.value}</div>
              <div style={{ color: COLORS.muted, fontSize: 10.5, marginTop: 4, letterSpacing: 0.2 }}>{s.label}</div>
              <div style={{ color: statColors[idx], fontSize: 9.5, marginTop: 2, opacity: 0.75 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Weekly bar chart ── */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: "18px 16px", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ color: COLORS.text, fontWeight: 800, fontSize: 14 }}>This Week</div>
            <div style={{ color: COLORS.muted, fontSize: 11 }}>Sessions per day</div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 72 }}>
            {weekBars.map((cnt, i) => {
              const isToday = i === 6;
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  {cnt > 0 && <div style={{ fontSize: 8, color: COLORS.sage, fontWeight: 700 }}>{cnt}</div>}
                  <div style={{ width: "100%", height: Math.max(cnt / maxBar * 52, cnt > 0 ? 10 : 4), borderRadius: "6px 6px 0 0", transition: "height 0.5s ease",
                    background: cnt > 0
                      ? isToday ? `linear-gradient(180deg, ${COLORS.coral}, #C03055)` : `linear-gradient(180deg, ${COLORS.sage}, ${COLORS.deepSage})`
                      : COLORS.surface,
                    boxShadow: cnt > 0 && isToday ? `0 0 10px ${COLORS.coral}50` : "none",
                  }} />
                  <div style={{ fontSize: 9, color: isToday ? COLORS.coral : COLORS.subtle, fontWeight: isToday ? 800 : 600 }}>{weekDays[i]}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            💧 WATER INTAKE TRACKER
        ══════════════════════════════════════════════════ */}
        <div style={{ background: `linear-gradient(135deg, #051828 0%, #0A2030 100%)`, border: "1px solid #4AABB535", borderRadius: 22, padding: "18px 18px 20px", marginBottom: 16, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%", background: "radial-gradient(circle, #4AABB520 0%, transparent 65%)", pointerEvents: "none" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>💧</span>
                <div style={{ color: "white", fontWeight: 800, fontSize: 15 }}>Water Intake</div>
              </div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, marginTop: 2 }}>Daily goal: {WATER_GOAL} glasses (2 L)</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#4AABB5", letterSpacing: -1 }}>{waterGlasses}<span style={{ fontSize: 14, fontWeight: 400, color: "rgba(255,255,255,0.35)" }}>/{WATER_GOAL}</span></div>
              <div style={{ fontSize: 10, color: waterGlasses >= WATER_GOAL ? "#4AABB5" : "rgba(255,255,255,0.3)" }}>
                {waterGlasses >= WATER_GOAL ? "✓ Goal reached!" : `${WATER_GOAL - waterGlasses} more to go`}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {Array.from({ length: WATER_GOAL }, (_, i) => (
              <div key={i} onClick={() => addWater(i < waterGlasses ? -1 * (waterGlasses - i) : (i + 1) - waterGlasses)} style={{
                width: 34, height: 40, borderRadius: 8, cursor: "pointer",
                background: i < waterGlasses ? "linear-gradient(180deg, #6DD6E8, #3A9BB5)" : "rgba(74,171,181,0.12)",
                border: `1.5px solid ${i < waterGlasses ? "#4AABB580" : "#4AABB525"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, transition: "all 0.2s",
                boxShadow: i < waterGlasses ? "0 2px 8px #4AABB540" : "none",
              }}>
                {i < waterGlasses ? "💧" : "🫙"}
              </div>
            ))}
          </div>
          <div style={{ height: 6, background: "rgba(74,171,181,0.15)", borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
            <div style={{ height: "100%", background: "linear-gradient(90deg, #4AABB5, #6DD6E8)", borderRadius: 4, width: `${Math.min((waterGlasses / WATER_GOAL) * 100, 100)}%`, transition: "width 0.4s ease" }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => addWater(-1)} style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "rgba(255,255,255,0.5)", fontSize: 18, cursor: "pointer", fontFamily: FONT }}>−</button>
            <button onClick={() => addWater(1)} disabled={waterGlasses >= WATER_GOAL + 2} style={{ flex: 2, padding: "10px", background: "linear-gradient(135deg, #4AABB5, #3A8FA0)", border: "none", borderRadius: 12, color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT, boxShadow: "0 4px 14px #4AABB540", letterSpacing: 0.3 }}>
              + Add Glass (250 ml)
            </button>
          </div>
          {waterGlasses >= WATER_GOAL && (
            <div style={{ marginTop: 10, textAlign: "center", color: "#4AABB5", fontSize: 11, fontWeight: 700 }}>🎉 Excellent hydration! Your body thanks you.</div>
          )}
        </div>

        {/* ══════════════════════════════════════════════════
            🥗 DAILY NUTRITION TIP
        ══════════════════════════════════════════════════ */}
        <div style={{ background: `linear-gradient(135deg, ${dailyTip.color}18 0%, ${dailyTip.color}08 100%)`, border: `1px solid ${dailyTip.color}30`, borderRadius: 22, padding: "18px 18px 16px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 22 }}>{dailyTip.icon}</span>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <div style={{ color: COLORS.text, fontWeight: 800, fontSize: 14 }}>{dailyTip.title}</div>
                <div style={{ background: `${dailyTip.color}25`, border: `1px solid ${dailyTip.color}40`, borderRadius: 20, padding: "2px 8px" }}>
                  <span style={{ color: dailyTip.color, fontSize: 9.5, fontWeight: 700 }}>{dailyTip.tag}</span>
                </div>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 10.5, marginTop: 2 }}>Tip of the day</div>
            </div>
          </div>
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12.5, lineHeight: 1.75, marginBottom: 14 }}>{dailyTip.tip}</div>
          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 14, padding: "12px 14px" }}>
            <div style={{ color: COLORS.muted, fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>Balanced Plate Guide</div>
            {MACROS.map(m => (
              <div key={m.label} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, fontWeight: 600 }}>{m.label}</span>
                  <span style={{ color: m.color, fontSize: 11, fontWeight: 700 }}>{m.pct}%</span>
                </div>
                <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}>
                  <div style={{ height: "100%", width: `${m.pct}%`, background: `linear-gradient(90deg, ${m.color}, ${m.color}88)`, borderRadius: 3 }} />
                </div>
                <div style={{ color: "rgba(255,255,255,0.28)", fontSize: 9.5, marginTop: 3 }}>{m.ex}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            🍽 MEAL TIMING GUIDE
        ══════════════════════════════════════════════════ */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: "16px 16px", marginBottom: 16 }}>
          <div style={{ color: COLORS.text, fontWeight: 800, fontSize: 14, marginBottom: 12 }}>🍽 Meal Timing Guide</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { time: "7–8 AM",   meal: "Breakfast",       icon: "🌅", tip: "High protein + complex carbs. Fuels your morning workout.", color: COLORS.coral },
              { time: "10–11 AM", meal: "Mid-Morning",      icon: "🍎", tip: "Light snack if needed — fruit, nuts, or yogurt.", color: COLORS.gold },
              { time: "12–2 PM",  meal: "Lunch",            icon: "☀️", tip: "Biggest meal. Include all macros + lots of veggies.", color: COLORS.sage },
              { time: "4–5 PM",   meal: "Pre-Workout",      icon: "⚡", tip: "Quick carbs + protein 45 min before training.", color: COLORS.lavender },
              { time: "6–8 PM",   meal: "Dinner",           icon: "🌙", tip: "Lighter. High protein + low carb for better sleep.", color: COLORS.blue },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: `${item.color}09`, border: `1px solid ${item.color}20`, borderRadius: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: `${item.color}18`, border: `1.5px solid ${item.color}35`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <ConditionIconStyles/><MealIcon meal={item.meal} color={item.color} size={24}/>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ color: item.color, fontSize: 11, fontWeight: 700 }}>{item.time}</span>
                    <span style={{ color: COLORS.text, fontSize: 12, fontWeight: 600 }}>{item.meal}</span>
                  </div>
                  <div style={{ color: COLORS.muted, fontSize: 10.5, marginTop: 2, lineHeight: 1.4 }}>{item.tip}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── AI Insights ── */}
        {(() => {
          const days7 = Array.from({length:7},(_,i)=>{const d=new Date();d.setDate(d.getDate()-i);return d.toISOString().slice(0,10);});
          const thisWeek = sessions.filter(s => days7.includes(s.date?.slice(0,10)));
          const dayCounts = {};
          sessions.forEach(s => { const d = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date(s.date).getDay()]; dayCounts[d]=(dayCounts[d]||0)+1; });
          const bestDay = Object.entries(dayCounts).sort((a,b)=>b[1]-a[1])[0];
          const types = { workout:0, breathing:0, yoga:0 };
          sessions.forEach(s => { if(s.type in types) types[s.type]++; });
          const sortedTypes = Object.entries(types).sort((a,b)=>b[1]-a[1]);
          const dominant = sortedTypes[0];
          const missing = Object.entries(types).filter(([,v])=>v===0).map(([k])=>k);
          const insights = [];
          if (sessions.length >= 3 && bestDay) insights.push({ icon:"calendar", color:COLORS.blue, tag:"Pattern", title:`${bestDay[0]} is your power day`, desc:`${bestDay[1]} sessions — you show up most on ${bestDay[0]}.` });
          if (sessions.length >= 5 && dominant) insights.push({ icon:"chart", color:COLORS.sage, tag:"Balance", title:`${dominant[0].charAt(0).toUpperCase()+dominant[0].slice(1)} is your go-to`, desc: missing.length>0 ? `Try adding ${missing[0]} for a more complete routine.` : "Great variety across all activity types!" });
          if (streak >= 2) insights.push({ icon:"fire", color:COLORS.coral, tag:"Streak", title:`${streak}-day streak`, desc: streak>=7 ? "A full week — elite consistency! 🔥" : streak>=4 ? "Almost a week. Don't break it!" : "Nice start — momentum is everything." });
          if (totalCals > 0) insights.push({ icon:"bolt", color:COLORS.gold, tag:"Output", title:`~${totalCals} kcal burned total`, desc:`Across all ${sessions.length} sessions. Every rep counts.` });
          if (thisWeek.length > 0) insights.push({ icon:"target", color:COLORS.lavender, tag:"This Week", title:`${thisWeek.length} session${thisWeek.length>1?"s":""} this week`, desc: thisWeek.length>=4?"Exceptional week — you're in top form!":thisWeek.length>=2?"Solid momentum. Push for one more?":"Week's young. Let's add more!" });
          if (insights.length === 0) return null;
          return (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                <div style={{ color:COLORS.text, fontWeight:800, fontFamily:FONT, fontSize:14 }}>✦ AI Insights</div>
                <div style={{ background:`${COLORS.sage}20`, border:`1px solid ${COLORS.sage}40`, borderRadius:20, padding:"2px 9px" }}>
                  <span style={{ color:COLORS.sage, fontSize:10, fontFamily:FONT, fontWeight:700 }}>Personalised</span>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:9 }}>
                {insights.slice(0,4).map((ins,i) => (
                  <div key={i} style={{ background:`linear-gradient(135deg,${COLORS.card},${ins.color}0D)`, border:`1px solid ${ins.color}30`, borderRadius:16, padding:"12px 13px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                      <AppIconStyles/><AppIcon name={ins.icon} color={ins.color} size={17}/>
                      <span style={{ background:`${ins.color}25`, color:ins.color, fontSize:9, fontFamily:FONT, fontWeight:800, padding:"2px 7px", borderRadius:8 }}>{ins.tag}</span>
                    </div>
                    <div style={{ color:COLORS.text, fontFamily:FONT, fontWeight:700, fontSize:12, marginBottom:3, lineHeight:1.3 }}>{ins.title}</div>
                    <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:11, lineHeight:1.4 }}>{ins.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Recent Activity ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ color: COLORS.text, fontWeight: 800, fontFamily: FONT, fontSize: 14 }}>Recent Activity</div>
          {sessions.length > 4 && (
            <button onClick={onViewSessions} style={{ background: "none", border: "none", color: COLORS.sage, fontFamily: FONT, fontSize: 12, cursor: "pointer", fontWeight: 700 }}>See all →</button>
          )}
        </div>
        {recent.length === 0 ? (
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: "28px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}><Ico a="float">🌱</Ico></div>
            <div style={{ fontFamily: FONT, color: COLORS.text, fontWeight: 700, fontSize: 14, marginBottom: 6 }}>No activity yet</div>
            <div style={{ fontFamily: FONT, color: COLORS.muted, fontSize: 12, lineHeight: 1.7 }}>Complete workouts or breathing sessions to track your progress here.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {recent.map((s, i) => {
              const typeColor = s.type === "workout" ? COLORS.coral : s.type === "breathing" ? COLORS.blue : COLORS.sage;
              return (
                <div key={s.id || i} style={{ background: `linear-gradient(135deg, ${COLORS.card}, ${typeColor}08)`, border: `1px solid ${typeColor}25`, borderRadius: 16, padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 13, background: `${typeColor}22`, border: `1px solid ${typeColor}40`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <AppIconStyles/>
                    <AppIcon name={s.type === "workout" ? "dumbbell" : s.type === "breathing" ? "lungs" : "yoga"} color={typeColor} size={22}/>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: FONT, color: COLORS.text, fontWeight: 700, fontSize: 13 }}>{s.title}</div>
                    <div style={{ fontFamily: FONT, color: COLORS.muted, fontSize: 11, marginTop: 3 }}>{s.category} · {formatSessionDate(s.date)}</div>
                  </div>
                  <div style={{ fontSize: 10, color: typeColor, fontFamily: FONT, fontWeight: 800, background: `${typeColor}18`, border: `1px solid ${typeColor}30`, padding: "4px 10px", borderRadius: 10 }}>✓ Done</div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

// ─── YOGA GIRL MASCOT (SVG cartoon — tree pose / namaste) ────────────────────
function YogaGirlMascot({ size = 180 }) {
  return (
    <svg width={size} height={size * 1.3} viewBox="0 0 180 234" fill="none" style={{ display:"block", filter:"drop-shadow(0 8px 20px rgba(91,163,217,0.25))" }}>
      <ellipse cx="90" cy="228" rx="30" ry="6" fill="rgba(0,0,0,0.18)"/>
      <rect x="79" y="128" width="22" height="92" rx="11" fill="#4A8FE8"/>
      <ellipse cx="90" cy="222" rx="14" ry="7" fill="#4A8FE8"/>
      <ellipse cx="90" cy="222" rx="10" ry="4" fill="#3A7FD8"/>
      <path d="M96 138 Q130 145 132 162 Q130 172 120 173 Q108 168 100 158 Q94 148 96 138Z" fill="#4A8FE8"/>
      <ellipse cx="100" cy="165" rx="9" ry="5" fill="#3A7FE0" transform="rotate(-15 100 165)"/>
      <path d="M62 95 Q60 112 60 128 L120 128 Q120 112 118 95 Q108 88 90 87 Q72 88 62 95Z" fill="#F0EDE8"/>
      <path d="M62 95 Q60 108 60 120 Q68 124 90 124 Q112 124 120 120 Q120 108 118 95 Q112 100 90 100 Q68 100 62 95Z" fill="rgba(0,0,0,0.04)"/>
      <path d="M80 88 Q90 94 100 88" stroke="#D5D0C8" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <path d="M62 95 Q48 100 46 116 Q50 120 58 118 Q64 106 68 100Z" fill="#F0EDE8"/>
      <path d="M118 95 Q132 100 134 116 Q130 120 122 118 Q116 106 112 100Z" fill="#F0EDE8"/>
      <path d="M55 110 Q68 108 80 112 Q84 116 80 118 Q68 116 55 116Z" fill="#F5C5A0"/>
      <path d="M125 110 Q112 108 100 112 Q96 116 100 118 Q112 116 125 116Z" fill="#F5C5A0"/>
      <ellipse cx="90" cy="114" rx="12" ry="8" fill="#F5C5A0"/>
      <line x1="84" y1="108" x2="84" y2="120" stroke="#E8A880" strokeWidth="0.8" opacity="0.5"/>
      <line x1="88" y1="107" x2="88" y2="121" stroke="#E8A880" strokeWidth="0.8" opacity="0.5"/>
      <line x1="92" y1="107" x2="92" y2="121" stroke="#E8A880" strokeWidth="0.8" opacity="0.5"/>
      <line x1="96" y1="108" x2="96" y2="120" stroke="#E8A880" strokeWidth="0.8" opacity="0.5"/>
      <rect x="83" y="75" width="14" height="16" rx="7" fill="#F5C5A0"/>
      <ellipse cx="90" cy="52" rx="28" ry="30" fill="#F5C5A0"/>
      <path d="M63 48 Q62 20 90 16 Q118 20 117 48 Q112 36 90 34 Q68 36 63 48Z" fill="#8B4513"/>
      <ellipse cx="90" cy="24" rx="26" ry="16" fill="#8B4513"/>
      <path d="M64 42 Q48 50 46 66 Q50 80 60 78 Q54 64 62 55 Q66 48 64 42Z" fill="#8B4513"/>
      <ellipse cx="53" cy="72" rx="8" ry="5" fill="#7A3B10" transform="rotate(15 53 72)"/>
      <path d="M116 42 Q132 50 134 66 Q130 80 120 78 Q126 64 118 55 Q114 48 116 42Z" fill="#8B4513"/>
      <ellipse cx="127" cy="72" rx="8" ry="5" fill="#7A3B10" transform="rotate(-15 127 72)"/>
      <ellipse cx="81" cy="50" rx="5" ry="5.5" fill="#1A0A00"/>
      <ellipse cx="81" cy="50" rx="4" ry="4.5" fill="#2D1600"/>
      <ellipse cx="79.5" cy="48.5" rx="1.5" ry="1.5" fill="white" opacity="0.85"/>
      <ellipse cx="99" cy="50" rx="5" ry="5.5" fill="#1A0A00"/>
      <ellipse cx="99" cy="50" rx="4" ry="4.5" fill="#2D1600"/>
      <ellipse cx="97.5" cy="48.5" rx="1.5" ry="1.5" fill="white" opacity="0.85"/>
      <path d="M76 44 Q81 42 86 44" stroke="#6B3010" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
      <path d="M94 44 Q99 42 104 44" stroke="#6B3010" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
      <ellipse cx="90" cy="58" rx="2.5" ry="2" fill="#E8A880"/>
      <path d="M82 64 Q90 71 98 64" stroke="#C8805A" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <ellipse cx="73" cy="60" rx="7" ry="5" fill="#FFB3B3" opacity="0.45"/>
      <ellipse cx="107" cy="60" rx="7" ry="5" fill="#FFB3B3" opacity="0.45"/>
      <circle cx="90" cy="114" r="16" stroke="#7FB89A" strokeWidth="1" opacity="0.3"
        style={{ animation:"ai-pulse 2s ease-in-out infinite", transformOrigin:"90px 114px" }}/>
    </svg>
  );
}

// ─── COUNTDOWN TIMER HOOK ─────────────────────────────────────────────────────
function useCountdown() {
  const [t, setT] = useState(() => {
    const stored = localStorage.getItem("zf_promo_expiry");
    if (stored) { const diff = parseInt(stored) - Date.now(); if (diff > 0) return Math.floor(diff/1000); }
    const expiry = Date.now() + 23 * 3600000 + 47 * 60000 + 12000;
    localStorage.setItem("zf_promo_expiry", expiry);
    return 23 * 3600 + 47 * 60 + 12;
  });
  useEffect(() => {
    const id = setInterval(() => setT(p => Math.max(0, p - 1)), 1000);
    return () => clearInterval(id);
  }, []);
  const h = String(Math.floor(t / 3600)).padStart(2,"0");
  const m = String(Math.floor((t % 3600) / 60)).padStart(2,"0");
  const s = String(t % 60).padStart(2,"0");
  return { h, m, s, expired: t === 0 };
}

function ProfileScreen({ user, isSubscribed, setIsSubscribed, trialStatus, onNavigate, onLogout }) {
  const [showPlans, setShowPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paying, setPaying] = useState(false);
  const [currency, setCurrency] = useState(CURRENCIES[0]);
  const [showCurrPicker, setShowCurrPicker] = useState(false);
  const countdown = useCountdown();
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState(null);
  const [promoMsg, setPromoMsg] = useState("");

  const handleApplyPromo = () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) { setPromoStatus("error"); setPromoMsg("Please enter a promo code."); return; }
    if (PROMO_CODES[code] === "free_premium") {
      setPromoStatus("success");
      setPromoMsg("🎉 Code applied! Activating your premium access...");
      setTimeout(() => {
        localStorage.setItem("cf_premium", "1");
        fbSavePremiumStatus(user?.uid || "guest", "promo_" + code);
        logEvent("promo_code_redeemed", { code });
        setIsSubscribed(true);
        setShowPlans(false);
      }, 1200);
    } else {
      setPromoStatus("error");
      setPromoMsg("Invalid or expired promo code. Please check and try again.");
    }
  };

  // Plans — prices driven by CURRENCIES config
  const PLAN_DEFS = [
    { id:"monthly",    label:"Monthly",     sub:"per month · billed monthly", highlight:false, badge:"",          amountINR:299 },
    { id:"halfyearly", label:"Half Yearly", sub:"6 months · Best value",      highlight:true,  badge:"POPULAR",   amountINR:799 },
    { id:"yearly",     label:"Annual",      sub:"per year · Max savings",      highlight:false, badge:"BEST DEAL", amountINR:1099 },
  ];
  const fmtP = (v) => currency.code==="INR" ? `${currency.symbol}${Number(v).toLocaleString("en-IN")}` : `${currency.symbol}${v}`;
  const plans = PLAN_DEFS.map((p, i) => ({
    ...p,
    price:    fmtP(currency.rates[i]),
    original: fmtP(+(currency.rates[i]*2).toFixed(2)),
    amount:   p.amountINR,  // always charge INR via Razorpay
    save: "50% OFF",
  }));

  if (showPlans && !isSubscribed) return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100 }}>

      {/* ── BIG HERO: Yoga Girl + Offer Banner ── */}
      <div style={{ position:"relative", background:`linear-gradient(160deg, #0A1E10 0%, #081208 60%, rgba(10,14,8,0.8) 100%)`, padding: "48px 0 0", textAlign:"center", overflow:"hidden" }}>

        {/* Background glow behind mascot */}
        <div style={{ position:"absolute", top:20, left:"50%", transform:"translateX(-50%)", width:260, height:260, borderRadius:"50%",
          background:`radial-gradient(circle, rgba(127,184,154,0.18) 0%, rgba(91,163,217,0.10) 50%, transparent 70%)`, pointerEvents:"none" }}/>

        {/* 50% OFF ribbon sticker — top left */}
        <div style={{ position:"absolute", top:52, left:14, zIndex:10 }}>
          <div style={{ position:"relative", width:80, height:80 }}>
            {/* Starburst background */}
            <svg width={80} height={80} viewBox="0 0 80 80" style={{ position:"absolute", top:0, left:0 }}>
              <path d="M40 2 L44 30 L70 16 L50 36 L78 40 L50 44 L70 64 L44 50 L40 78 L36 50 L10 64 L30 44 L2 40 L30 36 L10 16 L36 30Z"
                fill="#E85D75" style={{ animation:"starSpin 8s linear infinite", transformOrigin:"40px 40px" }}/>
              <circle cx="40" cy="40" r="26" fill="#FF2D55"/>
            </svg>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
              <div style={{ color:"white", fontSize:9, fontFamily:FONT, fontWeight:800, letterSpacing:0.5 }}>LAUNCH</div>
              <div style={{ color:"white", fontSize:17, fontFamily:FONT, fontWeight:900, lineHeight:1 }}>50%</div>
              <div style={{ color:"white", fontSize:9, fontFamily:FONT, fontWeight:800 }}>OFF</div>
            </div>
          </div>
        </div>

        {/* "LIMITED TIME" badge — top right */}
        <div style={{ position:"absolute", top:56, right:14, zIndex:10,
          background:"linear-gradient(135deg,#D4A853,#C4902A)", borderRadius:12, padding:"6px 10px",
          boxShadow:"0 4px 14px rgba(212,168,83,0.45)", animation:"badgePulse 2s ease-in-out infinite" }}>
          <div style={{ color:"#080400", fontSize:8, fontFamily:FONT, fontWeight:800, letterSpacing:0.8 }}><Ico a="pulse">⚡</Ico> LIMITED TIME</div>
          <div style={{ color:"#080400", fontSize:7, fontFamily:FONT, fontWeight:700, marginTop:1 }}>Today Only!</div>
        </div>

        {/* Yoga Girl Mascot */}
        <div style={{ display:"inline-block", animation:"floatMascot 3.5s ease-in-out infinite" }}>
          <YogaGirlMascot size={170}/>
        </div>

        {/* Headline */}
        <div style={{ padding:"10px 20px 0" }}>
          <h1 style={{ color:COLORS.text, fontSize:22, margin:"0 0 4px", fontFamily:FONT, fontWeight:900, letterSpacing:-0.5 }}>
            CalmFit <span style={{ color:COLORS.sage }}>Premium</span>
          </h1>
          <p style={{ color:COLORS.muted, fontFamily:FONT, margin:"0 0 12px", fontSize:12, lineHeight:1.5 }}>
            Your complete wellness journey — breathing, fitness & mindfulness
          </p>
        </div>

        {/* Social proof strip */}
        <div style={{ display:"flex", justifyContent:"center", gap:16, padding:"0 20px 14px" }}>
          {[["⭐","twinkle","4.9 Rating"],["👥","bounce","14K+ Users"],["🏆","tada","#1 Wellness"]].map(([icon,anim,label],i) => (
            <div key={label} style={{ textAlign:"center" }}>
              <div style={{ fontSize:14 }}><Ico a={anim} d={`${i*0.3}s`}>{icon}</Ico></div>
              <div style={{ color:COLORS.muted, fontSize:9, fontFamily:FONT, fontWeight:600 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Countdown timer */}
        {!countdown.expired && (
          <div style={{ background:"rgba(232,93,117,0.12)", border:"1px solid rgba(232,93,117,0.3)", borderRadius:14,
            margin:"0 18px 16px", padding:"10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ color:COLORS.coral, fontFamily:FONT, fontSize:11, fontWeight:700 }}><Ico a="fire">🔥</Ico> Offer expires in</div>
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              {[countdown.h, countdown.m, countdown.s].map((v, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:i < 2 ? 6 : 0 }}>
                  <div style={{ background:"rgba(232,93,117,0.25)", borderRadius:8, padding:"4px 8px", minWidth:34, textAlign:"center" }}>
                    <div style={{ color:COLORS.coral, fontSize:15, fontFamily:FONT, fontWeight:900, lineHeight:1 }}>{v}</div>
                    <div style={{ color:"rgba(232,93,117,0.6)", fontSize:7, fontFamily:FONT }}>{["HRS","MIN","SEC"][i]}</div>
                  </div>
                  {i < 2 && <span style={{ color:COLORS.coral, fontWeight:900, fontSize:14 }}>:</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {trialStatus.active && (
          <div style={{ background:COLORS.goldDim, border:`1px solid ${COLORS.gold}40`, borderRadius:20,
            padding:"5px 14px", display:"inline-block", marginBottom:14, fontFamily:FONT, color:COLORS.gold, fontSize:11, fontWeight:700 }}>
            🎁 {trialStatus.remaining} trial day{trialStatus.remaining !== 1 ? "s" : ""} left
          </div>
        )}
      </div>

      {/* ── Plan cards ── */}
      <div style={{ padding:"16px 16px 0" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:10, fontWeight:700, letterSpacing:0.8, textTransform:"uppercase" }}>Plans · 50% off</div>
          <div style={{ position:"relative" }}>
            <button onClick={() => setShowCurrPicker(p=>!p)} style={{ display:"flex", alignItems:"center", gap:5,
              padding:"5px 10px", borderRadius:10, background:COLORS.card, border:`1px solid ${COLORS.border}`,
              color:COLORS.text, fontFamily:FONT, fontSize:11, cursor:"pointer", fontWeight:600 }}>
              <span>{currency.flag}</span><span>{currency.code}</span><span style={{ color:COLORS.muted, fontSize:9 }}>▾</span>
            </button>
            {showCurrPicker && (
              <div style={{ position:"absolute", right:0, top:34, zIndex:100, background:"rgba(14,12,8,0.97)",
                backdropFilter:"blur(20px)", border:`1px solid ${COLORS.border}`, borderRadius:16, padding:"8px 6px",
                minWidth:210, boxShadow:"0 16px 40px rgba(0,0,0,0.9)",
                animation:"sPickerIn 0.2s cubic-bezier(0.22,1,0.36,1)", maxHeight:280, overflowY:"auto" }}>
                {CURRENCIES.map(c => (
                  <div key={c.code} onClick={() => { setCurrency(c); setShowCurrPicker(false); }}
                    style={{ display:"flex", alignItems:"center", gap:9, padding:"8px 10px", borderRadius:10,
                      background: currency.code===c.code ? `${COLORS.sage}20` : "transparent", cursor:"pointer" }}>
                    <span style={{ fontSize:16 }}>{c.flag}</span>
                    <div>
                      <div style={{ color: currency.code===c.code ? COLORS.sage : COLORS.text, fontSize:12, fontFamily:FONT, fontWeight:600 }}>{c.code}</div>
                      <div style={{ color:COLORS.muted, fontSize:9, fontFamily:FONT }}>{c.label}</div>
                    </div>
                    {currency.code===c.code && <div style={{ marginLeft:"auto", width:6, height:6, borderRadius:"50%", background:COLORS.sage }}/>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:14 }}>
          {plans.map(plan => {
            const isSelected = selectedPlan === plan.id;
            return (
              <div key={plan.id} onClick={() => setSelectedPlan(plan.id)} style={{
                padding:"14px 16px", borderRadius:18, cursor:"pointer", transition:"all 0.22s", position:"relative", overflow:"hidden",
                border:`2px solid ${isSelected ? COLORS.sage : plan.highlight ? COLORS.gold+"70" : COLORS.border}`,
                background: isSelected ? `${COLORS.sage}18` : plan.highlight ? `${COLORS.gold}0E` : COLORS.card,
                boxShadow: isSelected ? `0 4px 20px ${COLORS.sage}30` : plan.highlight ? `0 4px 16px ${COLORS.gold}18` : "none",
              }}>
                {/* Popular / Best Deal badge */}
                {plan.badge && (
                  <div style={{ position:"absolute", top:0, right:0,
                    background: plan.highlight ? `linear-gradient(135deg,${COLORS.gold},#C4902A)` : `linear-gradient(135deg,${COLORS.coral},#C0404A)`,
                    color:"white", fontSize:8, fontFamily:FONT, fontWeight:800, padding:"3px 10px 3px 6px",
                    borderRadius:"0 16px 0 10px", letterSpacing:0.5 }}>
                    {plan.badge}
                  </div>
                )}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                      <span style={{ fontFamily:FONT, fontWeight:700, color:COLORS.text, fontSize:14 }}>{plan.label}</span>
                      <span style={{ fontSize:8, background:`linear-gradient(135deg,#E85D75,#C0404A)`, color:"white",
                        padding:"2px 8px", borderRadius:8, fontFamily:FONT, fontWeight:800, letterSpacing:0.3 }}>{plan.save}</span>
                    </div>
                    <div style={{ fontFamily:FONT, fontSize:10, color:COLORS.muted, marginTop:3 }}>{plan.sub}</div>
                  </div>
                  <div style={{ textAlign:"right", display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2 }}>
                    {/* Slashed original price */}
                    <span style={{ fontFamily:FONT, fontSize:11, color:COLORS.subtle, textDecoration:"line-through", lineHeight:1 }}>{plan.original}</span>
                    {/* Actual price */}
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontFamily:FONT, fontWeight:900, fontSize:20, color: isSelected ? COLORS.sage : plan.highlight ? COLORS.gold : COLORS.text, lineHeight:1 }}>{plan.price}</span>
                      <div style={{ width:22, height:22, borderRadius:"50%",
                        border:`2px solid ${isSelected ? COLORS.sage : COLORS.border}`,
                        background: isSelected ? COLORS.sage : "transparent",
                        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {isSelected && <div style={{ width:8, height:8, borderRadius:"50%", background:COLORS.bg }}/>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Features quick list */}
        <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:16, padding:"12px 14px", marginBottom:14 }}>
          <div style={{ color:COLORS.sage, fontFamily:FONT, fontSize:10, fontWeight:700, letterSpacing:0.5, marginBottom:8 }}>✦ Everything included</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 8px" }}>
            {[["🏋️","bounce","45+ workouts"],["🫁","nod","6 breathing tech."],["🤖","shake","AI Coach"],["🎵","swing","Meditation music"],["📊","pulse","Progress analytics"],["🔔","ring","Daily reminders"]].map(([icon,anim,text],i) => (
              <div key={text} style={{ display:"flex", alignItems:"center", gap:5 }}>
                <Ico a={anim} d={`${i*0.2}s`} style={{ fontSize:12 }}>{icon}</Ico>
                <span style={{ fontFamily:FONT, color:COLORS.muted, fontSize:10 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Promo Code ── */}
        <div style={{ marginBottom:14 }}>
          {!showPromoInput ? (
            <div style={{ textAlign:"center", padding:"4px 0" }}>
              <span onClick={() => setShowPromoInput(true)}
                style={{ color:`${COLORS.sage}BB`, fontFamily:FONT, fontSize:12, fontWeight:600, cursor:"pointer", borderBottom:`1px dashed ${COLORS.sage}50`, paddingBottom:1 }}>
                🎟️ Have a promo code?
              </span>
            </div>
          ) : (
            <div style={{ animation:"slideDown 0.25s ease" }}>
              <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:11, fontWeight:600, letterSpacing:0.8, textTransform:"uppercase", marginBottom:8 }}>Promo Code</div>
              <div style={{ display:"flex", gap:8 }}>
                <input
                  value={promoCode} onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); }}
                  onKeyDown={e => e.key === "Enter" && handleApplyPromo()}
                  placeholder="e.g. FRIENDPASS"
                  style={{ flex:1, padding:"12px 14px", borderRadius:12, border:`1.5px solid ${promoStatus==="error" ? "rgba(232,93,117,0.5)" : promoStatus==="success" ? `${COLORS.sage}60` : COLORS.border}`, background:COLORS.surface, color:COLORS.text, fontFamily:FONT, fontSize:13, fontWeight:700, letterSpacing:1.5, outline:"none" }}
                />
                <button onClick={handleApplyPromo}
                  style={{ padding:"12px 16px", borderRadius:12, border:"none", background:COLORS.sage, color:"white", fontFamily:FONT, fontWeight:800, fontSize:13, cursor:"pointer", flexShrink:0 }}>
                  Apply
                </button>
              </div>
              {promoStatus && (
                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:9, padding:"9px 12px", borderRadius:11,
                  background: promoStatus==="success" ? `${COLORS.sage}15` : "rgba(232,93,117,0.10)",
                  border: `1px solid ${promoStatus==="success" ? `${COLORS.sage}35` : "rgba(232,93,117,0.25)"}` }}>
                  <span style={{ fontSize:14 }}>{promoStatus==="success" ? "✅" : "❌"}</span>
                  <span style={{ color: promoStatus==="success" ? COLORS.sage : "#E85D75", fontFamily:FONT, fontSize:12, fontWeight:600 }}>{promoMsg}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <button onClick={() => {
          if (!selectedPlan) return;
          const plan = plans.find(p => p.id === selectedPlan);
          if (!plan) return;
          setPaying(true);
          openRazorpay({ planLabel: plan.label, amount: plan.amount, user,
            onSuccess: (resp) => {
              setPaying(false);
              setIsSubscribed(true);
              localStorage.setItem("cf_premium", "1");
              setShowPlans(false);
              logEvent("purchase_complete", { plan: plan.id, payment_id: resp.razorpay_payment_id });
            },
            onFail: () => setPaying(false),
          });
        }} style={{ width:"100%", padding:"16px", borderRadius:16, border:"none", fontSize:15,
          fontFamily:FONT, cursor: selectedPlan ? "pointer" : "default", fontWeight:800,
          background: selectedPlan ? `linear-gradient(135deg, ${COLORS.sage}, #5A9A78)` : COLORS.surface,
          color: selectedPlan ? "#fff" : COLORS.subtle,
          boxShadow: selectedPlan ? `0 6px 24px ${COLORS.sage}45` : "none",
          opacity: paying ? 0.7 : 1, transition:"all 0.22s", marginBottom:8 }}>
          {paying ? "Opening payment…" : selectedPlan ? `🔒 Activate for ${plans.find(p=>p.id===selectedPlan)?.price} — 50% off` : "Select a plan above"}
        </button>
        <p style={{ fontFamily:FONT, color:COLORS.subtle, fontSize:10, textAlign:"center", marginBottom:10 }}>
          🔒 Secured by Razorpay · UPI · Cards · Netbanking · Wallets · Cancel anytime
        </p>

        {/* ── PayPal international button ── */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <div style={{ flex:1, height:1, background:COLORS.border }}/>
            <span style={{ color:COLORS.subtle, fontFamily:FONT, fontSize:10, fontWeight:600, letterSpacing:0.5 }}>OR PAY INTERNATIONALLY</span>
            <div style={{ flex:1, height:1, background:COLORS.border }}/>
          </div>
          <a href="https://www.paypal.com/ncp/payment/FMBZAY4ANL8Q8" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"block" }}>
            <button style={{ width:"100%", padding:"14px", borderRadius:16, border:"1.5px solid rgba(0,156,222,0.4)",
              background:"rgba(0,156,222,0.08)", color:COLORS.text, fontFamily:FONT, fontWeight:700, fontSize:13,
              cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8,
              boxShadow:"0 4px 16px rgba(0,156,222,0.12)", transition:"all 0.2s" }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }}>
                <rect width="24" height="24" rx="6" fill="#003087"/>
                <path d="M8.5 6h4.2c2.1 0 3.5 1.2 3.2 3.2-.4 2.4-2 3.3-4.1 3.3H10.7l-.6 3.5H7.5L8.5 6z" fill="#009CDE"/>
                <path d="M9.5 7.5h3.5c1.6 0 2.6.9 2.4 2.4-.3 1.8-1.5 2.5-3.1 2.5H11l-.5 3H8.1l1.4-7.9z" fill="white"/>
              </svg>
              🌍 Pay with PayPal — all currencies
              <span style={{ marginLeft:"auto", fontSize:12, color:"rgba(0,156,222,0.9)", fontWeight:800 }}>›</span>
            </button>
          </a>
          <p style={{ fontFamily:FONT, color:COLORS.subtle, fontSize:10, textAlign:"center", marginTop:5, marginBottom:0 }}>
            Visa · Mastercard · Amex · PayPal Balance — worldwide
          </p>
        </div>

        <button onClick={() => setShowPlans(false)} style={{ width:"100%", padding:"12px", background:"transparent",
          border:`1px solid ${COLORS.border}`, borderRadius:12, color:COLORS.muted, fontFamily:FONT, fontSize:12, cursor:"pointer" }}>
          ← Back to Profile
        </button>
      </div>

      <style>{`
        @keyframes starSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes floatMascot{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        @keyframes badgePulse{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}
      `}</style>
    </div>
  );

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100 }}>
      {/* Profile header */}
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 22px 24px", textAlign: "center" }}>
        <div style={{ width: 72, height: 72, borderRadius: 24, background: COLORS.sageDim, border: `1.5px solid ${COLORS.sage}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 12px" }}>🌿</div>
        <h1 style={{ color: COLORS.text, fontSize: 21, margin: "0 0 3px", fontFamily: FONT, fontWeight: 800 }}>{user.name}</h1>
        <p style={{ color: COLORS.muted, margin: "0 0 12px", fontFamily: FONT, fontSize: 12 }}>Goal: {user.goal || "Wellness & Fitness"}</p>
        {isSubscribed
          ? <span style={{ background: COLORS.goldDim, border: `1px solid ${COLORS.gold}50`, color: COLORS.gold, padding: "4px 14px", borderRadius: 20, fontSize: 11, fontFamily: FONT, fontWeight: 700 }}>✦ Premium Member</span>
          : <span style={{ background: COLORS.sageDim, border: `1px solid ${COLORS.sage}40`, color: COLORS.sage, padding: "4px 14px", borderRadius: 20, fontSize: 11, fontFamily: FONT, fontWeight: 700 }}>🌿 Free Plan</span>
        }
      </div>

      <div style={{ padding: "0 18px" }}>
        {/* Subscribe / Premium CTA */}
        {!isSubscribed && (
          <div onClick={() => setShowPlans(true)}
            style={{ background: `linear-gradient(135deg, #0E1A12 0%, #1A2E20 100%)`, border: `1px solid ${COLORS.sage}30`, borderRadius: 18, padding: 16, marginBottom: 16, cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: COLORS.sageDim, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🌿</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: COLORS.text, fontWeight: 700, fontFamily: FONT, fontSize: 14 }}>Subscribe to CalmFit</div>
              <div style={{ color: COLORS.muted, fontFamily: FONT, fontSize: 12, marginTop: 2 }}>From ₹299/month · Cancel anytime</div>
            </div>
            <div style={{ color: COLORS.sage, fontSize: 20 }}>›</div>
          </div>
        )}
        {isSubscribed && (
          <div style={{ background: COLORS.goldDim, border: `1px solid ${COLORS.gold}40`, borderRadius: 16, padding: 14, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22 }}>✦</span>
            <div>
              <div style={{ fontFamily: FONT, color: COLORS.gold, fontWeight: 700, fontSize: 13 }}>Premium Active</div>
              <div style={{ fontFamily: FONT, color: COLORS.muted, fontSize: 11, marginTop: 2 }}>Renews next cycle · Cancel anytime</div>
            </div>
          </div>
        )}

        {/* Menu items */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 18, overflow: "hidden", marginBottom: 16 }}>
          {[
            { icon: "yoga",     label: "My Favorites",  key: "favorites",     color: COLORS.lavender },
            { icon: "calendar", label: "Schedule",       key: "schedule",      color: COLORS.blue },
            { icon: "bell",     label: "Notifications",  key: "notifications", color: COLORS.coral },
            { icon: "chat",     label: "Help & Support", key: "help",          color: COLORS.sage },
          ].map((item, i, arr) => (
            <div key={item.key} onClick={() => onNavigate(item.key)}
              style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.border}` : "none", cursor: "pointer" }}>
              <div style={{ width: 36, height: 36, borderRadius: 11, background: `${item.color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><AppIconStyles/><AppIcon name={item.icon} color={item.color} size={20}/></div>
              <span style={{ fontFamily: FONT, color: COLORS.text, flex: 1, fontSize: 13, fontWeight: 600 }}>{item.label}</span>
              <span style={{ color: COLORS.subtle, fontSize: 16 }}>›</span>
            </div>
          ))}
        </div>

        {/* Logout */}
        <div onClick={onLogout}
          style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}>
          <div style={{ width: 36, height: 36, borderRadius: 11, background: COLORS.coralDim, display: "flex", alignItems: "center", justifyContent: "center" }}><AppIcon name="door" color={COLORS.red} size={20}/></div>
          <span style={{ fontFamily: FONT, color: COLORS.red, flex: 1, fontSize: 13, fontWeight: 700 }}>Log Out</span>
        </div>
      </div>
    </div>
  );
}

// ─── CURRENCY CONFIG ─────────────────────────────────────────────────────────
const CURRENCIES = [
  { code:"INR", symbol:"₹",   label:"India (INR)",         flag:"🇮🇳", rates:[299, 799, 1099] },
  { code:"USD", symbol:"$",   label:"United States (USD)",  flag:"🇺🇸", rates:[3.59, 9.59, 12.99] },
  { code:"EUR", symbol:"€",   label:"Europe (EUR)",         flag:"🇪🇺", rates:[3.29, 8.79, 11.99] },
  { code:"GBP", symbol:"£",   label:"United Kingdom (GBP)", flag:"🇬🇧", rates:[2.79, 7.49, 10.49] },
  { code:"AED", symbol:"د.إ", label:"UAE (AED)",            flag:"🇦🇪", rates:[12.99, 34.99, 47.99] },
  { code:"SGD", symbol:"S$",  label:"Singapore (SGD)",      flag:"🇸🇬", rates:[4.79, 12.99, 17.99] },
  { code:"AUD", symbol:"A$",  label:"Australia (AUD)",      flag:"🇦🇺", rates:[5.49, 14.99, 20.99] },
  { code:"CAD", symbol:"C$",  label:"Canada (CAD)",         flag:"🇨🇦", rates:[4.89, 13.49, 18.99] },
];

// ── Stripe removed — international payments now handled via Razorpay + PayPal ──

// ─── HARD PAYWALL SCREEN ──────────────────────────────────────────────────────
// Shown when trial expires — cannot be dismissed without paying
function PaywallScreen({ user, onSubscribed }) {
  const [selectedPlan, setSelectedPlan] = useState("halfyearly");
  const [paying, setPaying] = useState(false);
  const [payMode, setPayMode] = useState("india"); // "india" | "intl"
  const [intlCurr, setIntlCurr] = useState(CURRENCIES[1]); // USD default
  const [showCurrPicker, setShowCurrPicker] = useState(false);
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState(null); // null | "success" | "error"
  const [promoMsg, setPromoMsg] = useState("");

  const handleApplyPromo = () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) { setPromoStatus("error"); setPromoMsg("Please enter a promo code."); return; }
    if (PROMO_CODES[code] === "free_premium") {
      setPromoStatus("success");
      setPromoMsg("🎉 Code applied! Activating your premium access...");
      setTimeout(() => {
        localStorage.setItem("cf_premium", "1");
        fbSavePremiumStatus(user?.uid || "guest", "promo_" + code);
        logEvent("promo_code_redeemed", { code });
        onSubscribed();
      }, 1200);
    } else {
      setPromoStatus("error");
      setPromoMsg("Invalid or expired promo code. Please check and try again.");
    }
  };

  const PAYWALL_PLANS = [
    { id:"monthly",    label:"Monthly",    price:"₹299",  sub:"billed monthly",    amountINR:299,  badge:"",          highlight:false },
    { id:"halfyearly", label:"6 Months",   price:"₹799",  sub:"save 55% · popular",amountINR:799,  badge:"POPULAR",   highlight:true  },
    { id:"yearly",     label:"Annual",     price:"₹1,099",sub:"save 70% · best deal",amountINR:1099,badge:"BEST DEAL",highlight:false },
  ];

  const BENEFITS = [
    { icon:"yoga",     text:"5 Full Yoga & Pilates Classes",   col:"#5B9BD5" },
    { icon:"wave",     text:"Wu Style Tai Chi (45 min)",        col:"#4AABB5" },
    { icon:"lungs",    text:"6 Breathing Techniques",          col:"#4A9B7F" },
    { icon:"moon",     text:"8 Guided Meditations",            col:"#7B5EA7" },
    { icon:"dumbbell", text:"45+ Workout Exercises",           col:"#E07840" },
    { icon:"chart",    text:"Progress Analytics & Streak",     col:"#D4A853" },
    { icon:"target",   text:"30-Day Challenge Program",        col:"#D45B75" },
    { icon:"sparkles", text:"AI Wellness Coach",               col:"#C4A055" },
  ];

  const plan = PAYWALL_PLANS.find(p => p.id === selectedPlan);

  const handlePay = () => {
    if (!plan) return;
    setPaying(true);
    logEvent("paywall_pay_tap", { plan: plan.id });
    openRazorpay({
      planLabel: plan.label, amount: plan.amountINR, user,
      onSuccess: (resp) => {
        setPaying(false);
        localStorage.setItem("cf_premium", "1");
        fbSavePremiumStatus(
          user?.uid || user?.name?.toLowerCase().replace(/\s+/g,"_") || "guest",
          resp.razorpay_payment_id
        );
        logEvent("purchase_complete", { plan: plan.id, payment_id: resp.razorpay_payment_id });
        onSubscribed();
      },
      onFail: () => setPaying(false),
    });
  };

  return (
    <div style={{ background:"#080906", minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-start", overflowY:"auto", paddingBottom:40 }}>
      <style>{`
        @keyframes pwGlow{0%,100%{box-shadow:0 0 40px rgba(74,155,127,0.18)}50%{box-shadow:0 0 70px rgba(74,155,127,0.35)}}
        @keyframes pwFadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes lockPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.07)}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      <div style={{ width:"100%", maxWidth:430, padding:"0 0 24px" }}>

        {/* ── Hero: Trial ended ── */}
        <div style={{ background:"linear-gradient(180deg,#041A12 0%,#020D08 100%)", padding:"56px 24px 28px", textAlign:"center", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, background:"radial-gradient(ellipse 70% 50% at 50% 0%,rgba(74,155,127,0.14) 0%,transparent 70%)", pointerEvents:"none" }}/>

          {/* Lock icon */}
          <div style={{ width:80, height:80, borderRadius:24, background:"rgba(74,155,127,0.12)", border:"2px solid rgba(74,155,127,0.35)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 18px", animation:"lockPulse 2.5s ease-in-out infinite", animation:"pwGlow 2.5s ease-in-out infinite" }}>
            <svg width={38} height={38} viewBox="0 0 40 40" fill="none">
              <rect x="8" y="20" width="24" height="17" rx="5" fill="#4A9B7F" opacity="0.25" stroke="#4A9B7F" strokeWidth="2"/>
              <path d="M13 20 L13 13.5 C13 8.5 27 8.5 27 13.5 L27 20" stroke="#4A9B7F" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
              <circle cx="20" cy="29" r="3" fill="#4A9B7F"/>
              <line x1="20" y1="29" x2="20" y2="32" stroke="#4A9B7F" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>

          <div style={{ color:"#4A9B7F", fontFamily:FONT, fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>Free Trial Ended</div>
          <h1 style={{ color:"#F0EBE0", fontFamily:FONT, fontWeight:900, fontSize:26, margin:"0 0 10px", letterSpacing:-0.8 }}>Unlock CalmFit<br/>Premium</h1>
          <p style={{ color:"rgba(240,235,224,0.5)", fontFamily:FONT, fontSize:13, margin:0, lineHeight:1.6 }}>Unlock all classes, workouts & yoga.<br/>Continue your wellness journey — from ₹299/mo.</p>
        </div>

        <div style={{ padding:"0 18px" }}>

          {/* ── Benefits grid ── */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, margin:"20px 0 22px" }}>
            {BENEFITS.map((b,i) => (
              <div key={i} style={{ background:"rgba(255,255,255,0.04)", border:`1px solid ${b.col}25`, borderRadius:14, padding:"12px 12px", display:"flex", alignItems:"center", gap:9, animation:`pwFadeUp ${0.2+i*0.05}s ease` }}>
                <div style={{ width:32, height:32, borderRadius:10, background:`${b.col}18`, border:`1px solid ${b.col}35`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <AppIconStyles/><AppIcon name={b.icon} color={b.col} size={16}/>
                </div>
                <span style={{ color:"rgba(240,235,224,0.8)", fontFamily:FONT, fontSize:11, fontWeight:600, lineHeight:1.3 }}>{b.text}</span>
              </div>
            ))}
          </div>

          {/* ── Plan selector ── */}
          <div style={{ marginBottom:18 }}>
            <div style={{ color:"rgba(240,235,224,0.5)", fontFamily:FONT, fontSize:11, fontWeight:600, letterSpacing:1, textTransform:"uppercase", marginBottom:12 }}>Choose your plan</div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {PAYWALL_PLANS.map(p => (
                <div key={p.id} onClick={() => setSelectedPlan(p.id)}
                  style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", borderRadius:16, cursor:"pointer", transition:"all 0.18s",
                    background: selectedPlan===p.id ? "rgba(74,155,127,0.14)" : "rgba(255,255,255,0.04)",
                    border: `1.5px solid ${selectedPlan===p.id ? "#4A9B7F80" : "rgba(255,255,255,0.1)"}`,
                    boxShadow: selectedPlan===p.id && p.highlight ? "0 6px 22px rgba(74,155,127,0.25)" : "none",
                  }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    {/* Radio dot */}
                    <div style={{ width:20, height:20, borderRadius:"50%", border:`2px solid ${selectedPlan===p.id ? "#4A9B7F" : "rgba(255,255,255,0.2)"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {selectedPlan===p.id && <div style={{ width:10, height:10, borderRadius:"50%", background:"#4A9B7F" }}/>}
                    </div>
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <span style={{ color:"#F0EBE0", fontFamily:FONT, fontWeight:700, fontSize:14 }}>{p.label}</span>
                        {p.badge && <span style={{ background: p.highlight ? "#4A9B7F" : "rgba(255,255,255,0.12)", color: p.highlight ? "white" : "rgba(240,235,224,0.6)", fontSize:9, fontFamily:FONT, fontWeight:800, padding:"2px 7px", borderRadius:6, letterSpacing:0.4 }}>{p.badge}</span>}
                      </div>
                      <div style={{ color:"rgba(240,235,224,0.42)", fontFamily:FONT, fontSize:10, marginTop:2 }}>{p.sub}</div>
                    </div>
                  </div>
                  <div style={{ color: selectedPlan===p.id ? "#4A9B7F" : "#F0EBE0", fontFamily:FONT, fontWeight:800, fontSize:15 }}>{p.price}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Promo code ── */}
          <div style={{ marginBottom:16 }}>
            {!showPromoInput ? (
              <div style={{ textAlign:"center" }}>
                <span onClick={() => setShowPromoInput(true)}
                  style={{ color:"rgba(74,155,127,0.75)", fontFamily:FONT, fontSize:12, fontWeight:600, cursor:"pointer", borderBottom:"1px dashed rgba(74,155,127,0.4)", paddingBottom:1 }}>
                  🎟️ Have a promo code?
                </span>
              </div>
            ) : (
              <div style={{ animation:"slideDown 0.25s ease" }}>
                <div style={{ color:"rgba(240,235,224,0.45)", fontFamily:FONT, fontSize:11, fontWeight:600, letterSpacing:0.8, textTransform:"uppercase", marginBottom:8 }}>Promo Code</div>
                <div style={{ display:"flex", gap:8 }}>
                  <input
                    value={promoCode} onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); }}
                    onKeyDown={e => e.key === "Enter" && handleApplyPromo()}
                    placeholder="Enter code e.g. FRIENDPASS"
                    style={{ flex:1, padding:"12px 14px", borderRadius:12, border:`1.5px solid ${promoStatus==="error" ? "rgba(232,93,117,0.5)" : promoStatus==="success" ? "rgba(74,155,127,0.6)" : "rgba(255,255,255,0.14)"}`, background:"rgba(255,255,255,0.05)", color:"#F0EBE0", fontFamily:FONT, fontSize:13, fontWeight:700, letterSpacing:1.5, outline:"none" }}
                  />
                  <button onClick={handleApplyPromo}
                    style={{ padding:"12px 16px", borderRadius:12, border:"none", background:"rgba(74,155,127,0.85)", color:"white", fontFamily:FONT, fontWeight:800, fontSize:13, cursor:"pointer", flexShrink:0 }}>
                    Apply
                  </button>
                </div>
                {promoStatus && (
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:9, padding:"9px 12px", borderRadius:11, animation:"slideDown 0.2s ease",
                    background: promoStatus==="success" ? "rgba(74,155,127,0.12)" : "rgba(232,93,117,0.10)",
                    border: `1px solid ${promoStatus==="success" ? "rgba(74,155,127,0.3)" : "rgba(232,93,117,0.25)"}` }}>
                    <span style={{ fontSize:14 }}>{promoStatus==="success" ? "✅" : "❌"}</span>
                    <span style={{ color: promoStatus==="success" ? "#4A9B7F" : "#E85D75", fontFamily:FONT, fontSize:12, fontWeight:600 }}>{promoMsg}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Pay method toggle ── */}
          <div style={{ display:"flex", background:"rgba(255,255,255,0.06)", borderRadius:14, padding:4, marginBottom:14, gap:4 }}>
            {[["india","🇮🇳 India (UPI / Cards)"],["intl","🌍 International Card"]].map(([mode,label]) => (
              <button key={mode} onClick={() => setPayMode(mode)} style={{ flex:1, padding:"9px 4px", borderRadius:10, border:"none",
                background: payMode===mode ? "rgba(74,155,127,0.85)" : "transparent",
                color: payMode===mode ? "white" : "rgba(240,235,224,0.4)",
                fontFamily:FONT, fontSize:10, fontWeight:700, cursor:"pointer", transition:"all 0.18s", letterSpacing:0.2 }}>
                {label}
              </button>
            ))}
          </div>

          {payMode === "india" ? (<>
            {/* ── Razorpay CTA ── */}
            <button onClick={handlePay} disabled={paying}
              style={{ width:"100%", padding:"17px", borderRadius:18, border:"none", background: paying ? "rgba(74,155,127,0.4)" : "linear-gradient(135deg,#4A9B7F,#2D7A5F)", color:"white", fontFamily:FONT, fontWeight:900, fontSize:16, cursor: paying ? "default" : "pointer", boxShadow:"0 8px 28px rgba(74,155,127,0.4)", marginBottom:14, letterSpacing:-0.3, transition:"all 0.2s" }}>
              {paying ? "Opening secure payment…" : `Unlock Premium · ${plan?.price}`}
            </button>
            <div style={{ display:"flex", justifyContent:"center", gap:14, flexWrap:"wrap", marginBottom:18 }}>
              {["🔒 Secured by Razorpay","✦ UPI · Cards · Netbanking · Wallets","↩ Cancel anytime"].map((t,i) => (
                <span key={i} style={{ color:"rgba(240,235,224,0.35)", fontFamily:FONT, fontSize:10 }}>{t}</span>
              ))}
            </div>
          </>) : (<>
            {/* ── International: currency picker ── */}
            <div style={{ marginBottom:10, position:"relative" }}>
              <div style={{ color:"rgba(240,235,224,0.42)", fontFamily:FONT, fontSize:10, fontWeight:600, letterSpacing:0.8, textTransform:"uppercase", marginBottom:6 }}>Currency</div>
              <button onClick={() => setShowCurrPicker(p=>!p)} style={{ display:"flex", alignItems:"center", gap:7,
                padding:"9px 14px", borderRadius:12, background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.14)",
                color:"rgba(240,235,224,0.9)", fontFamily:FONT, fontSize:12, cursor:"pointer", fontWeight:600, width:"100%" }}>
                <span>{intlCurr.flag}</span><span>{intlCurr.code} — {intlCurr.label}</span>
                <span style={{ marginLeft:"auto", color:"rgba(240,235,224,0.35)", fontSize:9 }}>▾</span>
              </button>
              {showCurrPicker && (
                <div style={{ position:"absolute", left:0, right:0, top:74, zIndex:100, background:"rgba(10,12,10,0.98)",
                  backdropFilter:"blur(20px)", border:"1px solid rgba(255,255,255,0.14)", borderRadius:14, padding:"8px 6px",
                  boxShadow:"0 16px 40px rgba(0,0,0,0.9)", maxHeight:240, overflowY:"auto" }}>
                  {CURRENCIES.filter(c=>c.code!=="INR").map(c => (
                    <div key={c.code} onClick={() => { setIntlCurr(c); setShowCurrPicker(false); }}
                      style={{ display:"flex", alignItems:"center", gap:9, padding:"8px 10px", borderRadius:10,
                        background: intlCurr.code===c.code ? "rgba(74,155,127,0.15)" : "transparent", cursor:"pointer" }}>
                      <span style={{ fontSize:16 }}>{c.flag}</span>
                      <div>
                        <div style={{ color: intlCurr.code===c.code ? "#4A9B7F" : "rgba(240,235,224,0.85)", fontSize:12, fontFamily:FONT, fontWeight:600 }}>{c.code}</div>
                        <div style={{ color:"rgba(240,235,224,0.4)", fontSize:9, fontFamily:FONT }}>{c.label}</div>
                      </div>
                      {intlCurr.code===c.code && <div style={{ marginLeft:"auto", width:6, height:6, borderRadius:"50%", background:"#4A9B7F" }}/>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Price preview */}
            <div style={{ background:"rgba(74,155,127,0.08)", border:"1px solid rgba(74,155,127,0.22)", borderRadius:14, padding:"12px 16px", marginBottom:12, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ color:"rgba(240,235,224,0.6)", fontFamily:FONT, fontSize:11 }}>{PAYWALL_PLANS.find(p=>p.id===selectedPlan)?.label} plan</div>
                <div style={{ color:"rgba(240,235,224,0.3)", fontFamily:FONT, fontSize:9, marginTop:2 }}>Approx. charge in {intlCurr.code}</div>
              </div>
              <div style={{ color:"#4A9B7F", fontFamily:FONT, fontWeight:900, fontSize:24 }}>
                {intlCurr.symbol}{intlCurr.rates[PAYWALL_PLANS.findIndex(p=>p.id===selectedPlan)]}
              </div>
            </div>

            {/* PayPal direct button */}
            <a href="https://www.paypal.com/ncp/payment/FMBZAY4ANL8Q8" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"block", marginBottom:14 }}>
              <button style={{ width:"100%", padding:"16px", borderRadius:18, border:"1.5px solid rgba(0,156,222,0.45)",
                background:"linear-gradient(135deg,#003087,#009CDE)", color:"white",
                fontFamily:FONT, fontWeight:900, fontSize:14, cursor:"pointer",
                boxShadow:"0 8px 24px rgba(0,156,222,0.3)", letterSpacing:-0.2, transition:"all 0.2s",
                display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }}>
                  <rect width="24" height="24" rx="6" fill="white" fillOpacity="0.15"/>
                  <path d="M8.5 6h4.2c2.1 0 3.5 1.2 3.2 3.2-.4 2.4-2 3.3-4.1 3.3H10.7l-.6 3.5H7.5L8.5 6z" fill="#009CDE"/>
                  <path d="M9.5 7.5h3.5c1.6 0 2.6.9 2.4 2.4-.3 1.8-1.5 2.5-3.1 2.5H11l-.5 3H8.1l1.4-7.9z" fill="white"/>
                </svg>
                🌍 Pay with PayPal — {intlCurr.symbol}{intlCurr.rates[PAYWALL_PLANS.findIndex(p=>p.id===selectedPlan)]}
              </button>
            </a>
            <div style={{ display:"flex", justifyContent:"center", gap:14, flexWrap:"wrap", marginBottom:18 }}>
              {["🔒 Secured by Razorpay","✦ UPI · Cards · PayPal · Wallets","↩ Cancel anytime"].map((t,i) => (
                <span key={i} style={{ color:"rgba(240,235,224,0.35)", fontFamily:FONT, fontSize:10 }}>{t}</span>
              ))}
            </div>
          </>)}

          {/* ── Testimonials ── */}
          <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, padding:"14px 16px" }}>
            <div style={{ color:"#D4A853", fontFamily:FONT, fontSize:11, fontWeight:700, marginBottom:10 }}>★★★★★ What our members say</div>
            {[
              { name:"Sneha R., Pune", avatar:"🧘‍♀️", stars:5, text:'"Down 6kg in 6 weeks. I do the morning yoga flow every day and the Physiological Sigh before bed — my sleep is the best it has been in years."' },
              { name:"Arjun M., Delhi", avatar:"💪", stars:5, text:'"The AI coach told me to switch from squats to glute bridges because of my knee issue. That level of personalisation in a ₹299 app blew my mind."' },
              { name:"Kavya T., Hyderabad", avatar:"🌬️", stars:5, text:'"I had a panic attack at work and did Box Breathing for 5 minutes. Genuinely calmed down. My therapist now recommends this app to her other patients."' },
            ].map((r,i) => (
              <div key={i} style={{ marginBottom: i<2 ? 12 : 0, paddingBottom: i<2 ? 12 : 0, borderBottom: i<2 ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:"rgba(74,155,127,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>{r.avatar}</div>
                  <div>
                    <div style={{ color:"rgba(240,235,224,0.8)", fontFamily:FONT, fontSize:11, fontWeight:700 }}>{r.name}</div>
                    <div style={{ color:"#D4A853", fontSize:9, letterSpacing:1 }}>{"★".repeat(r.stars)}</div>
                  </div>
                </div>
                <div style={{ color:"rgba(240,235,224,0.6)", fontFamily:FONT, fontSize:11, fontStyle:"italic", lineHeight:1.6 }}>{r.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PROMO SPLASH SCREEN (shown once right after login) ─────────────────────
function PromoSplashScreen({ user, onContinue, onSubscribeClick }) {
  const countdown = useCountdown();
  const [currency, setCurrency] = useState(CURRENCIES[0]);
  const [showCurrPicker, setShowCurrPicker] = useState(false);

  const PLAN_META = [
    { id:"monthly",    label:"Monthly",     sub:"billed monthly",        badge:"",          highlight:false },
    { id:"halfyearly", label:"Half Yearly", sub:"6 months · best value", badge:"POPULAR",   highlight:true  },
    { id:"yearly",     label:"Annual",      sub:"per year · max savings", badge:"BEST DEAL", highlight:false },
  ];

  const handleSkip = () => { localStorage.setItem("zf_promo_seen","1"); onContinue(); };

  return (
    <div style={{ minHeight:"100vh", fontFamily:FONT, overflowY:"auto", paddingBottom:28 }}>

      {/* Floating skip button */}
      <button onClick={handleSkip} style={{ position:"fixed", top:16, right:16, zIndex:50,
        background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)",
        color:"rgba(255,255,255,0.45)", padding:"6px 14px", borderRadius:20,
        fontFamily:FONT, fontSize:11, cursor:"pointer", backdropFilter:"blur(10px)" }}>
        Skip →
      </button>

      {/* ── Hero section ── */}
      <div style={{ background:"linear-gradient(160deg,#081408 0%,#0a1e0e 55%,rgba(8,10,8,0.9) 100%)",
        textAlign:"center", padding:"52px 18px 0", position:"relative", overflow:"hidden" }}>

        <div style={{ position:"absolute", top:0, left:"50%", transform:"translateX(-50%)", width:320,
          height:260, borderRadius:"50%", pointerEvents:"none",
          background:"radial-gradient(circle,rgba(127,184,154,0.18) 0%,rgba(91,163,217,0.08) 50%,transparent 70%)" }}/>

        {/* Spinning 50% OFF starburst */}
        <div style={{ position:"absolute", top:52, left:16, zIndex:10 }}>
          <div style={{ position:"relative", width:82, height:82 }}>
            <svg width={82} height={82} viewBox="0 0 80 80" style={{ position:"absolute" }}>
              <path d="M40 2 L44 30 L70 16 L50 36 L78 40 L50 44 L70 64 L44 50 L40 78 L36 50 L10 64 L30 44 L2 40 L30 36 L10 16 L36 30Z"
                fill="#E85D75" style={{ animation:"starSpin 10s linear infinite", transformOrigin:"40px 40px" }}/>
              <circle cx="40" cy="40" r="26" fill="#FF2D55"/>
            </svg>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
              <div style={{ color:"white", fontSize:8, fontFamily:FONT, fontWeight:800 }}>LAUNCH</div>
              <div style={{ color:"white", fontSize:18, fontFamily:FONT, fontWeight:900, lineHeight:1 }}>50%</div>
              <div style={{ color:"white", fontSize:8, fontFamily:FONT, fontWeight:800 }}>OFF</div>
            </div>
          </div>
        </div>

        {/* Gold LIMITED TIME badge */}
        <div style={{ position:"absolute", top:58, right:16, zIndex:10,
          background:"linear-gradient(135deg,#D4A853,#C4902A)", borderRadius:12, padding:"7px 11px",
          boxShadow:"0 4px 14px rgba(212,168,83,0.45)", animation:"badgePulse 2s ease-in-out infinite" }}>
          <div style={{ color:"#080400", fontSize:8, fontFamily:FONT, fontWeight:900, letterSpacing:0.8 }}><Ico a="pulse">⚡</Ico> LIMITED TIME</div>
          <div style={{ color:"#080400", fontSize:7, fontFamily:FONT, fontWeight:700, marginTop:1 }}>Today Only!</div>
        </div>

        {/* Floating yoga mascot */}
        <div style={{ display:"inline-block", animation:"floatMascot 3.5s ease-in-out infinite" }}>
          <YogaGirlMascot size={155}/>
        </div>

        <h1 style={{ color:COLORS.text, fontSize:23, margin:"8px 0 5px", fontWeight:900, letterSpacing:-0.5 }}>
          Hey {user?.name?.split(" ")[0] || "there"}! 👋
        </h1>
        <p style={{ color:COLORS.muted, fontSize:13, margin:"0 0 12px", lineHeight:1.6 }}>
          Unlock your full wellness potential.<br/>
          <span style={{ color:COLORS.sage, fontWeight:700 }}>50% launch offer — only for new members.</span>
        </p>

        {/* Social proof */}
        <div style={{ display:"flex", justifyContent:"center", gap:18, marginBottom:12 }}>
          {[["⭐","twinkle","4.9 Stars"],["👥","bounce","12K+ Users"],["🌍","spinslow","180+ Countries"]].map(([e,a,l],i) => (
            <div key={l} style={{ textAlign:"center" }}>
              <div style={{ fontSize:16 }}><Ico a={a} d={`${i*0.25}s`}>{e}</Ico></div>
              <div style={{ color:COLORS.muted, fontSize:9, fontWeight:600 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Countdown timer */}
        {!countdown.expired && (
          <div style={{ background:"rgba(232,93,117,0.12)", border:"1px solid rgba(232,93,117,0.3)",
            borderRadius:14, margin:"0 0 14px", padding:"9px 14px",
            display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ color:COLORS.coral, fontSize:11, fontWeight:700 }}><Ico a="fire">🔥</Ico> Offer expires in</div>
            <div style={{ display:"flex", gap:5, alignItems:"center" }}>
              {[countdown.h, countdown.m, countdown.s].map((v,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap: i<2?5:0 }}>
                  <div style={{ background:"rgba(232,93,117,0.25)", borderRadius:7, padding:"4px 7px", minWidth:32, textAlign:"center" }}>
                    <div style={{ color:COLORS.coral, fontSize:14, fontWeight:900, lineHeight:1 }}>{v}</div>
                    <div style={{ color:"rgba(232,93,117,0.55)", fontSize:6 }}>{["HRS","MIN","SEC"][i]}</div>
                  </div>
                  {i<2 && <span style={{ color:COLORS.coral, fontWeight:900, fontSize:13 }}>:</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Plans + currency picker ── */}
      <div style={{ padding:"14px 16px 0" }}>

        {/* Header row with currency picker */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ color:COLORS.muted, fontSize:10, fontWeight:700, letterSpacing:0.8, textTransform:"uppercase" }}>
            Choose Plan · 50% off
          </div>
          <div style={{ position:"relative" }}>
            <button onClick={() => setShowCurrPicker(p=>!p)} style={{ display:"flex", alignItems:"center", gap:5,
              padding:"5px 10px", borderRadius:10, background:COLORS.card, border:`1px solid ${COLORS.border}`,
              color:COLORS.text, fontFamily:FONT, fontSize:11, cursor:"pointer", fontWeight:600 }}>
              <span>{currency.flag}</span><span>{currency.code}</span>
              <span style={{ color:COLORS.muted, fontSize:9 }}>▾</span>
            </button>
            {showCurrPicker && (
              <div style={{ position:"absolute", right:0, top:34, zIndex:50,
                background:"rgba(14,12,8,0.97)", backdropFilter:"blur(20px)",
                border:`1px solid ${COLORS.border}`, borderRadius:16, padding:"8px 6px",
                minWidth:210, boxShadow:"0 16px 40px rgba(0,0,0,0.8)",
                animation:"sPickerIn 0.2s cubic-bezier(0.22,1,0.36,1)", maxHeight:280, overflowY:"auto" }}>
                {CURRENCIES.map(c => (
                  <div key={c.code} onClick={() => { setCurrency(c); setShowCurrPicker(false); }}
                    style={{ display:"flex", alignItems:"center", gap:9, padding:"8px 10px", borderRadius:10,
                      background: currency.code===c.code ? `${COLORS.sage}20` : "transparent",
                      cursor:"pointer", transition:"background 0.15s" }}>
                    <span style={{ fontSize:16 }}>{c.flag}</span>
                    <div>
                      <div style={{ color: currency.code===c.code ? COLORS.sage : COLORS.text, fontSize:12, fontWeight:600 }}>{c.code}</div>
                      <div style={{ color:COLORS.muted, fontSize:9 }}>{c.label}</div>
                    </div>
                    {currency.code===c.code && <div style={{ marginLeft:"auto", width:6, height:6, borderRadius:"50%", background:COLORS.sage }}/>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Plan cards */}
        <div style={{ display:"flex", flexDirection:"column", gap:9, marginBottom:14 }}>
          {PLAN_META.map((plan, idx) => {
            const price    = currency.rates[idx];
            const origP    = +(price * 2).toFixed(2);
            const fmt      = v => currency.code==="INR" ? `${currency.symbol}${Number(v).toLocaleString("en-IN")}` : `${currency.symbol}${v}`;
            return (
              <div key={plan.id} onClick={() => { onSubscribeClick(); handleSkip(); }}
                style={{ padding:"14px 16px", borderRadius:18, cursor:"pointer", position:"relative", overflow:"hidden",
                  border:`2px solid ${plan.highlight ? COLORS.gold+"70" : COLORS.border}`,
                  background: plan.highlight ? `${COLORS.gold}0E` : COLORS.card,
                  boxShadow: plan.highlight ? `0 4px 16px ${COLORS.gold}18` : "none", transition:"transform 0.15s" }}>
                {plan.badge && (
                  <div style={{ position:"absolute", top:0, right:0,
                    background: plan.highlight ? `linear-gradient(135deg,${COLORS.gold},#C4902A)` : `linear-gradient(135deg,${COLORS.coral},#C0404A)`,
                    color:"white", fontSize:8, fontFamily:FONT, fontWeight:800, padding:"3px 10px 3px 6px",
                    borderRadius:"0 16px 0 10px" }}>{plan.badge}</div>
                )}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:2 }}>
                      <span style={{ fontFamily:FONT, fontWeight:700, color:COLORS.text, fontSize:14 }}>{plan.label}</span>
                      <span style={{ fontSize:8, background:"linear-gradient(135deg,#E85D75,#C0404A)", color:"white",
                        padding:"2px 8px", borderRadius:8, fontFamily:FONT, fontWeight:800 }}>50% OFF</span>
                    </div>
                    <div style={{ fontFamily:FONT, fontSize:10, color:COLORS.muted }}>{plan.sub}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ color:COLORS.subtle, fontSize:11, fontFamily:FONT, textDecoration:"line-through" }}>{fmt(origP)}</div>
                    <div style={{ color: plan.highlight ? COLORS.gold : COLORS.text, fontSize:22, fontFamily:FONT, fontWeight:900, lineHeight:1.1 }}>{fmt(price)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* International payment notice */}
        <div style={{ background:`${COLORS.blue}12`, border:`1px solid ${COLORS.blue}30`, borderRadius:12,
          padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"flex-start", gap:10 }}>
          <span style={{ fontSize:18, flexShrink:0 }}>🌍</span>
          <div>
            <div style={{ color:COLORS.blue, fontFamily:FONT, fontSize:11, fontWeight:700, marginBottom:2 }}>International payments accepted</div>
            <div style={{ color:COLORS.muted, fontFamily:FONT, fontSize:10, lineHeight:1.5 }}>
              PayPal accepted worldwide via Razorpay — select PayPal in checkout for international payments.
            </div>
          </div>
        </div>

        {/* What's included */}
        <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:16, padding:"12px 14px", marginBottom:14 }}>
          <div style={{ color:COLORS.sage, fontFamily:FONT, fontSize:10, fontWeight:700, letterSpacing:0.5, marginBottom:8 }}>✦ Everything included</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 8px" }}>
            {[["🏋️","45+ workouts"],["🫁","6 breathing tech."],["🤖","AI Coach"],["🎵","Meditation music"],["📊","Analytics"],["🔔","Daily reminders"],["🎵","Focus music"],["🌍","Global access"]].map(([ic,tx]) => (
              <div key={tx} style={{ display:"flex", alignItems:"center", gap:5 }}>
                <span style={{ fontSize:12 }}>{ic}</span>
                <span style={{ fontFamily:FONT, color:COLORS.muted, fontSize:10 }}>{tx}</span>
              </div>
            ))}
          </div>
        </div>

        <p style={{ fontFamily:FONT, color:COLORS.subtle, fontSize:10, textAlign:"center", marginBottom:8 }}>
          🔒 India: Razorpay (UPI · Cards · Netbanking · Wallets) · International: PayPal via Razorpay
        </p>
        <button onClick={handleSkip} style={{ width:"100%", padding:"12px", background:"transparent",
          border:`1px solid ${COLORS.border}`, borderRadius:12, color:COLORS.muted, fontFamily:FONT, fontSize:12, cursor:"pointer" }}>
          Maybe later — explore free first
        </button>
      </div>
    </div>
  );
}

// ─── LOGIN / SIGN-UP SCREEN ───────────────────────────────────────────────────
// Detects if Firebase is properly configured (env var is set and not a placeholder)
const FIREBASE_READY = !!FIREBASE_CONFIG.apiKey && !FIREBASE_CONFIG.apiKey.startsWith("REPLACE_");

function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState("login"); // "login" | "signup" | "forgot"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState("general"); // "email"|"password"|"general"
  const [showPass, setShowPass] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const setErr = (msg, type = "general") => { setError(msg); setErrorType(type); };

  async function handleForgotPassword() {
    if (!email) { setErr("Please enter your email address first.", "email"); return; }
    setLoading(true); setError("");
    const res = await fbResetPassword(email);
    setLoading(false);
    if (res.error) { setErr(res.error, "email"); return; }
    setResetSent(true);
  }

  // ── LOCAL-ONLY login (when Firebase not set up yet) ──
  function handleLocalLogin() {
    if (!name.trim()) { setError("Please enter your name to continue."); return; }
    // Check localStorage for returning user
    const saved = localStorage.getItem("zf_local_user");
    if (saved) {
      const u = JSON.parse(saved);
      if (u.name.toLowerCase() === name.trim().toLowerCase()) {
        onLogin(u); return;
      }
    }
    const u = { uid: "local_" + Date.now(), name: name.trim(), email: "", joinDate: new Date().toISOString() };
    localStorage.setItem("zf_local_user", JSON.stringify(u));
    onLogin(u);
  }

  // ── Firebase login / signup ──
  async function handleSubmit() {
    if (!email || !password) { setErr("Please fill in all fields."); return; }
    if (mode === "signup" && !name) { setErr("Please enter your name."); return; }
    setLoading(true); setError("");
    const res = mode === "signup"
      ? await fbSignUp(email, password, name)
      : await fbSignIn(email, password);
    setLoading(false);
    if (res.error) {
      const msg = res.error.replace("Firebase: ", "").replace(/\(auth\/[^)]+\)\.?/g, "").trim();
      if (msg.includes("invalid-credential") || msg.includes("wrong-password") || msg.includes("user-not-found"))
        setErr("Incorrect email or password. Please try again.", "password");
      else if (msg.includes("email-already-in-use"))
        setErr("This email is already registered. Try logging in instead.", "email");
      else if (msg.includes("weak-password"))
        setErr("Password must be at least 6 characters.", "password");
      else if (msg.includes("invalid-email") || msg.includes("badly formatted"))
        setErr("Please enter a valid email address.", "email");
      else setErr(msg || "Something went wrong. Please try again.");
      return;
    }
    onLogin({ uid: res.user.uid, name: res.user.displayName || name || email.split("@")[0], email: res.user.email, joinDate: new Date().toISOString() });
  }

  async function handleGoogle() {
    setLoading(true); setError("");
    const res = await fbSignInGoogle();
    setLoading(false);
    if (res.error) { setError("Google sign-in failed. Please try again."); return; }
    onLogin({ uid: res.user.uid, name: res.user.displayName || res.user.email.split("@")[0], email: res.user.email, joinDate: new Date().toISOString() });
  }

  const inp = (ph, val, set, type = "text") => (
    <div style={{ position: "relative", marginBottom: 11 }}>
      <input
        type={type === "password" ? (showPass ? "text" : "password") : type}
        placeholder={ph} value={val} onChange={e => set(e.target.value)}
        onKeyDown={e => e.key === "Enter" && (FIREBASE_READY ? handleSubmit() : handleLocalLogin())}
        style={{ width: "100%", padding: "13px 16px", border: `1.5px solid ${val ? COLORS.sage : "#D4CCB8"}`, borderRadius: 13, fontSize: 14, fontFamily: FONT, background: "white", outline: "none", boxSizing: "border-box", color: COLORS.charcoal }}
      />
      {type === "password" && (
        <button onClick={() => setShowPass(p => !p)} style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16 }}>
          {showPass ? "🙈" : "👁️"}
        </button>
      )}
    </div>
  );

  const inpStyle = (val) => ({
    width: "100%", padding: "14px 16px",
    border: `1.5px solid ${val ? COLORS.sage + "80" : COLORS.border}`,
    borderRadius: 14, fontSize: 14, fontFamily: FONT,
    background: COLORS.surface, outline: "none",
    boxSizing: "border-box", color: COLORS.text,
    transition: "border-color 0.2s",
  });

  const fieldStyle = (val) => ({
    width: "100%", padding: "14px 16px 14px 44px",
    border: `1.5px solid ${val ? COLORS.sage+"60" : "rgba(255,255,255,0.08)"}`,
    borderRadius: 16, fontSize: 15, fontFamily: FONT,
    background: "rgba(255,255,255,0.05)", outline: "none",
    boxSizing: "border-box", color: COLORS.text,
    transition: "border-color 0.25s, background 0.25s",
    WebkitAppearance: "none",
  });
  const iconWrap = { position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", opacity:0.5, fontSize:17, pointerEvents:"none" };

  return (
    <div style={{ minHeight:"100vh", background:"transparent", display:"flex", flexDirection:"column", fontFamily:FONT, position:"relative", overflow:"hidden" }}>

      {/* ── Top hero section ── */}
      <div style={{ flex:"0 0 auto", padding:"60px 28px 32px", textAlign:"center", position:"relative" }}>
        {/* Background glow */}
        <div style={{ position:"absolute", top:-60, left:"50%", transform:"translateX(-50%)", width:320, height:320, borderRadius:"50%", background:`radial-gradient(circle, ${COLORS.sage}1A 0%, transparent 70%)`, pointerEvents:"none" }}/>

        {/* ── Animated Logo ── */}
        <div style={{ position:"relative", width:120, height:120, margin:"0 auto 24px" }}>
          {/* Outer glow pulse */}
          <div style={{ position:"absolute", inset:-20, borderRadius:"50%", background:`radial-gradient(circle, ${COLORS.sage}22 0%, transparent 70%)`, animation:"logoGlow 3s ease-in-out infinite" }}/>
          {/* Orbital rings */}
          <div style={{ position:"absolute", inset:-14, borderRadius:"50%", border:`1px solid ${COLORS.sage}20`, animation:"orbitSpin 8s linear infinite" }}/>
          <div style={{ position:"absolute", inset:-6, borderRadius:"50%", border:`1.5px solid ${COLORS.sage}30`, animation:"orbitSpin 5s linear infinite reverse" }}/>
          {/* Floating sparkle dots */}
          {[
            { top:"8%",  left:"68%", delay:"0s",    size:4  },
            { top:"72%", left:"80%", delay:"0.8s",  size:3  },
            { top:"78%", left:"14%", delay:"1.4s",  size:5  },
            { top:"12%", left:"22%", delay:"2s",    size:3  },
            { top:"48%", left:"90%", delay:"0.4s",  size:3.5},
          ].map((d,i) => (
            <div key={i} style={{ position:"absolute", top:d.top, left:d.left, width:d.size, height:d.size, borderRadius:"50%", background:COLORS.sage, opacity:0.7, animation:`sparkleFloat 2.8s ease-in-out infinite`, animationDelay:d.delay }}/>
          ))}
          {/* Main icon card */}
          <div style={{ position:"absolute", inset:0, borderRadius:32, background:`linear-gradient(145deg, #1B3A28 0%, #0D1F12 60%, #0A1A0F 100%)`, border:`1.5px solid ${COLORS.sage}45`, boxShadow:`0 16px 48px ${COLORS.sage}35, 0 4px 16px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.10)`, display:"flex", alignItems:"center", justifyContent:"center", animation:"leafFloat 4s ease-in-out infinite" }}>
            {/* SVG Leaf */}
            <svg width="58" height="58" viewBox="0 0 58 58" fill="none" style={{ animation:"leafSway 4s ease-in-out infinite", transformOrigin:"50% 85%" }}>
              {/* Glow filter */}
              <defs>
                <filter id="leafGlow">
                  <feGaussianBlur stdDeviation="2.5" result="blur"/>
                  <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
                <linearGradient id="leafGrad" x1="15" y1="8" x2="42" y2="50" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#7FD4A8"/>
                  <stop offset="50%" stopColor="#4A9B7F"/>
                  <stop offset="100%" stopColor="#2D6B55"/>
                </linearGradient>
                <linearGradient id="stemGrad" x1="29" y1="50" x2="29" y2="10" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#2D6B55"/>
                  <stop offset="100%" stopColor="#4A9B7F"/>
                </linearGradient>
              </defs>
              {/* Main leaf shape */}
              <path d="M29 48 C29 48 10 38 10 22 C10 12 20 6 29 6 C38 6 48 12 48 22 C48 38 29 48 29 48Z" fill="url(#leafGrad)" filter="url(#leafGlow)" opacity="0.95"/>
              {/* Highlight sheen */}
              <path d="M29 10 C29 10 18 16 17 24 C16.5 28 18 31 20 33" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
              {/* Center vein */}
              <path d="M29 46 L29 12" stroke="rgba(255,255,255,0.30)" strokeWidth="1.2" strokeLinecap="round"/>
              {/* Side veins */}
              <path d="M29 22 L19 18 M29 28 L19 26 M29 34 L21 34 M29 22 L39 18 M29 28 L39 26 M29 34 L37 34" stroke="rgba(255,255,255,0.18)" strokeWidth="0.9" strokeLinecap="round"/>
              {/* Stem */}
              <path d="M29 48 C29 48 28 52 27 54" stroke="url(#stemGrad)" strokeWidth="2" strokeLinecap="round" fill="none"/>
            </svg>
          </div>
        </div>

        <h1 style={{ fontSize:36, color:COLORS.text, margin:"0 0 6px", fontWeight:900, letterSpacing:-1.5 }}>CalmFit</h1>
        <p style={{ color:COLORS.muted, fontSize:12, margin:"0 0 24px", letterSpacing:2, textTransform:"uppercase", fontWeight:600 }}>Workouts · Breathing · Wellness</p>

        {/* Trust badges */}
        <div style={{ display:"flex", justifyContent:"center", gap:10, flexWrap:"wrap" }}>
          {[["⭐","twinkle","4.9 Rating"],["👥","bounce","14K+ Users"],["🏆","tada","#1 Wellness"]].map(([ic,a,lb],i) => (
            <div key={lb} style={{ display:"flex", alignItems:"center", gap:5, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:20, padding:"5px 12px" }}>
              <Ico a={a} d={`${i*0.3}s`} style={{ fontSize:11 }}>{ic}</Ico>
              <span style={{ color:COLORS.muted, fontSize:11, fontWeight:600 }}>{lb}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom card ── */}
      <div style={{ flex:"1 1 auto", background:`linear-gradient(180deg, rgba(14,12,8,0) 0%, rgba(10,9,6,0.98) 8%)`, borderRadius:"32px 32px 0 0", padding:"32px 24px 40px", position:"relative" }}>
        <div style={{ maxWidth:400, margin:"0 auto" }}>

          {/* ── LOCAL MODE ── */}
          {!FIREBASE_READY && (
            <>
              <div style={{ background:COLORS.goldDim, border:`1px solid ${COLORS.gold}30`, borderRadius:14, padding:"10px 14px", marginBottom:22, display:"flex", gap:10, alignItems:"flex-start" }}>
                <span style={{ fontSize:16 }}>⚙️</span>
                <div>
                  <div style={{ fontWeight:700, color:COLORS.gold, fontSize:12, marginBottom:2 }}>Local Mode</div>
                  <div style={{ color:COLORS.muted, fontSize:11, lineHeight:1.5 }}>Add Firebase keys to enable real accounts.</div>
                </div>
              </div>
              <div style={{ position:"relative", marginBottom:14 }}>
                <span style={iconWrap}>👤</span>
                <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleLocalLogin()} style={fieldStyle(name)} />
              </div>
              {error && <div style={{ background:"rgba(232,93,117,0.12)", border:"1px solid rgba(232,93,117,0.3)", borderRadius:12, padding:"10px 14px", marginBottom:14, color:"#E85D75", fontSize:12, lineHeight:1.5 }}>⚠️ {error}</div>}
              <button onClick={handleLocalLogin} style={{ width:"100%", padding:"16px", background:`linear-gradient(135deg, #4A9B7F, #2D7A5F)`, color:"white", border:"none", borderRadius:18, fontSize:15, fontFamily:FONT, fontWeight:800, cursor:"pointer", boxShadow:`0 8px 24px ${COLORS.sage}40`, letterSpacing:0.3 }}>
                Continue →
              </button>
            </>
          )}

          {/* ── FIREBASE MODE ── */}
          {FIREBASE_READY && (
            <>
              {/* Tab switcher */}
              <div style={{ display:"flex", background:"rgba(255,255,255,0.04)", borderRadius:18, padding:4, marginBottom:24, border:"1px solid rgba(255,255,255,0.07)" }}>
                {[["login","Log In"],["signup","Sign Up"]].map(([m,lb]) => (
                  <button key={m} onClick={() => { setMode(m); setError(""); }}
                    style={{ flex:1, padding:"11px 0", borderRadius:15, border:"none", fontFamily:FONT, fontSize:14, fontWeight:700, cursor:"pointer", transition:"all 0.25s",
                      background: mode===m ? `linear-gradient(135deg, #4A9B7F22, #4A9B7F11)` : "transparent",
                      color: mode===m ? COLORS.sage : COLORS.muted,
                      boxShadow: mode===m ? `inset 0 0 0 1px ${COLORS.sage}40` : "none",
                    }}>{lb}</button>
                ))}
              </div>

              {/* Fields */}
              {mode === "signup" && (
                <div style={{ position:"relative", marginBottom:12 }}>
                  <span style={iconWrap}>👤</span>
                  <input type="text" placeholder="Full name" value={name} onChange={e => setName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSubmit()} style={fieldStyle(name)} />
                </div>
              )}
              <div style={{ position:"relative", marginBottom:12 }}>
                <span style={iconWrap}>✉️</span>
                <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSubmit()} style={fieldStyle(email)} />
              </div>
              {/* Password field — hidden on forgot screen */}
              {mode !== "forgot" && (
                <div style={{ position:"relative", marginBottom: mode === "login" ? 6 : 16 }}>
                  <span style={iconWrap}>🔒</span>
                  <input type={showPass ? "text" : "password"} placeholder="Password (min 6 chars)" value={password}
                    onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSubmit()}
                    style={{ ...fieldStyle(password), paddingRight:48,
                      borderColor: errorType==="password" && error ? "rgba(232,93,117,0.6)" : undefined }} />
                  <button onClick={() => setShowPass(p => !p)}
                    style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:16, opacity:0.5, padding:4 }}>
                    {showPass ? "🙈" : "👁️"}
                  </button>
                </div>
              )}

              {/* Forgot password link — login mode only */}
              {mode === "login" && (
                <div style={{ textAlign:"right", marginBottom:16 }}>
                  <span onClick={() => { setMode("forgot"); setError(""); setResetSent(false); }}
                    style={{ color:COLORS.sage, fontSize:12, fontWeight:600, cursor:"pointer", opacity:0.85 }}>
                    Forgot password?
                  </span>
                </div>
              )}

              {/* ── FORGOT PASSWORD VIEW ── */}
              {mode === "forgot" && (
                <div style={{ marginBottom:16 }}>
                  {resetSent ? (
                    <div style={{ background:"rgba(74,155,127,0.12)", border:"1px solid rgba(74,155,127,0.35)", borderRadius:16, padding:"18px 16px", textAlign:"center", animation:"slideDown 0.3s ease" }}>
                      <div style={{ fontSize:32, marginBottom:10 }}>📬</div>
                      <div style={{ color:COLORS.sage, fontWeight:800, fontSize:14, marginBottom:6 }}>Reset email sent!</div>
                      <div style={{ color:COLORS.muted, fontSize:12, lineHeight:1.6 }}>Check your inbox at <strong style={{ color:COLORS.text }}>{email}</strong> and follow the link to reset your password.</div>
                      <button onClick={() => { setMode("login"); setResetSent(false); setError(""); }}
                        style={{ marginTop:14, background:"none", border:`1px solid ${COLORS.sage}50`, borderRadius:10, color:COLORS.sage, padding:"8px 18px", fontSize:12, fontFamily:FONT, fontWeight:700, cursor:"pointer" }}>
                        ← Back to Log In
                      </button>
                    </div>
                  ) : (
                    <>
                      <p style={{ color:COLORS.muted, fontSize:13, lineHeight:1.6, marginBottom:14, marginTop:0 }}>
                        Enter your email and we'll send you a link to reset your password.
                      </p>
                      <button onClick={handleForgotPassword} disabled={loading}
                        style={{ width:"100%", padding:"15px", background:`linear-gradient(135deg, #4A9B7F, #2D7A5F)`, color:"white", border:"none", borderRadius:18, fontSize:15, fontFamily:FONT, fontWeight:800, cursor:"pointer", opacity:loading ? 0.7 : 1, boxShadow:`0 8px 28px ${COLORS.sage}35`, transition:"opacity 0.2s" }}>
                        {loading ? "⏳ Sending..." : "Send Reset Link →"}
                      </button>
                      <p style={{ textAlign:"center", marginTop:14, marginBottom:0 }}>
                        <span onClick={() => { setMode("login"); setError(""); }}
                          style={{ color:COLORS.sage, fontSize:12, fontWeight:600, cursor:"pointer" }}>
                          ← Back to Log In
                        </span>
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* ── POLISHED ERROR BANNER ── */}
              {error && (
                <div style={{ display:"flex", alignItems:"flex-start", gap:10, background:"rgba(232,93,117,0.10)", border:"1px solid rgba(232,93,117,0.28)", borderRadius:14, padding:"12px 14px", marginBottom:14, animation:"slideDown 0.25s ease" }}>
                  <div style={{ fontSize:16, flexShrink:0, marginTop:1 }}>
                    {errorType === "email" ? "✉️" : errorType === "password" ? "🔐" : "⚠️"}
                  </div>
                  <div>
                    <div style={{ color:"#E85D75", fontSize:13, fontWeight:700, marginBottom:2 }}>
                      {errorType === "email" ? "Email issue" : errorType === "password" ? "Password issue" : "Something's off"}
                    </div>
                    <div style={{ color:"rgba(232,93,117,0.85)", fontSize:12, lineHeight:1.5 }}>{error}</div>
                  </div>
                </div>
              )}

              {/* Main CTA — hidden on forgot screen */}
              {mode !== "forgot" && (
                <button onClick={handleSubmit} disabled={loading}
                  style={{ width:"100%", padding:"16px", background:`linear-gradient(135deg, #4A9B7F, #2D7A5F)`, color:"white", border:"none", borderRadius:18, fontSize:15, fontFamily:FONT, fontWeight:800, cursor:"pointer", marginBottom:16, opacity:loading ? 0.7 : 1, boxShadow:`0 8px 28px ${COLORS.sage}35`, letterSpacing:0.3, transition:"opacity 0.2s" }}>
                  {loading ? "⏳ Please wait..." : mode === "login" ? "Log In →" : "Create Account →"}
                </button>
              )}

              {/* Divider — hidden on forgot screen */}
              {mode !== "forgot" && (
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
                  <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.07)" }}/>
                  <span style={{ color:"rgba(255,255,255,0.2)", fontSize:12, fontWeight:600 }}>OR</span>
                  <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.07)" }}/>
                </div>
              )}

              {/* Google button — hidden on forgot screen */}
              {mode !== "forgot" && (
                <button onClick={handleGoogle} disabled={loading}
                  style={{ width:"100%", padding:"14px", background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:18, fontSize:14, fontFamily:FONT, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:12, color:COLORS.text, fontWeight:700, transition:"background 0.2s" }}>
                  <svg width="18" height="18" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Continue with Google
                </button>
              )}

              {/* Switch mode hint */}
              {mode !== "forgot" && (
                <p style={{ textAlign:"center", color:COLORS.muted, fontSize:12, marginTop:18, marginBottom:0 }}>
                  {mode === "login" ? "New to CalmFit? " : "Already have an account? "}
                  <span onClick={() => { setMode(mode==="login"?"signup":"login"); setError(""); }}
                    style={{ color:COLORS.sage, fontWeight:700, cursor:"pointer" }}>
                    {mode === "login" ? "Sign Up free" : "Log In"}
                  </span>
                </p>
              )}
            </>
          )}

          <p style={{ color:"rgba(255,255,255,0.15)", fontSize:11, marginTop:20, textAlign:"center" }}>
            🔒 {FIREBASE_READY ? "Secured by Firebase · Your data is private" : "Data saved locally on this device"}
          </p>
        </div>
      </div>

      <style>{`
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        input::placeholder { color: rgba(240,235,224,0.25); }
        input:focus { border-color: ${COLORS.sage}80 !important; background: rgba(74,155,127,0.06) !important; }
        @keyframes logoGlow    { 0%,100%{opacity:0.6;transform:scale(1)}   50%{opacity:1;transform:scale(1.12)} }
        @keyframes orbitSpin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes leafFloat   { 0%,100%{transform:translateY(0)}          50%{transform:translateY(-5px)} }
        @keyframes leafSway    { 0%,100%{transform:rotate(-4deg) scale(1)} 50%{transform:rotate(4deg) scale(1.04)} }
        @keyframes sparkleFloat{ 0%,100%{opacity:0.2;transform:translateY(0) scale(1)} 50%{opacity:0.9;transform:translateY(-6px) scale(1.3)} }
        @keyframes slideDown   { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

// ─── MY FAVORITES SCREEN ──────────────────────────────────────────────────────
function FavoritesScreen({ favorites, onBack, onRemove }) {
  const allExercises = exerciseCategories.flatMap(cat => cat.exercises.map(ex => ({ ...ex, cat })));
  const favExercises = allExercises.filter(ex => favorites.includes(ex.name));
  const [selected, setSelected] = useState(null);
  const selectedCat = selected ? exerciseCategories.find(c => c.id === selected.cat.id) : null;

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100, fontFamily: FONT }}>
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 22px 20px" }}>
        <button onClick={onBack} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: "6px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12, marginBottom: 16, fontWeight: 600 }}>← Back</button>
        <div style={{ fontSize: 26, marginBottom: 6 }}>🧘</div>
        <h1 style={{ color: COLORS.text, fontSize: 24, margin: "0 0 3px", fontWeight: 800, letterSpacing: -0.5 }}>My Favorites</h1>
        <p style={{ color: COLORS.muted, margin: 0, fontSize: 13 }}>{favExercises.length} saved exercises</p>
      </div>
      <div style={{ padding: "0 18px" }}>
        {favExercises.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px 20px" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>🤍</div>
            <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, marginBottom: 8, fontFamily: FONT }}>No favorites yet</div>
            <div style={{ color: COLORS.muted, fontSize: 13, fontFamily: FONT, lineHeight: 1.6 }}>Tap the ❤️ on any exercise to save it here.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {favExercises.map((ex, i) => (
              <div key={i} onClick={() => setSelected(ex)}
                style={{ background: COLORS.card, border: `1px solid ${ex.cat.color}20`, borderRadius: 16, padding: 13, display: "flex", gap: 13, alignItems: "center", cursor: "pointer" }}>
                <div style={{ width: 60, height: 60, borderRadius: 14, background: `${ex.cat.color}12`, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img src={ex.gif} alt={ex.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 13 }}>{ex.name}</div>
                  <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 2 }}>{ex.cat.label} · {ex.difficulty}</div>
                  <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 1 }}>{ex.muscles}</div>
                </div>
                <button onClick={e => { e.stopPropagation(); onRemove(ex.name); }}
                  style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", padding: 4 }}>❤️</button>
              </div>
            ))}
          </div>
        )}
      </div>
      {selected && selectedCat && <ExerciseModal exercise={selected} category={selectedCat} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ─── SCHEDULE SCREEN ──────────────────────────────────────────────────────────
function ScheduleScreen({ schedule, onSave, onBack, userId }) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const fullDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const workoutTypes = ["Legs 🦵", "Shoulders 💪", "Arms 💪", "Back 🦾", "Abs 🔥", "Full Body ⚡", "Yoga 🧘", "Breathing 🫁", "Rest Day 😴"];
  const [local, setLocal] = useState(schedule || {});
  const [saved, setSaved] = useState(false);

  function toggle(day) {
    setLocal(prev => ({ ...prev, [day]: prev[day] ? { ...prev[day], enabled: !prev[day].enabled } : { enabled: true, type: "Full Body ⚡", time: "07:00" } }));
  }
  function setField(day, field, val) {
    setLocal(prev => ({ ...prev, [day]: { ...prev[day], [field]: val } }));
  }
  function save() {
    onSave(local);
    fbSaveSchedule(userId, local);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100, fontFamily: FONT }}>
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 22px 20px" }}>
        <button onClick={onBack} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: "6px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12, marginBottom: 16, fontWeight: 600 }}>← Back</button>
        <div style={{ fontSize: 26, marginBottom: 6 }}>📅</div>
        <h1 style={{ color: COLORS.text, fontSize: 24, margin: "0 0 3px", fontWeight: 800, letterSpacing: -0.5 }}>Weekly Schedule</h1>
        <p style={{ color: COLORS.muted, margin: 0, fontSize: 13 }}>Plan your week · Stay consistent</p>
      </div>
      <div style={{ padding: "0 18px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {days.map((day, i) => {
            const d = local[day] || { enabled: false, type: "Full Body ⚡", time: "07:00" };
            return (
              <div key={day} style={{ background: COLORS.card, borderRadius: 16, padding: "13px 15px", border: `1.5px solid ${d.enabled ? COLORS.blue + "60" : COLORS.border}`, transition: "border 0.2s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div onClick={() => toggle(day)} style={{ width: 44, height: 26, borderRadius: 13, background: d.enabled ? COLORS.blue : COLORS.surface, position: "relative", cursor: "pointer", transition: "background 0.25s", flexShrink: 0, border: `1px solid ${d.enabled ? "transparent" : COLORS.border}` }}>
                    <div style={{ position: "absolute", width: 20, height: 20, borderRadius: "50%", background: "white", top: 3, left: d.enabled ? 21 : 3, transition: "left 0.25s", boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 14 }}>{fullDays[i]}</div>
                    {!d.enabled && <div style={{ color: COLORS.subtle, fontSize: 11 }}>Rest</div>}
                  </div>
                </div>
                {d.enabled && (
                  <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                    <select value={d.type} onChange={e => setField(day, "type", e.target.value)}
                      style={{ flex: 1, padding: "8px 10px", borderRadius: 10, border: `1px solid ${COLORS.border}`, fontFamily: FONT, fontSize: 12, color: COLORS.text, background: COLORS.surface, outline: "none" }}>
                      {workoutTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input type="time" value={d.time} onChange={e => setField(day, "time", e.target.value)}
                      style={{ width: 90, padding: "8px 10px", borderRadius: 10, border: `1px solid ${COLORS.border}`, fontFamily: FONT, fontSize: 12, color: COLORS.text, background: COLORS.surface, outline: "none" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={save}
          style={{ width: "100%", padding: "14px", background: saved ? COLORS.sage : COLORS.blue, color: saved ? COLORS.bg : "white", border: "none", borderRadius: 14, fontSize: 14, fontFamily: FONT, fontWeight: 800, cursor: "pointer", transition: "background 0.3s" }}>
          {saved ? "✅ Schedule Saved!" : "Save Schedule"}
        </button>
      </div>
    </div>
  );
}

// ─── NOTIFICATIONS SCREEN ─────────────────────────────────────────────────────
function NotificationsScreen({ onBack }) {
  const [perms, setPerms] = useLocalStorage("zf_notif_perms", {
    workoutReminder: true,
    streakAlert: true,
    breathingReminder: false,
    weeklyReport: true,
    newContent: false,
  });
  const [granted, setGranted] = useState(Notification?.permission === "granted");
  const [requesting, setRequesting] = useState(false);
  const [saved, setSaved] = useState(false);

  async function requestPermission() {
    if (!("Notification" in window)) { alert("Your browser doesn't support notifications."); return; }
    setRequesting(true);
    const result = await Notification.requestPermission();
    setGranted(result === "granted");
    setRequesting(false);
  }

  function save() { setSaved(true); setTimeout(() => setSaved(false), 2000); }

  const toggleItem = [
    { key: "workoutReminder",  icon: "dumbbell",  title: "Workout Reminder",  desc: "Daily reminder at your scheduled time" },
    { key: "streakAlert",      icon: "fire",      title: "Streak Alert",      desc: "Don't break your streak! Daily nudge" },
    { key: "breathingReminder",icon: "lungs",     title: "Breathing Reminder",desc: "Mid-day breathing session reminder" },
    { key: "weeklyReport",     icon: "chart",     title: "Weekly Report",     desc: "Your weekly progress summary" },
    { key: "newContent",       icon: "sparkles",  title: "New Content",       desc: "New yoga classes and exercises added" },
  ];

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100, fontFamily: FONT }}>
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 22px 20px" }}>
        <button onClick={onBack} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: "6px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12, marginBottom: 16, fontWeight: 600 }}>← Back</button>
        <div style={{ marginBottom: 6 }}><AppIconStyles/><AppIcon name="bell" color={COLORS.text} size={28}/></div>
        <h1 style={{ color: COLORS.text, fontSize: 24, margin: "0 0 3px", fontWeight: 800, letterSpacing: -0.5 }}>Notifications</h1>
        <p style={{ color: COLORS.muted, margin: 0, fontSize: 13 }}>Stay on track with your wellness goals</p>
      </div>
      <div style={{ padding: "0 18px" }}>
        {!granted && (
          <div style={{ background: COLORS.goldDim, border: `1px solid ${COLORS.gold}40`, borderRadius: 15, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 13 }}>
            <span style={{ fontSize: 22 }}>🔕</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 13 }}>Notifications are off</div>
              <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 2 }}>Enable to get reminders and alerts</div>
            </div>
            <button onClick={requestPermission} style={{ background: COLORS.sage, color: COLORS.bg, border: "none", borderRadius: 10, padding: "7px 12px", fontFamily: FONT, fontSize: 11, cursor: "pointer", fontWeight: 800, whiteSpace: "nowrap" }}>
              {requesting ? "…" : "Enable"}
            </button>
          </div>
        )}
        {granted && (
          <div style={{ background: COLORS.sageDim, border: `1px solid ${COLORS.sage}40`, borderRadius: 14, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>✅</span>
            <div style={{ color: COLORS.sage, fontWeight: 700, fontSize: 13 }}>Notifications enabled</div>
          </div>
        )}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 16, overflow: "hidden", marginBottom: 16 }}>
          {toggleItem.map((item, i) => (
            <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 13, padding: "14px 16px", borderBottom: i < toggleItem.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
              <span style={{ flexShrink: 0, display:"flex", alignItems:"center" }}><AppIcon name={item.icon} color={COLORS.sage} size={22}/></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 13 }}>{item.title}</div>
                <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 1 }}>{item.desc}</div>
              </div>
              <div onClick={() => setPerms(p => ({ ...p, [item.key]: !p[item.key] }))}
                style={{ width: 44, height: 26, borderRadius: 13, background: perms[item.key] ? COLORS.sage : COLORS.surface, border: `1px solid ${perms[item.key] ? "transparent" : COLORS.border}`, position: "relative", cursor: "pointer", transition: "background 0.25s", flexShrink: 0 }}>
                <div style={{ position: "absolute", width: 20, height: 20, borderRadius: "50%", background: "white", top: 3, left: perms[item.key] ? 21 : 3, transition: "left 0.25s", boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }} />
              </div>
            </div>
          ))}
        </div>
        <button onClick={save}
          style={{ width: "100%", padding: "14px", background: saved ? COLORS.sage : COLORS.lavender, color: saved ? COLORS.bg : "white", border: "none", borderRadius: 14, fontSize: 14, fontFamily: FONT, fontWeight: 800, cursor: "pointer", transition: "background 0.3s" }}>
          {saved ? "✅ Preferences Saved!" : "Save Preferences"}
        </button>
      </div>
    </div>
  );
}

// ─── HELP & SUPPORT SCREEN ────────────────────────────────────────────────────
function HelpScreen({ onBack }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const faqs = [
    { q: "Is CalmFit free to use?", a: "Yes! CalmFit has a permanent free plan — no credit card needed. Free users get Box Breathing, Physiological Sigh, Body Scan meditation, first 3 exercises per category, and Week 1 of the 30-day challenge. Upgrade to Premium for full access." },
    { q: "How do I cancel my subscription?", a: "Cancel anytime from your Profile page. Access continues until the end of the current billing period." },
    { q: "Why are some GIFs not showing?", a: "GIFs load from your device. If one doesn't show, try scrolling away and back, or check your connection." },
    { q: "Is my workout data saved to the cloud?", a: "Yes! All your sessions, favorites, and schedule sync to Firebase automatically." },
    { q: "Can I use CalmFit offline?", a: "Yes — workouts, breathing, and yoga work offline. Data syncs when you're back online." },
    { q: "How do I reset my password?", a: "On the login screen, use the 'Forgot password?' link. We'll send a reset email instantly." },
    { q: "Which payment methods are accepted?", a: "India: UPI, Credit/Debit cards, Netbanking, and wallets via Razorpay. International: PayPal via Razorpay — all currencies accepted. 100% secure." },
  ];

  function sendMessage() {
    if (!form.subject || !form.message) return;
    setSent(true);
    setForm({ subject: "", message: "" });
  }

  return (
    <div style={{ background: "transparent", minHeight: "100vh", paddingBottom: 100, fontFamily: FONT }}>
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgWarm} 0%, transparent 100%)`, padding: "54px 22px 20px" }}>
        <button onClick={onBack} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: "6px 14px", borderRadius: 20, fontFamily: FONT, cursor: "pointer", fontSize: 12, marginBottom: 16, fontWeight: 600 }}>← Back</button>
        <div style={{ fontSize: 26, marginBottom: 6 }}>💬</div>
        <h1 style={{ color: COLORS.text, fontSize: 24, margin: "0 0 3px", fontWeight: 800, letterSpacing: -0.5 }}>Help & Support</h1>
        <p style={{ color: COLORS.muted, margin: 0, fontSize: 13 }}>We're here to help you stay consistent</p>
      </div>
      <div style={{ padding: "0 18px" }}>
        {/* Quick contact */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
          <a href="mailto:kartikpandey6666@gmail.com?subject=CalmFit Support Query" style={{ textDecoration: "none" }}>
            <div style={{ background: COLORS.card, border: `1px solid ${COLORS.sage}25`, borderRadius: 14, padding: "14px 12px", textAlign: "center", cursor: "pointer" }}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>📧</div>
              <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 13 }}>Email Us</div>
              <div style={{ color: COLORS.muted, fontSize: 10, marginTop: 2, wordBreak: "break-all" }}>kartikpandey6666@gmail.com</div>
            </div>
          </a>
          <a href="https://wa.me/918109391785?text=Hi%20Kartik%2C%20I%20have%20a%20query%20about%20CalmFit%20%F0%9F%99%8F" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <div style={{ background: COLORS.card, border: `1px solid #25D36625`, borderRadius: 14, padding: "14px 12px", textAlign: "center", cursor: "pointer" }}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>💬</div>
              <div style={{ fontWeight: 700, color: "#25D366", fontSize: 13 }}>WhatsApp</div>
              <div style={{ color: COLORS.muted, fontSize: 10, marginTop: 2 }}>+91 81093 91785</div>
            </div>
          </a>
        </div>

        {/* FAQs */}
        <div style={{ color: COLORS.muted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10 }}>FAQ</div>
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
          {faqs.map((faq, i) => (
            <div key={i} style={{ borderBottom: i < faqs.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
              <div onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                <div style={{ flex: 1, fontWeight: 600, color: COLORS.text, fontSize: 13 }}>{faq.q}</div>
                <span style={{ color: COLORS.muted, fontSize: 14, transition: "transform 0.2s", display: "inline-block", transform: openFaq === i ? "rotate(180deg)" : "none" }}>▾</span>
              </div>
              {openFaq === i && (
                <div style={{ padding: "0 16px 14px", color: COLORS.muted, fontSize: 13, lineHeight: 1.7 }}>{faq.a}</div>
              )}
            </div>
          ))}
        </div>

        {/* Contact form */}
        <div style={{ color: COLORS.muted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10 }}>Send a Message</div>
        {sent ? (
          <div style={{ background: COLORS.sageDim, border: `1px solid ${COLORS.sage}40`, borderRadius: 16, padding: "24px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
            <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 14, marginBottom: 4 }}>Message Sent!</div>
            <div style={{ color: COLORS.muted, fontSize: 12 }}>We'll get back to you within 24 hours.</div>
            <button onClick={() => setSent(false)} style={{ marginTop: 14, background: "none", border: "none", color: COLORS.sage, fontFamily: FONT, fontSize: 13, cursor: "pointer", fontWeight: 700 }}>Send another →</button>
          </div>
        ) : (
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 16 }}>
            <input placeholder="Subject" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${form.subject ? COLORS.sage + "80" : COLORS.border}`, borderRadius: 11, fontSize: 13, fontFamily: FONT, marginBottom: 10, outline: "none", boxSizing: "border-box", background: COLORS.surface, color: COLORS.text }} />
            <textarea placeholder="Describe your issue or question…" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={4}
              style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${form.message ? COLORS.sage + "80" : COLORS.border}`, borderRadius: 11, fontSize: 13, fontFamily: FONT, resize: "none", outline: "none", boxSizing: "border-box", background: COLORS.surface, color: COLORS.text }} />
            <button onClick={sendMessage}
              style={{ width: "100%", padding: "13px", background: form.subject && form.message ? COLORS.coral : COLORS.surface, color: form.subject && form.message ? "white" : COLORS.subtle, border: `1px solid ${form.subject && form.message ? "transparent" : COLORS.border}`, borderRadius: 11, fontSize: 13, fontFamily: FONT, fontWeight: 800, cursor: "pointer", marginTop: 10, transition: "all 0.2s" }}>
              Send Message 📨
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DYNAMIC SEO HOOK ─────────────────────────────────────────────────────────
// Updates title, description, og tags, canonical URL on every screen change
// Maximises crawlability and ASO/SEO ranking signals
function useSEO(screen, subScreen, extraData) {
  useEffect(() => {
    const base = "CalmFit";
    const siteName = "CalmFit — Yoga & Wellness App India";

    const metas = {
      home: {
        title: `${base} — Best Yoga App India | Morning Yoga, Meditation & Breathing Exercises`,
        desc:  "India's #1 yoga app. Morning Sun Flow, Pilates, Tai Chi, Stress Relief & 6 breathing techniques. Animated GIF pose guides. Free forever — no card needed. Works on Android & iPhone.",
        canonical: "https://calmfitapp.in/",
        keywords: "yoga app India, best yoga app India, morning yoga routine, yoga at home India, breathing exercise app, meditation app India",
      },
      explore: {
        title: `Yoga Classes Online India — Morning Sun Flow, Pilates, Tai Chi | ${base}`,
        desc:  "5 guided yoga classes: Morning Sun Flow (20 min), Core Pilates Blast (30 min), Wu Style Tai Chi (45 min), Power Vinyasa (45 min), Stress Relief Flow (25 min). Animated pose GIFs included.",
        canonical: "https://calmfitapp.in/?screen=explore",
        keywords: "yoga classes online India, pilates app India, tai chi India, vinyasa yoga app, yoga for beginners India",
      },
      breathing: {
        title: `Breathing Exercises & Meditation App India | Box Breathing, 4-7-8, Physiological Sigh | ${base}`,
        desc:  "6 science-based breathing techniques: Box Breathing, 4-7-8 (sleep), Physiological Sigh (Stanford stress reset), Heart Coherence, Calm & Resonance. Live animated visualizer + meditation music.",
        canonical: "https://calmfitapp.in/?screen=breathing",
        keywords: "breathing exercise app India, 4-7-8 breathing app, box breathing India, physiological sigh app, pranayama app India, meditation app India, anxiety relief app",
      },
      workout: {
        title: `Home Workout App India — 45+ Exercises | No Gym Needed | ${base}`,
        desc:  "45+ animated GIF workout exercises across 7 muscle groups — chest, back, shoulders, arms, legs, abs & calisthenics. Form tips, calorie tracking, no gym needed.",
        canonical: "https://calmfitapp.in/?screen=workout",
        keywords: "home workout app India, gym exercises at home, no equipment workout India, calisthenics app India, fitness app India",
      },
      progress: {
        title: `Track Your Yoga Streak & Progress | ${base}`,
        desc:  "Visual streak calendar, session history, calorie tracking and weekly progress chart. Stay motivated and build a daily wellness habit.",
        canonical: "https://calmfitapp.in/?screen=progress",
        keywords: "yoga streak tracker, fitness progress app India, workout tracker India",
      },
      profile: {
        title: `CalmFit Premium — Yoga Subscription India | ₹99/month`,
        desc:  "Get full access to all yoga, pilates, tai chi & breathing classes. Plans from ₹99/month. Pay via UPI, credit/debit card, net banking. Free plan available — upgrade anytime.",
        canonical: "https://calmfitapp.in/?screen=subscribe",
        keywords: "yoga subscription India cheap, best yoga app subscription, online yoga class India affordable, calmfit premium",
      },
      chat: {
        title: `AI Wellness Coach India — Personalised Yoga & Breathing Advice | ${base}`,
        desc:  "Chat with your AI wellness coach. Get personalised yoga recommendations, breathing guidance and daily motivation based on your mood and goals.",
        canonical: "https://calmfitapp.in/?screen=chat",
        keywords: "AI wellness coach India, yoga AI app, personalised fitness advice India",
      },
    };

    // Support class-level deep links
    if (screen === "explore" && extraData?.className) {
      const cn = extraData.className.toLowerCase().replace(/\s+/g, "-");
      metas.explore.canonical = `https://calmfitapp.in/?session=${cn}`;
      metas.explore.title = `${extraData.className} — Online Yoga Class India | ${base}`;
    }

    const key = subScreen || screen || "home";
    const seo = metas[key] || metas.home;

    // Update all SEO signals
    document.title = seo.title;

    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };

    setMeta('meta[name="description"]',         "content", seo.desc);
    setMeta('meta[name="keywords"]',            "content", seo.keywords || "");
    setMeta('meta[property="og:title"]',        "content", seo.title);
    setMeta('meta[property="og:description"]',  "content", seo.desc);
    setMeta('meta[property="og:url"]',          "content", seo.canonical);
    setMeta('meta[name="twitter:title"]',       "content", seo.title);
    setMeta('meta[name="twitter:description"]', "content", seo.desc);

    // Update canonical link
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = seo.canonical;

    // Push to history API for virtual page tracking (Google Analytics / GA4)
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: seo.title,
        page_location: seo.canonical,
        page_path: new URL(seo.canonical).pathname + new URL(seo.canonical).search,
      });
    }
  }, [screen, subScreen, extraData?.className]);
}

// ─── SEO LANDING PAGE ─────────────────────────────────────────────────────────
// Public-facing page search engines crawl & rank. Shows on first visit only.
function SEOLandingPage({ onGetStarted }) {
  const [faqOpen, setFaqOpen] = useState(null);

  const features = [
    { emoji: "🌅", title: "Morning Sun Flow", desc: "20-min beginner yoga — Mountain Pose, Sun Salutation, Warriors, Triangle & Savasana with animated GIF demos", tag: "Beginner" },
    { emoji: "🔥", title: "Core Pilates Blast", desc: "30-min intermediate core workout using classic Pilates principles for posture & deep strength", tag: "Intermediate" },
    { emoji: "☯️", title: "Wu Style Tai Chi", desc: "45-min flowing tai chi to cultivate qi, improve balance and calm the nervous system", tag: "Beginner" },
    { emoji: "⚡", title: "Power Vinyasa", desc: "45-min advanced dynamic yoga linking breath and movement for strength, flexibility & endurance", tag: "Advanced" },
    { emoji: "🌊", title: "Stress Relief Flow", desc: "25-min gentle yoga releasing tension from shoulders, hips and lower back — perfect after work", tag: "All Levels" },
  ];

  const faqs = [
    { q: "What's included in the free plan?", a: "Free users get 2 breathing techniques (Box Breathing & Physiological Sigh), Body Scan meditation, first 3 exercises in every workout category, and Week 1 of the 30-day challenge. Premium unlocks everything." },
    { q: "Do I need yoga experience?", a: "Not at all. Our Morning Sun Flow and Stress Relief classes are designed for complete beginners. Every pose has an animated GIF so you can check your form at home." },
    { q: "Does it work on Android & iPhone?", a: "CalmFit is a Progressive Web App — it works instantly in any browser on Android, iPhone, or desktop. No app store download needed." },
    { q: "Can people outside India subscribe?", a: "Yes! We support 8 currencies — INR, USD, EUR, GBP, AED, SGD, AUD, CAD. Pricing is automatically shown in your local currency." },
    { q: "What makes CalmFit different from YouTube yoga?", a: "CalmFit tracks your streak, shows you pose-by-pose animated GIF guidance, lets you pick up mid-session, plays nature sounds, and gives you a structured program — not just random videos." },
  ];

  return (
    <div style={{ minHeight:"100vh", background:"#080C09", fontFamily:FONT, color:"white", overflowX:"hidden" }}>
      <style>{`
        @keyframes heroFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
        .seo-fade { animation: fadeUp 0.7s ease forwards; }
        .seo-card:hover { transform:translateY(-4px); box-shadow:0 16px 48px rgba(124,158,135,0.25) !important; }
        .seo-card { transition:all 0.3s ease; }
        .seo-btn-primary:hover { transform:scale(1.04); box-shadow:0 8px 32px rgba(124,158,135,0.5) !important; }
        .seo-btn-primary { transition:all 0.25s ease; }
      `}</style>

      {/* ── HERO ── */}
      <div style={{ position:"relative", padding:"60px 24px 48px", textAlign:"center", background:"linear-gradient(180deg, #0A1F14 0%, #080C09 100%)" }}>
        {/* Ambient glow */}
        <div style={{ position:"absolute", top:0, left:"50%", transform:"translateX(-50%)", width:400, height:400, background:"radial-gradient(circle, rgba(124,158,135,0.18) 0%, transparent 70%)", pointerEvents:"none" }} />

        {/* Badge */}
        <div className="seo-fade" style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(124,158,135,0.15)", border:"1px solid rgba(124,158,135,0.35)", borderRadius:20, padding:"5px 14px", marginBottom:20, fontSize:12, color:"#A8C9B4" }}>
          <span>⭐</span> Rated 4.9 · India&apos;s #1 Yoga App
        </div>

        {/* H1 — most important SEO element */}
        <h1 className="seo-fade" style={{ fontSize:36, fontWeight:800, lineHeight:1.2, margin:"0 0 16px", animation:"fadeUp 0.7s 0.1s ease both" }}>
          Your Daily Yoga &amp;<br />
          <span style={{ background:"linear-gradient(90deg, #7C9E87, #A8C9B4)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
            Wellness Journey
          </span>
        </h1>

        <p className="seo-fade" style={{ fontSize:16, color:"rgba(255,255,255,0.65)", maxWidth:320, margin:"0 auto 28px", lineHeight:1.7, animation:"fadeUp 0.7s 0.2s ease both" }}>
          Guided yoga, pilates, tai chi &amp; breathing sessions with animated pose demos. Built for beginners in India.
        </p>

        {/* Stats row */}
        <div style={{ display:"flex", justifyContent:"center", gap:24, marginBottom:32, animation:"fadeUp 0.7s 0.3s ease both" }}>
          {[["10,000+","Active Users"], ["5","Class Types"], ["₹299","Per Month"]].map(([n,l]) => (
            <div key={l} style={{ textAlign:"center" }}>
              <div style={{ fontSize:20, fontWeight:800, color:"#7C9E87" }}>{n}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:2 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* CTA button */}
        <button className="seo-btn-primary" onClick={onGetStarted}
          style={{ background:"linear-gradient(135deg, #7C9E87, #56806A)", border:"none", color:"white", padding:"16px 40px", borderRadius:30, fontSize:17, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"block", margin:"0 auto 14px", width:"100%", maxWidth:300 }}>
          🧘 Start Free 3-Day Trial
        </button>
        <p style={{ color:"rgba(255,255,255,0.35)", fontSize:12, margin:0 }}>No credit card needed · Cancel anytime</p>
      </div>

      {/* ── YOGA CLASSES — H2 ── */}
      <div style={{ padding:"48px 20px 0" }}>
        <h2 style={{ fontSize:22, fontWeight:800, textAlign:"center", marginBottom:6 }}>
          5 Expert-Led Wellness Classes
        </h2>
        <p style={{ textAlign:"center", color:"rgba(255,255,255,0.45)", fontSize:14, marginBottom:24, marginTop:0 }}>
          Yoga for beginners to advanced · Pilates · Tai Chi · Stress Relief
        </p>

        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {features.map((f, i) => (
            <article key={f.title} className="seo-card"
              style={{ background:"rgba(22,32,26,0.85)", border:"1px solid rgba(124,158,135,0.15)", borderRadius:18, padding:"16px 18px", display:"flex", gap:14, alignItems:"flex-start" }}>
              <div style={{ fontSize:28, lineHeight:1, flexShrink:0 }}><Ico a="float" d={`${i*0.15}s`}>{f.emoji}</Ico></div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  {/* H3 for each class name */}
                  <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"white" }}>{f.title}</h3>
                  <span style={{ fontSize:10, background:"rgba(124,158,135,0.2)", color:"#7C9E87", borderRadius:8, padding:"2px 8px" }}>{f.tag}</span>
                </div>
                <p style={{ margin:0, fontSize:12.5, color:"rgba(255,255,255,0.55)", lineHeight:1.55 }}>{f.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* ── WHY ZENFLOW — H2 ── */}
      <div style={{ padding:"48px 20px 0" }}>
        <h2 style={{ fontSize:22, fontWeight:800, textAlign:"center", marginBottom:24 }}>
          Why Choose CalmFit?
        </h2>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          {[
            ["🎯","Animated GIF pose demos so you get the form right every time"],
            ["🔥","Daily streak tracker to build a lasting habit"],
            ["🎵","Nature sounds — rain, ocean, forest, waterfall"],
            ["🌍","Works worldwide — INR, USD, EUR, GBP & more"],
            ["📱","No app install needed — open in your browser"],
            ["⏱","Sessions as short as 20 min for busy schedules"],
          ].map(([em, txt]) => (
            <div key={txt} className="seo-card"
              style={{ background:"rgba(22,32,26,0.7)", border:"1px solid rgba(124,158,135,0.1)", borderRadius:16, padding:"16px 14px", textAlign:"center" }}>
              <div style={{ fontSize:24, marginBottom:8 }}>{em}</div>
              <p style={{ margin:0, fontSize:11.5, color:"rgba(255,255,255,0.6)", lineHeight:1.5 }}>{txt}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── PRICING — H2 ── */}
      <div style={{ padding:"48px 20px 0" }}>
        <h2 style={{ fontSize:22, fontWeight:800, textAlign:"center", marginBottom:6 }}>Simple Pricing</h2>
        <p style={{ textAlign:"center", color:"rgba(255,255,255,0.45)", fontSize:14, marginBottom:24, marginTop:0 }}>
          Less than a single yoga class in a studio
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {[
            { label:"Monthly", price:"₹299", period:"/month", badge:null, desc:"Full access, cancel anytime" },
            { label:"6 Months", price:"₹799", period:"/6 months", badge:"POPULAR", desc:"Save ₹ vs monthly — best value starter" },
            { label:"Annual", price:"₹1,099", period:"/year", badge:"BEST DEAL", desc:"Just ₹92/month — maximum savings" },
          ].map(p => (
            <div key={p.label} className="seo-card"
              style={{ background: p.badge === "BEST DEAL" ? "rgba(124,158,135,0.12)" : "rgba(22,32,26,0.7)", border:`1px solid ${p.badge ? "rgba(124,158,135,0.4)" : "rgba(124,158,135,0.1)"}`, borderRadius:18, padding:"18px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontWeight:700, fontSize:15 }}>{p.label}</span>
                  {p.badge && <span style={{ fontSize:9, background:"#7C9E87", color:"white", borderRadius:6, padding:"2px 7px", fontWeight:700 }}>{p.badge}</span>}
                </div>
                <div style={{ color:"rgba(255,255,255,0.45)", fontSize:12, marginTop:3 }}>{p.desc}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <span style={{ fontSize:22, fontWeight:800, color:"#A8C9B4" }}>{p.price}</span>
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{p.period}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ — H2 ── */}
      <div style={{ padding:"48px 20px 0" }}>
        <h2 style={{ fontSize:22, fontWeight:800, textAlign:"center", marginBottom:24 }}>
          Frequently Asked Questions
        </h2>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {faqs.map((f, i) => (
            <div key={i} style={{ background:"rgba(22,32,26,0.7)", border:"1px solid rgba(124,158,135,0.12)", borderRadius:16, overflow:"hidden" }}>
              <button onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                style={{ width:"100%", background:"none", border:"none", padding:"16px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer", fontFamily:FONT, color:"white", textAlign:"left" }}>
                {/* H3 for each FAQ question */}
                <h3 style={{ margin:0, fontSize:14, fontWeight:600 }}>{f.q}</h3>
                <span style={{ color:"#7C9E87", fontSize:18, fontWeight:700, flexShrink:0, marginLeft:10 }}>{faqOpen === i ? "−" : "+"}</span>
              </button>
              {faqOpen === i && (
                <div style={{ padding:"0 18px 16px", color:"rgba(255,255,255,0.55)", fontSize:13, lineHeight:1.65 }}>{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── BOTTOM CTA ── */}
      <div style={{ padding:"48px 24px 60px", textAlign:"center" }}>
        <h2 style={{ fontSize:24, fontWeight:800, marginBottom:12 }}>
          Start Your Wellness Journey Today
        </h2>
        <p style={{ color:"rgba(255,255,255,0.5)", fontSize:14, marginBottom:28 }}>
          Join 10,000+ Indians building a daily yoga habit with CalmFit
        </p>
        <button className="seo-btn-primary" onClick={onGetStarted}
          style={{ background:"linear-gradient(135deg, #7C9E87, #56806A)", border:"none", color:"white", padding:"16px 40px", borderRadius:30, fontSize:17, fontWeight:700, cursor:"pointer", fontFamily:FONT, width:"100%", maxWidth:320 }}>
          🧘 Try Free for 3 Days
        </button>
        <p style={{ color:"rgba(255,255,255,0.3)", fontSize:12, marginTop:14 }}>
          Morning yoga · Pilates · Tai chi · Meditation · Breathing
        </p>
        {/* Footer links — keyword anchor text for SEO */}
        <div style={{ marginTop:32, paddingTop:24, borderTop:"1px solid rgba(255,255,255,0.06)", display:"flex", flexWrap:"wrap", justifyContent:"center", gap:"8px 20px" }}>
          {["Yoga for Beginners","Morning Yoga Routine","Pilates Online India","Meditation App","Breathing Exercises","Tai Chi India","Sun Salutation","Stress Relief Yoga"].map(kw => (
            <span key={kw} style={{ fontSize:11, color:"rgba(255,255,255,0.25)" }}>{kw}</span>
          ))}
        </div>
        <p style={{ color:"rgba(255,255,255,0.15)", fontSize:11, marginTop:16 }}>© 2026 CalmFit Wellness · calmfitapp.in</p>
      </div>
    </div>
  );
}

// ─── GLOBAL BACKGROUND ────────────────────────────────────────────────────────
// Beautiful full-screen background image with dark overlay + ambient glow
const BG_IMAGE = "https://images.unsplash.com/photo-1448375240586-882707db888b?w=900&q=75";

function GlobalBackground() {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:-1, overflow:"hidden", pointerEvents:"none" }}>
      {/* Nature image — dark misty forest */}
      <div style={{
        position:"absolute", inset:0,
        backgroundImage:`url('${BG_IMAGE}')`,
        backgroundSize:"cover", backgroundPosition:"center",
        filter:"blur(3px) brightness(0.22) saturate(1.0)",
        transform:"scale(1.12)",
      }}/>
      {/* Warm dark overlay */}
      <div style={{
        position:"absolute", inset:0,
        background:"linear-gradient(180deg, rgba(8,10,8,0.55) 0%, rgba(10,12,8,0.35) 40%, rgba(8,10,8,0.72) 100%)",
      }}/>
      {/* Edge vignette — prevents warm tones from bleeding at viewport border */}
      <div style={{
        position:"absolute", inset:0,
        background:"radial-gradient(ellipse at 50% 50%, transparent 60%, rgba(5,8,5,0.92) 100%)",
      }}/>
      {/* Sage/green ambient glow — top */}
      <div style={{
        position:"absolute", top:-120, left:"15%",
        width:420, height:320, borderRadius:"50%",
        background:`radial-gradient(circle, rgba(127,184,154,0.10) 0%, transparent 70%)`,
      }}/>
      {/* Coral ambient glow — bottom right */}
      <div style={{
        position:"absolute", bottom:-80, right:"5%",
        width:300, height:280, borderRadius:"50%",
        background:`radial-gradient(circle, rgba(232,113,74,0.07) 0%, transparent 70%)`,
      }}/>
      {/* Gold glow — mid left */}
      <div style={{
        position:"absolute", top:"40%", left:-60,
        width:220, height:220, borderRadius:"50%",
        background:`radial-gradient(circle, rgba(212,168,83,0.06) 0%, transparent 70%)`,
      }}/>
    </div>
  );
}

// ─── RATE US PROMPT ───────────────────────────────────────────────────────────
function RatingPrompt({ onDone }) {
  const [stars, setStars] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [step, setStep] = useState("stars"); // "stars" | "thanks" | "store"

  const handleStar = (n) => {
    setStars(n);
    if (n >= 4) {
      setStep("store"); // Happy user → send to store
    } else {
      setStep("thanks"); // Unhappy → thank + collect feedback quietly
    }
    localStorage.setItem("zf_rated", "1");
  };

  // Play Store / App Store links (update with real links when published)
  const PLAY_STORE = "https://play.google.com/store/apps/details?id=in.calmfitapp";
  const APP_STORE  = "https://apps.apple.com/in/app/calmfit/id000000000";

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,0.72)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}
      onClick={e => { if (e.target === e.currentTarget) onDone(); }}>
      <div style={{ width:"100%", maxWidth:430, background:"#12201A", borderRadius:"28px 28px 0 0", padding:"28px 24px 40px", boxShadow:"0 -8px 48px rgba(0,0,0,0.6)" }}>

        {step === "stars" && (<>
          {/* Mascot + header */}
          <div style={{ textAlign:"center", marginBottom:20 }}>
            <div style={{ fontSize:48, marginBottom:8, animation:"floatMascot 3s ease-in-out infinite" }}>🧘‍♀️</div>
            <h2 style={{ margin:"0 0 6px", color:"white", fontSize:20, fontWeight:800, fontFamily:FONT }}>Enjoying CalmFit?</h2>
            <p style={{ margin:0, color:"rgba(255,255,255,0.5)", fontSize:13, fontFamily:FONT }}>
              You've completed {localStorage.getItem("zf_session_count") || 3} sessions — great work! 🌟<br/>
              A quick rating helps others find us.
            </p>
          </div>

          {/* Star picker */}
          <div style={{ display:"flex", justifyContent:"center", gap:10, marginBottom:24 }}>
            {[1,2,3,4,5].map(n => (
              <span key={n}
                onMouseEnter={() => setHovered(n)}
                onMouseLeave={() => setHovered(0)}
                onClick={() => handleStar(n)}
                style={{ fontSize:40, cursor:"pointer", filter:(hovered||stars) >= n ? "none" : "grayscale(1) opacity(0.4)", transition:"all 0.15s", transform:(hovered||stars) >= n ? "scale(1.2)" : "scale(1)" }}>
                ⭐
              </span>
            ))}
          </div>

          <button onClick={onDone}
            style={{ width:"100%", background:"none", border:"none", color:"rgba(255,255,255,0.3)", fontSize:13, fontFamily:FONT, cursor:"pointer", padding:8 }}>
            Maybe later
          </button>
        </>)}

        {step === "thanks" && (<>
          <div style={{ textAlign:"center", padding:"12px 0 20px" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🙏</div>
            <h2 style={{ margin:"0 0 10px", color:"white", fontSize:20, fontWeight:800, fontFamily:FONT }}>Thanks for your honesty</h2>
            <p style={{ margin:"0 0 24px", color:"rgba(255,255,255,0.5)", fontSize:13, fontFamily:FONT, lineHeight:1.6 }}>
              We're always improving. If something felt off, reply to our welcome email — we read every message.
            </p>
            <button onClick={onDone}
              style={{ background:"rgba(124,158,135,0.2)", border:"1px solid rgba(124,158,135,0.4)", color:"#A8C9B4", padding:"12px 32px", borderRadius:20, fontFamily:FONT, fontSize:14, fontWeight:600, cursor:"pointer" }}>
              Got it, thanks 👍
            </button>
          </div>
        </>)}

        {step === "store" && (<>
          <div style={{ textAlign:"center", padding:"8px 0 16px" }}>
            <div style={{ fontSize:44, marginBottom:10 }}>🎉</div>
            <h2 style={{ margin:"0 0 6px", color:"white", fontSize:20, fontWeight:800, fontFamily:FONT }}>You're amazing!</h2>
            <p style={{ margin:"0 0 22px", color:"rgba(255,255,255,0.5)", fontSize:13, fontFamily:FONT, lineHeight:1.6 }}>
              Would you mind leaving a review? It takes 30 seconds and helps thousands of people discover CalmFit.
            </p>
            {/* Store buttons */}
            <a href={PLAY_STORE} target="_blank" rel="noreferrer"
              style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, background:"linear-gradient(135deg,#1a9e43,#14803a)", borderRadius:16, padding:"14px 20px", marginBottom:10, textDecoration:"none" }}>
              <span style={{ fontSize:22 }}>▶</span>
              <div style={{ textAlign:"left" }}>
                <div style={{ color:"rgba(255,255,255,0.7)", fontSize:10, fontFamily:FONT }}>Review on</div>
                <div style={{ color:"white", fontSize:15, fontWeight:700, fontFamily:FONT }}>Google Play Store</div>
              </div>
            </a>
            <a href={APP_STORE} target="_blank" rel="noreferrer"
              style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, background:"linear-gradient(135deg,#555,#333)", borderRadius:16, padding:"14px 20px", marginBottom:16, textDecoration:"none" }}>
              <span style={{ fontSize:22 }}>🍎</span>
              <div style={{ textAlign:"left" }}>
                <div style={{ color:"rgba(255,255,255,0.7)", fontSize:10, fontFamily:FONT }}>Review on</div>
                <div style={{ color:"white", fontSize:15, fontWeight:700, fontFamily:FONT }}>Apple App Store</div>
              </div>
            </a>
            <button onClick={onDone}
              style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", fontSize:13, fontFamily:FONT, cursor:"pointer" }}>
              I already rated it ✓
            </button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ─── DEV MODE BANNER ──────────────────────────────────────────────────────────
// Only rendered when IS_PRODUCTION === false (i.e. npm run dev)
// Gives a clear visual signal that you are in the SIT/test environment
function DevBanner() {
  const [visible, setVisible] = useState(true);
  if (IS_PRODUCTION || !visible) return null;
  const rzpKey = process.env.REACT_APP_RAZORPAY_KEY || "";
  const keyType = rzpKey.startsWith("rzp_live_") ? "⚠️ LIVE KEY" : "rzp_test ✓";
  return (
    <div style={{
      position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
      width: "100%", maxWidth: 430, zIndex: 99999,
      background: "linear-gradient(90deg, #1a0a00 0%, #3d1500 50%, #1a0a00 100%)",
      borderBottom: "2px solid #ff6a00",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "6px 12px", boxShadow: "0 2px 16px rgba(255,106,0,0.45)",
    }}>
      {/* Left: env badge */}
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <div style={{
          background: "#ff6a00", color: "#fff", fontFamily: FONT,
          fontSize: 9, fontWeight: 900, letterSpacing: 1.5,
          padding: "2px 8px", borderRadius: 4,
        }}>DEV MODE</div>
        <div style={{ color:"#ff9a4d", fontFamily:FONT, fontSize:10, fontWeight:700 }}>
          SIT · {APP_ENV}
        </div>
      </div>
      {/* Center: key status */}
      <div style={{ color:"rgba(255,200,150,0.85)", fontFamily:FONT, fontSize:9, textAlign:"center" }}>
        Razorpay: {keyType} · Firestore: dev_*
      </div>
      {/* Right: dismiss */}
      <button onClick={() => setVisible(false)} style={{
        background: "none", border: "1px solid rgba(255,106,0,0.4)",
        color: "rgba(255,180,100,0.7)", fontFamily: FONT, fontSize: 11,
        borderRadius: 4, padding: "1px 7px", cursor: "pointer", lineHeight: 1.6,
      }}>✕</button>
    </div>
  );
}

// ─── FEATURE WALKTHROUGH ──────────────────────────────────────────────────────
function FeatureWalkthrough({ onDone }) {
  const [step, setStep] = useState(0);
  const slides = [
    {
      icon: "lungs", color: "#4A9B7F", bg: "linear-gradient(160deg, #0A1F18 0%, #030608 100%)",
      label: "BREATHE", title: "Guided Breathing", subtitle: "6 science-backed techniques",
      desc: "Box Breathing, 4-7-8, Wim Hof, Nadi Shodhana and more — each with a live animated visualizer and calming bell sounds.",
      features: ["Navy SEAL box breathing", "Dr. Weil's 4-7-8 sleep cure", "Wim Hof power method", "Alternate nostril balance"],
    },
    {
      icon: "leaf", color: "#7B5EA7", bg: "linear-gradient(160deg, #0F0820 0%, #030206 100%)",
      label: "MEDITATE", title: "Deep Meditation", subtitle: "10 guided techniques",
      desc: "From beginner Mindfulness to advanced Vipassana and Yoga Nidra — each with a rotating mandala and optional real voice guidance.",
      features: ["Yoga Nidra yogic sleep", "Chakra energy balancing", "Tibetan Tonglen compassion", "Real voice guidance audio"],
    },
    {
      icon: "dumbbell", color: "#E07840", bg: "linear-gradient(160deg, #1A0C04 0%, #060302 100%)",
      label: "MOVE", title: "Workouts & Yoga", subtitle: "150+ exercises with GIFs",
      desc: "Full body workouts, yoga flows, pilates and tai chi — all with animated GIF guides so you always know exactly what to do.",
      features: ["Beginner to advanced", "Animated GIF for every move", "Yoga for your conditions", "30-day programmes"],
    },
    {
      icon: "chart", color: "#C4A055", bg: "linear-gradient(160deg, #150E03 0%, #060402 100%)",
      label: "TRACK", title: "Progress & Streaks", subtitle: "See your journey grow",
      desc: "Every session logged automatically. Watch your streak build, review your history, and share milestones with friends.",
      features: ["Daily streak tracking", "Full session history", "Calorie & duration stats", "Share your achievements"],
    },
  ];
  const s = slides[step];
  const isLast = step === slides.length - 1;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background: s.bg, display:"flex", flexDirection:"column", alignItems:"center", fontFamily:FONT, transition:"background 0.5s" }}>
      <div style={{ width:"100%", maxWidth:430, flex:1, display:"flex", flexDirection:"column", padding:"60px 28px 40px" }}>

        {/* Progress dots */}
        <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:40 }}>
          {slides.map((_,i) => (
            <div key={i} style={{ height:4, borderRadius:2, background: i === step ? s.color : "rgba(255,255,255,0.15)", width: i === step ? 24 : 8, transition:"all 0.35s" }} />
          ))}
        </div>

        {/* Animated icon */}
        <div style={{ display:"flex", justifyContent:"center", marginBottom:32 }}>
          <div style={{ position:"relative", width:100, height:100, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ position:"absolute", width:100, height:100, borderRadius:"50%", background:`radial-gradient(circle, ${s.color}30 0%, transparent 70%)`, animation:"wk-pulse 2.5s ease-in-out infinite" }} />
            <div style={{ position:"absolute", width:72, height:72, borderRadius:"50%", border:`1px solid ${s.color}40`, animation:"wk-ring 3s ease-in-out infinite" }} />
            <div style={{ width:62, height:62, borderRadius:"50%", background:`${s.color}18`, border:`1.5px solid ${s.color}55`, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:`0 0 28px ${s.color}40` }}>
              <AppIconStyles/><AppIcon name={s.icon} color={s.color} size={30}/>
            </div>
          </div>
        </div>

        {/* Text */}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:10, letterSpacing:4, color:s.color, fontWeight:700, marginBottom:8, textTransform:"uppercase" }}>{s.label}</div>
          <h1 style={{ fontSize:28, fontWeight:800, color:"white", margin:"0 0 6px", letterSpacing:-0.5 }}>{s.title}</h1>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", marginBottom:16 }}>{s.subtitle}</div>
          <p style={{ fontSize:13.5, color:"rgba(255,255,255,0.6)", lineHeight:1.7, margin:0 }}>{s.desc}</p>
        </div>

        {/* Feature pills */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center", marginBottom:"auto" }}>
          {s.features.map((f,i) => (
            <span key={i} style={{ background:`${s.color}15`, border:`1px solid ${s.color}35`, borderRadius:20, padding:"5px 12px", fontSize:11, color:s.color, fontWeight:600 }}>✓ {f}</span>
          ))}
        </div>

        {/* Buttons */}
        <div style={{ marginTop:32, display:"flex", flexDirection:"column", gap:10 }}>
          <button onClick={() => isLast ? onDone() : setStep(s => s + 1)} style={{
            width:"100%", padding:"16px", borderRadius:16, border:"none", cursor:"pointer",
            background:`linear-gradient(135deg, ${s.color}, ${s.color}BB)`,
            color:"white", fontSize:15, fontFamily:FONT, fontWeight:700,
            boxShadow:`0 8px 24px ${s.color}45`, transition:"background 0.4s",
          }}>
            {isLast ? "Let's Begin →" : "Next →"}
          </button>
          {!isLast && (
            <button onClick={onDone} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", fontSize:12, fontFamily:FONT, cursor:"pointer", padding:"8px" }}>
              Skip tour
            </button>
          )}
        </div>
      </div>
      <style>{`
        @keyframes wk-pulse { 0%,100%{transform:scale(1);opacity:0.7} 50%{transform:scale(1.2);opacity:1} }
        @keyframes wk-ring  { 0%,100%{transform:scale(1);opacity:0.4} 50%{transform:scale(1.12);opacity:0.75} }
        @keyframes ai-breathe{0%,100%{transform:scaleX(1) scaleY(1)}50%{transform:scaleX(1.13) scaleY(0.87)}}
        @keyframes ai-fire{0%,100%{transform:scaleY(1) scaleX(1)}33%{transform:scaleY(1.08) scaleX(0.93)}66%{transform:scaleY(0.93) scaleX(1.06)}}
        @keyframes ai-wave{0%,100%{transform:translateX(0)}50%{transform:translateX(-5px)}}
        @keyframes ai-hbeat{0%,80%,100%{transform:scale(1)}15%{transform:scale(1.24)}30%{transform:scale(1)}45%{transform:scale(1.16)}}
        @keyframes ai-swing{0%,100%{transform:rotate(-15deg)}50%{transform:rotate(15deg)}}
        @keyframes ai-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
        @keyframes ai-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.14)}}
        @keyframes ai-twinkle{0%,100%{opacity:0.15;transform:scale(0.6)}50%{opacity:1;transform:scale(1)}}
        @keyframes ai-grow{0%,100%{transform:scaleY(0.5)}50%{transform:scaleY(1)}}
        @keyframes ai-dot1{0%,60%,100%{opacity:0.2;transform:translateY(0)}25%{opacity:1;transform:translateY(-3px)}}
        @keyframes ai-dot2{0%,15%,75%,100%{opacity:0.2;transform:translateY(0)}45%{opacity:1;transform:translateY(-3px)}}
        @keyframes ai-dot3{0%,30%,90%,100%{opacity:0.2;transform:translateY(0)}65%{opacity:1;transform:translateY(-3px)}}
      `}</style>
    </div>
  );
}

// ─── SESSION NUDGE TOAST ──────────────────────────────────────────────────────
function SessionNudge({ nudge }) {
  if (!nudge) return null;
  return (
    <div style={{
      position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)",
      zIndex: 9999, display: "flex", alignItems: "center", gap: 10,
      background: "rgba(16,14,8,0.96)", border: `1.5px solid ${nudge.color}40`,
      borderRadius: 20, padding: "13px 20px", boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${nudge.color}20`,
      backdropFilter: "blur(12px)", maxWidth: 320, animation: "nudgeIn 0.4s cubic-bezier(0.34,1.56,0.64,1)",
      pointerEvents: "none",
    }}>
      <span style={{ fontSize: 26, lineHeight: 1 }}><Ico a="tada">{nudge.emoji}</Ico></span>
      <div>
        <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: nudge.color, marginBottom: 1 }}>
          Session Complete!
        </div>
        <div style={{ fontFamily: FONT, fontSize: 12, color: "rgba(240,235,224,0.8)", lineHeight: 1.4 }}>
          {nudge.msg}
        </div>
      </div>
      <style>{`@keyframes nudgeIn{from{opacity:0;transform:translateX(-50%) translateY(18px) scale(0.92)}to{opacity:1;transform:translateX(-50%) translateY(0) scale(1)}}`}</style>
    </div>
  );
}

// ─── ERROR BOUNDARY ───────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("CalmFit crash:", error, info); }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ minHeight:"100vh", background:"#080906", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32, fontFamily:"'Inter',sans-serif", textAlign:"center" }}>
        <div style={{ fontSize:56, marginBottom:16 }}>🌿</div>
        <h2 style={{ color:"#F0EBE0", fontSize:22, fontWeight:800, margin:"0 0 10px" }}>Something went wrong</h2>
        <p style={{ color:"rgba(240,235,224,0.5)", fontSize:14, lineHeight:1.6, margin:"0 0 28px", maxWidth:300 }}>
          Don't worry — your progress is saved. Tap below to reload and continue your wellness journey.
        </p>
        <button onClick={() => { this.setState({ hasError:false, error:null }); window.location.reload(); }}
          style={{ background:"linear-gradient(135deg,#4A9B7F,#2D7A5F)", color:"white", border:"none", borderRadius:16,
            padding:"14px 32px", fontSize:15, fontWeight:800, cursor:"pointer", boxShadow:"0 8px 24px rgba(74,155,127,0.4)" }}>
          Reload CalmFit
        </button>
        <p style={{ color:"rgba(240,235,224,0.25)", fontSize:11, marginTop:20 }}>
          {this.state.error?.message || "Unknown error"}
        </p>
      </div>
    );
  }
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  // Show SEO landing page on very first visit (before onboarding)
  const [landingSeen, setLandingSeen]   = useState(() => !!localStorage.getItem("zf_landing_seen"));
  const [onboarded, setOnboarded]       = useState(false);
  const [loggedIn, setLoggedIn]         = useState(false);
  const [healthProfileDone, setHealthProfileDone] = useState(() => !!localStorage.getItem("zf_health_profile"));
  const [workoutDeepLink, setWorkoutDeepLink] = useState(null); // { catId, exName } for direct navigation
  const [showPromo, setShowPromo]       = useState(false); // post-login promo splash
  const [showWalkthrough, setShowWalkthrough] = useState(() => !localStorage.getItem("zf_tour_done")); // feature tour
  const [showRating, setShowRating]     = useState(false); // rate-us prompt
  const [sessionNudge, setSessionNudge] = useState(null);  // post-session motivational toast
  const [user, setUser]                 = useState(null);
  const [screen, setScreen]             = useState("home");
  const [subScreen, setSubScreen]       = useState(null); // "favorites"|"schedule"|"notifications"|"help"|"sessions"
  const [isSubscribed, setIsSubscribed] = useState(() => !!localStorage.getItem("cf_premium"));
  const [sessions, setSessions]         = useLocalStorage("zf_sessions", []);
  const [favorites, setFavorites]       = useLocalStorage("zf_favs", []);
  const [schedule, setSchedule]         = useLocalStorage("zf_schedule", {});
  const [firebaseSynced, setFirebaseSynced] = useState(false);

  const userId = user?.uid || user?.name?.toLowerCase().replace(/\s+/g, "_") || "guest";

  // ── Persistent subscription setter ──────────────────────────────────────────
  const handleSetSubscribed = useCallback((val, paymentId) => {
    setIsSubscribed(val);
    if (val) {
      localStorage.setItem("cf_premium", "1");
      fbSavePremiumStatus(userId, paymentId);
      logEvent("purchase_premium", { payment_id: paymentId });
    } else {
      localStorage.removeItem("cf_premium");
    }
  }, [userId]);

  // ── Check Firebase for premium status on login (cross-device sync + expiry) ──
  useEffect(() => {
    if (!user) return;
    // Admin / developer bypass — full premium without payment
    if (isAdminUser(user.email)) {
      setIsSubscribed(true);
      localStorage.setItem("cf_premium", "1");
      return;
    }
    // Always validate with Firestore (handles expiry, new device, cache clears)
    fbLoadPremiumStatus(userId).then(isPrem => {
      if (isPrem) {
        setIsSubscribed(true);
        localStorage.setItem("cf_premium", "1");
      } else {
        // Firestore says not premium or subscription expired — clear local flag
        setIsSubscribed(false);
        localStorage.removeItem("cf_premium");
      }
    });
  }, [user, userId]); // Note: intentionally excludes isSubscribed — must run on every login

  // Sync from Firebase when user logs in
  useEffect(() => {
    if (!user || firebaseSynced) return;
    fbLoadSessions(userId).then(fbSessions => {
      if (fbSessions && fbSessions.length > 0) {
        setSessions(prev => {
          const ids = new Set(prev.map(s => s.id));
          const merged = [...prev, ...fbSessions.filter(s => !ids.has(s.id))];
          merged.sort((a, b) => new Date(b.date) - new Date(a.date));
          return merged;
        });
      }
      setFirebaseSynced(true);
    });
    fbSaveUser(userId, { name: user.name, goal: user.goal, email: user.email || "", joinDate: user.joinDate });
  }, [user, firebaseSynced, userId, setSessions]);

  // ── Notification scheduler ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.notifEnabled || !user?.notifHour) return;
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    const firstName = user.name?.split(" ")[0] || "there";
    let timeoutId;
    const schedule = () => {
      const now = new Date();
      const next = new Date();
      next.setHours(user.notifHour, 0, 0, 0);
      if (next <= now) next.setDate(next.getDate() + 1);
      const delay = next - now;
      timeoutId = setTimeout(() => {
        const msg = NOTIF_MESSAGES[Math.floor(Math.random() * NOTIF_MESSAGES.length)];
        try {
          new Notification(msg.title.replace(/\{n\}/g, firstName), {
            body: msg.body, icon: "/favicon.ico", badge: "/favicon.ico",
          });
        } catch(_) {}
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timeoutId);
  }, [user?.notifEnabled, user?.notifHour, user?.name]);

  const handleLogSession = useCallback((session) => {
    setSessions(prev => {
      const updated = [session, ...prev];
      // Track total session count for the rating prompt message
      localStorage.setItem("zf_session_count", updated.length);
      // Trigger Rate Us prompt at exactly 3rd session (and only if never rated)
      if (updated.length === 3 && !localStorage.getItem("zf_rated")) {
        setTimeout(() => setShowRating(true), 1800);
      }
      // ── Post-session motivational nudge ───────────────────────────────────
      const nudgesByType = {
        workout: [
          { emoji:"💪", msg:"Crushed it! Every rep is progress.", color:"#E8714A" },
          { emoji:"🔥", msg:"Strength builds one session at a time.", color:"#E8714A" },
          { emoji:"⚡", msg:"Your future self thanks you for this.", color:"#D4A853" },
        ],
        meditation: [
          { emoji:"🧘", msg:"Your mind is clearer already. Well done.", color:"#9B8EC5" },
          { emoji:"🌙", msg:"Peace isn't found — it's practiced. Good work.", color:"#9B8EC5" },
          { emoji:"✨", msg:"5 minutes of stillness changes your whole day.", color:"#7FB89A" },
        ],
        breathing: [
          { emoji:"🌬️", msg:"Breathwork done! Your nervous system loves you.", color:"#5BA3D9" },
          { emoji:"💙", msg:"Calm is a superpower. You just used it.", color:"#5BA3D9" },
          { emoji:"🌊", msg:"That was exactly what your body needed.", color:"#7FB89A" },
        ],
        yoga: [
          { emoji:"🌿", msg:"Flow complete! Flexibility is strength too.", color:"#7FB89A" },
          { emoji:"🙏", msg:"You showed up for yourself today. That matters.", color:"#7FB89A" },
          { emoji:"☀️", msg:"Great session! Consistency is your superpower.", color:"#D4A853" },
        ],
      };
      const pool = nudgesByType[session.type] || [
        { emoji:"🌟", msg:"Session logged! Keep the momentum going.", color:"#D4A853" },
        { emoji:"✅", msg:"Done! Every session makes you stronger.", color:"#7FB89A" },
      ];
      const nudge = pool[Math.floor(Math.random() * pool.length)];
      // Show streak bonus nudge if 3+ day streak
      const today = new Date().toDateString();
      const recentDates = updated.slice(0, 10).map(s => new Date(s.date).toDateString());
      const uniqueDays = [...new Set(recentDates)];
      if (uniqueDays.length >= 3 && uniqueDays[0] === today) {
        setTimeout(() => setSessionNudge({ emoji:"🔥", msg:`${uniqueDays.length}-session streak! You're on fire!`, color:"#E8714A" }), 600);
      } else {
        setTimeout(() => setSessionNudge(nudge), 600);
      }
      setTimeout(() => setSessionNudge(null), 4200);
      return updated;
    });
    fbSaveSession(userId, session);
    // Firebase Analytics — session completion event
    logEvent("session_complete", {
      session_type: session.type,
      session_title: session.title,
      calories: session.calories,
    });
  }, [userId, setSessions]);

  const handleToggleFav = useCallback((name) => {
    setFavorites(prev => {
      const next = prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name];
      fbSaveFavorites(userId, next);
      return next;
    });
  }, [userId, setFavorites]);

  const handleLogin = (userData) => {
    const u = { ...userData, joinDate: userData.joinDate || new Date().toISOString() };
    // Merge any existing health profile data into user
    try {
      const savedProfile = JSON.parse(localStorage.getItem("zf_health_profile") || "null");
      if (savedProfile) Object.assign(u, savedProfile);
    } catch(_) {}
    setUser(u);
    setLoggedIn(true);
    setOnboarded(true);
    // Show promo splash once per device (skip if already seen or already subscribed)
    if (!localStorage.getItem("zf_promo_seen") && !isSubscribed) setShowPromo(true);
  };

  // ── Dynamic SEO: update title + meta on every screen change ──
  useSEO(screen, subScreen);

  // ── Firebase Analytics: log screen_view on every screen change ──────────────
  useEffect(() => {
    if (!user) return;
    logEvent("screen_view", { screen_name: screen, user_id: userId });
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SEO Landing Page — shown ONCE on very first visit ──
  if (!landingSeen) return (
    <SEOLandingPage onGetStarted={() => {
      localStorage.setItem("zf_landing_seen", "1");
      setLandingSeen(true);
    }} />
  );

  // ── Login comes FIRST — before personalization ──
  if (!loggedIn) return (<><GlobalBackground /><LoginScreen onLogin={handleLogin} /></>);

  // ── Health Profile — collected once after login ──
  if (!healthProfileDone) return (
    <><GlobalBackground /><HealthProfileScreen user={user} onComplete={profileData => {
      // Merge health profile data into user state
      setUser(prev => ({ ...prev, ...profileData }));
      setHealthProfileDone(true);
      setOnboarded(true);
    }} /></>
  );

  // ── Feature Walkthrough — shown once after health profile ──
  if (showWalkthrough) return (
    <><GlobalBackground /><FeatureWalkthrough onDone={() => {
      localStorage.setItem("zf_tour_done", "1");
      setShowWalkthrough(false);
    }} /></>
  );

  // ── Post-login Promo Splash (shown once) ──
  if (showPromo && !isSubscribed) return (
    <>
      <GlobalBackground />
      <div style={{ maxWidth:430, margin:"0 auto", minHeight:"100vh", position:"relative", overflowX:"hidden" }}>
        <PromoSplashScreen
          user={user}
          onContinue={() => setShowPromo(false)}
          onSubscribeClick={() => { setShowPromo(false); setScreen("profile"); }}
        />
        <style>{`*{-webkit-tap-highlight-color:transparent;box-sizing:border-box;}html,body{background:#080906;margin:0;padding:0;}
          @keyframes starSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
          @keyframes floatMascot{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
          @keyframes badgePulse{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}
          @keyframes sPickerIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
        `}</style>
      </div>
    </>
  );

  const trialStatus = getTrialStatus(user.joinDate);
  // Freemium model: no hard paywall — users always have app access, just with locked premium features
  const hasAccess   = true; // eslint-disable-line no-unused-vars

  const wrap = (child) => (
    <>
      <GlobalBackground />
      <DevBanner />
      <div style={{ maxWidth: 430, margin: "0 auto", background: "transparent", minHeight: "100vh", position: "relative", overflowX: "hidden",
        paddingTop: IS_PRODUCTION ? 0 : 34
      }}>
        {child}
        <style>{`*{-webkit-tap-highlight-color:transparent;box-sizing:border-box;}`}</style>
      </div>
    </>
  );

  // ── Sub-screens (profile sub-pages) ──
  if (subScreen === "sessions")      return wrap(<SessionsScreen sessions={sessions} onBack={() => setSubScreen(null)} />);
  if (subScreen === "favorites")     return wrap(<FavoritesScreen favorites={favorites} onBack={() => setSubScreen(null)} onRemove={handleToggleFav} />);
  if (subScreen === "schedule")      return wrap(<ScheduleScreen schedule={schedule} onSave={setSchedule} onBack={() => setSubScreen(null)} userId={userId} />);
  if (subScreen === "notifications") return wrap(<NotificationsScreen onBack={() => setSubScreen(null)} />);
  if (subScreen === "help")          return wrap(<HelpScreen onBack={() => setSubScreen(null)} />);

  return (
    <ErrorBoundary>
    <>
      <GlobalBackground />
      <DevBanner />
      <div style={{ maxWidth: 430, margin: "0 auto", background: "transparent", minHeight: "100vh", position: "relative", overflowX: "hidden",
        paddingTop: IS_PRODUCTION ? 0 : 34  /* push content below DEV banner in dev mode */
      }}>
        {screen === "home"      && <HomeScreen user={user} setScreen={setScreen} trialStatus={trialStatus} isSubscribed={isSubscribed} sessions={sessions} onNavigateToExercise={(catId, exName) => { setWorkoutDeepLink({ catId, exName }); setScreen("workout"); }} onConditionChange={(newCondition) => {
          setUser(prev => {
            const updated = { ...prev, condition: newCondition };
            // Persist to localStorage
            try {
              const saved = JSON.parse(localStorage.getItem("zf_health_profile") || "{}");
              localStorage.setItem("zf_health_profile", JSON.stringify({ ...saved, condition: newCondition }));
            } catch(_) {}
            return updated;
          });
        }} />}
        {screen === "challenge" && <ChallengeScreen onBack={() => setScreen("home")} setScreen={setScreen} isSubscribed={isSubscribed} onUpgrade={() => setScreen("profile")} />}
        {screen === "breathe"  && <BreathingScreen onBack={() => setScreen("home")} onLogSession={handleLogSession} isSubscribed={isSubscribed} onUpgrade={() => setScreen("profile")} />}
        {screen === "meditate" && <MeditationScreen onBack={() => setScreen("home")} onLogSession={handleLogSession} isSubscribed={isSubscribed} onUpgrade={() => setScreen("profile")} />}
        {screen === "explore"  && <ExploreScreen onLogSession={handleLogSession} isSubscribed={isSubscribed} onUpgrade={() => setScreen("profile")} />}
        {screen === "workout" && <WorkoutScreen setScreen={setScreen} trialExpired={!hasAccess} onLogSession={handleLogSession} onToggleFav={handleToggleFav} favorites={favorites} deepLink={workoutDeepLink} onDeepLinkConsumed={() => setWorkoutDeepLink(null)} isSubscribed={isSubscribed} onUpgrade={() => setScreen("profile")} />}
        {screen === "progress"&& <ProgressScreen sessions={sessions} onViewSessions={() => setSubScreen("sessions")} />}
        {screen === "profile" && (
          <ProfileScreen
            user={user} isSubscribed={isSubscribed} setIsSubscribed={handleSetSubscribed}
            trialStatus={trialStatus} onNavigate={setSubScreen}
            onLogout={() => { fbSignOut(); setLoggedIn(false); setUser(null); setOnboarded(false); setFirebaseSynced(false); }}
          />
        )}
        <BottomNav active={screen} setActive={setScreen} />
        {screen !== "breathe" && screen !== "meditate" && <AIChatCoach user={user} sessions={sessions} setScreen={setScreen} />}
        {/* Rate Us prompt — slides up after 3rd completed session */}
        {showRating && <RatingPrompt onDone={() => setShowRating(false)} />}
        {/* Post-session motivational nudge toast */}
        <SessionNudge nudge={sessionNudge} />
        <style>{`
          @keyframes wave{0%{transform:scaleY(0.3)}100%{transform:scaleY(1)}}
          *{-webkit-tap-highlight-color:transparent;box-sizing:border-box;}
          html,body{background:#080906;margin:0;padding:0;overscroll-behavior:none;-webkit-overflow-scrolling:touch;}
          html{height:-webkit-fill-available;}
          input,textarea,select{font-size:16px !important;}
          button{touch-action:manipulation;}
          img{-webkit-user-drag:none;user-select:none;}
          @keyframes chatSlideIn{from{transform:translateX(-50%) translateY(100%)}to{transform:translateX(-50%) translateY(0)}}
          @keyframes promoBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
          @keyframes slideDown{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        `}</style>
        <IcoStyles/>
      </div>
    </>
    </ErrorBoundary>
  );
}
